import { Application, Assets, BlurFilter, Container, FillGradient, Graphics, Rectangle, RenderTexture, Sprite, Text, Texture } from 'pixi.js';
import { gameConfig } from './config/gameConfig.js';
import { AnimationController } from './animation/AnimationController.js';
import { loadSpineAssets } from './rendering/spineLoader.js';
import { SlotMachine } from './components/SlotMachine.js';
import {
  getCelebrationSequenceDurationSec,
  winCelebrationMinMultiple,
  winMultipleToTier,
} from './components/WinCelebrationOverlay.js';
import { DebugMode } from './debug/DebugMode.js';
import { exchangeOneTimeToken } from './api/launcherSession.js';
import { ShiningCrownService } from './api/shiningCrownService.js';
import { ActionStatus, decodePaytable, toMatrix } from './api/hubResultDecoder.js';
import { translationStore } from './features/i18n/translationStore.js';
import { getParamsFromUrl, loadTranslations } from './features/i18n/loadTranslations.js';
import {
  formatCurrencyAmount,
  formatCurrencyDigits,
  formatCurrencyRulesLabel,
  normalizeCurrencyCode,
} from './lib/formatCurrency.js';
import { isPrimaryPointerAction } from './utils/pointerPrimary.js';
import { assetUrl } from './utils/assetUrl.js';
import { createArcTextController } from './utils/arcTextPixi.js';
import { installGameIdleReload } from './utils/gameIdleReload.js';
import { createLandscapeOrientationBlocker } from './utils/landscapeOrientationBlocker.js';
import { installHubConnectionMonitor } from './utils/hubConnectionMonitor.js';
import {
  clearRoundStatus,
  loadRoundStatus,
  roundStatusStorageKey,
  saveRoundStatus,
} from './utils/gameRoundStatusStore.js';
import { sound } from './utils/soundManager.js';
import {
  createQuickBetChipPixi,
  fontSizeWhenFormattedLong,
} from './components/quickBetChipPixi.js';

/** Wait before connection-lost modal (avoids flash on brief reconnect). */
const CONNECTION_LOST_GRACE_MS = 4000;
/** Airplane mode / `offline` — show error quickly. */
const CONNECTION_OFFLINE_GRACE_MS = 800;
/** How long the “connection restored” toast stays visible. */
const CONNECTION_RESTORED_TOAST_MS = 3500;
/** After this long in background, force hub restart on return (iOS stale socket). */
const IOS_BACKGROUND_RESTART_MS = 2500;
/** Wait for ForceDisconnect after hub close before classifying disconnect (ms). */
const FORCE_DISCONNECT_WAIT_MS = 2000;
/** Ignore hub close/reconnect flaps right after a successful bootstrap (ms). */
const POST_BOOT_HUB_GRACE_MS = 8000;
/** Auto-retry hub ping while the connection-lost modal is open (ms). */
const CONNECTION_AUTO_RETRY_MS = 5000;
/** Shared across tabs — only the latest instance may show session-ended. */
const SESSION_INSTANCE_LS_KEY = 'shiningCrownLatestSession';
/** Fallback round-status key when partner/game params are unavailable during flush. */
const ROUND_STATUS_FALLBACK_KEY = 'scRoundStatus:last';

function claimLatestSessionInstance() {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SESSION_INSTANCE_LS_KEY, id);
    }
  } catch (_) {
    /* ignore */
  }
  return id;
}

function relinquishSessionInstance(id) {
  if (!id) return;
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(SESSION_INSTANCE_LS_KEY) === id) {
      localStorage.removeItem(SESSION_INSTANCE_LS_KEY);
    }
  } catch (_) {
    /* ignore */
  }
}

function isLatestSessionInstance(id) {
  if (!id) return true;
  try {
    if (typeof localStorage === 'undefined') return true;
    const latest = localStorage.getItem(SESSION_INSTANCE_LS_KEY);
    return !latest || latest === id;
  } catch (_) {
    return true;
  }
}
/** Connection-lost modal — same chrome as settings ({@link SETTINGS_PANEL_DESKTOP}). */
const CONNECTION_LOST_PANEL_DESKTOP_W = 520;
const CONNECTION_LOST_MODAL_SCALE_DESKTOP = 1.2;
const CONNECTION_LOST_MARGIN_CSS_COMPACT = 16;
const CONNECTION_LOST_MARGIN_CSS_DESKTOP = 32;
const CONNECTION_LOST_PANEL_MIN_W = 280;

/** Loading screen — 1922×1080 design space; background uses cover fit + blur. */
const LOADING_DESIGN_W = 1922;
const LOADING_DESIGN_H = 1080;
const LOADING_BLUR_STRENGTH = 8;
/** Extra bleed so blur filter does not expose empty edges. */
const LOADING_BG_BLEED = 48;
/** Desktop progress bar cap (CSS px); mobile/tablet scale from viewport width. */
const LOADING_BAR_W_MAX = 420;
const LOADING_BAR_H_MAX = 14;
/** Minimum time the loading overlay stays visible (ms). */
const LOADING_MIN_DISPLAY_MS = 3000;

function readWebGlRenderLimit() {
  if (typeof document === 'undefined') return Infinity;
  const canvas = document.createElement('canvas');
  const gl =
    canvas.getContext('webgl2') ||
    canvas.getContext('webgl') ||
    canvas.getContext('experimental-webgl');
  if (!gl) return Infinity;
  const maxTexture = gl.getParameter(gl.MAX_TEXTURE_SIZE) || Infinity;
  const maxRenderbuffer = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) || maxTexture;
  const loseContext = gl.getExtension('WEBGL_lose_context');
  loseContext?.loseContext();
  return Math.min(maxTexture, maxRenderbuffer);
}

function getPixiRendererProfile() {
  const dpr = Math.max(1, Number(window.devicePixelRatio) || 1);
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isIOS =
    /iP(hone|ad|od)/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isTouch = window.matchMedia?.('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
  const deviceMemory = Number(navigator.deviceMemory) || Infinity;
  const lowMemory = deviceMemory <= 4;
  const maxRenderLimit = readWebGlRenderLimit();
  const designW = 1922;
  const designH = 1080;
  const limitCap = Number.isFinite(maxRenderLimit)
    ? Math.max(1, Math.min(maxRenderLimit / designW, maxRenderLimit / designH) * 0.9)
    : Infinity;

  let resolutionCap = 2;
  let antialias = true;
  let powerPreference = 'high-performance';

  if (isAndroid && isTouch) {
    // Adreno 610 / Mali low-end Android phones can freeze the entire browser
    // process with a 3844x2160 MSAA framebuffer. 1.5x is still oversampled on
    // 1080px-wide phones after the 1922px canvas is scaled down.
    resolutionCap = lowMemory ? 1.35 : 1.5;
    antialias = false;
    powerPreference = 'default';
  } else if (isIOS && isTouch) {
    // Safari WebGL + Spine + masked reels: cap DPR and skip MSAA to reduce frame spikes
    // during deceleration without a visible layout change (canvas still CSS-scaled).
    resolutionCap = Math.min(resolutionCap, 1.75);
    antialias = false;
    powerPreference = 'default';
  } else if (isTouch && lowMemory) {
    resolutionCap = 1.5;
    antialias = false;
    powerPreference = 'default';
  }

  return {
    antialias,
    powerPreference,
    resolution: Math.min(dpr, resolutionCap, limitCap),
  };
}

/** Horizontal nudge for BALANCE vs LAST WIN columns (portrait merged bar + desktop stat panels). */
const CANVAS_STAT_SHIFT_BALANCE_X = -40;
const CANVAS_STAT_SHIFT_LAST_WIN_X = 40;

/** Portrait merged-bar centre prompt (two lines; matches UILayer pattern). */
const PORTRAIT_BET_PROMPT_LINES = 'PLEASE PLACE\nYOUR BET';
/** Logical height of merged BALANCE | prompt | LAST WIN bar — keep in sync everywhere `portraitTopStatsH` is set. */
const PORTRAIT_MERGED_STATS_BAR_H = 124;
/** Background sprite height only (shorter than {@link PORTRAIT_MERGED_STATS_BAR_H}); slots/prompt layout unchanged. */
const PORTRAIT_MERGED_STATS_BG_HEIGHT = 95;
const PORTRAIT_BET_PROMPT_FONT_SIZE = 26;
const PORTRAIT_BET_PROMPT_LINE_HEIGHT = 32;
/** Portrait-phone HUD scale (merged balance bar, quick bets, spin/autoplay/bet row). */
const MOBILE_HUD_FIT_TARGET = 1.20;
const MOBILE_HUD_FIT_MIN = 0.62;
const MOBILE_HUD_FIT_MAX = 1.24;
/** Quick-bet chip design size at hudFit = 1 (before {@link MOBILE_HUD_FIT_TARGET} multiplier). */
const MOBILE_QB_CHIP_REF_W = 112;
const MOBILE_QB_CHIP_REF_H = 104;
/** SPIN / autoplay / bet row scale base (multiplied by {@link GameApp#_getMobileHudFit}). */
const MOBILE_CONTROL_SCALE_BASE = 2.06;
/** Desktop spin diameter the autoplay-remain label was tuned for. */
const SPIN_BTN_REF_SIZE = 194;
/** Autoplay rounds counter on the stop button (desktop design px). */
const SPIN_AUTOPLAY_REMAIN_DESKTOP = { x: 5, y: -6, fontSize: 26, fontInf: 42 };
/** Portrait phone nudge for {@link SPIN_AUTOPLAY_REMAIN_DESKTOP} (+X right, −Y up). */
const SPIN_AUTOPLAY_REMAIN_MOBILE_NUDGE = { x: 6, y: -12 };
/** Portrait-phone cabinet lift at max width (760px); extra applied as width → 320px. Negative = up. */
const MOBILE_GAME_VISUAL_LIFT_BASE = -115;
const MOBILE_GAME_VISUAL_LIFT_EXTRA_NARROW = -48;
const MOBILE_PORTRAIT_WORLD_OFFSET_NARROW = -34;
const MOBILE_SLOT_ROOT_UP_BIAS_NARROW = 28;
/** Portrait stacked HUD: keep corner icons (home / sound / menu) inside the 1080px design stage. */
const PORTRAIT_DESIGN_H = 1080;
const PORTRAIT_CORNER_ICONS_BOTTOM_PAD = 10;

/**
 * Figma mobile bet modal (Frame 207) — uniform scale to viewport; desktop path ignores these.
 * Grid Frame 132: 338×372, gap 12, square cells 58×58 (chip art uses uniform scale, no stretch).
 */
const MOBILE_BET_MODAL_REF = { w: 370, h: 474 };
const MOBILE_BET_MODAL_GRID_INNER_W = 338;
const MOBILE_BET_MODAL_GRID_INNER_H = 372;
const MOBILE_BET_CHIP_REF_W = 58;
const MOBILE_BET_CHIP_REF_H = 58;
const MOBILE_BET_CHIP_GAP_REF = 12;
const MOBILE_BET_MODAL_TITLE_FS = 18;
const MOBILE_BET_MODAL_GRID_INSET_X = 16;
const MOBILE_BET_MODAL_TITLE_TOP = 21;
const MOBILE_BET_MODAL_GRID_TOP = 65;
const MOBILE_BET_MODAL_GRID_BOTTOM = 37;
const MOBILE_BET_MODAL_CORNER_R = 20;
/** Portrait phone: CSS margin from viewport edges when centering bet modal. */
const MOBILE_BET_MODAL_VIEWPORT_MARGIN_CSS = 12;
const MOBILE_BET_MODAL_CLOSE_TOP_REF = 20;
const MOBILE_BET_MODAL_CLOSE_RIGHT_REF = 24;
const MOBILE_BET_MODAL_CLOSE_SIZE_REF = 24;

/**
 * Figma tablet bet modal (Frame 207) — 666×633; grid 602×490; square chips 74×74; gap 14.
 */
const TABLET_BET_MODAL_REF = { w: 666, h: 633 };
const TABLET_BET_MODAL_GRID_INNER_W = 602;
const TABLET_BET_MODAL_GRID_INNER_H = 490;
const TABLET_BET_CHIP_REF_W = 74;
const TABLET_BET_CHIP_REF_H = 74;
const TABLET_BET_CHIP_GAP_REF = 14;
const TABLET_BET_MODAL_TITLE_FS = 24;
const TABLET_BET_MODAL_TITLE_TOP = 40;
/** Title block shifted +4.5px from horizontal center (Figma). */
const TABLET_BET_MODAL_TITLE_CENTER_NUDGE_X = 4.5;
const TABLET_BET_MODAL_GRID_TOP = 104;
const TABLET_BET_MODAL_GRID_BOTTOM = 39;
const TABLET_BET_MODAL_CORNER_R = 20;
/** Panel root nudge in ref px (positive = move modal down). Figma had −33; increased so tablet sits lower. */
const TABLET_BET_MODAL_OFFSET_Y_REF = 78;
const TABLET_BET_MODAL_CLOSE_TOP_REF = 23;
const TABLET_BET_MODAL_CLOSE_RIGHT_REF = 28;
const TABLET_BET_MODAL_CLOSE_SIZE_REF = 36;

/** Figma desktop Frame 207 (900×706): scale {@link DESKTOP_BET_MODAL_CORNER_R} with panel width. */
const DESKTOP_BET_MODAL_CORNER_REF_W = 900;
const DESKTOP_BET_MODAL_CORNER_R = 20;
/** CSS `border-image`: linear-gradient(360deg, #692416 0%, #CF472B 100%) — dark at bottom, orange at top. */
const BET_POPUP_BORDER_GRADIENT = new FillGradient({
  type: 'linear',
  start: { x: 0, y: 1 },
  end: { x: 0, y: 0 },
  textureSpace: 'local',
  colorStops: [
    { offset: 0, color: '#692416' },
    { offset: 1, color: '#CF472B' },
  ],
});

/**
 * Portrait canvas HUD — tweak here; landscape/desktop uses {@link GameApp#_canvasDesktopLayout} snapshot.
 * Phone = narrow portrait (width ≤ 760). Tablet = taller portrait tablets.
 */
const PORTRAIT_TUNING = {
  tablet: { gameToHudGap: 14, spinRowNudgeY: 0 },
  phone: {
    gameToHudGapBase: 22,
    gameToHudGapSpacingBiasMul: 8,
    /** Entire autoplay / SPIN / bet / turbo row; negative = up, positive = down (mobile only). */
    spinRowNudgeY: -38,
    /** Portrait phone quick-bet strip: shift left (logical px). */
    quickBetShiftLeftPx: 20,
    /** Merged BALANCE | prompt | LAST WIN bar; positive = down (mobile only). */
    statsBarNudgeY: 14,
    /** Home / sound / menu inset from the visible frame edge (not toward reel center). */
    cornerIconEdgePad: 14,
    /** Small inward nudge from frame corners (+X = toward center, mobile only). */
    cornerIconTowardCenter: 25,
  },
};

/** Settings modal panel size in design px (before the modal’s uniform {@link GameApp#_createSettingsModalPixi} SCALE). */
const SETTINGS_PANEL_DESKTOP = { w: 666, h: 580 };
/**
 * Portrait-phone settings shell reference (design px at modalFit = 1).
 * Final size comes from {@link GameApp#_getSettingsPanelCompactMetrics} so width/height
 * keep the same proportions on every device.
 */
const SETTINGS_PANEL_MOBILE = { w: 390, h: 850 };
/** Target share of the CSS viewport used by the rendered panel (after SCALE + shell correction). */
/** Rendered width ≈ cw × this value (height often binds modalFit — see {@link GameApp#_getSettingsPanelCompactMetrics}). */
const SETTINGS_PANEL_MOBILE_VIEWPORT_W = 0.99;
const SETTINGS_PANEL_MOBILE_VIEWPORT_H = 0.95;
/** Portrait tablet uses the narrowed desktop shell with slightly smaller viewport targets. */
const SETTINGS_PANEL_TABLET_VIEWPORT_W = 0.9;
const SETTINGS_PANEL_TABLET_VIEWPORT_H = 0.8;
/** Uniform modalFit clamp for compact (phone/tablet) layouts. */
const SETTINGS_PANEL_COMPACT_FIT_MIN = 0.72;
const SETTINGS_PANEL_COMPACT_FIT_MAX = 1.28;
/** Uniform panel up-scale applied in {@link GameApp#_createSettingsModalPixi}. */
const SETTINGS_MODAL_PANEL_SCALE = 1.2;
/** CSS viewport width where legacy width-only fit = 1 (connection-lost modal). */
const SETTINGS_PANEL_MOBILE_REF_CW = 390;
/** Figma-width baseline for horizontal proportions (tab icons, switches block). */
const SETTINGS_PANEL_MOBILE_LAYOUT_REF_W = 350;
/** Height baseline matching {@link SETTINGS_PANEL_MOBILE.h} for vertical proportions. */
const SETTINGS_PANEL_MOBILE_LAYOUT_REF_H = 850;
/** Distance from panel bottom to the horizontal rule above the tab row (520 − 463). */
const SETTINGS_PANEL_MOBILE_BOTTOM_GAP = 57;
/** Extra Y offset for the active-tab underline below tab icons (+Y = lower). */
const SETTINGS_ACTIVE_TAB_LINE_Y_NUDGE = 6;
/** Default square slot for settings-modal tab icons (settings / autoplay / info). */
const SETTINGS_TAB_ICON_SIZE = 24;

/**
 * Fit a tab icon inside a square slot without stretching (settings.svg is 21×20, others 24×24).
 * @param {import('pixi.js').Sprite} sprite
 * @param {number} boxSize
 */
function fitSettingsTabIcon(sprite, boxSize) {
  const tex = sprite?.texture;
  const tw = tex?.width > 0 ? tex.width : boxSize;
  const th = tex?.height > 0 ? tex.height : boxSize;
  const k = Math.min(boxSize / tw, boxSize / th);
  sprite.width = tw * k;
  sprite.height = th * k;
}
/**
 * Push the mobile settings modal down on the stage at a 1080px-tall viewport (+Y = lower).
 * Scaled by actual viewport height in {@link GameApp#_getSettingsPanelCompactMetrics}.
 */
const SETTINGS_PANEL_MOBILE_ROOT_OFFSET_Y = 180;
/** Gap (stage px, H=1080) between the bottom edge of the settings panel and the bottom of the stage on portrait mobile. */
const SETTINGS_PANEL_MOBILE_BOTTOM_STAGE_GAP = 0;

/** Autoplay tab layout widths (design px) — use actual sizes when centring, not rx multiples. */
const AUTOPLAY_CHIPS_TOTAL_W = 309;
const AUTOPLAY_SLIDER_ROW_W = 348;
const AUTOPLAY_PLAY_BTN_W = 88;
const AUTOPLAY_BALANCE_BLOCK_W = 82;
const AUTOPLAY_BET_BLOCK_W = 48;
/** Desktop BALANCE/BET horizontal insets (design px). */
const AUTOPLAY_BALANCE_BLOCK_X = 33;
const AUTOPLAY_BET_BLOCK_X_INSET = 102;
/**
 * Fixed gap between BALANCE and BET blocks on portrait phone (design px).
 * Does not grow with panel width — the row is centred as a unit instead.
 */
const AUTOPLAY_MOBILE_BALANCE_BET_BLOCK_GAP = 220;
/** Autoplay tab: slider row height (matches {@link GameApp#_buildAutoplaySlider} rowH). */
const AUTOPLAY_SLIDER_ROW_H = 63;
/** Y position of the third Stop-Autoplay slider in design px. */
const AUTOPLAY_SLIDER3_Y = 418;
/** Gap below last slider before BALANCE / BET on portrait mobile. */
const AUTOPLAY_MOBILE_BALANCE_BET_GAP = 18;
/**
 * Desktop keeps horizontal inset on the settings blur mask; mobile uses full panel width
 * (inset was ~100×scale design px total — too much on a narrow panel).
 */
const SETTINGS_PANEL_MOBILE_BLUR_INSET_X = 0;
/**
 * Extra horizontal inset (each side, panel design px) for the Information tab viewport only —
 * {@link GameApp#_syncSettingsModalLayout} scales it by `rx` on portrait phone; tablet uses this value as-is.
 */
const SETTINGS_INFO_VIEW_EXTRA_SIDE_INSET = 18;
/** Information tab — section headings, WILD/SCATTER titles, top-tier (x5) payout multipliers (#FFEA00). */
const INFO_TAB_TEXT_YELLOW = 0xffea00;
/** Portrait phone: info-tab font/icon multipliers (word-wrap width stays fixed — no shell widening). */
const SETTINGS_INFO_MOBILE_TEXT_MUL = 1.62;
const SETTINGS_INFO_MOBILE_ICON_MUL = 1.22;
/** Portrait phone: settings-tab heading + switch labels. */
const SETTINGS_MOBILE_UI_TEXT_MUL = 1.35;
/** Gap between a settings-tab switch label and its toggle (design px). */
const SETTINGS_SWITCH_LABEL_GAP = 14;
/** Extra safety pad after measured label width (glyph overhang / font load). */
const SETTINGS_SWITCH_LABEL_PAD = 4;
/** Horizontal gap between Sound and Quick Spin columns on desktop/tablet. */
const SETTINGS_SWITCH_COLUMN_GAP = 24;
/** Toggle width in {@link GameApp#_createSettingsModalPixi} makeSwitch rows. */
const SETTINGS_SWITCH_TOGGLE_W = 42;
/** Min horizontal inset from panel edge when sizing settings switch rows. */
const SETTINGS_SWITCH_EDGE_INSET = 28;

/**
 * Wraps label copy to a pixel width using Canvas measureText (same family of metrics as Pixi Text),
 * inserting newline characters only. Pixi {@link Text} should use wordWrap: false with this string
 * so layout does not depend on Pixi's internal word-wrap heuristics.
 * @param {string} raw
 * @param {number} maxWidthPx
 * @param {CanvasRenderingContext2D | null} ctx
 * @param {{ fontFamily: string, fontSize: number, fontWeight: string }} fontOpts
 * @returns {string}
 */
function wrapSettingsTableCellText(raw, maxWidthPx, ctx, fontOpts) {
  const text = String(raw ?? '');
  if (!ctx || !text) return text;
  const budget = Math.max(1, Math.floor(maxWidthPx) - 2);
  ctx.font = `${fontOpts.fontWeight} ${fontOpts.fontSize}px ${fontOpts.fontFamily}`;

  const widthOf = (s) => ctx.measureText(s).width;

  /** Longest prefix of s with width <= budget (at least one code unit if s is non-empty). */
  const longestFitPrefixLen = (s) => {
    if (!s) return 0;
    if (widthOf(s) <= budget) return s.length;
    let lo = 1;
    let hi = s.length;
    let best = 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (widthOf(s.slice(0, mid)) <= budget) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  };

  const lines = [];
  let line = '';
  const flush = () => {
    const t = line.replace(/\s+$/u, '');
    if (t.length > 0) lines.push(t);
    line = '';
  };

  const pushHardBroken = (word) => {
    let rest = word;
    while (rest.length > 0) {
      const n = longestFitPrefixLen(rest);
      lines.push(rest.slice(0, n));
      rest = rest.slice(n);
    }
  };

  for (const word of text.split(/\s+/u)) {
    if (!word) continue;
    const trial = line ? `${line} ${word}` : word;
    if (widthOf(trial) <= budget) {
      line = trial;
      continue;
    }
    if (line) flush();
    if (widthOf(word) <= budget) {
      line = word;
    } else {
      pushHardBroken(word);
    }
  }
  if (line) flush();

  return lines.length > 0 ? lines.join('\n') : text;
}

export class GameApp {
  constructor() {
    /** @type {Application | null} */
    this.app = null;
    this.animationController = new AnimationController();
    this.debug = new DebugMode();
    this.backend = new ShiningCrownService();

    /** @type {Record<number, any>} */
    this.spineAssets = {};
    this.world = new Container();
    /** @type {SlotMachine | null} */
    this.slotMachine = null;
    /** True while {@link handleSpin} is running (including post-win collect / delays). */
    this._spinCycleActive = false;
    /** True while reels are in motion and the player may request stop. */
    this._spinning = false;
    /** True only while reels are physically spinning (between beginPendingSpin and spinToGrid resolving). */
    this._reelsSpinning = false;
    this._initializing = false;
    /** True after InitialState succeeds (quick bets + balance from server). */
    this._gameDataReady = false;
    /** Live SignalR session; false while disconnected / reconnecting. */
    this._hubConnected = false;
    /** InitialState / bootstrap failed — show connection modal immediately. */
    this._sessionLoadFailed = false;
    /** True only after {@link CONNECTION_LOST_GRACE_MS} with no recovery. */
    this._connectionLostUIOpen = false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._connLostGraceTimer = null;
    /** @type {'lost' | 'duplicate'} */
    this._connModalMode = 'lost';
    this._connGraceMs = CONNECTION_LOST_GRACE_MS;
    this._hadConnectionLoss = false;
    this._duplicateSessionEnded = false;
    /** @type {(() => void) | null} */
    this._hubMonitorDispose = null;
    /** @type {HTMLDivElement | null} */
    this._connRestoredToastEl = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._connRestoredToastTimer = null;
    this._sessionSyncInFlight = false;
    /** @type {Promise<boolean> | null} Deduped session ping / reconnect. */
    this._sessionSyncPromise = null;
    /** Last tab-background duration on foreground (iOS stale socket probe). */
    this._lastForegroundHiddenMs = 0;
    /** False until InitialState succeeds — blocks spurious hub/online handlers during boot. */
    this._connectionMonitoringEnabled = false;
    this._forceDisconnectReceived = false;
    /** Hub closed while tab was in background — recover silently on return (no error modal). */
    this._reconnectAfterTabVisible = false;
    /** @type {ReturnType<typeof setInterval> | null} */
    this._connectionAutoRetryTimer = null;
    this._connectionModalReconnecting = false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._disconnectResolveTimer = null;
    /** Hub drop/reconnect ignored until this timestamp (post-bootstrap flap guard). */
    this._hubIgnoreUntilTs = 0;
    this._isDemo = false;
    this._oneTimeToken = null;
    this._accessToken = null;
    this._sessionParams = null;
    /** Browser-tab instance id — latest claim suppresses spurious session-ended UI. */
    this._sessionInstanceId = null;
    /** Launcher return URL (`LobbyUrl` / `lobbyUrl` / `lobbyURL`); empty hides Exit to lobby. */
    this._lobbyUrl = '';
    /** @type {(() => void) | null} */
    this._idleReloadDispose = null;
    /** @type {HTMLDivElement | null} */
    this._idleTimeoutOverlay = null;
    /** @type {{ sync: () => void, refreshCopy: () => void, dispose: () => void } | null} */
    this._landscapeBlocker = null;
    /** @type {import('pixi.js').Container | null} Full-screen connection-lost modal. */
    this._connectionLostRoot = null;
    /** @type {Promise<void> | null} Dedupes concurrent modal builds. */
    this._connectionLostEnsurePromise = null;
    /** @type {Promise<void> | null} Dedupes concurrent show passes. */
    this._connectionLostShowPromise = null;
    /** @type {import('pixi.js').Container | null} Centered panel shell (viewport aspect correction). */
    this._connectionLostPanelRoot = null;
    /** @type {import('pixi.js').Texture | null} */
    this._connLostIconTex = null;
    this.translation = translationStore;
    this._currencyCode = 'FUN';
    this._currencyPrecision = 2;
    this._currencySymbol = undefined;
    this._localizedTextRefs = [];
    /** Reposition inline WILD column icons after translated line widths change. */
    this._infoWildRelayout = null;
    /**
     * Info-tab payout lines driven by server paytable × current bet.
     * @type {{ multText: import('pixi.js').Text, amountText: import('pixi.js').Text, count: number, odd: number }[]}
     */
    this._infoPayoutAmountTexts = [];
    /**
     * Server paytable from StartGameAction (index 10). Odds are multiples of spin Amount.
     * @type {{ symbol: number, kind: number, minCount: number, odds: number[] }[]}
     */
    this._serverPaytable = [];
    /** RULES min/max + max-win rows; max-win visibility toggled after InitialState. */
    this._infoRuleLimitLineRefreshers = [];
    this._infoRuleMaxWinLines = [];
    /** Re-wrap + reflow the Information tab button table when translations change. */
    this._infoButtonTableRelayout = null;
    /** Children of the info scroll `content` below the button table (Y-shifted when table height changes). */
    this._infoAfterTableChildren = null;
    this._infoLastTableTotalH = 0;
    this._infoViewportTop = 66;
    this._infoViewportBottom = 522;

    /** @type {HTMLElement | null} */
    this._containerEl = null;
    /** @type {ResizeObserver | null} */
    this._resizeObserver = null;
    /** @type {import('pixi.js').Container | null} */
    this._settingsRoot = null;
    /** @type {import('pixi.js').Container | null} Settings modal panel wrapper; counter-scaled so panel keeps aspect under non-uniform canvas CSS scale. */
    this._settingsPanelRoot = null;
    /** @type {import('pixi.js').Container | null} */
    this._backdropRoot = null;
    /** @type {Sprite | null} */
    this._backdropSprite = null;
    /** @type {Texture | null} */
    this._bgTexDesktop = null;
    /** @type {Texture | null} */
    this._bgTexMobile = null;
    /** @type {Texture | null} */
    this._bgTexTablet = null;
    this._settingsBlurFilter = null;
    this._settingsBlurLayer = null;
    this._settingsBlurSprite = null;
    this._settingsBlurTexture = null;
    /** @type {import('pixi.js').Container | null} Full-screen dim layer (sibling of panel; not aspect-corrected). */
    this._betPopupDimRoot = null;
    /** @type {import('pixi.js').Container | null} Modal panel wrapper; counter-scaled so the panel is not stretched by canvas CSS. */
    this._betPopupPanelRoot = null;
    /** @type {import('pixi.js').Container | null} */
    this._betPopupChipsArea = null;
    /** @type {import('pixi.js').Graphics | null} */
    this._betPopupDim = null;
    /** @type {Sprite | null} */
    this._betPopupTriggerBtn = null;
    this._betPopupTriggerControlsIndex = 0;
    /** @type {import('pixi.js').Text | null} */
    this._betPopupTitle = null;
    /** @type {import('pixi.js').Sprite | null} */
    this._betPopupCloseBtn = null;
    /** @type {import('pixi.js').Container | null} Inner panel (bg + title + chips), centered under {@link #_betPopupPanelRoot}. */
    this._betPopupPanel = null;
    /** Native pixel size of quick-bets panel art (for desktop aspect fit). */
    this._betPopupBgNatW = 1000;
    this._betPopupBgNatH = 780;
    /** @type {import('pixi.js').Texture | null} */
    this._betPopupBgTexDefault = null;
    /** @type {import('pixi.js').Texture | null} */
    this._betPopupBgTexMobile = null;
    /** @type {import('pixi.js').Texture | null} */
    this._betPopupBgTexTablet = null;
    /** Last Figma scale factor for mobile bet modal (1 on desktop). */
    this._betPopupMobileModalFit = 1;
    /** Rounded black fill behind modal art (Figma Frame 207). */
    this._betPopupPanelFill = null;
    /** Rounded-rect mask for {@link #_betPopupBgSprite}. */
    this._betPopupRoundMask = null;
    /** Rounded stroke on the bet-options panel (desktop + compact). */
    this._betPopupPanelBorder = null;
    /** @type {HTMLDivElement | null} */
    this._betTapZone = null;
    this._muted = false;
    this._settingsSoundOn = true;
    this._settingsQuickSpinOn = false;
    this._paylinesVisible = false;
    this._autoplaying = false;
    /** 1–3: lit arrows on bet-speed chip; click cycles 1→2→3→1 */
    this._betSpeedTier = 1;
    /** Restored when Quick Spin is turned off after forcing tier 3. */
    this._betSpeedTierBeforeQuickSpin = null;
    /** @type {Sprite[] | null} */
    this._betSpeedArrowSprites = null;
    /** @type {Texture | null} */
    this._betSpeedActiveTex = null;
    /** @type {Texture | null} */
    this._betSpeedInactiveTex = null;
    this._turboHoldActive = false;
    this._spinHoldInProgress = false;
    this._spinHoldTriggeredTurbo = false;
    this._spinHoldPointerId = null;
    /** True after {@link HTMLCanvasElement#setPointerCapture} for the active spin-hold pointer. */
    this._spinHoldPointerCaptureActive = false;
    /** Window-level pointerup/cancel (capture) so release is seen even if Pixi misses it under load. */
    this._spinHoldWindowGuardActive = false;
    /** Window-level collect / big-win dismiss (capture) — same rationale as spin hold. */
    this._winCollectWindowGuardActive = false;
    this._onSpinHoldWindowPointerEnd = this._onSpinHoldWindowPointerEnd.bind(this);
    this._onWinCollectWindowPointerStart = this._onWinCollectWindowPointerStart.bind(this);
    this._onWinCollectWindowPointerEnd = this._onWinCollectWindowPointerEnd.bind(this);
    this._spinHoldRaf = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._spinHoldTimer = null;
    this._spinHoldStartTs = 0;
    this._spinButtonBaseScale = 1;
    this._spinHoldMs = 1300;
    this._spinHoldVisualDelayMs = 300;
    this._spinHoldMaxScale = 1.1;
    this._spinToggleCooldownMs = 400;
    this._spinToggleLockUntilTs = 0;
    /** @type {Container | null} spin image + autoplay remaining counter */
    this._spinButtonRoot = null;
    /** @type {Sprite | null} */
    this._spinButtonSprite = null;
    /** @type {Text | null} */
    this._spinAutoplayRemainText = null;
    /** @type {ReturnType<typeof createArcTextController> | null} */
    this._spinTurboHint = null;
    this._spinPressAccepted = false;
    this._stopRequested = false;
    this._winRollupRaf = null;
    /** @type {(() => void) | null} */
    this._winRollupResolve = null;
    /** @type {number | null} */
    this._winRollupTarget = null;
    /** Collect-win tap is shown until rollup finishes or the player taps to skip. */
    this._winCollectPending = false;
    this._winCollectHandling = false;
    /** Reload resume: show win animation but keep spin (no collect-hold UI). */
    this._resumeRoundSkipCollect = false;
    /** @type {(() => void) | null} */
    this._resolveWinCollectUser = null;
    /** Win celebration replay/static hold deferred until the loading overlay is gone. */
    this._deferredRoundResumeVisuals = null;
    /** @type {import('./utils/gameRoundStatusStore.js').RoundStatusSnapshot | null} */
    this._roundStatusLive = null;
    /** @type {(() => void) | null} */
    this._roundStatusPageHideFlush = null;
    /** @type {number | null} */
    this._resizeRaf = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._orientTimer = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._autoplayTimer = null;
    /** Autoplay round preset picker (canvas dim + cluster). */
    this._autoplayPickerOpen = false;
    /** null = unlimited; finite = spins left before autoplay stops */
    this._autoplayRoundsRemaining = null;
    /** @type {Container | null} */
    this._autoplayCluster = null;
    /** @type {Container | null} */
    this._autoplayPickerRow = null;
    /** @type {Graphics | null} fullscreen dim while autoplay preset picker is open */
    this._autoplayPixiDim = null;
    /** Saved `controls` child index when cluster is lifted onto `world` */
    this._autoplayClusterControlsIndex = 0;
    /** Selected preset spins for the autoplay-settings modal (null = ∞). */
    this._autoplayModalSpinsSelected = 10;
    /** Threshold: stop autoplay if balance drops by ≥ this much from start (0 = disabled). */
    this._autoplayStopOnBalanceDecreaseBy = 0;
    /** Threshold: stop autoplay if a single win is ≥ this much (0 = disabled). */
    this._autoplayStopOnSingleWinExceeds = 0;
    /** Threshold: stop autoplay if balance grew by ≥ this much from start (0 = disabled). */
    this._autoplayStopOnBalanceIncreaseBy = 0;
    /** Balance recorded when current autoplay run begins (used by the threshold checks). */
    this._autoplayStartBalance = null;
    /** Largest single-spin win observed during current autoplay run. */
    this._autoplayLastSingleWin = 0;
    /** @type {Container | null} Wrapper holding the original sound/quick-spin switches view. */
    this._settingsViewContainer = null;
    /** @type {Container | null} Wrapper holding the autoplay-settings view. */
    this._autoplayViewContainer = null;
    /** @type {Text | null} */
    this._settingsHeadingText = null;
    /** Bottom tab centres for the settings modal (design px); updated for portrait mobile layout. */
    this._settingsTabCenters = { left: 94, mid: 333, right: 572 };
    /** Active tab name for repositioning the underline after layout sync. */
    this._settingsModalActiveTab = 'settings';
    /** @type {boolean | undefined} Last portrait-mobile flag applied by {@link GameApp#_syncSettingsModalLayout}. */
    this._settingsLayoutMobile = undefined;
    /** @type {object | null} Pixi nodes mutated when switching mobile/desktop settings modal layout. */
    this._settingsModalRefs = null;
    /** @type {object | null} Autoplay tab controls repositioned with panel width. */
    this._autoplayModalLayoutParts = null;
    /** @type {object | null} Info tab scroll/mask targets for layout sync. */
    this._infoSettingsLayoutRefs = null;
    /** Total scrollable content height in the info tab (set in {@link GameApp#_buildInfoSettingsView}). */
    this._infoScrollContentHeight = 0;
    /** @type {{ minY: number, maxY: number, naturalTop: number } | null} Clamps for info scroll; rebuilt on resize. */
    this._infoScrollClamp = null;
    /** @type {Container[]} Preset chip buttons in autoplay-settings modal. */
    this._autoplayChipButtons = [];
    /** @type {((bet: number) => void) | null} */
    this._autoplayChipsRefresh = null;
    /** @type {((balance: number, bet: number) => void) | null} */
    this._autoplaySidebarRefresh = null;
    /** @type {Array<() => void>} Per-slider refresh callbacks (rebuild on bet/balance change). */
    this._autoplaySliderRefreshers = [];
    /** @type {import('pixi.js').Container | null} */
    this._loadingRoot = null;
    /** @type {import('pixi.js').Container | null} */
    this._loadingContent = null;
    /** @type {import('pixi.js').Sprite | null} */
    this._loadingBgSprite = null;
    /** @type {import('pixi.js').Texture | null} */
    this._loadingBgTexDesktop = null;
    /** @type {import('pixi.js').Texture | null} */
    this._loadingBgTexTablet = null;
    /** @type {import('pixi.js').Texture | null} */
    this._loadingBgTexMobile = null;
    /** @type {import('pixi.js').Texture | null} */
    this._loadingStudioTexDesktop = null;
    /** @type {import('pixi.js').Texture | null} */
    this._loadingStudioTexMobile = null;
    /** @type {import('pixi.js').Graphics | null} */
    this._loadingBarFill = null;
    /** @type {import('pixi.js').Graphics | null} */
    this._loadingBarTrack = null;
    /** @type {import('pixi.js').Sprite | null} */
    this._loadingStudioSprite = null;
    /** @type {import('pixi.js').Sprite | null} */
    this._loadingGameLogoSprite = null;
    /** @type {((ticker: import('pixi.js').Ticker) => void) | null} */
    this._loadingProgressTick = null;
    this._loadingProgress = 0;
    this._loadingBarW = LOADING_BAR_W_MAX;
    this._loadingBarH = LOADING_BAR_H_MAX;
    /** @type {number | null} */
    this._loadingShownAt = null;
    /** @type {Container | null} */
    this._canvasControls = null;
    this._controlObjectUrls = [];
    /** @type {Sprite | null} */
    this._frameSprite = null;
    /** @type {Sprite | null} */
    this._glowSprite = null;
    /** @type {Sprite | null} */
    this._matrixBgSprite = null;
    /** @type {Sprite | null} */
    this._slotBackgroundLinesSprite = null;
    /** Uniform scale for reel divider lines only (matrix unchanged). */
    this._slotBackgroundLinesScale = 1.6;
    /** Vertical nudge for lines only (negative = up). */
    this._slotBackgroundLinesOffsetY = -44;
    /** @type {Sprite | null} */
    this._logoSprite = null;
    /** @type {import('pixi.js').Container | null} Reels + frame + matrix/glow; shrinkable on phones — HUD sibling stays unscaled. */
    this._gameVisualLayer = null;
    /** Uniform scale for {@link #_gameVisualLayer} when {@link GameApp#_isPortraitMobileViewport} (buttons unchanged). */
    this._mobileGameVisualScale = 0.80;
    /** @deprecated Use {@link GameApp#_getMobileGameVisualLiftY} — kept for devtools overrides. */
    this._mobileGameVisualLiftY = MOBILE_GAME_VISUAL_LIFT_BASE;
    /** Viewport-fit factor for mobile HUD controls (0..1), recalculated on resize. */
    this._mobileHudFitScale = 1;
    this._slotRootBaseX = null;
    this._slotRootBaseY = null;
    /** Snapshot of canvas control positions for restoring desktop after mobile layout. */
    this._canvasDesktopLayout = null;
    /** Desktop-only sizes/fonts for {@link GameApp#_restoreMobileCanvasChrome}. */
    this._desktopChromeDefaults = null;
    /** @type {Container | null} Vertical-only merged top stat row background. */
    this._portraitTopStatsBar = null;
    /** @type {Sprite | null} */
    this._portraitTopStatsBgSprite = null;
    /** @type {Texture | null} */
    this._portraitTopStatsDefaultTex = null;
    /** @type {Texture | null} */
    this._mobilePortraitTopStatsTex = null;
    /** @type {Text | null} */
    this._portraitBalanceValueText = null;
    /** @type {Text | null} */
    this._portraitWinValueText = null;
    /** @type {Text | null} */
    this._portraitTopPromptText = null;
    this._betPromptVisible = true;
    /** Turbo plate height (logical px) — keeps turbo button glued under SPIN when repositioning. */
    this._betSpeedPlateH = 0;
    this._frameControl = {
      padX: 76,
      padYTop: 96,
      padYBottom: 70,
      offsetX: 13,
      offsetY: -34,
      scale: 1,
    };
    /** Quick-bet strip: drag handlers attached once (see {@link _ensureCanvasQbScrollHandlers}). */
    this._qbScrollHandlersInstalled = false;
    /** @type {import('pixi.js').Container | null} */
    this._canvasQbScrollWrapper = null;
    this._canvasQbBetCount = 0;
    /** @type {number | null} */
    this._qbDragAnchorGlobalX = null;
    /** @type {number | null} */
    this._qbDragAnchorGlobalY = null;
    /** @type {number | null} */
    this._qbDragLastGlobalX = null;
    this._qbDragScrollCommitted = false;
    /** CSS-pixel finger drift that distinguishes a “tap” (place bet) from a
     *  “drag” (scroll strip, no bet). Kept in CSS px and converted to stage
     *  units in {@link _qbTapDriftStagePx} so it feels identical on phones,
     *  tablets and desktop instead of varying with the viewport scale. */
    this._qbTapDriftCssPx = 6;
    /** Canvas DOM listeners while dragging quick bets (pointer may leave the masked strip). */
    this._qbCanvasDragListening = false;
    /** True if current pointer gesture on the strip was a drag (suppress bet on card pointerup). */
    this._qbGestureWasDrag = false;
    /** Flick coast velocity for quick-bet strip (qbGroup-local px/ms). */
    this._qbScrollVelocity = 0;
    this._qbScrollVelocityLastT = 0;
    /** @type {number | null} */
    this._qbInertiaRaf = null;
    this._qbInertiaLastT = 0;
    /** @type {number | null} Arrow nudge: coast stops at this qbGroup-local scroll offset. */
    this._qbInertiaTargetX = null;
    this._onResize = () => {
      if (this._resizeRaf != null) cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = requestAnimationFrame(() => {
        this._resizeRaf = null;
        this._hideSettingsPanel();
        this._hideBetPopup();
        this._hideAutoplayPicker();
        this._fitRendererToContainer();
      });
    };
    this._onOrientation = () => {
      if (this._orientTimer) clearTimeout(this._orientTimer);
      this._orientTimer = setTimeout(() => {
        this._orientTimer = null;
        this._hideSettingsPanel();
        this._hideBetPopup();
        this._hideAutoplayPicker();
        this._fitRendererToContainer();
      }, 200);
    };
  }

  _readContainerSize() {
    const barReserve = 0;
    const el = this._containerEl;
    if (el) {
      const r = el.getBoundingClientRect();
      let w = Math.floor(r.width);
      let h = Math.max(8, Math.floor(r.height) - barReserve);
      if (w < 8 || h < 8) {
        const vv = window.visualViewport;
        if (vv && vv.width > 0 && vv.height > 0) {
          w = Math.max(w, Math.floor(vv.width));
          h = Math.max(h, Math.floor(vv.height) - barReserve);
        }
      }
      if (w >= 2 && h >= 2) return { width: w, height: h };
    }
    const w = Math.max(2, Math.floor(window.innerWidth));
    const h = Math.max(2, Math.floor(window.innerHeight) - barReserve);
    return { width: w, height: h };
  }

  _fitRendererToContainer() {
    const canvas = this.app?.canvas;
    const containerEl = this._containerEl;
    if (!canvas || !containerEl) return;
    const r = containerEl.getBoundingClientRect();
    const cw = Math.max(2, Math.floor(r.width) || window.innerWidth);
    const ch = Math.max(2, Math.floor(r.height) || window.innerHeight);
    const designW = 1922;
    const designH = 1080;
    const scaleX = cw / designW;
    const scaleY = ch / designH;
    if (this._loadingRoot) {
      /* Uniform scale while loading: fills viewport without stretching logos. */
      const u = Math.max(scaleX, scaleY);
      const scaledW = designW * u;
      const scaledH = designH * u;
      const tx = (cw - scaledW) / 2;
      const ty = (ch - scaledH) / 2;
      canvas.style.transform = `translate(${tx}px, ${ty}px) scale(${u}, ${u})`;
      this._loadingRoot.scale.set(1, 1);
      this._loadingRoot.x = 0;
      this._loadingRoot.y = 0;
    } else {
      canvas.style.transform = `translate(0px, 0px) scale(${scaleX}, ${scaleY})`;
    }
    canvas.style.transformOrigin = '0 0';
    this._lastScales = { x: scaleX, y: scaleY };
    this._syncPortraitShellClass();
    this._syncStageBackdropForViewport();
    this._syncLandscapeBlocker();
    this.layout();
    this._applySlotAspectLock(scaleX, scaleY);
    this._applyMobileGameVisualScale();
    this._applyCanvasControlsViewportLayout();
    this._syncLinesPanelForOrientation();
    this._applyBetPopupViewportScale(scaleX, scaleY);
    this._syncBetPopupLayout();
    this._applySettingsViewportScale(scaleX, scaleY);
    this._syncSettingsModalLayout();
    this._applyConnectionLostViewportScale(scaleX, scaleY);
    if (this._connectionLostRoot) this._layoutConnectionLostModal();
    if (this._loadingRoot) this._layoutLoadingScreen();
  }

  _pickBackdropTextureForViewport() {
    const { width: cw, height: ch } = this._readContainerSize();
    const portrait = ch > cw;
    if (portrait && cw <= 760) return this._bgTexMobile || this._bgTexDesktop || Texture.EMPTY;
    if (portrait) return this._bgTexTablet || this._bgTexDesktop || Texture.EMPTY;
    return this._bgTexDesktop || Texture.EMPTY;
  }

  _syncStageBackdropForViewport() {
    const spr = this._backdropSprite;
    if (!spr) return;
    const W = 1922;
    const H = 1080;
    const { width: cw, height: ch } = this._readContainerSize();
    const portrait = ch > cw;
    const tex = this._pickBackdropTextureForViewport();
    if (tex && spr.texture !== tex) spr.texture = tex;
    if (portrait) {
      // Portrait backgrounds must always fill the entire visible screen area.
      spr.scale.set(1);
      spr.x = 0;
      spr.y = 0;
      spr.width = W;
      spr.height = H;
    } else if (tex?.width > 0 && tex?.height > 0) {
      const s = Math.max(W / tex.width, H / tex.height);
      spr.scale.set(s);
      spr.x = (W - tex.width * s) / 2;
      spr.y = (H - tex.height * s) / 2;
    } else {
      spr.scale.set(1);
      spr.x = 0;
      spr.y = 0;
      spr.width = W;
      spr.height = H;
    }
  }

  /**
   * Matches index.html: `html/body.slot-portrait-mobile` + #292929 stage, bottom gradient (::before).
   */
  _syncPortraitShellClass() {
    if (typeof document === 'undefined') return;
    const { width: cw, height: ch } = this._readContainerSize();
    const mobile = ch > cw && cw <= 760;
    document.documentElement.classList.toggle('slot-portrait-mobile', mobile);
    if (this._containerEl) {
      this._containerEl.classList.toggle('slot-portrait-mobile', mobile);
      this._containerEl.style.background = mobile ? '#292929' : '#000';
    }
  }

  _isPortraitMobileViewport() {
    const { width: cw, height: ch } = this._readContainerSize();
    return ch > cw && cw <= 760;
  }

  /**
   * Portrait phone: lift reel cabinet toward the top; narrower widths get more lift.
   * @returns {number} logical Y offset (negative = up), 0 when not portrait mobile
   */
  _getMobileGameVisualLiftY() {
    const { width: cw, height: ch } = this._readContainerSize();
    if (!(ch > cw && cw <= 760)) return 0;
    const t = Math.max(0, Math.min(1, (760 - cw) / (760 - 320)));
    const base = this._mobileGameVisualLiftY ?? MOBILE_GAME_VISUAL_LIFT_BASE;
    return base + MOBILE_GAME_VISUAL_LIFT_EXTRA_NARROW * t;
  }

  /** 0..1 how “narrow” the portrait phone is (760px wide → 0, 320px → 1). */
  _getNarrowPortraitWidthT() {
    const { width: cw, height: ch } = this._readContainerSize();
    if (!(ch > cw && cw <= 760)) return 0;
    return Math.max(0, Math.min(1, (760 - cw) / (760 - 320)));
  }

  /** Clamped portrait-phone HUD scale used by balance, quick bets, and spin row. */
  _getMobileHudFit() {
    const v = this._mobileHudFitScale ?? 1;
    return Math.max(MOBILE_HUD_FIT_MIN, Math.min(MOBILE_HUD_FIT_MAX, v));
  }

  /**
   * Portrait stacked layout: lowest allowed Y for the bottom edge of corner chrome (controls space).
   * @returns {number}
   */
  _getPortraitCornerIconsMaxBottomY() {
    const { height: ch } = this._readContainerSize();
    const mobile = this._isPortraitMobileViewport();
    const spacingBias = mobile ? Math.max(-1, Math.min(1, (ch - 860) / 220)) : 0;
    const pad = mobile
      ? Math.max(6, Math.round(PORTRAIT_CORNER_ICONS_BOTTOM_PAD + spacingBias * 3))
      : PORTRAIT_CORNER_ICONS_BOTTOM_PAD + 6;
    return PORTRAIT_DESIGN_H - pad;
  }

  /**
   * @param {number} iconY `_canvasBottomBar`-local top of corner icons
   * @param {number} iconSize
   * @returns {number}
   */
  _clampPortraitCornerIconY(iconY, iconSize) {
    const bottomBar = this._canvasBottomBar;
    if (!bottomBar || !(iconSize > 0)) return iconY;
    const maxBottom = this._getPortraitCornerIconsMaxBottomY();
    const iconBottom = bottomBar.y + iconY + iconSize;
    if (iconBottom <= maxBottom) return iconY;
    /* Visibility wins on critical viewports — pull icons up into the 1080px stage. */
    return maxBottom - bottomBar.y - iconSize;
  }

  /**
   * Portrait stacked HUD: left/right edges for corner chrome in controls (reel-root) space.
   * Mobile uses the scaled frame bounds so icons stay on screen corners as width changes.
   * @param {number} reelBlockW
   * @returns {{ left: number, right: number }}
   */
  _resolvePortraitCornerIconXBounds(reelBlockW) {
    const mobile = this._isPortraitMobileViewport();
    const layer = this._gameVisualLayer;
    const fr = this._frameSprite;
    if (mobile && layer && fr?.width > 0) {
      const g = layer.scale?.x ?? 1;
      const lx = layer.position?.x ?? 0;
      const frameLeft = lx + fr.x * g;
      const frameRight = lx + (fr.x + fr.width) * g;
      const span = frameRight - frameLeft;
      /* Frame art is offset slightly right ({@link #_frameControl.offsetX}) — center the span on
         the reel block so home vs menu get matching edge gaps. */
      if (span > 0) {
        const cx = reelBlockW / 2;
        return { left: cx - span / 2, right: cx + span / 2 };
      }
    }
    return { left: 0, right: reelBlockW };
  }

  /**
   * Map a CSS viewport Y to world-local Y (inverse of layout world pivot/scale + canvas scaleY).
   * @param {number} cssY
   * @returns {number}
   */
  _worldLocalYFromCssY(cssY) {
    const designH = 1080;
    const scaleY =
      this._lastScales?.y > 0 && Number.isFinite(this._lastScales.y)
        ? this._lastScales.y
        : cssY / designH;
    const designY = cssY / scaleY;
    const pivotY = designH / 2;
    const s = Math.max(1e-6, this.world?.scale?.y ?? 1);
    const { width: cw, height: ch } = this._readContainerSize();
    let worldOffY = 0;
    if (ch > cw && cw <= 760) {
      worldOffY = MOBILE_PORTRAIT_WORLD_OFFSET_NARROW * this._getNarrowPortraitWidthT();
    } else if (this._isTabletViewport() && ch > cw) {
      worldOffY = gameConfig.viewport?.tabletWorldOffsetY ?? 0;
    }
    const posY = pivotY + worldOffY;
    return pivotY + (designY - posY) / s;
  }

  /** Portrait phone: vertically centred in the viewport; clamped so the panel stays fully visible. */
  _resolvePhoneBetPopupCenterY(bgH) {
    const { height: ch } = this._readContainerSize();
    const margin = MOBILE_BET_MODAL_VIEWPORT_MARGIN_CSS;
    const localTop = this._worldLocalYFromCssY(margin);
    const localBottom = this._worldLocalYFromCssY(ch - margin);
    let centerY = (localTop + localBottom) / 2;
    const half = bgH / 2;
    if (centerY - half < localTop) centerY = localTop + half;
    if (centerY + half > localBottom) centerY = localBottom - half;
    return centerY;
  }

  /** Home (index 0) is disabled on all devices — keep slot for layout indices. */
  _hideCanvasHomeIcon() {
    const home = this._canvasIconSprites?.[0];
    if (!home) return;
    home.visible = false;
    home.renderable = false;
    home.eventMode = 'none';
  }

  /** Re-clamp home / sound / menu after tablet bottom-strip nudge or resize. */
  _clampPortraitCornerIconsToVisibleArea() {
    if (!this._isPortraitStackedHud()) return;
    const icons = this._canvasIconSprites;
    const bottomBar = this._canvasBottomBar;
    if (!icons?.length || !bottomBar) return;
    const visible = icons.filter((s) => s.visible);
    if (!visible.length) return;
    const iz = visible[0].height;
    const clamped = this._clampPortraitCornerIconY(visible[0].y, iz);
    if (clamped === visible[0].y) return;
    visible.forEach((s) => {
      s.y = clamped;
    });
  }

  /** Portrait merged stats bar: scale label/value fonts only (width stays {@link #_relayoutBottomBarForViewport}). */
  _syncPortraitTopStatsTextScale(mul) {
    const k = Math.max(1, mul);
    const setFs = (node, base) => {
      if (!node?.style) return;
      node.style.fontSize = Math.round(base * k);
    };
    setFs(this._portraitBalanceLabelText, 11);
    setFs(this._portraitBalanceValueText, 22);
    setFs(this._portraitBalanceCurrencyText, 13);
    setFs(this._portraitWinLabelText, 11);
    setFs(this._portraitWinValueText, 22);
    setFs(this._portraitWinCurrencyText, 13);
    if (this._portraitTopPromptText?.style) {
      this._portraitTopPromptText.style.fontSize = Math.round(PORTRAIT_BET_PROMPT_FONT_SIZE * k);
      this._portraitTopPromptText.style.lineHeight = Math.round(PORTRAIT_BET_PROMPT_LINE_HEIGHT * k);
    }
    this._relayoutPortraitBalanceStack?.();
    this._relayoutPortraitWinStack?.();
  }

  /**
   * Portrait phone: fit HUD stack under the scaled reel cabinet without clipping.
   * @param {number} cw CSS viewport width
   * @param {number} ch CSS viewport height
   * @param {number} reelBlockH logical reel grid height
   * @returns {number}
   */
  _computeMobileHudFitScale(cw, ch, reelBlockH) {
    const spacingBias = Math.max(-1, Math.min(1, (ch - 860) / 220));
    const portraitRatio = cw / Math.max(1, ch);
    const spacingTightness = Math.max(0, Math.min(1, (portraitRatio - 0.50) / 0.35));
    const p = PORTRAIT_TUNING.phone;
    const gameToHudGap = Math.max(
      0,
      Math.round(p.gameToHudGapBase + spacingBias * p.gameToHudGapSpacingBiasMul),
    );
    const gapStatsToQb = Math.max(4, Math.round(10 + spacingBias * 6 - spacingTightness * 4));
    const gapQbToSpin = Math.max(32, Math.round(76 + spacingBias * 10 - spacingTightness * 12));
    const spinHalf = Math.round((120 * MOBILE_CONTROL_SCALE_BASE) / 2);
    const g = this._mobileGameVisualScale ?? 1;
    const liftY = this._getMobileGameVisualLiftY();
    const reelBottom = (reelBlockH * (1 + g)) / 2 + liftY;
    const iconReserve = 92;
    const hudStackRef =
      gameToHudGap +
      PORTRAIT_MERGED_STATS_BAR_H +
      gapStatsToQb +
      MOBILE_QB_CHIP_REF_H +
      gapQbToSpin +
      spinHalf;
    const budget = 1080 - reelBottom - iconReserve;
    const spaceFit = budget / Math.max(1, hudStackRef);
    /* Reels scale up on phones while HUD stays in design px — never shrink below 1.0; boost
       modestly when tight, up to TARGET when the stack fits (existing nudges absorb overlap). */
    const boosted = Math.max(MOBILE_HUD_FIT_TARGET * 0.96, spaceFit * 1.18);
    return Math.max(
      1.12,
      Math.min(MOBILE_HUD_FIT_MAX, Math.max(MOBILE_HUD_FIT_MIN, Math.min(MOBILE_HUD_FIT_TARGET, boosted))),
    );
  }

  /**
   * Portrait phone only: legacy width-only fit (connection-lost modal).
   * @returns {number}
   */
  _getSettingsPanelMobileFit() {
    const { width: cw } = this._readContainerSize();
    const t = cw / SETTINGS_PANEL_MOBILE_REF_CW;
    return Math.max(SETTINGS_PANEL_COMPACT_FIT_MIN, Math.min(SETTINGS_PANEL_COMPACT_FIT_MAX, t));
  }

  /**
   * Portrait phone: design-width from compact metrics.
   * @returns {number}
   */
  _getSettingsPanelMobileWidth() {
    return this._getSettingsPanelCompactMetrics('mobile').panelW;
  }

  /**
   * Phone/tablet: size the settings shell from CSS viewport so rendered width/height keep
   * {@link SETTINGS_PANEL_MOBILE} (or narrowed desktop) proportions on every device.
   * Rendered size: panelDim × {@link SETTINGS_MODAL_PANEL_SCALE} × (ch/1080); width also uses shell sy/sx.
   * @param {'mobile' | 'tablet'} kind
   * @returns {{ panelW: number, panelH: number, modalFit: number, rootOffsetY: number, layoutRefW: number, layoutRefH: number }}
   */
  _getSettingsPanelCompactMetrics(kind) {
    const { width: cw, height: ch } = this._readContainerSize();
    const mobile = kind === 'mobile';
    const ref = mobile
      ? SETTINGS_PANEL_MOBILE
      : {
          w: SETTINGS_PANEL_DESKTOP.w - 2 * SETTINGS_INFO_VIEW_EXTRA_SIDE_INSET,
          h: SETTINGS_PANEL_DESKTOP.h,
        };
    const viewportW = mobile ? SETTINGS_PANEL_MOBILE_VIEWPORT_W : SETTINGS_PANEL_TABLET_VIEWPORT_W;
    const viewportH = mobile ? SETTINGS_PANEL_MOBILE_VIEWPORT_H : SETTINGS_PANEL_TABLET_VIEWPORT_H;
    const layoutRefW = mobile ? SETTINGS_PANEL_MOBILE_LAYOUT_REF_W : ref.w;
    const layoutRefH = mobile ? SETTINGS_PANEL_MOBILE_LAYOUT_REF_H : ref.h;

    if (!(cw > 0 && ch > 0)) {
      return {
        panelW: ref.w,
        panelH: ref.h,
        modalFit: 1,
        rootOffsetY: 0,
        layoutRefW,
        layoutRefH,
      };
    }

    const sy = ch / 1080;
    const denom = Math.max(1e-6, SETTINGS_MODAL_PANEL_SCALE * sy);
    const fitW = (cw * viewportW) / (ref.w * denom);
    const fitH = (ch * viewportH) / (ref.h * denom);

    let modalFit;
    let panelW;
    let panelH;

    if (mobile) {
      /* Tall phone shell: height sets scale; width follows VIEWPORT_W so raising that constant
         actually widens the modal (min(fitW, fitH) would ignore VIEWPORT_W whenever fitH wins). */
      modalFit = Math.max(
        SETTINGS_PANEL_COMPACT_FIT_MIN,
        Math.min(SETTINGS_PANEL_COMPACT_FIT_MAX, fitH),
      );
      panelH = Math.round(ref.h * modalFit);
      const panelWFromHeight = Math.round(ref.w * modalFit);
      const panelWFromViewport = Math.round((cw * viewportW) / denom);
      panelW = Math.max(panelWFromHeight, panelWFromViewport);
    } else {
      modalFit = Math.min(fitW, fitH);
      modalFit = Math.max(
        SETTINGS_PANEL_COMPACT_FIT_MIN,
        Math.min(SETTINGS_PANEL_COMPACT_FIT_MAX, modalFit),
      );
      panelW = Math.round(ref.w * modalFit);
      panelH = Math.round(ref.h * modalFit);
    }
    const rootOffsetY = mobile
      ? Math.round((ch / 1080) * SETTINGS_PANEL_MOBILE_ROOT_OFFSET_Y)
      : Math.round((ch / 1080) * 24);

    return { panelW, panelH, modalFit, rootOffsetY, layoutRefW, layoutRefH };
  }

  /**
   * Width-fill boost for portrait phones by aspect ratio (w/h), organized from wider to taller.
   * Wider ratios get lower boost to avoid vertical overflow.
   * @param {number} portraitRatio
   */
  _getMobilePortraitWidthBoost(portraitRatio) {
    const r = Number.isFinite(portraitRatio) ? portraitRatio : 0;
    const breakpoints = [
      { minRatio: 0.90, boost: 0.85 },
      { minRatio: 0.85, boost: 0.85 },
      { minRatio: 0.80, boost: 0.9 },
      { minRatio: 0.70, boost: 0.95 },
      { minRatio: 0.65, boost: 1.10 },
      { minRatio: 0.60, boost: 1.20 },
      { minRatio: 0.55, boost: 1.25 },
      { minRatio: 0.50, boost: 1.4 },
    ];
    for (const bp of breakpoints) {
      if (r >= bp.minRatio) return bp.boost;
    }
    return 1.42;
  }

  /** Any portrait viewport — balance / prompt / win stack above QB strip and spin row. */
  _isPortraitStackedHud() {
    const { width: cw, height: ch } = this._readContainerSize();
    return ch > cw;
  }

  /**
   * Tablet-class viewport for uniform scale boost ({@link gameConfig.viewport.tabletWorldScaleMul}).
   * Portrait: wider than narrow-phone breakpoint (matches tablet HUD paths).
   * Landscape: primary pointer is touch and dimensions match common tablets (excludes typical desktops).
   */
  _isTabletViewport() {
    const { width: cw, height: ch } = this._readContainerSize();
    const portrait = ch > cw;
    if (portrait) return cw > 760;
    const touch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
    if (!touch) return false;
    const smin = Math.min(cw, ch);
    const smax = Math.max(cw, ch);
    return smin >= 768 && smax <= 1366;
  }

  /** Phone in landscape orientation (mobile-sized, touch device, not tablet, not portrait). */
  _isMobileLandscapeViewport() {
    const { width: cw, height: ch } = this._readContainerSize();
    if (ch >= cw) return false;
    if (Math.min(cw, ch) > 760) return false;
    const touch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
    if (!touch) return false;
    if (this._isTabletViewport()) return false;
    return true;
  }

  _syncLandscapeBlocker() {
    this._landscapeBlocker?.sync?.();
  }

  /** Desktop landscape currently rendered into the browser's fullscreen element. */
  _isDesktopFullscreen() {
    if (typeof document === 'undefined') return false;
    if (!document.fullscreenElement) return false;
    if (this._isPortraitMobileViewport()) return false;
    if (this._isTabletViewport()) return false;
    return true;
  }

  /** Landscape desktop HUD (not tablet, not phone landscape, not portrait). */
  _isDesktopLandscapeViewport() {
    if (this._isPortraitStackedHud()) return false;
    if (this._isTabletViewport()) return false;
    if (this._isMobileLandscapeViewport()) return false;
    return true;
  }

  /** Hide the paylines/LINES chip on portrait viewports — layout targets horizontal screens only. */
  _syncLinesPanelForOrientation() {
    const lp = this._canvasLinesPanel;
    if (!lp) return;
    const { width: cw, height: ch } = this._readContainerSize();
    const vertical = ch > cw;
    lp.visible = !vertical;
    lp.eventMode = vertical ? 'none' : 'passive';
  }

  async init(containerEl) {
    this._containerEl = containerEl;
    this._landscapeBlocker = createLandscapeOrientationBlocker({
      isActive: () => this._isMobileLandscapeViewport(),
      getTitle: () => this._tu('ROTATE_DEVICE_TITLE'),
      getBody: () => this._t('ROTATE_DEVICE_BODY'),
    });
    this._syncLandscapeBlocker();
    this._syncLobbyUrlFromSearch();
    Object.assign(containerEl.style, {
      position: 'fixed',
      inset: '0',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      touchAction: 'none',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      background: '#000',
    });
    this._syncPortraitShellClass();

    /* ── Init Pixi with transparent background, fixed 1922×1080 ────────── */
    const rendererProfile = getPixiRendererProfile();
    this.app = new Application();
    await this.app.init({
      width: 1922,
      height: 1080,
      backgroundAlpha: 0,
      antialias: rendererProfile.antialias,
      powerPreference: rendererProfile.powerPreference,
      resolution: rendererProfile.resolution,
      autoDensity: true,
      preference: 'webgl',
    });

    const canvas = this.app.canvas;
    canvas.style.cssText =
      'display:block;position:absolute;top:0;left:0;width:1922px;height:1080px;touch-action:none;pointer-events:auto;z-index:1;transform-origin:0 0;';
    containerEl.appendChild(canvas);
    this._fitRendererToContainer();
    /* Full-screen overlay before any game content is attached so the canvas never flashes the cabinet. */
    await this._showLoading();

    /* Kick off audio decode in the background — fire-and-forget so it never
     * blocks first paint. Sound manager handles iOS unlock on first gesture. */
    sound.setMuted(!this._settingsSoundOn);
    sound.preload();

    // Load Spine assets for symbols
    this.spineAssets = await loadSpineAssets();

    /* ── SlotMachine (Pixi) ───────────────────────────────────────────── */
    this.slotMachine = new SlotMachine({
      spineAssets: this.spineAssets,
      animationController: this.animationController,
      onSpinRequest: () => this.handleSpin(),
      translate: (key, replace) => this._t(key, replace),
      onBetLayoutChanged: () => {},
      onBetSync: () => this._refreshInfoPaytableAmounts(),
      onAutoplayToggle: (on) => this.handleAutoplayToggle(on),
      onMaxBet: () => this.handleMaxBet(),
      onSettingsToggle: () => this._toggleSettingsPanel(),
      onBetPopupToggle: () => this._toggleBetPopup(),
    });
    this.slotMachine.reelContainer.setPaylineGuidesVisible(this._paylinesVisible);

    /* Hard cleanup: remove all non-card Pixi display objects from the tree */
    this.slotMachine._hideDecor = true;
    const smRoot = this.slotMachine.root;
    const detach = (node) => {
      if (node?.parent) node.parent.removeChild(node);
    };
    detach(this.slotMachine._headerLayer);
    detach(this.slotMachine._midBackdrop);
    detach(this.slotMachine.ui.root);
    detach(this.slotMachine.winFloater.root);

    /* Keep only reel container under cabinet group (remove old cabinet graphics). */
    this.slotMachine._cabinetGroup.children
      .slice()
      .forEach((child) => {
        if (child !== this.slotMachine.reelContainer.root) {
          this.slotMachine._cabinetGroup.removeChild(child);
        }
      });

    /* Remove payline/win overlays from reel container tree too. */
    detach(this.slotMachine.reelContainer.allLinesOverlay);
    detach(this.slotMachine.reelContainer.winOverlay);

    this.world.addChild(this.slotMachine.root);

    /* Slot window backplate (same container as frame & reels; behind glow + symbols) */
    let matrixTexture = null;
    for (const p of ['/assets/matrix.png', 'assets/matrix.png', '/matrix.png']) {
      try {
        matrixTexture = await Assets.load(p);
        if (matrixTexture) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    const matrixSprite = matrixTexture ? new Sprite(matrixTexture) : Sprite.from('/assets/matrix.png');
    matrixSprite.eventMode = 'none';
    matrixSprite.zIndex = 0;
    if (matrixSprite.texture?.source) {
      matrixSprite.texture.source.scaleMode = 'linear';
    }
    this.slotMachine.reelContainer.root.sortableChildren = true;
    this.slotMachine.reelContainer.root.addChild(matrixSprite);
    this._matrixBgSprite = matrixSprite;

    let slotLinesTexture = null;
    for (const p of [
      '/assets/slot_background_lines.png',
      'assets/slot_background_lines.png',
      '/slot_background_lines.png',
    ]) {
      try {
        slotLinesTexture = await Assets.load(p);
        if (slotLinesTexture) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    const slotLinesSprite = slotLinesTexture
      ? new Sprite(slotLinesTexture)
      : Sprite.from('/assets/slot_background_lines.png');
    slotLinesSprite.eventMode = 'none';
    slotLinesSprite.zIndex = 0.1;
    if (slotLinesSprite.texture?.source) {
      slotLinesSprite.texture.source.scaleMode = 'linear';
    }
    this.slotMachine.reelContainer.root.addChild(slotLinesSprite);
    this._slotBackgroundLinesSprite = slotLinesSprite;

    /* Glow behind frame (in same container as reels) */
    let glowTexture = null;
    for (const p of ['/assets/glow.png', 'assets/glow.png', '/glow.png']) {
      try {
        glowTexture = await Assets.load(p);
        if (glowTexture) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    const glowSprite = glowTexture ? new Sprite(glowTexture) : Sprite.from('/assets/glow.png');
    glowSprite.anchor.set(0.5, 0.5);
    glowSprite.alpha = 1;
    glowSprite.eventMode = 'none';
    glowSprite.zIndex = -1;
    this.slotMachine.reelContainer.root.addChild(glowSprite);
    this._glowSprite = glowSprite;

    /* Frame in Pixi canvas, anchored to the actual card/reel block */
    let frameTexture = null;
    for (const p of ['/assets/frame.png', 'assets/frame.png', '/frame.png']) {
      try {
        frameTexture = await Assets.load(p);
        if (frameTexture) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    const frameSprite = frameTexture ? new Sprite(frameTexture) : Sprite.from('/assets/frame.png');
    frameSprite.tint = 0xffffff;
    frameSprite.alpha = 1;
    frameSprite.eventMode = 'none';
    frameSprite.zIndex = 1;
    this.slotMachine.reelContainer.root.addChild(frameSprite);
    this._frameSprite = frameSprite;

    /* Logo at top center of frame, in same container */
    let logoTexture = null;
    for (const p of ['/assets/Logo0.png', 'assets/Logo0.png', '/Logo0.png']) {
      try {
        logoTexture = await Assets.load(p);
        if (logoTexture) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    /* Horizontal variant — swapped in on portrait phones (see {@link #_relayoutLogoForViewport}). */
    let logoTextureHorizontal = null;
    for (const p of ['/assets/logo_horizontal.png', 'assets/logo_horizontal.png', '/logo_horizontal.png']) {
      try {
        logoTextureHorizontal = await Assets.load(p);
        if (logoTextureHorizontal) break;
      } catch (_) {
        /* try next candidate path */
      }
    }
    this._logoTexDefault = logoTexture || null;
    this._logoTexHorizontal = logoTextureHorizontal || null;
    if (logoTexture) {
      const logoSprite = new Sprite(logoTexture);
      logoSprite.anchor.set(0.5, 0);
      logoSprite.eventMode = 'none';
      logoSprite.zIndex = 10;
      this.slotMachine.reelContainer.root.addChild(logoSprite);
      this._logoSprite = logoSprite;
    }

    this._applyFrameLayout();
    this._ensureGameVisualLayer();
    this._applyMobileGameVisualScale();
    await this._buildCanvasControls();
    await this._createPixiStageBackdrop();
    this.app.stage.addChild(this._backdropRoot);
    this.app.stage.addChild(this.world);

    /* Canvas should show only cards + frame */

    window.addEventListener('resize', this._onResize);
    window.visualViewport?.addEventListener('resize', this._onResize);
    window.addEventListener('orientationchange', this._onOrientation);
    document.addEventListener('fullscreenchange', this._onResize);
    window.addEventListener('keydown', this._onKey);
    this._attachWinCollectWindowPointerGuard();

    // ResizeObserver is not available on iOS 12 / iOS 13.0-13.3 and some old
    // Android WebViews; window/visualViewport resize listeners above cover
    // those fallbacks. Feature-detect so the rest of init can succeed.
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this._onResize());
      this._resizeObserver.observe(containerEl);
    }

    this._exposeDebugApi();
    this._installRoundStatusPageHideFlush();
    this._fitRendererToContainer();
    // Settings/info must be built AFTER StartGameAction so the server paytable
    // is available when payout rows are created (they are not rebuilt later).
    await this._loadInitialState();
    await this._createSettingsModalPixi();
    this.slotMachine.winFloater.root.visible = false;
    await this._ensureLoadingMinDisplayElapsed();
    this._hideLoading();
    this._fitRendererToContainer();
    this._scheduleDeferredRoundResumeVisuals({ replaySounds: true });
    requestAnimationFrame(() => {
      this._fitRendererToContainer();
      requestAnimationFrame(() => this._fitRendererToContainer());
    });
  }

  _onKey = (e) => {
    if (e.key === '`' || e.key === '~') {
      this.debug.toggle();
      this.debug.refreshOverlay();
    }
  };

  _t(key, replace) {
    return this.translation.t(key, replace);
  }

  _tu(key, replace) {
    return this._t(key, replace).toLocaleUpperCase();
  }

  _twoLineText(key) {
    const text = this._tu(key).replace(/\s+/g, ' ').trim();
    const words = text.split(' ').filter(Boolean);
    if (words.length <= 2) return text;
    const splitAt = Math.ceil(words.length / 2);
    return `${words.slice(0, splitAt).join(' ')}\n${words.slice(splitAt).join(' ')}`;
  }

  _currencyLabel() {
    return formatCurrencyRulesLabel(this._currencyCode, this._currencySymbol) || 'FUN';
  }

  _formatCurrencyDigits(amount) {
    return formatCurrencyDigits(Number(amount) || 0, this._currencyPrecision);
  }

  _formatCurrencyAmount(amount) {
    return formatCurrencyAmount(
      Number(amount) || 0,
      this._currencyCode,
      this._currencyPrecision,
      this._currencySymbol,
    );
  }

  /**
   * @param {number} symbolId
   * @returns {{ symbol: number, kind: number, minCount: number, odds: number[] } | null}
   */
  _paytableEntry(symbolId) {
    const id = Number(symbolId);
    if (!Number.isFinite(id)) return null;
    return this._serverPaytable?.find((e) => Number(e.symbol) === id) ?? null;
  }

  /**
   * Payout rows for an entry, highest count first (x5 → x2).
   * Amount = odd × current bet (matches live GameAction wins).
   * @param {{ minCount: number, odds: number[] } | null | undefined} entry
   * @returns {{ count: number, odd: number }[]}
   */
  _paytableCountOdds(entry) {
    if (!entry || !Array.isArray(entry.odds) || !entry.odds.length) return [];
    const minCount = Math.max(0, Number(entry.minCount) || 0);
    /** @type {{ count: number, odd: number }[]} */
    const rows = [];
    for (let i = 0; i < entry.odds.length; i++) {
      const odd = Number(entry.odds[i]);
      if (!Number.isFinite(odd) || odd <= 0) continue;
      rows.push({ count: minCount + i, odd });
    }
    rows.sort((a, b) => b.count - a.count);
    return rows;
  }

  /** Refresh info-tab payout amounts when bet / currency / paytable changes. */
  _refreshInfoPaytableAmounts() {
    if (!this._infoPayoutAmountTexts?.length) return;
    const bet = Number(this.slotMachine?.ui?.betValue) || 0;
    for (const row of this._infoPayoutAmountTexts) {
      if (!row?.multText || !row?.amountText) continue;
      const count = Number(row.count);
      const odd = Number(row.odd);
      if (!Number.isFinite(count) || !Number.isFinite(odd)) continue;
      row.multText.text = `x${count} - `;
      row.multText.style.fill = INFO_TAB_TEXT_YELLOW;
      row.amountText.text = this._formatCurrencyAmount(odd * bet);
      row.amountText.x = row.multText.width;
    }
  }

  _setCurrency({ currencyCode, currencyPrecision, currencySymbol } = {}) {
    const nextCode = normalizeCurrencyCode(currencyCode || this._currencyCode || 'FUN') || 'FUN';
    const nextPrecision = Number.isFinite(Number(currencyPrecision))
      ? Number(currencyPrecision)
      : this._currencyPrecision;
    const nextSymbol =
      currencySymbol == null || String(currencySymbol).trim() === ''
        ? undefined
        : String(currencySymbol).trim();
    this._currencyCode = nextCode;
    this._currencyPrecision = nextPrecision;
    this._currencySymbol = nextSymbol;
    this.slotMachine?.ui?.setCurrency?.({
      currencyCode: this._currencyCode,
      currencyPrecision: this._currencyPrecision,
      currencySymbol: this._currencySymbol,
    });
    const cur = {
      currencyCode: this._currencyCode,
      currencyPrecision: this._currencyPrecision,
      currencySymbol: this._currencySymbol,
    };
    this.slotMachine?.winCelebration?.setCurrency?.(cur);
    this.slotMachine?.winFloater?.setCurrency?.(cur);
    this._refreshLocalizedText();
  }

  _applyLocalizedTextSpec(spec) {
    if (!spec?.text) return;
    const replace = typeof spec.replace === 'function' ? spec.replace() : spec.replace;
    let value = spec.upper ? this._tu(spec.key, replace) : this._t(spec.key, replace);
    if (typeof spec.transform === 'function') value = spec.transform(value);
    spec.text.text = `${value}${spec.suffix ?? ''}`;
    spec.afterApply?.();
  }

  _bindLocalizedText(text, key, opts = {}) {
    const spec = {
      text,
      key,
      upper: !!opts.upper,
      suffix: opts.suffix ?? '',
      replace: opts.replace,
      transform: opts.transform,
      afterApply: opts.afterApply,
    };
    this._localizedTextRefs.push(spec);
    this._applyLocalizedTextSpec(spec);
    return text;
  }

  _refreshLocalizedText() {
    this.slotMachine?.ui?.setTranslator?.((key, replace) => this._t(key, replace));
    this.slotMachine?.refreshLocalizedHeaderTexts?.();
    this._localizedTextRefs.forEach((spec) => this._applyLocalizedTextSpec(spec));
    this._infoWildRelayout?.();
    this._refreshInfoPaytableAmounts();
    if (this._infoRuleLimitLineRefreshers?.length) {
      for (const refreshLine of this._infoRuleLimitLineRefreshers) refreshLine();
    }
    if (this._infoRuleMaxWinLines?.length) {
      const maxOdd = Number(gameConfig.maxOdd);
      const showMaxWin = Number.isFinite(maxOdd) && maxOdd > 0;
      for (const line of this._infoRuleMaxWinLines) {
        if (line) line.visible = showMaxWin;
      }
    }
    this._infoButtonTableRelayout?.();
    if (this._canvasBalanceLabelText) this._canvasBalanceLabelText.text = this._tu('BALANCE');
    if (this._canvasWinLabelText) this._canvasWinLabelText.text = this._tu('LAST_WIN');
    if (this._portraitBalanceLabelText) this._portraitBalanceLabelText.text = this._tu('BALANCE');
    if (this._portraitWinLabelText) this._portraitWinLabelText.text = this._tu('LAST_WIN');
    if (this._canvasBalanceCurrencyText) this._canvasBalanceCurrencyText.text = this._currencyLabel();
    if (this._canvasWinCurrencyText) this._canvasWinCurrencyText.text = this._currencyLabel();
    if (this._portraitBalanceCurrencyText) this._portraitBalanceCurrencyText.text = this._currencyLabel();
    if (this._portraitWinCurrencyText) this._portraitWinCurrencyText.text = this._currencyLabel();
    if (this._betPopupTitle) this._betPopupTitle.text = this._tu('BET_OPTIONS');
    if (this._settingsHeadingText) {
      this._settingsHeadingText.text =
        this._settingsModalActiveTab === 'autoplay'
          ? this._tu('AUTOPLAY_SETTINGS')
          : this._settingsModalActiveTab === 'info'
            ? this._tu('INFO')
            : this._tu('GAME_SETTINGS');
    }
    this._updateCanvasBetPrompt();
    this._canvasBalancePanelRelayout?.();
    this._canvasWinPanelRelayout?.();
    this._relayoutPortraitBalanceStack?.();
    this._relayoutPortraitWinStack?.();
    this._refreshConnectionLostModalCopy();
    this._landscapeBlocker?.refreshCopy?.();
    this._syncLandscapeBlocker();
    if (this._connLostReloadLabel) {
      this._connLostReloadLabel.text = this._tu('RELOAD_PAGE');
    }
    if (this._connLostStatusText) {
      this._connLostStatusText.visible = false;
    }
    this._syncConnectionLostModal();
    this._refreshQuickBets();
    this._refreshAutoplaySettingsView();
    this._relayoutSettingsSwitchesOnly();
    if (this._spinTurboHint) this._spinTurboHint.setText(this._tu('HOLD_FOR_TURBO_SPIN'));
    this._layoutSpinTurboHint();
    this._syncSpinTurboHintVisible();
  }

  _setSpinButtonTexture(tex) {
    if (!this._spinButtonSprite) return;
    this._spinButtonSprite.texture = tex;
    this._syncSpinTurboHintVisible();
  }

  _layoutSpinTurboHint() {
    if (!this._spinTurboHint) return;
    const design = 194;
    const size = this._spinButtonSprite?.width ?? design;
    const scale = size / design;
    this._spinTurboHint.setLayout({
      radius: 75 * scale,
      fontSize: Math.max(8, 12 * scale),
      letterSpacing: 1 * scale,
      yOffset: -7 * scale,
      arcOffset: 0,
      arcSweep: Math.PI * 0.88,
    });
    this._spinTurboHint.container.x = 2 * scale;
  }

  _syncSpinTurboHintVisible() {
    if (!this._spinTurboHint) return;
    const showStart = this._spinButtonSprite?.texture === this._spinStartTex;
    this._spinTurboHint.container.visible = !!showStart;
  }

  _exposeDebugApi() {
    const symCount = gameConfig.symbols.length;
    window.slotGameDebug = {
      forceNextGrid: (grid) => this.debug.forceNextGrid(grid),
      /** Paytable / info diagnostics after MessagePack StartGameAction. */
      getPaytableInfo: () => ({
        serverLen: this._serverPaytable?.length ?? 0,
        infoRows: this._infoPayoutAmountTexts?.length ?? 0,
        hasSettings: !!this._settingsRoot,
        bet: Number(this.slotMachine?.ui?.betValue) || 0,
        sample: this._serverPaytable?.[0] ?? null,
        firstLine: this._infoPayoutAmountTexts?.[0]
          ? {
              count: this._infoPayoutAmountTexts[0].count,
              odd: this._infoPayoutAmountTexts[0].odd,
              mult: this._infoPayoutAmountTexts[0].multText?.text,
              amount: this._infoPayoutAmountTexts[0].amountText?.text,
            }
          : null,
      }),
      /** Set overall content scale, e.g. slotGameDebug.setScale(0.75) */
      setScale: (s) => { this._contentScale = s; this.layout(); this._applySlotAspectLock(...Object.values(this._lastScales ?? { x: 1, y: 1 })); },
      /** Access bottom bar container (balance, win, quick-bets, icons) */
      get bottomBar() { return this._canvasBottomBar; },
      /** Five-of-kind on middle payline */
      forceMiddleLineJackpot: () => {
        const id = Math.min(5, symCount - 1);
        const grid = [];
        for (let c = 0; c < gameConfig.reelCount; c++) {
          grid[c] = [];
          for (let r = 0; r < gameConfig.rowCount; r++) {
            grid[c][r] = (r === 1 ? id : (c + r) % symCount) % symCount;
          }
        }
        this.debug.forceNextGrid(grid);
      },
      frame: {
        get: () => ({ ...this._frameControl }),
        set: (next = {}) => {
          if (!next || typeof next !== 'object') return;
          if (Number.isFinite(next.padX)) this._frameControl.padX = Number(next.padX);
          if (Number.isFinite(next.padY)) {
            const p = Number(next.padY);
            this._frameControl.padYTop = p;
            this._frameControl.padYBottom = p;
          }
          if (Number.isFinite(next.padYTop)) this._frameControl.padYTop = Number(next.padYTop);
          if (Number.isFinite(next.padYBottom)) this._frameControl.padYBottom = Number(next.padYBottom);
          if (Number.isFinite(next.offsetX)) this._frameControl.offsetX = Number(next.offsetX);
          if (Number.isFinite(next.offsetY)) this._frameControl.offsetY = Number(next.offsetY);
          if (Number.isFinite(next.scale) && next.scale > 0) this._frameControl.scale = Number(next.scale);
          this._applyFrameLayout();
        },
        reset: () => {
          this._frameControl = {
            padX: 76,
            padYTop: 96,
            padYBottom: 70,
            offsetX: 13,
            offsetY: -34,
            scale: 1,
          };
          this._applyFrameLayout();
        },
      },
      roundStatus: {
        get: () => this._loadRoundStatus(),
        getLive: () => this._roundStatusLive,
        getDeferred: () => this._deferredRoundResumeVisuals,
        isWinCollectPending: () => this._winCollectPending,
        isCelebrationVisible: () => this._isWinCelebrationVisible(),
      },
    };
  }

  /**
   * Groups reels, mask, and frame cosmetics under a child container so we can scale only the
   * “cabinet” on narrow phones; {@link #_canvasControls} stays a sibling at scale 1.
   */
  _ensureGameVisualLayer() {
    const rc = this.slotMachine?.reelContainer;
    const root = rc?.root;
    if (!rc || !root || this._gameVisualLayer) return;

    const layer = new Container();
    layer.sortableChildren = true;
    layer.eventMode = 'passive';
    this._gameVisualLayer = layer;

    const steal = (node) => {
      if (!node) return;
      if (node.parent) node.parent.removeChild(node);
      layer.addChild(node);
    };

    steal(rc.reelsLayer);
    steal(rc.expandingWildFxLayer);
    steal(rc._symbolWinLiftLayer);
    steal(rc.reelsMaskShape);
    rc.reelsLayer.mask = rc.reelsMaskShape;

    steal(this._matrixBgSprite);
    steal(this._slotBackgroundLinesSprite);
    steal(this._glowSprite);
    steal(this._frameSprite);
    steal(this._logoSprite);

    rc._reelsMaskHost = layer;
    root.addChildAt(layer, 0);
  }

  /** Narrow portrait phones only: scale the reel/frame cabinet so its frame edges fill the viewport width (uniform scale — no deformation); controls keep logical size. */
  _applyMobileGameVisualScale() {
    const layer = this._gameVisualLayer;
    if (!layer) return;
    const mobile = this._isPortraitMobileViewport();
    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;

    /**
     * Solve the cabinet's on-screen CSS width exactly from the live transform chain — no guessed magic
     * numbers, no clamps, no hardcoded assumptions about which ancestors exist.
     *
     * The frame sprite's design width times every ancestor scale on the way to the page must equal the
     * viewport's CSS width. We walk the live parent chain (so a new wrapper like `_cabinetGroup` from
     * {@link SlotMachine.layoutScreen} is picked up automatically) and read each `scale.x`, then add the
     * canvas's CSS-stretch factor (`_lastScales.x` set in {@link #_fitRendererToContainer}) and solve:
     *
     *     frameSprite.width · g · Π(ancestor.scale.x) · cssScaleX  =  cssW
     *
     * The single result `g` is applied uniformly to X and Y, so the cabinet's aspect ratio is preserved
     * exactly.
     */
    let g = 1;
    if (mobile && this._frameSprite && this._frameSprite.parent && this.app?.stage) {
      const { width: cssW } = this._readContainerSize();
      const frameLocalW = this._frameSprite.width;
      const cssScaleX = this._lastScales?.x ?? 1;
      /**
       * Cumulative scale.x of every ancestor *between* `_gameVisualLayer` and the Pixi stage
       * (so excluding the layer itself, whose scale is what we are solving for).
       */
      let ancestorScaleX = 1;
      const stage = this.app.stage;
      for (let node = layer.parent; node && node !== stage; node = node.parent) {
        const sx = node?.scale?.x;
        if (Number.isFinite(sx) && sx !== 0) ancestorScaleX *= sx;
      }
      const cssPerFrameLocal = frameLocalW * ancestorScaleX * cssScaleX;
      if (cssPerFrameLocal > 0 && cssW > 0) {
        g = cssW / cssPerFrameLocal;
      }
    }
    this._mobileGameVisualScale = g;

    layer.scale.set(g, g);
    const liftY = mobile ? this._getMobileGameVisualLiftY() : 0;
    layer.position.set((reelBlockW * (1 - g)) / 2, (reelBlockH * (1 - g)) / 2 + liftY);
  }

  _applyFrameLayout() {
    if (!this._frameSprite || !this.slotMachine) return;
    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;
    const c = this._frameControl;
    const padYTop = c.padYTop ?? c.padY ?? 70;
    const padYBottom = c.padYBottom ?? c.padY ?? 70;
    const baseW = reelBlockW + c.padX * 2;
    const baseH = reelBlockH + padYTop + padYBottom;
    const frameW = baseW * c.scale;
    const frameH = baseH * c.scale;
    this._frameSprite.width = frameW;
    this._frameSprite.height = frameH;
    this._frameSprite.x = -c.padX - (frameW - baseW) / 2 + c.offsetX;
    this._frameSprite.y = -padYTop - (frameH - baseH) / 2 + c.offsetY;

    // Matrix only behind the 5×3 symbol grid (not the full padded frame)
    if (this._matrixBgSprite) {
      const topFrameExtra = Math.max(0, padYTop - padYBottom) * c.scale;
      this._matrixBgSprite.x = 0;
      this._matrixBgSprite.y = -topFrameExtra;
      this._matrixBgSprite.width = reelBlockW;
      this._matrixBgSprite.height = reelBlockH + topFrameExtra;
    }

    if (this._slotBackgroundLinesSprite) {
      const topFrameExtra = Math.max(0, padYTop - padYBottom) * c.scale;
      const linesH = reelBlockH + topFrameExtra;
      const linesScale = this._slotBackgroundLinesScale ?? 1;
      const scaledW = reelBlockW * linesScale;
      const scaledH = linesH * linesScale;
      this._slotBackgroundLinesSprite.x = (reelBlockW - scaledW) / 2;
      this._slotBackgroundLinesSprite.y =
        -topFrameExtra + (linesH - scaledH) / 2 + (this._slotBackgroundLinesOffsetY ?? 0);
      this._slotBackgroundLinesSprite.width = scaledW;
      this._slotBackgroundLinesSprite.height = scaledH;
    }

    // Position glow behind frame (bigger and moved up slightly)
    if (this._glowSprite) {
      const glowScale = 1.4;  // 30% bigger than frame
      const topFrameExtra = Math.max(0, padYTop - padYBottom) * c.scale;
      const frameCenterX = this._frameSprite.x + frameW / 2;
      const frameCenterY = this._frameSprite.y + frameH / 2.2;
      this._glowSprite.width = frameW * glowScale;
      this._glowSprite.height = frameH * glowScale + topFrameExtra;
      this._glowSprite.x = frameCenterX;
      this._glowSprite.y = frameCenterY - 30 - topFrameExtra / 2;  // Extend extra glow upward when top frame padding is larger
    }

    // Position logo at top center of frame
    if (this._logoSprite) {
      const frameCenterX = this._frameSprite.x + frameW / 2;
      const frameTopY = this._frameSprite.y;
      const mobile = this._isPortraitMobileViewport();
      /* Portrait phones get the wider/shorter horizontal logo; other viewports keep stacked Logo0. */
      const desiredTex = mobile
        ? (this._logoTexHorizontal || this._logoTexDefault)
        : (this._logoTexDefault || this._logoTexHorizontal);
      if (desiredTex && this._logoSprite.texture !== desiredTex) {
        this._logoSprite.texture = desiredTex;
      }
      this._logoSprite.x = frameCenterX;
      this._logoSprite.y = frameTopY - (mobile ? 120 : 67);  // Position above frame top edge
      // Scale logo proportionally to frame
      const logoBaseWidth = mobile ? 670 : 440;  // Slightly bigger on mobile portrait
      this._logoSprite.width = logoBaseWidth;
      this._logoSprite.height = (this._logoSprite.texture.height / this._logoSprite.texture.width) * logoBaseWidth;
    }
  }

  _attachSpinHoldWindowReleaseGuard() {
    if (this._spinHoldWindowGuardActive) return;
    this._spinHoldWindowGuardActive = true;
    window.addEventListener('pointerup', this._onSpinHoldWindowPointerEnd, true);
    window.addEventListener('pointercancel', this._onSpinHoldWindowPointerEnd, true);
  }

  _detachSpinHoldWindowReleaseGuard() {
    if (!this._spinHoldWindowGuardActive) return;
    this._spinHoldWindowGuardActive = false;
    window.removeEventListener('pointerup', this._onSpinHoldWindowPointerEnd, true);
    window.removeEventListener('pointercancel', this._onSpinHoldWindowPointerEnd, true);
  }

  /**
   * @param {import('pixi.js').FederatedPointerEvent | PointerEvent} e
   */
  _onSpinHoldWindowPointerEnd(e) {
    this._finalizeSpinHoldFromPointerEnd(e);
  }

  _isWinCelebrationVisible() {
    return !!this.slotMachine?.winCelebration?.root?.visible;
  }

  /** Collect button state or big-win overlay still on screen. */
  _isWinCollectDismissActive() {
    if (this._resumeRoundSkipCollect) return !!this._winCollectPending;
    return this._winCollectPending || this._isWinCelebrationVisible();
  }

  _attachWinCollectWindowPointerGuard() {
    if (this._winCollectWindowGuardActive) return;
    this._winCollectWindowGuardActive = true;
    window.addEventListener('pointerdown', this._onWinCollectWindowPointerStart, true);
    window.addEventListener('pointerup', this._onWinCollectWindowPointerEnd, true);
    window.addEventListener('pointercancel', this._onWinCollectWindowPointerEnd, true);
  }

  _detachWinCollectWindowPointerGuard() {
    if (!this._winCollectWindowGuardActive) return;
    this._winCollectWindowGuardActive = false;
    window.removeEventListener('pointerdown', this._onWinCollectWindowPointerStart, true);
    window.removeEventListener('pointerup', this._onWinCollectWindowPointerEnd, true);
    window.removeEventListener('pointercancel', this._onWinCollectWindowPointerEnd, true);
  }

  /**
   * Consume pointer events during collect/big-win dismiss mode so no other UI actions
   * (spin, settings, chips, etc.) can process the same click gesture.
   * @param {PointerEvent} e
   */
  _consumeWinCollectPointerEvent(e) {
    try { e.preventDefault(); } catch { /* ignore */ }
    try { e.stopImmediatePropagation(); } catch { /* ignore */ }
    try { e.stopPropagation(); } catch { /* ignore */ }
  }

  /** Window capture start while collect / big-win overlay is up. */
  _onWinCollectWindowPointerStart(e) {
    if (!isPrimaryPointerAction(e)) return;
    if (!this._isWinCollectDismissActive()) return;
    this._consumeWinCollectPointerEvent(e);
  }

  /** Window capture release while collect / big-win overlay is up (same as collect button). */
  _onWinCollectWindowPointerEnd(e) {
    if (!isPrimaryPointerAction(e)) return;
    if (!this._isWinCollectDismissActive()) return;
    // Abort any pending spin-hold before consuming the event. Without this, stopImmediatePropagation
    // below prevents the spin-hold window guard and Pixi pointerup from running, leaving the
    // 1300 ms hold timer alive — which would fire turbo autoplay even though the user released.
    if (this._spinHoldInProgress) {
      const hadTurbo = this._spinHoldTriggeredTurbo;
      this._stopSpinHoldTracking();
      this._resetSpinButtonHoldVisual();
      this._spinHoldTriggeredTurbo = false;
      this._spinPressAccepted = false;
      if (hadTurbo) {
        this._turboHoldActive = false;
        this.handleAutoplayToggle(false);
      }
    }
    this._consumeWinCollectPointerEvent(e);
    void this._performWinCollectTap();
  }

  /**
   * Main spin/stop tap (same as a short press on the spin control).
   */
  _spinOrStopMainButtonTap() {
    sound.onUserGesture();
    const now = performance.now();
    if (this._spinning) {
      this._requestStopSpin();
    } else {
      this._spinToggleLockUntilTs = Math.max(
        this._spinToggleLockUntilTs,
        now + this._spinToggleCooldownMs,
      );
      this.handleSpin();
    }
  }

  /**
   * Ends spin-button hold tracking and either stops hold-turbo autoplay or performs a normal tap.
   * Shared by Pixi pointerup and window pointerup/cancel so release is never missed on slow devices.
   * @param {import('pixi.js').FederatedPointerEvent | PointerEvent} e
   */
  _finalizeSpinHoldFromPointerEnd(e) {
    if (!isPrimaryPointerAction(e)) return;

    // Reject mismatched pointer IDs before touching any state.
    if (
      this._spinHoldInProgress &&
      this._spinHoldPointerId != null &&
      e.pointerId != null &&
      e.pointerId !== this._spinHoldPointerId
    ) {
      return;
    }

    // Snapshot state before any mutation so we can decide what action to take afterwards.
    const wasHoldInProgress = this._spinHoldInProgress;
    const hadTurbo = this._spinHoldTriggeredTurbo;
    const wasAccepted = this._spinPressAccepted;

    // Always abort hold tracking on pointer release — regardless of win-collect state.
    // Deferring this until after the win-collect check was the original bug: the hold timer
    // could fire turbo autoplay after the pointer was already released.
    if (wasHoldInProgress) {
      this._stopSpinHoldTracking();
      this._resetSpinButtonHoldVisual();
      this._spinHoldTriggeredTurbo = false;
      this._spinPressAccepted = false;
      if (hadTurbo) {
        this._turboHoldActive = false;
        this.handleAutoplayToggle(false);
      }
    }

    // Win-collect phase: treat the release as a collect tap; hold cleanup already handled above.
    if (this._isWinCollectDismissActive()) {
      void this._performWinCollectTap();
      return;
    }

    // Nothing to do if this press was never accepted or no hold was in progress.
    if (!wasAccepted || !wasHoldInProgress || hadTurbo) return;
    this._spinOrStopMainButtonTap();
  }

  async _buildCanvasControls() {
    if (!this.slotMachine) return;
    const reelRoot = this.slotMachine.reelContainer?.root;
    if (!reelRoot) return;

    const controls = new Container();
    controls.eventMode = 'passive';
    controls.zIndex = 15;
    controls.sortableChildren = true;
    this._canvasControls = controls;
    reelRoot.addChild(controls);

    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap -4;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;

    const loadSvgTexture = (url) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(Texture.from(img));
        img.onerror = () => resolve(Texture.EMPTY);
        img.src = assetUrl(url);
      });

    const [startTex, stopTex, collectWinTex, autoplayTex, betTex, betSpeedBgTex, betSpeedActiveTex, betSpeedInactiveTex, autoplayPickerQbTex] =
      await Promise.all([
      loadSvgTexture('/assets/start.png'),
      loadSvgTexture('/assets/stop.svg'),
      loadSvgTexture('/assets/collect_win.svg'),
      loadSvgTexture('/assets/autoplay.png'),
      loadSvgTexture('/assets/bet.png'),
      loadSvgTexture('/assets/bet_speed.png'),
      loadSvgTexture('/assets/bet_speed_active_arrow.png'),
      loadSvgTexture('/assets/bet_speed_inactive_arrow.png'),
      loadSvgTexture('/assets/quick_bet.svg'),
    ]);

    const makeButton = ({ texture, width, height, x, y, onClick }) => {
      // Set texture to use better quality scaling
      if (texture.source) {
        texture.source.scaleMode = 'linear';
      }
      
      const sprite = new Sprite(texture);
      sprite.width = width;
      sprite.height = height;
      sprite.x = x;
      sprite.y = y;
      sprite.cursor = 'pointer';
      sprite.eventMode = 'static';
      sprite.roundPixels = true;
      sprite.on('pointerup', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        sound.onUserGesture();
        onClick();
      });
      controls.addChild(sprite);
      return sprite;
    };

    // padX=76 → frame right edge is at reelBlockW+76; start spin button ~40px before that so it straddles the frame border
    const spinX = reelBlockW;
    /** Negative = move start / autoplay / bet cluster upward together */
    const spinY = Math.round((reelBlockH - 194) / 2) - 55;

    // Single spin/stop toggle button (container: glyph + autoplay remaining on stop)
    if (startTex.source) startTex.source.scaleMode = 'linear';
    const spinRoot = new Container();
    spinRoot.x = spinX + 194 / 2;
    spinRoot.y = spinY + 194 / 2;
    spinRoot.eventMode = 'static';
    spinRoot.cursor = 'pointer';
    spinRoot.roundPixels = true;
    spinRoot.hitArea = new Rectangle(-97, -97, 194, 194);

    const spinImg = new Sprite(startTex);
    spinImg.width = 194;
    spinImg.height = 194;
    spinImg.anchor.set(0.5);

    const remainLabel = new Text({
      text: '',
      style: {
        fontFamily: '"Montserrat", "Arial", sans-serif',
        fontSize: 26,
        fontWeight: '900',
        fill: 0x000000,
        align: 'center',
      },
    });
    remainLabel.anchor.set(0.5, 0.5);
    remainLabel.x = 5;
    remainLabel.y = -6;
    remainLabel.visible = false;
    remainLabel.eventMode = 'none';

    const spinTurboHint = createArcTextController({
      radius: 70,
      fontSize: 12,
      fontWeight: '800',
      fill: 0xffffff,
      letterSpacing: 1,
      yOffset: -2,
    });
    spinTurboHint.setText(this._tu('HOLD_FOR_TURBO_SPIN'));
    spinTurboHint.container.zIndex = 2;

    spinRoot.addChild(spinImg, spinTurboHint.container, remainLabel);
    spinRoot.sortableChildren = true;
    controls.addChild(spinRoot);
    this._spinButtonRoot = spinRoot;
    this._spinButtonSprite = spinImg;
    this._spinAutoplayRemainText = remainLabel;
    this._spinTurboHint = spinTurboHint;
    this._spinStartTex = startTex;
    this._spinStopTex = stopTex;
    this._spinCollectTex = collectWinTex;
    this._spinButtonBaseScale = 1;
    this._layoutSpinTurboHint();
    this._syncSpinTurboHintVisible();
    spinRoot.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      if (this._isWinCollectDismissActive()) return;
      const now = performance.now();
      // Cooldown only applies to starting a spin from idle — never block an immediate stop tap
      // after start (same button reuses the lock from the previous pointerdown).
      if (!this._spinning && now < this._spinToggleLockUntilTs) {
        this._spinPressAccepted = false;
        return;
      }
      if (!this._spinning) {
        this._spinToggleLockUntilTs = now + this._spinToggleCooldownMs;
      }
      if (this._spinHoldInProgress) return;
      sound.onUserGesture();
      this._spinPressAccepted = true;
      this._spinHoldInProgress = true;
      this._spinHoldTriggeredTurbo = false;
      this._spinHoldPointerId = e.pointerId;
      this._spinHoldStartTs = performance.now();
      this._attachSpinHoldWindowReleaseGuard();
      const canvas = this.app?.canvas;
      const native = e.nativeEvent;
      if (
        canvas &&
        native &&
        typeof canvas.setPointerCapture === 'function' &&
        typeof native.pointerId === 'number'
      ) {
        try {
          canvas.setPointerCapture(native.pointerId);
          this._spinHoldPointerCaptureActive = true;
        } catch {
          this._spinHoldPointerCaptureActive = false;
        }
      } else {
        this._spinHoldPointerCaptureActive = false;
      }
      const tick = () => {
        if (!this._spinHoldInProgress) return;
        const elapsed = performance.now() - this._spinHoldStartTs;
        let progress = 0;
        const visualDelay = Math.max(0, Number(this._spinHoldVisualDelayMs) || 0);
        if (elapsed <= visualDelay) {
          this._resetSpinButtonHoldVisual();
        } else {
          const holdSpan = Math.max(1, this._spinHoldMs - visualDelay);
          progress = (elapsed - visualDelay) / holdSpan;
          this._setSpinButtonHoldProgress(progress);
        }
        if (progress < 1) {
          this._spinHoldRaf = requestAnimationFrame(tick);
        } else {
          this._spinHoldRaf = null;
        }
      };
      this._spinHoldRaf = requestAnimationFrame(tick);
      if (this._spinHoldTimer != null) clearTimeout(this._spinHoldTimer);
      this._spinHoldTimer = setTimeout(() => {
        this._spinHoldTimer = null;
        if (!this._spinHoldInProgress || this._spinHoldTriggeredTurbo) return;
        this._activateTurboFromHold();
      }, this._spinHoldMs);
    });
    const onSpinPointerRelease = (e) => {
      this._finalizeSpinHoldFromPointerEnd(e);
    };
    spinRoot.on('pointerup', onSpinPointerRelease);
    spinRoot.on('pointerupoutside', onSpinPointerRelease);

    if (
      betSpeedBgTex?.width > 0 &&
      betSpeedActiveTex?.width > 0 &&
      betSpeedInactiveTex?.width > 0
    ) {
      if (betSpeedBgTex.source) betSpeedBgTex.source.scaleMode = 'linear';
      if (betSpeedActiveTex.source) betSpeedActiveTex.source.scaleMode = 'linear';
      if (betSpeedInactiveTex.source) betSpeedInactiveTex.source.scaleMode = 'linear';

      this._betSpeedActiveTex = betSpeedActiveTex;
      this._betSpeedInactiveTex = betSpeedInactiveTex;

      const betSpeedWrap = new Container();
      betSpeedWrap.eventMode = 'static';
      betSpeedWrap.cursor = 'pointer';

      const bgW = 82;
      const bgH = (betSpeedBgTex.height / betSpeedBgTex.width) * bgW;
      const betBg = new Sprite(betSpeedBgTex);
      betBg.anchor.set(0.5);
      betBg.width = bgW;
      betBg.height = bgH;

      const arrowSprites = [];
      const arrowW = 8;
      /** Horizontal gap between arrow edges (row stays centered on the plate). */
      const gapBetweenArrows = 4;
      const totalArrowRowW = 3 * arrowW + 2 * gapBetweenArrows;
      const arrowRow = new Container();
      for (let i = 0; i < 3; i++) {
        const a = new Sprite(betSpeedInactiveTex);
        a.anchor.set(0.5);
        const iw = betSpeedInactiveTex.width;
        const ih = betSpeedInactiveTex.height;
        a.width = arrowW;
        a.height = (ih / iw) * arrowW;
        a.x = -totalArrowRowW / 2 + arrowW / 2 + i * (arrowW + gapBetweenArrows);
        arrowSprites.push(a);
        arrowRow.addChild(a);
      }
      arrowRow.y = -bgH * 0.02;
      this._betSpeedArrowSprites = arrowSprites;

      betSpeedWrap.addChild(betBg, arrowRow);
      betSpeedWrap.x = spinRoot.x;
      const betSpeedLowerOffset = 14;
      betSpeedWrap.y = spinRoot.y + 194 / 2 - bgH / 2 + betSpeedLowerOffset;

      const hitPad = 3;
      betSpeedWrap.hitArea = new Rectangle(
        -bgW / 2 - hitPad,
        -bgH / 2 - hitPad,
        bgW + 2 * hitPad,
        bgH + 2 * hitPad,
      );

      const stopBetSpeedBubble = (e) => {
        if (typeof e.stopPropagation === 'function') e.stopPropagation();
      };
      betSpeedWrap.on('pointerdown', stopBetSpeedBubble);
      betSpeedWrap.on('pointerup', stopBetSpeedBubble);
      betSpeedWrap.on('pointerupoutside', stopBetSpeedBubble);
      betSpeedWrap.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        stopBetSpeedBubble(e);
        if (this._settingsQuickSpinOn) return;
        this._betSpeedTier = this._betSpeedTier >= 3 ? 1 : this._betSpeedTier + 1;
        this._syncBetSpeedArrows();
        sound.play('uiSpinSpeed');
      });

      controls.addChild(betSpeedWrap);
      this._betSpeedWrap = betSpeedWrap;
      this._betSpeedPlateH = bgH;
      this._betSpeedTier = 1;
      this._syncBetSpeedArrows();
    }

    const autoplayBtnW = 100;
    const autoplayBtnH = 100;
    const pickGap = 8;
    const pickBtnW = 100;
    const pickBtnH = 95;
    const pickRowW = 5 * pickBtnW + 4 * pickGap;
    const autoplayCluster = new Container();
    autoplayCluster.x = spinX + 47 - pickRowW - pickGap;
    autoplayCluster.y = spinY - 108;
    autoplayCluster.eventMode = 'static';
    autoplayCluster.sortableChildren = true;
    autoplayCluster.zIndex = 20;

    const pickerRow = new Container();
    pickerRow.visible = false;
    pickerRow.zIndex = 1;
    const fontQB = '"Montserrat", "Arial", sans-serif';
    const roundSpecs = [
      { label: '10', remaining: 10 },
      { label: '20', remaining: 20 },
      { label: '50', remaining: 50 },
      { label: '100', remaining: 100 },
      { label: '∞', remaining: null },
    ];
    const qbTex = autoplayPickerQbTex?.width > 0 ? autoplayPickerQbTex : Texture.EMPTY;
    if (qbTex?.source) qbTex.source.scaleMode = 'linear';
    roundSpecs.forEach((spec, i) => {
      const bg = new Sprite(qbTex);
      bg.width = pickBtnW;
      bg.height = pickBtnH;
      const amt = new Text({
        text: spec.label,
        style: {
          fontFamily: fontQB,
          fontSize: spec.label === '∞' ? 50 : fontSizeWhenFormattedLong(spec.label, 26, 18),
          fontWeight: '700',
          fill: 0xffffff,
        },
      });
      amt.anchor.set(0.5, 0.5);
      amt.x = pickBtnW / 2;
      amt.y = pickBtnH * 0.48;

      const btn = new Container();
      btn.addChild(bg, amt);
      btn.eventMode = 'static';
      btn.cursor = 'pointer';
      btn.hitArea = new Rectangle(0, 0, pickBtnW, pickBtnH);
      btn.on('pointerup', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        sound.play('uiClick');
        this._startAutoplayWithRounds(spec.remaining);
      });
      pickerRow.addChild(btn);
    });

    const autoplaySprite = new Sprite(autoplayTex);
    autoplaySprite.width = autoplayBtnW;
    autoplaySprite.height = autoplayBtnH;
    if (autoplayTex?.source) autoplayTex.source.scaleMode = 'linear';
    autoplaySprite.cursor = 'pointer';
    autoplaySprite.eventMode = 'static';
    autoplaySprite.roundPixels = true;
    autoplaySprite.zIndex = 2;
    autoplaySprite.on('pointerup', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._onAutoplayIconPointerUp();
    });

    autoplayCluster.addChild(pickerRow, autoplaySprite);
    controls.addChild(autoplayCluster);
    this._autoplayCluster = autoplayCluster;
    this._autoplaySprite = autoplaySprite;
    this._autoplayPickerRow = pickerRow;
    this._layoutAutoplayPickerCluster(false);

    const betPopupBtn = makeButton({
      texture: betTex,
      width: 100,
      height: 100,
      x: spinX + 52,
      y: spinY + 217,
      onClick: () => this._toggleBetPopup(),
    });
    this._betPopupTriggerBtn = betPopupBtn;
    this._betPopupTriggerControlsIndex = controls.getChildIndex(betPopupBtn);

    /* ── Balance / Last-win / Quick-bets panels ──────────────────────────
     * HTML design positions (1922×1080):
     *   balance:    left=30,   top=933
     *   last win:   left=1696, top=933
     *   quick bets: left=631,  top=930
     *
     * Converting to reelRoot-local via effective scale ≈ 1.183:
     *   balX ≈ -344,  winX ≈ 1063,  qbStartX ≈ 164,  panelY ≈ reelBlockH+76
     * ------------------------------------------------------------------ */
    const [balBgTex, mobileBalBgTex, qbNormTex, qbSelTex, homeTex, fullscreenTex, soundOnTex, soundOffTex, menuTex, arrowLeftTex, arrowRightTex] = await Promise.all([
      loadSvgTexture('/assets/balance_background.svg'),
      Assets.load('/assets/mobile_balance_background.png').catch(() => Texture.EMPTY),
      loadSvgTexture('/assets/quick_bet.svg'),
      loadSvgTexture('/assets/selected_quick_bet.svg'),
      loadSvgTexture('/assets/home.svg'),
      loadSvgTexture('/assets/full_screen.svg'),
      loadSvgTexture('/assets/sounds_on.svg'),
      loadSvgTexture('/assets/sounds_off.svg'),
      loadSvgTexture('/assets/menu.svg'),
      loadSvgTexture('/assets/arrow_left.svg'),
      loadSvgTexture('/assets/arrow_right.svg'),
    ]);
    const linesBgTex = await Assets.load('/assets/lines_background.png').catch(() => balBgTex);
    this._mobilePortraitTopStatsTex = mobileBalBgTex?.width > 0 ? mobileBalBgTex : null;

    this._qbNormTex = qbNormTex;
    this._qbSelTex  = qbSelTex;

    /* Lines panel (left side, shows active line count) - same structure as balance/win */
    const linesPanelW = 151;
    const linesPanelH = 133;
    
    const linesPanel = new Container();
    
    const linesBg = new Sprite(linesBgTex);
    linesBg.width = linesPanelW;
    linesBg.height = linesPanelH;
    
    const linesNum = new Text({
      text: `${gameConfig.paylines.length}`,
      style: {
        fontFamily: 'Montserrat',
        fontSize: 37.9167,
        fontWeight: '700',
        fill: 0xffffff,
        align: 'center',
      }
    });
    linesNum.anchor.set(0.5, 0.5);
    linesNum.x = linesPanelW / 2;
    linesNum.y = linesPanelH * 0.35;
    
    const linesLabel = new Text({
      text: 'LINES',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 20.4167,
        fontWeight: '600',
        fill: 0xffffff,
        align: 'center',
      }
    });
    linesLabel.anchor.set(0.5, 0.5);
    linesLabel.x = linesPanelW / 2;
    linesLabel.y = linesPanelH * 0.60;
    
    linesPanel.addChild(linesBg, linesNum, linesLabel);
    linesPanel.x = -250;  // Position to the left of reels, closer to frame
    linesPanel.y = spinY + 30;  // Align roughly with spin button
    linesPanel.eventMode = 'passive';
    
    controls.addChild(linesPanel);
    this._canvasLinesPanel = linesPanel;

    /* ── Bottom Bar Container (balance, win, quick-bets, icons) ──────────── */
    const bottomBar = new Container();
    bottomBar.sortableChildren = true;
    bottomBar.y = reelBlockH + 50;  // Moved closer to reels (was +76)
    bottomBar.eventMode = 'passive';
    controls.addChild(bottomBar);
    this._canvasBottomBar = bottomBar;

    const panelW  = 196;
    const panelH  = 90;
    /** Merged portrait top row (BALANCE | prompt | LAST WIN): height & column split — also applied in {@link #_relayoutBottomBarForViewport}. */
    const portraitTopStatsH = PORTRAIT_MERGED_STATS_BAR_H;
    const portraitTopColBal = 0.38;
    const portraitTopColMid = 0.26;
    const portraitTopColWin = 0.36;
    const font    = '"Montserrat", "Arial", sans-serif';

    const layoutStatPanelStack = (lbl, val, cur, boxH, centerX) => {
      const gap1 = 1; /* label → amount */
      const gap2 = 2; /* amount → currency */
      lbl.anchor.set(0.5, 0);
      val.anchor.set(0.5, 0);
      cur.anchor.set(0.5, 0);
      lbl.x = centerX;
      val.x = centerX;
      cur.x = centerX;
      const total = lbl.height + gap1 + val.height + gap2 + cur.height;
      const stackBiasY = -3; /* small downward bias from vertical center; lower value = stack sits slightly higher */
      const y = Math.max(4, (boxH - total) / 2) + stackBiasY;
      /* Title (“BALANCE” / “LAST WIN”) only — shift down; amount + FUN positions unchanged */
      const titleLower = 2;
      lbl.y = y + titleLower;
      val.y = y + lbl.height + gap1;
      cur.y = val.y + val.height + gap2;
    };

    const makeStatPanel = (x, labelStr) => {
      const bg = new Sprite(balBgTex);
      bg.width = panelW; bg.height = panelH;

      const lbl = new Text({ text: labelStr, style: { fontFamily: font, fontSize: 10, fontWeight: '700', fill: 0xffffff } });
      const val = new Text({ text: '0.00', style: { fontFamily: font, fontSize: 20, fontWeight: '700', fill: 0xd0ff00 } });
      const cur = new Text({ text: this._currencyLabel(), style: { fontFamily: font, fontSize: 12, fontWeight: '700', fill: 0xffea00 } });

      const relayout = () => layoutStatPanelStack(lbl, val, cur, panelH, panelW / 2);
      relayout();

      const panel = new Container();
      panel.addChild(bg, lbl, val, cur);
      panel.x = x; panel.y = 0;
      bottomBar.addChild(panel);
      return { label: lbl, val, currency: cur, panel, relayout };
    };

    /* Quick-bets scrollable masked group (show ~6 buttons at a time) */
    const qbMaskG = new Graphics();
    const qbVisibleW = 6 * (100 + 8);  // 6 buttons visible
    const qbH = 95;
    qbMaskG.rect(0, 0, qbVisibleW, qbH).fill(0xffffff);
    
    // Center the entire bottom bar layout (balance + arrow + qb + arrow + win)
    const reelCenter = reelBlockW / 2;
    const arrowBtnW = arrowLeftTex.width;  // Use natural arrow dimensions
    const arrowBtnH = arrowLeftTex.height;
    const qbToArrowGap = 20;  // 20px between quick bets and arrows
    const arrowToPanelGap = 40;  // 40px between arrows and balance/win panels
    
    // Total width of the centered group: panel + gap + arrow + gap + qb + gap + arrow + gap + panel
    const totalGroupW = panelW + arrowToPanelGap + arrowBtnW + qbToArrowGap + qbVisibleW + qbToArrowGap + arrowBtnW + arrowToPanelGap + panelW;
    const groupStartX = reelCenter - totalGroupW / 2;
    
    // Calculate positions from left to right
    const balanceX = groupStartX;
    const leftArrowX = balanceX + panelW + arrowToPanelGap;
    const qbX = leftArrowX + arrowBtnW + qbToArrowGap;
    const rightArrowX = qbX + qbVisibleW + qbToArrowGap;
    const winX = rightArrowX + arrowBtnW + arrowToPanelGap;
    
    const balPanel = makeStatPanel(balanceX + CANVAS_STAT_SHIFT_BALANCE_X, this._tu('BALANCE'));
    const winPanel = makeStatPanel(winX + CANVAS_STAT_SHIFT_LAST_WIN_X, this._tu('LAST_WIN'));
    this._canvasBalanceLabelText = balPanel.label;
    this._canvasWinLabelText = winPanel.label;
    this._canvasBalanceText = balPanel.val;
    this._canvasWinText = winPanel.val;
    this._canvasBalanceCurrencyText = balPanel.currency;
    this._canvasWinCurrencyText = winPanel.currency;
    this._canvasBalancePanel = balPanel.panel;
    this._canvasWinPanel = winPanel.panel;
    this._canvasBalancePanelRelayout = balPanel.relayout;
    this._canvasWinPanelRelayout = winPanel.relayout;

    /* Vertical-only merged header background: BALANCE | two-line bet prompt | LAST WIN */
    const portraitTopBar = new Container();
    portraitTopBar.sortableChildren = true;
    portraitTopBar.visible = false;
    const portraitBg = new Sprite(balBgTex);
    portraitBg.width = reelBlockW - 20;
    portraitBg.height = PORTRAIT_MERGED_STATS_BG_HEIGHT;
    portraitBg.y = (portraitTopStatsH - PORTRAIT_MERGED_STATS_BG_HEIGHT) / 2;
    portraitTopBar.addChild(portraitBg);
    /* Currency intentionally slightly larger than the column title (label). */
    const slotLabelStyle = { fontFamily: font, fontSize: 11, fontWeight: '700', fill: 0xffffff };
    const slotValueStyle = { fontFamily: font, fontSize: 22, fontWeight: '700', fill: 0xd0ff00 };
    const slotCurrencyStyle = { fontFamily: font, fontSize: 13, fontWeight: '700', fill: 0xffea00 };
    const makeTopSlot = (label, valueText, x) => {
      const g = new Container();
      g.x = x;
      const lbl = new Text({ text: label, style: slotLabelStyle });
      const val = new Text({ text: valueText, style: slotValueStyle });
      const cur = new Text({ text: this._currencyLabel(), style: slotCurrencyStyle });
      const gap1 = 1; /* label → amount */
      const gap2 = 2; /* amount → currency */
      const relayout = () => {
        lbl.anchor.set(0.5, 0);
        val.anchor.set(0.5, 0);
        cur.anchor.set(0.5, 0);
        lbl.x = 0;
        val.x = 0;
        cur.x = 0;
        const total = lbl.height + gap1 + val.height + gap2 + cur.height;
        const stackBiasY = -3;
        const y0 = Math.max(4, (portraitTopStatsH - total) / 2) + stackBiasY;
        const titleLower = 2;
        lbl.y = y0 + titleLower;
        val.y = y0 + lbl.height + gap1;
        cur.y = val.y + val.height + gap2;
      };
      relayout();
      g.addChild(lbl, val, cur);
      portraitTopBar.addChild(g);
      return { label: lbl, val, currency: cur, relayout };
    };
    const ptW0 = portraitBg.width;
    const ptWB = ptW0 * portraitTopColBal;
    const ptWC = ptW0 * portraitTopColMid;
    const ptWW = ptW0 * portraitTopColWin;
    const balTop = makeTopSlot(
      this._tu('BALANCE'),
      this._canvasBalanceText.text || '0.00',
      ptWB * 0.5 + CANVAS_STAT_SHIFT_BALANCE_X,
    );
    this._portraitBalanceLabelText = balTop.label;
    this._portraitBalanceValueText = balTop.val;
    this._portraitBalanceCurrencyText = balTop.currency;
    this._relayoutPortraitBalanceStack = balTop.relayout;
    const centerSlot = new Container();
    centerSlot.zIndex = 3;
    centerSlot.x = ptWB + ptWC * 0.5;
    const centerPrompt = new Text({
      text: this._twoLineText('PLEASE_PLACE_YOUR_BET'),
      style: {
        fontFamily: font,
        fontSize: PORTRAIT_BET_PROMPT_FONT_SIZE,
        fontWeight: '900',
        fill: 0xffffff,
        align: 'center',
        lineHeight: PORTRAIT_BET_PROMPT_LINE_HEIGHT,
      },
    });
    centerPrompt.anchor.set(0.5, 0.5);
    centerPrompt.x = 0;
    centerPrompt.y = portraitTopStatsH * 0.46;
    centerSlot.addChild(centerPrompt);
    this._portraitTopPromptText = centerPrompt;
    portraitTopBar.addChild(centerSlot);
    const winTop = makeTopSlot(
      this._tu('LAST_WIN'),
      this._canvasWinText.text || '0.00',
      ptWB + ptWC + ptWW * 0.5 + CANVAS_STAT_SHIFT_LAST_WIN_X,
    );
    this._portraitWinLabelText = winTop.label;
    this._portraitWinValueText = winTop.val;
    this._portraitWinCurrencyText = winTop.currency;
    this._relayoutPortraitWinStack = winTop.relayout;
    bottomBar.addChildAt(portraitTopBar, 0);
    this._portraitTopStatsBar = portraitTopBar;
    this._portraitTopStatsBgSprite = portraitBg;
    this._portraitTopStatsDefaultTex = balBgTex;

    qbMaskG.x = qbX; qbMaskG.y = 2;
    bottomBar.addChild(qbMaskG);
    this._canvasQbMaskG = qbMaskG;

    const qbGroup = new Container();
    qbGroup.mask = qbMaskG;
    qbGroup.x = qbX; qbGroup.y = 2;
    qbGroup.eventMode = 'static';
    bottomBar.addChild(qbGroup);
    this._canvasQbGroup = qbGroup;
    this._qbScrollX = 0;
    this._qbChipW = 100;
    this._qbChipH = 95;
    this._qbChipGap = 8;
    this._qbChipStride = 108;
    this._ensureCanvasQbScrollHandlers();

    /* Arrow buttons for scrolling quick bets */
    const arrowY = 2 + (qbH - arrowBtnH) / 2;  // Vertically centered with quick bets
    const makeArrowButton = (tex, x, scrollDir) => {
      const sprite = new Sprite(tex);
      // Use natural texture dimensions (no width/height override)
      sprite.x = x;
      sprite.y = arrowY;
      sprite.cursor = 'pointer';
      sprite.eventMode = 'static';
      sprite.roundPixels = true;
      sprite.on('pointerup', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        this._nudgeQbScrollByArrow(scrollDir);
      });
      bottomBar.addChild(sprite);
      return sprite;
    };
    
    this._qbArrowLeft = makeArrowButton(arrowLeftTex, leftArrowX, 1);  // Scroll right (positive direction)
    this._qbArrowRight = makeArrowButton(arrowRightTex, rightArrowX, -1);  // Scroll left (negative direction)

    /* "Please place your bets" — just under the symbol grid / frame, above the bottom bar */
    const underFramePromptY = reelBlockH + 35;
    const betPromptText = this._tu('PLEASE_PLACE_YOUR_BETS');
    const betPrompt = new Text({
      text: betPromptText,
      style: {
        fontFamily: '"Montserrat", "Arial", sans-serif',
        fontSize: 20,
        fontWeight: '900',
        fill: 0xffffff,
        align: 'center',
        letterSpacing: 0.5,
        wordWrap: true,
        wordWrapWidth: Math.max(280, reelBlockW - 48),
      },
    });
    betPrompt.anchor.set(0.5, 0.5);
    betPrompt.x = reelBlockW / 2;
    betPrompt.y = underFramePromptY;
    betPrompt.eventMode = 'none';
    betPrompt.roundPixels = true;
    betPrompt.zIndex = 16;
    controls.addChild(betPrompt);
    this._canvasBetPrompt = betPrompt;
    this._setBetPromptVisible(this._betPromptVisible);

    /* Side icon buttons (home, fullscreen, sound, menu) bottom-aligned with balance/win */
    const iconBtnSize = 36;
    const iconY = panelH - iconBtnSize;  // bottom-aligned with balance/win panels
    const iconGap = 80;  // 60px distance from balance/win panels

    this._canvasIconSprites = [];
    const makeIconButton = (tex, x, onClick) => {
      const sprite = new Sprite(tex);
      sprite.width = iconBtnSize;
      sprite.height = iconBtnSize;
      sprite.x = x;
      sprite.y = iconY;
      sprite.cursor = 'pointer';
      sprite.eventMode = 'static';
      sprite.on('pointerup', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        onClick();
      });
      bottomBar.addChild(sprite);
      this._canvasIconSprites.push(sprite);
      return sprite;
    };

    // Calculate icon button positions based on the centered panels
    const balanceLeftEdge = balanceX + CANVAS_STAT_SHIFT_BALANCE_X;
    const winRightEdge = winX + panelW + CANVAS_STAT_SHIFT_LAST_WIN_X;

    // Left side: Home (left) + Fullscreen (right)
    const iconSpacing = 10;  // Small gap between the two icons on same side
    // Home icon hidden on all devices.
    // makeIconButton(homeTex, balanceLeftEdge - iconGap - iconBtnSize * 2 - iconSpacing, () => {
    //   sound.play('uiClick');
    //   window.location.href = '/';
    // });
    const homeHidden = new Sprite(Texture.EMPTY);
    homeHidden.visible = false;
    homeHidden.renderable = false;
    homeHidden.eventMode = 'none';
    this._canvasIconSprites.push(homeHidden);
    makeIconButton(fullscreenTex, balanceLeftEdge - iconGap - iconBtnSize * 2 - iconSpacing, () => {
      sound.play('uiClick');
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    });

    // Right side: Sound + Menu (60px right of win panel)
    this._soundOnTex = soundOnTex;
    this._soundOffTex = soundOffTex;
    this._canvasSoundIconSprite = makeIconButton(soundOnTex, winRightEdge + iconGap, () => {
      this._settingsSoundOn = !this._settingsSoundOn;
      sound.setMuted(!this._settingsSoundOn);
      this._syncCanvasSoundIcon();
      /* Play *after* setMuted: when turning sound back ON we want the
       * confirmation click to be audible. */
      sound.play('uiClick');
    });
    this._syncCanvasSoundIcon();
    makeIconButton(menuTex, winRightEdge + iconGap + iconBtnSize + iconSpacing, () => {
      this._toggleSettingsPanel();
    });

    await this._createBetPopupPixi();

    this._snapshotDesktopCanvasLayout();
  }

  _syncCanvasSoundIcon() {
    const sprite = this._canvasSoundIconSprite;
    if (!sprite || !this._soundOnTex || !this._soundOffTex) return;
    sprite.texture = this._settingsSoundOn ? this._soundOnTex : this._soundOffTex;
  }

  /** Called once after canvas controls are built — restores baseline positions when exiting mobile. */
  _snapshotDesktopCanvasLayout() {
    const spin = this._spinButtonRoot;
    const ap = this._autoplayCluster;
    const betBtn = this._betPopupTriggerBtn;
    const bb = this._canvasBottomBar;
    const prompt = this._canvasBetPrompt;
    const lines = this._canvasLinesPanel;
    const speed = this._betSpeedWrap;
    if (!spin || !bb) return;
    this._canvasDesktopLayout = {
      spinRoot: { x: spin.x, y: spin.y },
      autoplayCluster: ap ? { x: ap.x, y: ap.y } : null,
      betPopupTrigger: betBtn ? { x: betBtn.x, y: betBtn.y } : null,
      bottomBar: { y: bb.y },
      betPrompt: prompt ? { x: prompt.x, y: prompt.y } : null,
      betPromptWordWrapWidth: prompt?.style?.wordWrapWidth ?? null,
      linesPanel: lines ? { x: lines.x, y: lines.y } : null,
      betSpeedWrap: speed ? { x: speed.x, y: speed.y } : null,
    };
    const sp = this._spinButtonSprite;
    const aps = this._autoplaySprite;
    this._desktopChromeDefaults = {
      spinImg: sp ? { w: sp.width, h: sp.height } : { w: 194, h: 194 },
      spinHitHalf: 97,
      autoplay: aps ? { w: aps.width, h: aps.height } : { w: 100, h: 100 },
      betPopup: betBtn ? { w: betBtn.width, h: betBtn.height } : { w: 100, h: 100 },
      betPromptFontSize: prompt?.style?.fontSize ?? 20,
      iconBtnSize: 36,
      betSpeedScale: speed ? speed.scale.x : 1,
    };
  }

  /**
   * Recenters balance / quick-bet strip / win row when chip size changes (mobile 58×54 vs desktop 100×95).
   * Portrait: row 1 = balance | prompt (centered separately) | win; row 2 = full-width QB strip (no arrows).
   */
  _relayoutBottomBarForViewport() {
    if (!this._canvasBottomBar || !this._canvasBalancePanel || !this._canvasWinPanel) return;

    const mobile = this._isPortraitMobileViewport();
    const hudFit = mobile ? this._getMobileHudFit() : 1;
    const btnW = mobile ? Math.max(70, Math.round(MOBILE_QB_CHIP_REF_W * hudFit)) : 100;
    const btnH = mobile ? Math.max(66, Math.round(MOBILE_QB_CHIP_REF_H * hudFit)) : 95;
    const gap = 8;
    const stride = btnW + gap;
    this._qbChipW = btnW;
    this._qbChipH = btnH;
    this._qbChipGap = gap;
    this._qbChipStride = stride;

    const panelW = 196;
    const panelH = 90;
    /** Keep in sync with merged stats row in {@link #_buildCanvasControls}. */
    const portraitTopStatsH = PORTRAIT_MERGED_STATS_BAR_H;
    const portraitTopColBal = 0.38;
    const portraitTopColMid = 0.26;
    const portraitTopColWin = 0.36;
    const pad = 10;
    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap - 4;

    const arL = this._qbArrowLeft;
    const arR = this._qbArrowRight;
    const arrowBtnH = arR?.height ?? 32;

    if (this._isPortraitStackedHud()) {
      const hudFit = mobile ? this._getMobileHudFit() : 1;
      /** Same horizontal placement as before (inset from left); width shortened so the strip still fits within {@link reelBlockW}. */
      const qbRightShift = 90;
      const tabletPortraitForQb = !mobile && this._isTabletViewport();
      const qbShiftLeftPx = mobile
        ? (PORTRAIT_TUNING.phone.quickBetShiftLeftPx ?? 0)
        : tabletPortraitForQb
          ? (gameConfig.viewport?.tabletPortraitQuickBetShiftLeftPx ?? 0)
          : 0;
      const qbX = pad + qbRightShift - qbShiftLeftPx;
      const qbFullW = Math.max(stride, reelBlockW - 2 * pad - qbRightShift);

      if (arL) arL.visible = false;
      if (arR) arR.visible = false;

      /** Tablet portrait: same balance / LAST WIN card art as landscape desktop; phone keeps merged strip. */
      const tabletDeskPanels = !mobile && this._isTabletViewport();

      /* Scroll endpoint should land on the right edge of LAST WIN so the bottom
         HUD shares one consistent right alignment on both phone (merged stats
         bar) and tablet portrait (separate panel). `_qbScrollVisibleW` now
         stores that target distance from `qbX`, expressed in qbGroup-local
         pixels; {@link _qbScrollMaxLocal} reads it to compute scroll bounds.
         Falling back to `qbFullW` when geometry can't be derived (very narrow
         viewports) keeps strips usable in degenerate cases. */
      let lastWinRightEdge;
      if (tabletDeskPanels) {
        const towardCenterPx =
          gameConfig.viewport?.tabletPortraitStatPanelsTowardCenterPx ?? 0;
        lastWinRightEdge = reelBlockW - pad + CANVAS_STAT_SHIFT_LAST_WIN_X - towardCenterPx;
      } else {
        const fullWForBar = Math.max(240, reelBlockW - 2 * pad);
        const mergedW = Math.min(
          reelBlockW - 2 * pad,
          Math.max(200, Math.floor(fullWForBar * 0.82)),
        );
        const barX = Math.round((reelBlockW - mergedW) / 2);
        lastWinRightEdge = barX + mergedW;
      }
      const qbEndTarget = Math.max(stride, lastWinRightEdge - qbX);
      this._qbScrollVisibleW = qbEndTarget;
      if (this._canvasBalancePanel) this._canvasBalancePanel.visible = tabletDeskPanels;
      if (this._canvasWinPanel) this._canvasWinPanel.visible = tabletDeskPanels;
      this._setBetPromptVisible(this._betPromptVisible);
      const statsBarNudgeY =
        mobile && !tabletDeskPanels ? (PORTRAIT_TUNING.phone.statsBarNudgeY ?? 0) : 0;
      if (this._portraitTopStatsBar) {
        this._portraitTopStatsBar.visible = true;
        this._portraitTopStatsBar.y = statsBarNudgeY;
        const bg = this._portraitTopStatsBgSprite ?? this._portraitTopStatsBar.children?.[0];
        const leftSlot = this._portraitTopStatsBar.children?.[1];
        const centerSlot = this._portraitTopStatsBar.children?.[2];
        const rightSlot = this._portraitTopStatsBar.children?.[3];

        if (tabletDeskPanels) {
          this._portraitTopStatsBar.x = 0;
          this._portraitTopStatsBar.scale.set(1);
          this._syncPortraitTopStatsTextScale(1);
          /* Draw center prompt above side panels where columns meet (Pixi z-sort). */
          this._portraitTopStatsBar.zIndex = 4;
          if (this._canvasBalancePanel) this._canvasBalancePanel.zIndex = 1;
          if (this._canvasWinPanel) this._canvasWinPanel.zIndex = 1;
          if (bg) bg.visible = false;
          if (leftSlot) leftSlot.visible = false;
          if (rightSlot) rightSlot.visible = false;
          if (centerSlot) {
            centerSlot.visible = true;
            centerSlot.x = reelBlockW * 0.5;
            centerSlot.zIndex = 3;
          }
        } else {
          const fullW = Math.max(240, reelBlockW - 2 * pad);
          const mergedW = Math.min(
            reelBlockW - 2 * pad,
            Math.max(200, Math.floor(fullW * 0.82)),
          );
          /* Width fixed at mergedW (< reel block); mobile grows height + fonts only. */
          this._portraitTopStatsBar.scale.set(1);
          this._portraitTopStatsBar.x = Math.round((reelBlockW - mergedW) / 2);
          const useMobileBg = this._isPortraitMobileViewport();
          if (bg) {
            bg.visible = true;
            if (useMobileBg && this._mobilePortraitTopStatsTex) {
              bg.texture = this._mobilePortraitTopStatsTex;
            } else if (this._portraitTopStatsDefaultTex) {
              bg.texture = this._portraitTopStatsDefaultTex;
            }
          }
          if (bg) {
            bg.width = mergedW;
            const bgH = Math.round(
              PORTRAIT_MERGED_STATS_BG_HEIGHT * (mobile ? hudFit : 1),
            );
            bg.height = bgH;
            bg.y = (portraitTopStatsH - bgH) / 2;
          }
          this._syncPortraitTopStatsTextScale(mobile ? hudFit : 1);
          const wB = mergedW * portraitTopColBal;
          const wC = mergedW * portraitTopColMid;
          const wW = mergedW * portraitTopColWin;
          if (leftSlot) {
            leftSlot.visible = true;
            leftSlot.x = wB * 0.5 + CANVAS_STAT_SHIFT_BALANCE_X;
            leftSlot.zIndex = 1;
          }
          if (centerSlot) {
            centerSlot.x = wB + wC * 0.5;
            centerSlot.zIndex = 3;
          }
          if (rightSlot) {
            rightSlot.visible = true;
            rightSlot.x = wB + wC + wW * 0.5 + CANVAS_STAT_SHIFT_LAST_WIN_X;
            rightSlot.zIndex = 1;
          }
          this._portraitTopStatsBar.zIndex = 0;
          if (this._canvasBalancePanel) this._canvasBalancePanel.zIndex = 0;
          if (this._canvasWinPanel) this._canvasWinPanel.zIndex = 0;
        }
      }

      if (this._canvasQbMaskG) this._canvasQbMaskG.zIndex = 8;
      if (this._canvasQbGroup) this._canvasQbGroup.zIndex = 8;

      const rowPadY = Math.max(0, (portraitTopStatsH * hudFit - panelH) / 2);
      const towardCenterPx =
        tabletDeskPanels ? (gameConfig.viewport?.tabletPortraitStatPanelsTowardCenterPx ?? 0) : 0;
      if (this._canvasBalancePanel) {
        this._canvasBalancePanel.x = pad + CANVAS_STAT_SHIFT_BALANCE_X + towardCenterPx;
        this._canvasBalancePanel.y = tabletDeskPanels ? rowPadY : 0;
      }
      if (this._canvasWinPanel) {
        this._canvasWinPanel.x =
          reelBlockW - panelW - pad + CANVAS_STAT_SHIFT_LAST_WIN_X - towardCenterPx;
        this._canvasWinPanel.y = tabletDeskPanels ? rowPadY : 0;
      }
      if (tabletDeskPanels) {
        this._canvasBalancePanelRelayout?.();
        this._canvasWinPanelRelayout?.();
      }

      const tabletPortraitHud = !mobile && this._isTabletViewport();
      const tabSqExtra = tabletPortraitHud ? (gameConfig.viewport?.tabletPortraitGapStatsToQbExtra ?? 0) : 0;
      const tabIconsExtra = tabletPortraitHud ? (gameConfig.viewport?.tabletPortraitBottomIconsGapExtra ?? 0) : 0;
      const row1Gap = 12 + tabSqExtra;
      const qbY = portraitTopStatsH * hudFit + row1Gap + statsBarNudgeY;
      const arrowY = qbY + (btnH - arrowBtnH) / 2;

      if (this._canvasQbMaskG) {
        this._canvasQbMaskG.clear();
        this._canvasQbMaskG.rect(0, 0, qbFullW, btnH).fill(0xffffff);
        this._canvasQbMaskG.x = qbX;
        this._canvasQbMaskG.y = qbY;
        /* Portrait: no stencil mask — the masked strip was clipping the right side on phones; scroll bounds stay via {@link #_qbScrollVisibleW}. */
        this._canvasQbMaskG.visible = false;
        this._canvasQbMaskG.eventMode = 'none';
      }
      if (this._canvasQbGroup) {
        this._canvasQbGroup.mask = null;
        this._canvasQbGroup.x = qbX;
        this._canvasQbGroup.y = qbY;
      }
      if (arL) {
        arL.x = qbX;
        arL.y = arrowY;
      }
      if (arR) {
        arR.x = qbX + qbFullW;
        arR.y = arrowY;
      }

      const icons = this._canvasIconSprites;
      if (icons?.length >= 4) {
        icons.forEach((s, i) => {
          s.visible = i !== 0;
        });
        this._hideCanvasHomeIcon();
        /** Corner chrome: larger on portrait for home / sound / menu tap targets (fullscreen hidden on phone). */
        let iz = Math.max(50, Math.round(55 * hudFit));
        if (tabletPortraitHud) {
          const mul = gameConfig.viewport?.tabletPortraitCornerIconScaleMul ?? 1;
          if (mul !== 1) iz = Math.max(36, Math.round(iz * mul));
        }
        icons.forEach((s) => {
          s.width = iz;
          s.height = iz;
        });
        const iconGap = 12;
        const { left: cornerLeft, right: cornerRight } =
          this._resolvePortraitCornerIconXBounds(reelBlockW);
        const edgePad = mobile
          ? (PORTRAIT_TUNING.phone.cornerIconEdgePad ?? 14)
          : 24;
        const edgeAway =
          tabletPortraitHud ? (gameConfig.viewport?.tabletPortraitCornerIconsAwayFromCenterPx ?? 0) : 0;
        const towardCenter = mobile ? (PORTRAIT_TUNING.phone.cornerIconTowardCenter ?? 0) : 0;
        const pairW = iz * 2 + iconGap;
        const inset = edgePad + towardCenter;
        const betBtn = this._betPopupTriggerBtn;
        const iconDownNudge = mobile ? 80 : 16 + tabIconsExtra;
        let iconY = betBtn
          ? Math.round((betBtn.y + betBtn.height + 28 + iconDownNudge) - this._canvasBottomBar.y)
          : qbY + btnH + 10 + iconDownNudge;
        iconY = this._clampPortraitCornerIconY(iconY, iz);
        if (mobile) {
          /* Fullscreen sits in the former home (outer) slot; home stays hidden. */
          icons[1].x = cornerLeft + inset;
          icons[0].x = cornerLeft + inset + iz + iconGap;
          icons[3].x = cornerRight - inset - iz;
          icons[2].x = icons[3].x - iconGap - iz;
        } else {
          const leftStartX = cornerLeft + inset - edgeAway;
          const rightStartX = cornerRight - edgePad - pairW - towardCenter + edgeAway;
          icons[1].x = leftStartX;
          icons[0].x = leftStartX + iz + iconGap;
          icons[2].x = rightStartX;
          icons[3].x = rightStartX + iz + iconGap;
        }
        icons.forEach((s) => {
          s.y = iconY;
        });
        icons[1].visible = false; // hide fullscreen in portrait mobile/tablet
        this._hideCanvasHomeIcon();
      }

      if (this._canvasBetPrompt?.style) {
        const midGap = 16;
        const ww = Math.max(80, reelBlockW - 2 * panelW - 2 * pad - midGap);
        this._canvasBetPrompt.style.wordWrapWidth = ww;
      }

      this._refreshQuickBets();
      this._clampCanvasQbScroll();
      return;
    }

    this._qbScrollVisibleW = null;
    if (this._canvasBalancePanel) this._canvasBalancePanel.visible = true;
    if (this._canvasWinPanel) this._canvasWinPanel.visible = true;
    this._setBetPromptVisible(this._betPromptVisible);
    if (this._portraitTopStatsBar) {
      this._portraitTopStatsBar.visible = false;
      this._portraitTopStatsBar.scale.set(1);
      this._syncPortraitTopStatsTextScale(1);
    }

    const qbVisibleW = 6 * stride;
    const arrowToPanelGap = 40;
    const qbToArrowGap = 20;
    const arrowBtnW = arL?.width ?? 32;

    const reelCenter = reelBlockW / 2;
    const totalGroupW =
      panelW +
      arrowToPanelGap +
      arrowBtnW +
      qbToArrowGap +
      qbVisibleW +
      qbToArrowGap +
      arrowBtnW +
      arrowToPanelGap +
      panelW;
    const groupStartX = reelCenter - totalGroupW / 2;
    const balanceX = groupStartX;
    const leftArrowX = balanceX + panelW + arrowToPanelGap;
    const qbX = leftArrowX + arrowBtnW + qbToArrowGap;
    const rightArrowX = qbX + qbVisibleW + qbToArrowGap;
    const winX = rightArrowX + arrowBtnW + arrowToPanelGap;

    this._canvasBalancePanel.x = balanceX + CANVAS_STAT_SHIFT_BALANCE_X;
    this._canvasBalancePanel.y = 0;
    this._canvasWinPanel.x = winX + CANVAS_STAT_SHIFT_LAST_WIN_X;
    this._canvasWinPanel.y = 0;

    const arrowY = 2 + (btnH - arrowBtnH) / 2;
    if (arL) {
      arL.visible = true;
      arL.x = leftArrowX;
      arL.y = arrowY;
    }
    if (arR) {
      arR.visible = true;
      arR.x = rightArrowX;
      arR.y = arrowY;
    }

    if (this._canvasQbMaskG) {
      this._canvasQbMaskG.clear();
      this._canvasQbMaskG.rect(0, 0, qbVisibleW, btnH).fill(0xffffff);
      this._canvasQbMaskG.x = qbX;
      this._canvasQbMaskG.y = 2;
      this._canvasQbMaskG.visible = true;
      this._canvasQbMaskG.eventMode = 'none';
    }
    if (this._canvasQbGroup) {
      this._canvasQbGroup.mask = this._canvasQbMaskG;
      this._canvasQbGroup.x = qbX;
      this._canvasQbGroup.y = 2;
    }

    /* Corner chrome icons — keep aligned with balance / win panels when the strip is recentered. */
    const balanceLeftEdge = balanceX + CANVAS_STAT_SHIFT_BALANCE_X;
    const winRightEdge = winX + panelW + CANVAS_STAT_SHIFT_LAST_WIN_X;
    const iconGap = 80;
    const iconSpacing = 10;
    const icons = this._canvasIconSprites;
    const tabletLandIconGap =
      this._isTabletViewport() ? (gameConfig.viewport?.tabletLandscapeBottomIconsGapExtra ?? 0) : 0;
    const tabletLandInward =
      this._isTabletViewport()
        ? (gameConfig.viewport?.tabletLandscapeBottomIconsTowardCenterPx ?? 0)
        : 0;
    /* Desktop landscape pulls the corner icon pairs inward so they don't end up
       sitting right at the left/right edges of the screen. */
    const desktopInward = this._isDesktopLandscapeViewport()
      ? (gameConfig.viewport?.desktopBottomIconsTowardCenterPx ?? 0)
      : 0;
    const iconInward = desktopInward + tabletLandInward;
    if (icons && icons.length >= 4) {
      icons.forEach((s, i) => {
        s.visible = i !== 0;
      });
      this._hideCanvasHomeIcon();
      const iz = icons[0].width || 36;
      const iconY = panelH - iz + tabletLandIconGap;
      icons[1].x = balanceLeftEdge - iconGap - iz * 2 - iconSpacing + iconInward;
      icons[0].x = balanceLeftEdge - iconGap - iz + iconInward;
      icons[2].x = winRightEdge + iconGap - iconInward;
      icons[3].x = winRightEdge + iconGap + iz + iconSpacing - iconInward;
      icons.forEach((s) => {
        s.y = iconY;
      });
      if (this._isMobileLandscapeViewport()) icons[1].visible = false;
      this._hideCanvasHomeIcon();
    }

    this._refreshQuickBets();
    this._clampCanvasQbScroll();
  }

  /**
   * Maximum left-scroll distance (positive number, applied as a negative
   * `sw.x`) in qbGroup-local pixels. Single source of truth for the three
   * places that clamp the strip: arrow buttons, drag, and post-layout
   * clamp.
   *
   * `_qbScrollVisibleW` is the qbGroup-local x where the right edge of the
   * **last chip** must land when fully scrolled. On portrait this is set in
   * {@link _relayoutBottomBarForViewport} to the LAST WIN section's right
   * edge minus `qbX`, so the strip's scroll-end aligns with the LAST WIN
   * panel/bar on both phone and tablet. Using the precise content extent
   * `(n−1)·stride + btnW` (= `n·stride − gap`) — rather than `n·stride` —
   * makes the alignment exact instead of off-by-`gap`.
   */
  _qbScrollMaxLocal() {
    const n = this._canvasQbBetCount || 0;
    if (n <= 0) return 0;
    const btnStride = this._qbChipStride ?? 108;
    const gap = this._qbChipGap ?? 8;
    const contentExtent = Math.max(0, n * btnStride - gap);
    const target = this._qbScrollVisibleW ?? 6 * btnStride;
    return Math.max(0, contentExtent - target);
  }

  _clampCanvasQbScroll() {
    const sw = this._canvasQbScrollWrapper;
    if (!sw) return;
    const maxScroll = this._qbScrollMaxLocal();
    this._qbScrollX = Math.max(-maxScroll, Math.min(0, this._qbScrollX || 0));
    sw.x = this._qbScrollX;
  }

  /**
   * Scroll the canvas quick-bet strip so `amount` is visible (centered when possible).
   * Call after {@link #_refreshQuickBets} so chip count/stride match the layout.
   * @param {number} amount
   */
  _scrollQuickBetsToAmount(amount) {
    if (!this._shouldShowQuickBets()) return;
    const bets = this.slotMachine?.ui?._quickBets ?? [];
    if (!bets.length || !this._canvasQbScrollWrapper) return;
    const idx = bets.findIndex((b) => Math.abs(Number(b) - Number(amount)) < 0.001);
    if (idx < 0) return;

    const stride = this._qbChipStride ?? 108;
    const gap = this._qbChipGap ?? 8;
    const btnW = Math.max(1, stride - gap);
    const chipLeft = idx * stride;
    const chipRight = chipLeft + btnW;
    const visibleW = this._qbScrollVisibleW ?? 6 * stride;

    let scrollX = visibleW * 0.5 - (chipLeft + btnW * 0.5);
    if (chipRight + scrollX > visibleW) scrollX = visibleW - chipRight;
    if (chipLeft + scrollX < 0) scrollX = -chipLeft;

    const maxScroll = this._qbScrollMaxLocal();
    scrollX = Math.max(-maxScroll, Math.min(0, scrollX));
    this._qbScrollX = scrollX;
    this._canvasQbScrollWrapper.x = scrollX;
  }

  _applyBetFromBetPopup(amount) {
    sound.play('uiClick');
    this.slotMachine?.ui.setBet(amount);
    this._updateCanvasBetPrompt(amount);
    this._refreshQuickBets();
    this._scrollQuickBetsToAmount(amount);
    this._refreshInfoPaytableAmounts();
    this._hideBetPopup();
  }

  _applyMobileCanvasChrome(reelCx, spinCy) {
    if (!this._spinButtonSprite || !this._spinButtonRoot) return;

    /** Keep autoplay/start/bet on one shared scale factor for phone portrait. */
    const hudFit = this._getMobileHudFit();
    const MOBILE_CONTROL_SCALE = MOBILE_CONTROL_SCALE_BASE * hudFit;
    const MOBILE_SPIN = Math.round(120 * MOBILE_CONTROL_SCALE);
    const MOBILE_SIDE = Math.round(70 * MOBILE_CONTROL_SCALE);
    const MOBILE_GAP = 16;

    this._spinButtonSprite.width = MOBILE_SPIN;
    this._spinButtonSprite.height = MOBILE_SPIN;
    this._spinButtonRoot.hitArea = new Rectangle(-MOBILE_SPIN / 2, -MOBILE_SPIN / 2, MOBILE_SPIN, MOBILE_SPIN);
    this._layoutSpinTurboHint();

    if (this._autoplaySprite) {
      this._autoplaySprite.width = MOBILE_SIDE;
      this._autoplaySprite.height = MOBILE_SIDE;
    }
    if (this._betPopupTriggerBtn) {
      this._betPopupTriggerBtn.width = MOBILE_SIDE;
      this._betPopupTriggerBtn.height = MOBILE_SIDE;
    }

    const leftBtnCenterX = reelCx - (MOBILE_SPIN / 2 + MOBILE_GAP + MOBILE_SIDE / 2);
    if (this._autoplayCluster && this._autoplaySprite) {
      this._layoutAutoplayPickerCluster(true);
      this._autoplayCluster.x = leftBtnCenterX - MOBILE_SIDE / 2;
      this._autoplayCluster.y = spinCy - MOBILE_SIDE / 2;
    }
    if (this._betPopupTriggerBtn) {
      const rightBtnCenterX = reelCx + (MOBILE_SPIN / 2 + MOBILE_GAP + MOBILE_SIDE / 2);
      this._betPopupTriggerBtn.x = rightBtnCenterX - MOBILE_SIDE / 2;
      this._betPopupTriggerBtn.y = spinCy - MOBILE_SIDE / 2;
    }

    if (this._betSpeedWrap) {
      const bgH = this._betSpeedPlateH || 40;
      const betSpeedLowerOffset = 14;
      const turboScale = 1.6;
      this._betSpeedWrap.scale.set(turboScale);
      this._betSpeedWrap.x = this._spinButtonRoot.x;
      this._betSpeedWrap.y =
        this._spinButtonRoot.y + MOBILE_SPIN / 2 - (bgH * turboScale) / 2 + betSpeedLowerOffset;
    }

    if (this._canvasBetPrompt) {
      const promptFs = Math.round(19 * hudFit);
      this._canvasBetPrompt.style.fontSize = promptFs;
      this._canvasBetPrompt.style.lineHeight = Math.round(promptFs * 1.22);
    }

    this._refreshSpinAutoplayRemainLabel();

    const iconDesign = Math.max(42, Math.round(47 * hudFit));
    if (this._canvasIconSprites?.length) {
      this._canvasIconSprites.forEach((s) => {
        s.width = iconDesign;
        s.height = iconDesign;
      });
    }

  }

  /** Desktop-sized spin row below reels (tablet portrait — not narrow-phone chrome). */
  _applyPortraitTabletSpinRow(reelCx, spinCy) {
    const d = this._desktopChromeDefaults;
    if (!d || !this._spinButtonSprite || !this._spinButtonRoot) return;

    const SPIN = d.spinImg.w;
    const SIDE = d.autoplay.w;
    const GAP = 16;

    const leftBtnCenterX = reelCx - (SPIN / 2 + GAP + SIDE / 2);
    if (this._autoplayCluster && this._autoplaySprite) {
      this._layoutAutoplayPickerCluster(true);
      this._autoplayCluster.x = leftBtnCenterX - SIDE / 2;
      this._autoplayCluster.y = spinCy - SIDE / 2;
    }
    if (this._betPopupTriggerBtn) {
      const rightBtnCenterX = reelCx + (SPIN / 2 + GAP + SIDE / 2);
      this._betPopupTriggerBtn.x = rightBtnCenterX - SIDE / 2;
      this._betPopupTriggerBtn.y = spinCy - SIDE / 2;
    }
    if (this._betSpeedWrap) {
      const bgH = this._betSpeedPlateH || 40;
      this._betSpeedWrap.scale.set(d.betSpeedScale);
      this._betSpeedWrap.x = this._spinButtonRoot.x;
      this._betSpeedWrap.y = this._spinButtonRoot.y + SPIN / 2 - bgH / 2 + 14;
    }
  }

  _restoreMobileCanvasChrome() {
    const d = this._desktopChromeDefaults;
    if (!d || !this._spinButtonSprite || !this._spinButtonRoot) return;

    this._spinButtonSprite.width = d.spinImg.w;
    this._spinButtonSprite.height = d.spinImg.h;
    const hh = d.spinHitHalf;
    this._spinButtonRoot.hitArea = new Rectangle(-hh, -hh, hh * 2, hh * 2);
    this._layoutSpinTurboHint();

    if (this._autoplaySprite) {
      this._autoplaySprite.width = d.autoplay.w;
      this._autoplaySprite.height = d.autoplay.h;
    }
    if (this._betPopupTriggerBtn) {
      this._betPopupTriggerBtn.width = d.betPopup.w;
      this._betPopupTriggerBtn.height = d.betPopup.h;
    }
    if (this._betSpeedWrap) {
      this._betSpeedWrap.scale.set(d.betSpeedScale);
    }
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.style.fontSize = d.betPromptFontSize;
      this._canvasBetPrompt.style.lineHeight = Math.round(d.betPromptFontSize * 1.2);
    }
    const isz = d.iconBtnSize;
    if (this._canvasIconSprites?.length) {
      this._canvasIconSprites.forEach((s) => {
        s.width = isz;
        s.height = isz;
      });
    }
    if (this._autoplayCluster && this._autoplaySprite) {
      this._layoutAutoplayPickerCluster(false);
    }
    this._refreshSpinAutoplayRemainLabel();
  }

  /**
   * Horizontal row (desktop): presets to the left of the autoplay icon.
   * Vertical (portrait phone / portrait tablet): same preset order stacked above the icon, opening upward.
   */
  _layoutAutoplayPickerCluster(vertical) {
    const pickerRow = this._autoplayPickerRow;
    const sprite = this._autoplaySprite;
    if (!pickerRow || !sprite) return;

    const buttons = pickerRow.children;
    const n = buttons.length;
    if (!n) return;

    const gap = 8;
    const firstBg = buttons[0]?.children?.[0];
    const pickBtnW = firstBg?.width > 0 ? firstBg.width : 100;
    const pickBtnH = firstBg?.height > 0 ? firstBg.height : 95;
    const side = sprite.width;
    const sideH = sprite.height;

    pickerRow.position.set(0, 0);

    if (!vertical) {
      for (let i = 0; i < n; i++) {
        buttons[i].x = i * (pickBtnW + gap);
        buttons[i].y = (sideH - pickBtnH) / 2;
      }
      const pickRowW = n * pickBtnW + (n - 1) * gap;
      sprite.x = pickRowW + gap;
      sprite.y = 0;
      sprite.zIndex = 2;
      pickerRow.zIndex = 1;
      return;
    }

    const colW = Math.max(pickBtnW, side);
    const insetX = (colW - pickBtnW) / 2;
    for (let i = 0; i < n; i++) {
      const slotFromIcon = n - 1 - i;
      const y = -gap - (slotFromIcon + 1) * pickBtnH - slotFromIcon * gap;
      buttons[i].x = insetX;
      buttons[i].y = y;
    }
    sprite.x = (colW - side) / 2;
    sprite.y = 0;
    sprite.zIndex = 1;
    pickerRow.zIndex = 2;
  }

  /** True while spin/collect/init — quick-bet strip must not pan (arrows + drag). */
  _qbGesturesLocked() {
    return !!(this._spinCycleActive || this._spinning || this._winCollectPending || this._initializing);
  }

  _ensureCanvasQbScrollHandlers() {
    const group = this._canvasQbGroup;
    if (!group || this._qbScrollHandlersInstalled) return;
    this._qbScrollHandlersInstalled = true;
    this._qbBoundDown = this._onCanvasQbGroupPointerDown.bind(this);
    this._qbBoundUp = this._onCanvasQbGroupPointerEnd.bind(this);
    this._qbDomMove = (ev) => this._onCanvasQbDomPointerMove(ev);
    this._qbDomEnd = (ev) => this._onCanvasQbDomPointerEnd(ev);
    group.on('pointerdown', this._qbBoundDown);
    group.on('pointerup', this._qbBoundUp);
    group.on('pointerupoutside', this._qbBoundUp);
  }

  _attachQbCanvasDragListeners() {
    const canvas = this.app?.canvas;
    if (!canvas || this._qbCanvasDragListening) return;
    this._qbCanvasDragListening = true;
    canvas.addEventListener('pointermove', this._qbDomMove, { passive: true });
    canvas.addEventListener('pointerup', this._qbDomEnd);
    canvas.addEventListener('pointercancel', this._qbDomEnd);
  }

  _detachQbCanvasDragListeners() {
    const canvas = this.app?.canvas;
    if (!canvas || !this._qbCanvasDragListening) return;
    this._qbCanvasDragListening = false;
    canvas.removeEventListener('pointermove', this._qbDomMove);
    canvas.removeEventListener('pointerup', this._qbDomEnd);
    canvas.removeEventListener('pointercancel', this._qbDomEnd);
  }

  _clearQbDragState() {
    this._qbDragAnchorGlobalX = null;
    this._qbDragAnchorGlobalY = null;
    this._qbDragLastGlobalX = null;
    this._qbDragScrollCommitted = false;
    this._detachQbCanvasDragListeners();
  }

  _stopQbScrollInertia() {
    if (this._qbInertiaRaf != null) {
      cancelAnimationFrame(this._qbInertiaRaf);
      this._qbInertiaRaf = null;
    }
    this._qbScrollVelocity = 0;
    this._qbInertiaLastT = 0;
    this._qbInertiaTargetX = null;
  }

  /** Track flick speed from recent drag deltas (qbGroup-local px/ms). */
  _recordQbScrollVelocity(localDx) {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const lastT = this._qbScrollVelocityLastT;
    if (lastT > 0) {
      const dt = now - lastT;
      if (dt > 0 && dt < 140) {
        const instant = localDx / dt;
        const alpha = 0.4;
        this._qbScrollVelocity = this._qbScrollVelocity * (1 - alpha) + instant * alpha;
      } else if (dt >= 140) {
        this._qbScrollVelocity = localDx / dt;
      }
    }
    this._qbScrollVelocityLastT = now;
  }

  /**
   * Coast the quick-bet strip with the same friction used after a drag flick.
   * @param {number} velocity qbGroup-local px/ms
   * @param {{ targetX?: number }} [options] When set, coast stops at this scroll offset (arrow nudge).
   */
  _startQbScrollInertia(velocity, options = {}) {
    this._stopQbScrollInertia();
    if (!Number.isFinite(velocity) || Math.abs(velocity) < 0.1) return;
    const sw = this._canvasQbScrollWrapper;
    if (!sw) return;

    const targetX = options.targetX ?? null;
    this._qbInertiaTargetX = targetX;
    this._qbScrollVelocity = velocity;
    this._qbInertiaLastT = typeof performance !== 'undefined' ? performance.now() : Date.now();

    const frictionPerRef = 0.91;
    const refMs = 16.67;
    const minVelocity = 0.035;

    const tick = (now) => {
      if (!this._canvasQbScrollWrapper) {
        this._stopQbScrollInertia();
        return;
      }
      if (this._qbGesturesLocked()) {
        this._stopQbScrollInertia();
        return;
      }

      const last = this._qbInertiaLastT || now;
      const dtMs = Math.min(Math.max(now - last, 0), 48);
      this._qbInertiaLastT = now;
      if (dtMs <= 0) {
        this._qbInertiaRaf = requestAnimationFrame(tick);
        return;
      }

      let v = this._qbScrollVelocity * Math.pow(frictionPerRef, dtMs / refMs);
      const maxScroll = this._qbScrollMaxLocal();
      let x = (this._qbScrollX || 0) + v * dtMs;

      const coastTarget = this._qbInertiaTargetX;
      if (coastTarget != null) {
        if (v > 0 && x >= coastTarget) {
          x = coastTarget;
          v = 0;
        } else if (v < 0 && x <= coastTarget) {
          x = coastTarget;
          v = 0;
        }
      }

      if (x > 0) {
        x = 0;
        v = 0;
      } else if (x < -maxScroll) {
        x = -maxScroll;
        v = 0;
      }

      this._qbScrollX = x;
      this._canvasQbScrollWrapper.x = x;
      this._qbScrollVelocity = v;

      if (coastTarget != null && Math.abs(x - coastTarget) < 0.5) {
        this._stopQbScrollInertia();
        this._clampCanvasQbScroll();
        return;
      }

      if (Math.abs(v) < minVelocity) {
        this._stopQbScrollInertia();
        this._clampCanvasQbScroll();
        return;
      }

      this._qbInertiaRaf = requestAnimationFrame(tick);
    };

    this._qbInertiaRaf = requestAnimationFrame(tick);
  }

  /** Arrow buttons: coast one chip stride with the same friction as a drag flick. */
  _nudgeQbScrollByArrow(scrollDir) {
    if (!this._canvasQbScrollWrapper) return;
    if (this._qbGesturesLocked()) return;

    const stride = this._qbChipStride ?? 108;
    const maxScroll = this._qbScrollMaxLocal();
    const current = this._qbScrollX || 0;
    const target = Math.max(-maxScroll, Math.min(0, current + scrollDir * stride));
    if (Math.abs(target - current) < 0.5) return;

    const dist = target - current;
    const velocity = Math.sign(dist) * 0.65;
    this._startQbScrollInertia(velocity, { targetX: target });
  }

  _finishQbDragGesture() {
    const wasScroll = this._qbDragScrollCommitted;
    const releaseVelocity = this._qbScrollVelocity;
    this._clearQbDragState();
    if (!wasScroll || this._qbGesturesLocked()) return;
    this._startQbScrollInertia(releaseVelocity * 1.12);
  }

  /**
   * True if the pointer drifted beyond {@link _qbTapDriftStagePx} from where it
   * pressed (in either axis) — used by chip pointertap to reject accidental bets
   * on drags. `e` is a Pixi FederatedPointerEvent on the chip itself; `global`
   * is in stage coords just like the anchor stored in pointerdown.
   */
  _qbPointerWasDrag(e) {
    if (this._qbGestureWasDrag) return true;
    const ax = this._qbDragAnchorGlobalX;
    const ay = this._qbDragAnchorGlobalY;
    if (ax == null || ay == null) return false;
    const dx = e.global.x - ax;
    const dy = e.global.y - ay;
    return Math.hypot(dx, dy) >= this._qbTapDriftStagePx();
  }

  _onCanvasQbGroupPointerDown(e) {
    if (!isPrimaryPointerAction(e)) return;
    if (this._qbGesturesLocked()) return;
    this._stopQbScrollInertia();
    this._qbScrollVelocity = 0;
    this._qbScrollVelocityLastT = 0;
    this._qbGestureWasDrag = false;
    this._qbDragAnchorGlobalX = e.global.x;
    this._qbDragAnchorGlobalY = e.global.y;
    this._qbDragLastGlobalX = e.global.x;
    this._qbDragScrollCommitted = false;
    this._attachQbCanvasDragListeners();
  }

  /**
   * Tap-vs-drag drift threshold expressed in Pixi stage pixels (the unit of
   * `e.global.x/y`). Derives from a stable CSS-pixel constant so the same
   * finger movement feels like “the same drag” on every viewport — without
   * this, the threshold was ~2 CSS px on phone and ~8 CSS px on desktop.
   */
  _qbTapDriftStagePx() {
    const cssPx = this._qbTapDriftCssPx ?? 6;
    const scaleX = this._lastScales?.x;
    if (!scaleX || !Number.isFinite(scaleX) || scaleX <= 0) return cssPx;
    return cssPx / scaleX;
  }

  _onCanvasQbDomPointerMove(ev) {
    const canvas = this.app?.canvas;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    /* IMPORTANT: must match Pixi’s {@link EventSystem.mapPositionToPoint} so the
       dx we compute lives in the same stage-coordinate space as `e.global.x`
       captured during pointerdown. Pixi divides by `resolution` (autoDensity is
       on with `resolution = devicePixelRatio`), so the canvas drawing buffer is
       DPR× bigger than the stage. Omitting `/ resolution` here multiplied dx by
       DPR on retina mobiles/tablets — the strip then scrolled DPR× faster than
       the finger. */
    const resolution = this.app?.renderer?.resolution || 1;
    const scale = rect.width > 0 ? canvas.width / rect.width : 1;
    const x = (ev.clientX - rect.left) * scale / resolution;
    this._applyQbDragFromGlobalX(x);
  }

  /** @param {number} globalX stage-space X (same basis as Pixi FederatedEvent.global.x) */
  _applyQbDragFromGlobalX(globalX) {
    if (this._qbGesturesLocked()) return;
    if (this._qbDragLastGlobalX === null) return;
    const anchor = this._qbDragAnchorGlobalX;
    if (anchor === null) return;
    if (!this._qbDragScrollCommitted) {
      if (Math.abs(globalX - anchor) < this._qbTapDriftStagePx()) return;
      this._qbDragScrollCommitted = true;
      this._qbGestureWasDrag = true;
      this._qbDragLastGlobalX = globalX;
      return;
    }
    const dx = globalX - this._qbDragLastGlobalX;
    this._qbDragLastGlobalX = globalX;
    this._applyCanvasQbScrollDx(dx);
  }

  _onCanvasQbGroupPointerEnd() {
    this._finishQbDragGesture();
  }

  _onCanvasQbDomPointerEnd() {
    this._finishQbDragGesture();
  }

  /** @param {number} dx Pointer delta in Pixi stage coords (same space as `e.global.x`). */
  _applyCanvasQbScrollDx(dx) {
    const sw = this._canvasQbScrollWrapper;
    if (!sw) return;
    const maxScroll = this._qbScrollMaxLocal();
    const prevX = this._qbScrollX || 0;
    /* `sw.x` lives in qbGroup's LOCAL space, while `dx` is in stage space. The
       parent chain (`world.scale = s` and {@link _applySlotAspectLock} on
       reelContainer.root) scales everything inside. On phone portrait `s ≈ 1.32`
       and on tablet `s ≈ 0.9 × tabletWorldScaleMul`, so applying stage dx
       directly to a local coord made the strip travel faster than the finger.
       Dividing by the cumulative X scale (`worldTransform.a`) maps the drag
       1:1 with the finger across every device. */
    const groupScaleX = this._canvasQbGroup?.worldTransform?.a;
    const localDx = groupScaleX && Number.isFinite(groupScaleX) && Math.abs(groupScaleX) > 0.0001
      ? dx / groupScaleX
      : dx;
    this._qbScrollX = Math.max(-maxScroll, Math.min(0, prevX + localDx));
    sw.x = this._qbScrollX;
    if (Math.abs(this._qbScrollX - prevX) > 0.25) {
      this._qbGestureWasDrag = true;
      this._recordQbScrollVelocity(localDx);
    }
  }

  _buildCanvasQuickBets(bets, currentBet) {
    const group = this._canvasQbGroup;
    if (!group || !this._qbNormTex) return;
    group.removeChildren();

    const mobile = this._isPortraitMobileViewport();
    const hudFit = mobile ? this._getMobileHudFit() : 1;
    const btnW = mobile ? Math.max(70, Math.round(MOBILE_QB_CHIP_REF_W * hudFit)) : 100;
    const btnH = mobile ? Math.max(66, Math.round(MOBILE_QB_CHIP_REF_H * hudFit)) : 95;
    const gap = 8;
    const font = '"Montserrat", "Arial", sans-serif';
    const scrollWrapper = new Container();
    scrollWrapper.x = this._qbScrollX || 0;
    group.addChild(scrollWrapper);
    this._canvasQbScrollWrapper = scrollWrapper;
    this._canvasQbBetCount = bets.length;

    bets.forEach((amount, i) => {
      const sel = Math.abs(amount - currentBet) < 0.001;
      const showCollect = this._isWinCollectDismissActive();
      const showStopAll = !!this._spinning && !showCollect;
      const bottomLabel = showCollect ? this._tu('COLLECT') : showStopAll ? '' : this._tu('BET');

      const btn = createQuickBetChipPixi({
        betValue: amount,
        selected: sel,
        textureNormal: this._qbNormTex,
        textureSelected: this._qbSelTex,
        cellW: btnW,
        cellH: btnH,
        bottomText: bottomLabel,
        currencyLabel: this._currencyLabel(),
        currencyPrecision: this._currencyPrecision,
        fontFamily: font,
      });
      btn.x = i * (btnW + gap);
      btn.y = 0;
      btn.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        /* Full click only — ignore scroll drags that still look like a tap. */
        if (this._qbPointerWasDrag(e)) {
          this._qbGestureWasDrag = false;
          return;
        }
        this._handleCanvasQuickBet(amount);
      });
      scrollWrapper.addChild(btn);
    });
  }

  _applySlotAspectLock(worldScaleX, worldScaleY) {
    if (!this.slotMachine) return;
    const slotRoot = this.slotMachine.reelContainer?.root;
    if (!slotRoot) return;
    if (!(worldScaleX > 0) || !(worldScaleY > 0)) return;

    if (this._slotRootBaseX == null || this._slotRootBaseY == null) {
      this._slotRootBaseX = slotRoot.x;
      this._slotRootBaseY = slotRoot.y;
    }

    const { width: cssW, height: cssH } = this._readContainerSize();
    const portraitMobile = cssH > cssW && cssW <= 760;

    let localScaleX;
    let localScaleY;
    if (portraitMobile) {
      /** Phones: bias uniform scale toward filling the viewport width (reference ~390px-wide layout). */
      const wx = worldScaleX;
      const wy = worldScaleY;
      const portraitRatio = cssW / Math.max(1, cssH);
      const widthBoost = this._getMobilePortraitWidthBoost(portraitRatio);
      const uniform = Math.min(wx * widthBoost, wy * 1.06);
      localScaleX = uniform / wx;
      localScaleY = uniform / wy;
    } else {
      const uniformScreenScale = Math.min(worldScaleX, worldScaleY);
      localScaleX = uniformScreenScale / worldScaleX;
      localScaleY = uniformScreenScale / worldScaleY;
    }
    slotRoot.scale.set(localScaleX, localScaleY);

    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;
    slotRoot.x = this._slotRootBaseX + (reelBlockW * (1 - localScaleX)) / 2;
    let slotY = this._slotRootBaseY + (reelBlockH * (1 - localScaleY)) / 2;
    if (portraitMobile) {
      slotY -= MOBILE_SLOT_ROOT_UP_BIAS_NARROW * this._getNarrowPortraitWidthT();
    }
    slotRoot.y = slotY;
  }

  /**
   * Portrait-only: vertical positions for canvas HUD (logical px, reel-root space).
   * Stack is additive: reels → merged stats bar → gap → QB strip → gap → SPIN center.
   * Tune phone vs tablet in {@link PORTRAIT_TUNING}; landscape uses snapshot layout, not this.
   *
   * @param {number} cw
   * @param {number} ch
   * @param {boolean} narrowMobile portrait & width ≤ 760
   * @param {number} reelBlockH
   * @param {number} reelBlockW
   */
  _computePortraitCanvasHudLayout(cw, ch, narrowMobile, reelBlockH, reelBlockW) {
    const cx = reelBlockW / 2;
    const portraitTopStatsH = PORTRAIT_MERGED_STATS_BAR_H;
    const stackedStatsHudFit = this._isPortraitMobileViewport() ? this._getMobileHudFit() : 1;

    const spacingBias = narrowMobile
      ? Math.max(-1, Math.min(1, (ch - 860) / 220))
      : 0;
    const portraitRatio = narrowMobile ? cw / Math.max(1, ch) : 0;
    const spacingTightness = narrowMobile
      ? Math.max(0, Math.min(1, (portraitRatio - 0.50) / 0.35))
      : 0;

    const tabHudGap =
      !narrowMobile && this._isTabletViewport()
        ? {
            sq: gameConfig.viewport?.tabletPortraitGapStatsToQbExtra ?? 0,
            qs: gameConfig.viewport?.tabletPortraitGapQbToSpinExtra ?? 0,
          }
        : { sq: 0, qs: 0 };

    const gapStatsToQb =
      (narrowMobile
        ? Math.max(4, Math.round(10 + spacingBias * 6 - spacingTightness * 4))
        : 12) + tabHudGap.sq;
    const gapQbToSpin =
      (narrowMobile
        ? Math.max(32, Math.round(76 + spacingBias * 10 - spacingTightness * 12))
        : 16) + tabHudGap.qs;

    const hudFit = narrowMobile ? this._getMobileHudFit() : 1;
    const qbStripHeight = narrowMobile
      ? Math.max(66, Math.round(MOBILE_QB_CHIP_REF_H * hudFit))
      : 95;
    const mobileControlScale = MOBILE_CONTROL_SCALE_BASE * hudFit;
    const spinHalfHeight = narrowMobile
      ? Math.round((120 * mobileControlScale) / 2)
      : 97;

    const portraitLiftY = narrowMobile ? this._getMobileGameVisualLiftY() : -24;

    let gameToHudGap;
    if (narrowMobile) {
      const p = PORTRAIT_TUNING.phone;
      gameToHudGap = Math.max(
        0,
        Math.round(p.gameToHudGapBase + spacingBias * p.gameToHudGapSpacingBiasMul),
      );
    } else {
      gameToHudGap = PORTRAIT_TUNING.tablet.gameToHudGap;
    }

    /** Mirrors the exact full-width scale computed in {@link #_applyMobileGameVisualScale}; may exceed 1 on shorter portrait aspects so the cabinet can fill the viewport width. */
    const mobileCabinetScale = narrowMobile
      ? (this._mobileGameVisualScale ?? 1)
      : 1;
    const reelVisualBottomY = narrowMobile
      ? (reelBlockH * (1 + mobileCabinetScale)) / 2 + portraitLiftY
      : reelBlockH + portraitLiftY;

    const bottomBarY = reelVisualBottomY + gameToHudGap;
    const statsBarHeight = portraitTopStatsH * stackedStatsHudFit;
    const statsBarNudgeY = narrowMobile ? (PORTRAIT_TUNING.phone.statsBarNudgeY ?? 0) : 0;
    const betPromptCenterY = bottomBarY + statsBarNudgeY + statsBarHeight / 2;

    const spinNudgeY = narrowMobile
      ? PORTRAIT_TUNING.phone.spinRowNudgeY
      : PORTRAIT_TUNING.tablet.spinRowNudgeY;

    const spinCenterY =
      bottomBarY +
      statsBarNudgeY +
      statsBarHeight +
      gapStatsToQb +
      qbStripHeight +
      gapQbToSpin +
      spinHalfHeight +
      spinNudgeY;

    return { cx, bottomBarY, betPromptCenterY, spinCenterY };
  }

  /**
   * Repositions Pixi canvas controls for portrait: stats + QB strip under reels, then spin row.
   * Landscape restores the snapshot from build (desktop beside-the-reels layout).
   */
  _applyCanvasControlsViewportLayout() {
    const snap = this._canvasDesktopLayout;
    const spin = this._spinButtonRoot;
    const bottomBar = this._canvasBottomBar;
    if (!snap || !spin || !bottomBar) return;

    const { width: cw, height: ch } = this._readContainerSize();
    const portraitStacked = ch > cw;
    const narrowMobile = portraitStacked && cw <= 760;
    const reelBlockH = gameConfig.rowCount * gameConfig.cellHeight;
    this._mobileHudFitScale = narrowMobile
      ? this._computeMobileHudFitScale(cw, ch, reelBlockH)
      : 1;

    const restoreDesktop = () => {
      this._mobileHudFitScale = 1;
      spin.x = snap.spinRoot.x;
      spin.y = snap.spinRoot.y;
      if (this._autoplayCluster && snap.autoplayCluster) {
        this._autoplayCluster.x = snap.autoplayCluster.x;
        this._autoplayCluster.y = snap.autoplayCluster.y;
      }
      if (this._betPopupTriggerBtn && snap.betPopupTrigger) {
        this._betPopupTriggerBtn.x = snap.betPopupTrigger.x;
        this._betPopupTriggerBtn.y = snap.betPopupTrigger.y;
      }
      bottomBar.y = snap.bottomBar.y;
      if (this._canvasBetPrompt && snap.betPrompt) {
        this._canvasBetPrompt.x = snap.betPrompt.x;
        this._canvasBetPrompt.y = snap.betPrompt.y;
        if (snap.betPromptWordWrapWidth != null && this._canvasBetPrompt.style) {
          this._canvasBetPrompt.style.wordWrapWidth = snap.betPromptWordWrapWidth;
        }
      }
      if (this._canvasLinesPanel && snap.linesPanel) {
        this._canvasLinesPanel.x = snap.linesPanel.x;
        this._canvasLinesPanel.y = snap.linesPanel.y;
      }
      if (this._betSpeedWrap && snap.betSpeedWrap) {
        this._betSpeedWrap.x = snap.betSpeedWrap.x;
        this._betSpeedWrap.y = snap.betSpeedWrap.y;
      }
      this._restoreMobileCanvasChrome();
      this._relayoutBottomBarForViewport();
      this._applyTabletBottomMenuNudge();
    };

    if (!portraitStacked) {
      restoreDesktop();
      return;
    }

    const reelBlockW =
      gameConfig.reelCount * gameConfig.cellWidth + (gameConfig.reelCount - 1) * gameConfig.reelGap - 4;

    const hud = this._computePortraitCanvasHudLayout(cw, ch, narrowMobile, reelBlockH, reelBlockW);
    bottomBar.y = hud.bottomBarY;
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.x = hud.cx;
      this._canvasBetPrompt.y = hud.betPromptCenterY;
    }
    spin.x = hud.cx;
    spin.y = hud.spinCenterY;

    if (narrowMobile) {
      this._applyMobileCanvasChrome(hud.cx, hud.spinCenterY);
    } else {
      this._restoreMobileCanvasChrome();
      this._applyPortraitTabletSpinRow(hud.cx, hud.spinCenterY);
    }

    this._relayoutBottomBarForViewport();
    this._applyTabletBottomMenuNudge();
    this._clampPortraitCornerIconsToVisibleArea();
  }

  /**
   * Shifts only the canvas bottom menu strip on tablets ({@link gameConfig.viewport.tabletBottomMenuOffsetY}).
   * Landscape: moves `_canvasBottomBar` only (spin stays beside reels).
   * Portrait stacked: also moves spin row + related chrome so the stack stays aligned.
   */
  _applyTabletBottomMenuNudge() {
    const dy = this._isTabletViewport()
      ? (gameConfig.viewport?.tabletBottomMenuOffsetY ?? 0)
      : 0;
    if (!dy || !this._canvasBottomBar) return;

    this._canvasBottomBar.y += dy;

    if (!this._isPortraitStackedHud()) return;

    if (this._spinButtonRoot) this._spinButtonRoot.y += dy;
    if (this._canvasBetPrompt) this._canvasBetPrompt.y += dy;
    if (this._autoplayCluster) this._autoplayCluster.y += dy;
    if (this._betPopupTriggerBtn) this._betPopupTriggerBtn.y += dy;
    if (this._betSpeedWrap) this._betSpeedWrap.y += dy;
  }

  layout() {
    if (!this.app || !this.slotMachine) return;
    const width = 1922;
    const height = 1080;
    const { width: cw, height: ch } = this._readContainerSize();
    const portraitMobile = ch > cw && cw <= 760;
    const base = this._contentScale ?? 0.90;
    /** Uniform scale applied to the whole game world — values < 1 shrink everything. */
    let s = portraitMobile ? Math.min(1.32, base * 1.46) : base;
    /* Tablet world up-scale / vertical nudge are tuned for portrait tablets,
       where the 1922×1080 design needs to grow to fill a taller screen. In
       landscape the design already fits naturally, so applying the multiplier
       would just zoom the game in and crop content. */
    const tabletViewport = this._isTabletViewport();
    const tabletPortrait = tabletViewport && ch > cw;
    const tabletLandscape = tabletViewport && !tabletPortrait;
    const tabMul = gameConfig.viewport?.tabletWorldScaleMul ?? 1;
    if (tabMul !== 1 && tabletPortrait) {
      s = Math.min(1.5, s * tabMul);
    }
    /* Tablet landscape: shrink the whole world a touch so the corner chrome
       icons gain a margin from the screen edges instead of sitting flush. */
    const tabLandMul = gameConfig.viewport?.tabletLandscapeWorldScaleMul ?? 1;
    if (tabLandMul !== 1 && tabletLandscape) {
      s = s * tabLandMul;
    }
    this.slotMachine.setViewportProfile('desktop-landscape');
    
    // Scale the world from its center to keep content centered
    this.world.pivot.set(width / 2, height / 2);
    this.world.scale.set(s);
    let worldOffY = 0;
    if (portraitMobile) {
      worldOffY = MOBILE_PORTRAIT_WORLD_OFFSET_NARROW * this._getNarrowPortraitWidthT();
    } else if (tabletPortrait) {
      worldOffY = gameConfig.viewport?.tabletWorldOffsetY ?? 0;
    }
    this.world.position.set(width / 2, height / 2 + worldOffY);
    
    // Position slotMachine.root at origin (0, 0) in world-local space
    this.slotMachine.root.position.set(0, 0);

    const portrait = ch > cw;
    const vp = gameConfig.viewport ?? {};
    let celebrationMul = 1;
    if (portrait && cw <= 760) {
      const t = Math.max(0, Math.min(1, (cw - 320) / (760 - 320)));
      celebrationMul =
        (0.44 + t * 0.2) * (vp.narrowPortraitWinCelebrationMul ?? 1);
    } else if (portrait && cw <= 1024) {
      celebrationMul = 0.76 * (vp.portraitTabletWinCelebrationMul ?? 1);
    }
    if (this._isTabletViewport()) {
      celebrationMul *= vp.tabletWinCelebrationScaleMul ?? 1;
    }
    const desktopCelebration =
      !this._isTabletViewport() && !(portrait && cw <= 1024);
    if (desktopCelebration) {
      celebrationMul *= vp.desktopWinCelebrationScaleMul ?? 1;
    }
    const sx = this._lastScales?.x ?? 1;
    const sy = this._lastScales?.y ?? 1;
    const cssStretchRatio = portrait && sx > 0 && sy > 0 ? sy / sx : 1;
    this.slotMachine.winCelebration?.setCanvasStretchMulX?.(cssStretchRatio);

    this.slotMachine.layoutScreen(width, height, 'desktop-landscape');
    this.slotMachine.ui.setResponsiveViewport(width, height);

    this.slotMachine.winCelebration?.setCelebrationContentScale(celebrationMul);
    this.slotMachine.winCelebration?.setCelebrationRootExtraOffsetYPx(
      this._isTabletViewport() ? (gameConfig.viewport?.tabletWinCelebrationOffsetYPx ?? 0) : 0,
    );
  }

  _updateBetTapZone() {
    if (!this.slotMachine) return;
    const ui = this.slotMachine.ui;
    const isMobilePortrait = ui._layoutMode === 'mobile-portrait';
    if (!isMobilePortrait) {
      this._betTapZone.style.display = 'none';
      return;
    }

    const cr = this.app?.canvas?.getBoundingClientRect?.();
    if (!cr) { this._betTapZone.style.display = 'none'; return; }

    /* Replicate the layout maths from UILayer.setPanelWidth for mobile portrait. */
    const mul      = ui._uiMul || 1;
    const u        = ui.u;
    const panelW   = ui.panelWidth;
    const chromeScale = this.slotMachine.ui.root.scale.x;
    const panelH   = ui.panelHeight;
    const panelCSS_top = cr.height - panelH * chromeScale;   // relative to canvas top
    const containerRect = this._containerEl.getBoundingClientRect();
    const canvasOffsetY = cr.top - containerRect.top;        // canvas top in container

    const inset    = Math.round(u.panelPadX * mul);
    const top      = Math.round(u.panelPadY * mul);
    const gap      = Math.round(u.cardGap   * mul);
    const cardH    = Math.round(u.statRowH  * mul);
    const edge     = Math.max(10 * mul, inset - 10 * mul);
    const topRowH  = Math.max(52 * mul, cardH - 4 * mul);
    const rowGap   = Math.max(8  * mul, gap   - 2 * mul);
    const chipH    = Math.round(u.betChipH  * mul);
    const chipPadY = Math.round(u.cardPadY  * mul);
    const chipRowH = chipH + chipPadY * 2;
    const linesY   = top + topRowH + rowGap;
    const spinD_logical = ui._spinVisualW;
    const spinRowY = linesY + chipRowH + rowGap;
    const spinX    = Math.round((panelW - spinD_logical) / 2);
    const pD_logical = (ui._paytableBtnD ?? 54) * mul;
    const sideGap  = Math.max(Math.round(8 * mul), Math.floor((spinX - pD_logical - edge) / 2));
    const btnX_logical = Math.min(panelW - pD_logical - edge, spinX + spinD_logical + sideGap);
    const btnY_logical = spinRowY + Math.round((spinD_logical - pD_logical) / 2);

    /* Convert from panel-logical → CSS px */
    const cs = chromeScale;
    const cssLeft  = btnX_logical * cs;
    const cssTop   = canvasOffsetY + panelCSS_top + btnY_logical * cs;
    const cssSize  = pD_logical * cs;

    Object.assign(this._betTapZone.style, {
      display:   'block',
      position:  'absolute',
      left:      `${Math.round(cssLeft)}px`,
      top:       `${Math.round(cssTop)}px`,
      width:     `${Math.ceil(cssSize)}px`,
      height:    `${Math.ceil(cssSize)}px`,
      zIndex:    '17',
      cursor:    'pointer',
      WebkitTapHighlightColor: 'transparent',
    });
  }


  /* ─── Bet selection modal (Pixi, same assets as {@link _buildCanvasQuickBets}) ─── */

  /**
   * Undo non-uniform CSS canvas scaling for the quick-bets modal only (dim stays full-frame).
   * @param {number} scaleX
   * @param {number} scaleY
   */
  _applyBetPopupViewportScale(scaleX, scaleY) {
    const shell = this._betPopupPanelRoot;
    if (!shell) return;
    if (scaleX > 0 && scaleY > 0 && Number.isFinite(scaleX) && Number.isFinite(scaleY)) {
      shell.scale.set(scaleY / scaleX, 1);
    } else {
      shell.scale.set(1, 1);
    }
  }

  /**
   * Minimum chip-area height for Figma mobile/tablet bet grids.
   * @param {number} innerW
   * @param {number} betsCount
   * @param {boolean} phoneLayout narrow portrait phone ({@link GameApp#_isPortraitMobileViewport}); else tablet spec.
   * @returns {number}
   */
  _betPopupComputeMinChipAreaHeight(innerW, betsCount, phoneLayout) {
    const gw = phoneLayout ? MOBILE_BET_MODAL_GRID_INNER_W : TABLET_BET_MODAL_GRID_INNER_W;
    const gridInnerH = phoneLayout ? MOBILE_BET_MODAL_GRID_INNER_H : TABLET_BET_MODAL_GRID_INNER_H;
    const cw0 = phoneLayout ? MOBILE_BET_CHIP_REF_W : TABLET_BET_CHIP_REF_W;
    const ch0 = phoneLayout ? MOBILE_BET_CHIP_REF_H : TABLET_BET_CHIP_REF_H;
    const g0 = phoneLayout ? MOBILE_BET_CHIP_GAP_REF : TABLET_BET_CHIP_GAP_REF;
    const n = Math.max(0, Math.floor(betsCount));
    if (n === 0) return 80;
    const w = Math.max(1, innerW);
    const u = w / gw;
    let cw = cw0 * u;
    let ch = ch0 * u;
    let g = g0 * u;
    let cols = Math.max(1, Math.floor((w + g) / (cw + g)));
    cols = Math.min(cols, n);
    const rows = Math.ceil(n / cols);
    let needW = cols * cw + (cols - 1) * g;
    let needH = rows * ch + (rows - 1) * g;
    const innerHAvail = gridInnerH * (w / gw);
    const s = Math.min(w / Math.max(1, needW), innerHAvail / Math.max(1, needH), 1.55);
    cw *= s;
    ch *= s;
    g *= s;
    needH = rows * ch + (rows - 1) * g;
    return needH;
  }

  /**
   * Size the bet-options panel: desktop keeps art aspect; phones/tablets fill viewport width and grow height for the chip grid.
   */
  _syncBetPopupLayout() {
    const bg = this._betPopupBgSprite;
    const panel = this._betPopupPanel;
    const title = this._betPopupTitle;
    const closeBtn = this._betPopupCloseBtn;
    const chipsArea = this._betPopupChipsArea;
    if (!bg || !panel || !title || !closeBtn || !chipsArea) return;

    const designH = 1080;
    const { width: cw, height: ch } = this._readContainerSize();
    const scaleY =
      this._lastScales?.y > 0 && Number.isFinite(this._lastScales.y) ? this._lastScales.y : ch / designH;

    const compact = this._isPortraitMobileViewport() || this._isTabletViewport();
    const marginCss = compact ? 10 : 32;
    const maxScreenW = Math.max(100, cw - 2 * marginCss);
    const maxScreenH = Math.max(100, ch - 2 * marginCss);

    const tw = this._betPopupBgNatW > 0 ? this._betPopupBgNatW : 1000;
    const th = this._betPopupBgNatH > 0 ? this._betPopupBgNatH : 780;

    let maxBgW = Infinity;
    let maxBgH = Infinity;
    if (compact) {
      const worldS = Math.max(1e-6, this.world?.scale?.x ?? 1);
      const screenMul = scaleY * worldS;
      maxBgW = maxScreenW / screenMul;
      maxBgH = maxScreenH / screenMul;
      if (this._isPortraitMobileViewport()) {
        const m = MOBILE_BET_MODAL_VIEWPORT_MARGIN_CSS;
        const localTop = this._worldLocalYFromCssY(m);
        const localBottom = this._worldLocalYFromCssY(ch - m);
        const maxPhoneBgH = Math.max(160, (localBottom - localTop) * 0.96);
        maxBgH = Math.min(maxBgH, maxPhoneBgH, ((ch * 0.92) - 2 * m) / screenMul);
      }
    }

    let bgW;
    let bgH;
    let padX;
    let padTop;
    let padBot;
    let yNudge = 0;
    let betPopupCornerR = 20;

    if (compact) {
      const isPhone = this._isPortraitMobileViewport();
      const ref = isPhone ? MOBILE_BET_MODAL_REF : TABLET_BET_MODAL_REF;
      const gw = isPhone ? MOBILE_BET_MODAL_GRID_INNER_W : TABLET_BET_MODAL_GRID_INNER_W;
      let modalFit = Math.min(maxBgW / ref.w, maxBgH / ref.h);
      const n = this.slotMachine?.ui?._quickBets?.length ?? 0;

      for (let iter = 0; iter < 14; iter++) {
        bgW = ref.w * modalFit;
        bgH = ref.h * modalFit;
        padX = (bgW - gw * modalFit) / 2;
        padTop = (isPhone ? MOBILE_BET_MODAL_GRID_TOP : TABLET_BET_MODAL_GRID_TOP) * modalFit;
        padBot = (isPhone ? MOBILE_BET_MODAL_GRID_BOTTOM : TABLET_BET_MODAL_GRID_BOTTOM) * modalFit;
        const innerW = Math.max(1, bgW - 2 * padX);
        const innerH = bgH - padTop - padBot;
        const minChipH = this._betPopupComputeMinChipAreaHeight(innerW, n || 1, isPhone);
        if (minChipH <= innerH + 0.5 || modalFit < 0.18) break;
        modalFit *= 0.94;
      }

      bgW = ref.w * modalFit;
      bgH = ref.h * modalFit;
      padX = (bgW - gw * modalFit) / 2;
      padTop = (isPhone ? MOBILE_BET_MODAL_GRID_TOP : TABLET_BET_MODAL_GRID_TOP) * modalFit;
      padBot = (isPhone ? MOBILE_BET_MODAL_GRID_BOTTOM : TABLET_BET_MODAL_GRID_BOTTOM) * modalFit;

      title.style.fontSize = Math.round(
        (isPhone ? MOBILE_BET_MODAL_TITLE_FS : TABLET_BET_MODAL_TITLE_FS) * modalFit,
      );
      title.y = (isPhone ? MOBILE_BET_MODAL_TITLE_TOP : TABLET_BET_MODAL_TITLE_TOP) * modalFit;
      yNudge = isPhone ? 0 : TABLET_BET_MODAL_OFFSET_Y_REF * modalFit;
      this._betPopupMobileModalFit = modalFit;

      const fill = this._betPopupPanelFill;
      const mask = this._betPopupRoundMask;
      const cornerRef = isPhone ? MOBILE_BET_MODAL_CORNER_R : TABLET_BET_MODAL_CORNER_R;
      const rr = Math.min(cornerRef * modalFit, bgW * 0.075, bgH * 0.04);
      betPopupCornerR = rr;
      if (fill) {
        fill.visible = true;
        fill.clear();
        fill.roundRect(0, 0, bgW, bgH, rr).fill({ color: 0x000000, alpha: 1 });
      }
      if (mask) {
        mask.clear();
        mask.roundRect(0, 0, bgW, bgH, rr).fill({ color: 0xffffff });
        bg.mask = mask;
      }
      bg.alpha = 0.42;
    } else {
      /* Desktop / large: Figma Frame 207 — black fill, 20px corners (scaled), art clipped on top. */
      this._betPopupMobileModalFit = 1;
      const maxPanelW = 1000;
      const maxPanelH = 780;
      const fitScale = Math.min(maxPanelW / tw, maxPanelH / th);
      bgW = tw * fitScale;
      bgH = th * fitScale;

      const fill = this._betPopupPanelFill;
      const mask = this._betPopupRoundMask;
      const rrDesk = Math.min(
        DESKTOP_BET_MODAL_CORNER_R * (bgW / DESKTOP_BET_MODAL_CORNER_REF_W),
        bgW * 0.075,
        bgH * 0.04,
      );
      betPopupCornerR = rrDesk;
      if (fill) {
        fill.visible = true;
        fill.clear();
        fill.roundRect(0, 0, bgW, bgH, rrDesk).fill({ color: 0x000000, alpha: 1 });
      }
      if (mask) {
        mask.clear();
        mask.roundRect(0, 0, bgW, bgH, rrDesk).fill({ color: 0xffffff });
        bg.mask = mask;
      }
      bg.alpha = 1;

      title.style.fontSize = 25;
      padX = 48;
      padTop = 124;
      padBot = 26;
    }

    if (compact) {
      const pick =
        this._isPortraitMobileViewport() ? this._betPopupBgTexMobile : this._betPopupBgTexTablet;
      const fallback = this._betPopupBgTexDefault;
      const t =
        pick && pick !== Texture.EMPTY && pick.width > 0 ? pick : fallback && fallback.width > 0 ? fallback : bg.texture;
      if (t && t !== Texture.EMPTY) bg.texture = t;
    } else if (this._betPopupBgTexDefault && this._betPopupBgTexDefault !== Texture.EMPTY) {
      bg.texture = this._betPopupBgTexDefault;
    }

    bg.width = bgW;
    bg.height = bgH;

    if (compact) {
      const isPhone = this._isPortraitMobileViewport();
      const mf = this._betPopupMobileModalFit;
      title.x = isPhone ? bgW / 2 : bgW / 2 + TABLET_BET_MODAL_TITLE_CENTER_NUDGE_X * mf;
    } else {
      title.x = bgW / 2;
      title.y = 38;
    }

    if (!compact) {
      const closeW = Math.min(44, Math.max(28, bgW * 0.055));
      closeBtn.width = closeW;
      closeBtn.height = closeW;
      closeBtn.x = bgW - 40;
      closeBtn.y = 38;
    } else {
      const mf = this._betPopupMobileModalFit;
      const isPhone = this._isPortraitMobileViewport();
      const closeS = (isPhone ? MOBILE_BET_MODAL_CLOSE_SIZE_REF : TABLET_BET_MODAL_CLOSE_SIZE_REF) * mf;
      closeBtn.width = closeS;
      closeBtn.height = closeS;
      const closeRight = isPhone ? MOBILE_BET_MODAL_CLOSE_RIGHT_REF : TABLET_BET_MODAL_CLOSE_RIGHT_REF;
      const closeTop = isPhone ? MOBILE_BET_MODAL_CLOSE_TOP_REF : TABLET_BET_MODAL_CLOSE_TOP_REF;
      closeBtn.x = bgW - closeRight * mf - closeS / 2;
      closeBtn.y = closeTop * mf + closeS / 2;
    }

    chipsArea.x = padX;
    chipsArea.y = padTop;

    const borderG = this._betPopupPanelBorder;
    if (borderG) {
      borderG.clear();
      borderG.roundRect(0, 0, bgW, bgH, betPopupCornerR).stroke({
        width: 2,
        fill: BET_POPUP_BORDER_GRADIENT,
        join: 'round',
      });
    }

    this._betPopupChipInnerW = bgW - 2 * padX;
    this._betPopupChipInnerH = bgH - padTop - padBot;

    panel.hitArea = new Rectangle(0, 0, bgW, bgH);
    panel.x = -bgW / 2;
    panel.y = -bgH / 2;

    const pr = this._betPopupPanelRoot;
    if (pr) {
      let centerY = designH / 2 + yNudge;
      if (compact && this._isPortraitMobileViewport()) {
        centerY = this._resolvePhoneBetPopupCenterY(bgH);
      } else if (compact) {
        const worldS = Math.max(1e-6, this.world?.scale?.y ?? 1);
        const screenMul = scaleY * worldS;
        const minTop = marginCss / screenMul;
        const maxBottom = (ch - marginCss) / screenMul;
        const panelTop = centerY - bgH / 2;
        if (panelTop < minTop) centerY = minTop + bgH / 2;
        const panelBottom = centerY + bgH / 2;
        if (panelBottom > maxBottom) centerY = maxBottom - bgH / 2;
      }
      pr.position.set(1922 / 2, centerY);
    }

    if (this._betPopupDimRoot?.visible) {
      this._refreshBetPopupChips();
      this._syncBetPopupTriggerForModalOpen(true);
    }
  }

  /**
   * Undo non-uniform CSS canvas scaling for the settings modal panel only.
   * @param {number} scaleX
   * @param {number} scaleY
   */
  _applySettingsViewportScale(scaleX, scaleY) {
    const shell = this._settingsPanelRoot;
    if (!shell) return;
    if (scaleX > 0 && scaleY > 0 && Number.isFinite(scaleX) && Number.isFinite(scaleY)) {
      shell.scale.set(scaleY / scaleX, 1);
    } else {
      shell.scale.set(1, 1);
    }
  }

  /** Reliable label width for settings toggles (Pixi bounds + canvas measureText). */
  _measureSettingsSwitchLabelWidth(label) {
    if (!(label instanceof Text)) return 0;
    label.style.align = 'left';
    if (typeof label.updateText === 'function') label.updateText(true);

    const boundsW = Math.ceil(label.getLocalBounds().width);
    let canvasW = 0;
    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const s = label.style;
        const weight = s.fontWeight || '600';
        const size = s.fontSize || 18;
        const family = s.fontFamily || 'Montserrat';
        ctx.font = `${weight} ${size}px ${family}`;
        canvasW = Math.ceil(ctx.measureText(String(label.text ?? '')).width);
      }
    }
    return Math.max(boundsW, canvasW, Math.ceil(label.width || 0));
  }

  /**
   * Place a settings-tab toggle after its localized label (or pin to the row's right edge when stacked).
   * @param {import('pixi.js').Container} row
   * @param {number} gap
   * @param {number | null} [rowW] Full row width — enables wrap + right-pinned toggle.
   */
  _layoutSettingsSwitchRow(row, gap = SETTINGS_SWITCH_LABEL_GAP, rowW = null) {
    const label = row?.children?.[0];
    const sw = row?.children?.[1];
    if (!(label instanceof Text) || !sw) return;

    const toggleW = SETTINGS_SWITCH_TOGGLE_W;
    const pinToggleRight = rowW != null && Number.isFinite(rowW);
    if (pinToggleRight) {
      const wrapW = Math.max(40, rowW - toggleW - gap);
      label.style.wordWrap = true;
      label.style.wordWrapWidth = wrapW;
    } else {
      label.style.wordWrap = false;
      label.style.wordWrapWidth = 0;
    }

    const labelW =
      this._measureSettingsSwitchLabelWidth(label) + SETTINGS_SWITCH_LABEL_PAD;
    sw.x = pinToggleRight ? rowW - toggleW : labelW + gap;
    sw.y = label.y - (label.height || 22) / 2;
  }

  /** Re-run settings switch layout after locale / viewport changes. */
  _relayoutSettingsSwitchesOnly() {
    if (!this._settingsModalRefs?.switchesWrap) return;
    const mobile = this._isPortraitMobileViewport();
    const tablet = !mobile && this._isTabletViewport();
    const compact = mobile || tablet;
    const compactMetrics = compact
      ? this._getSettingsPanelCompactMetrics(mobile ? 'mobile' : 'tablet')
      : null;
    const panelW = compactMetrics ? compactMetrics.panelW : SETTINGS_PANEL_DESKTOP.w;
    const panelH = compactMetrics ? compactMetrics.panelH : SETTINGS_PANEL_DESKTOP.h;
    const rx = compactMetrics ? panelW / compactMetrics.layoutRefW : 1;
    const ry = compactMetrics ? panelH / compactMetrics.layoutRefH : 1;
    this._layoutSettingsSwitchesWrap({ mobile, tablet, panelW, rx, ry });

    const ew = this._settingsModalRefs.exitLobbyWrap;
    if (ew?.visible) {
      const soundLabel = this._settingsModalRefs.soundRow?.children?.[0];
      const quickLabel = this._settingsModalRefs.quickRow?.children?.[0];
      const soundH =
        soundLabel instanceof Text ? Math.max(22, Math.ceil(soundLabel.height)) : 22;
      const quickH =
        quickLabel instanceof Text ? Math.max(22, Math.ceil(quickLabel.height)) : 22;
      const stackGap = Math.round(12 * ry);
      ew.x = panelW / 2;
      ew.y = compact
        ? this._settingsModalRefs.switchesWrap.y + soundH + stackGap + quickH + Math.round(20 * ry)
        : 268;
    }
  }

  /**
   * Position Sound / Quick Spin toggles from measured label widths (locale + font size safe).
   * @param {{ mobile: boolean, tablet: boolean, panelW: number, rx: number, ry: number }} opts
   */
  _layoutSettingsSwitchesWrap({ mobile, tablet, panelW, rx, ry }) {
    const r = this._settingsModalRefs;
    if (!r?.switchesWrap || !r.soundRow || !r.quickRow) return;

    const edgeInset = mobile || tablet ? Math.round(SETTINGS_SWITCH_EDGE_INSET * rx) : SETTINGS_SWITCH_EDGE_INSET;
    const labelGap = mobile || tablet ? Math.round(SETTINGS_SWITCH_LABEL_GAP * rx) : SETTINGS_SWITCH_LABEL_GAP;
    const rowContentW = Math.max(120, panelW - edgeInset * 2);
    const colGap = mobile || tablet
      ? Math.round(SETTINGS_SWITCH_COLUMN_GAP * rx)
      : SETTINGS_SWITCH_COLUMN_GAP;

    const layoutRow = (row, rowW = null) => {
      this._layoutSettingsSwitchRow(row, labelGap, rowW);
      return rowW ?? row.children[1].x + SETTINGS_SWITCH_TOGGLE_W;
    };

    let soundW = layoutRow(r.soundRow);
    let quickW = layoutRow(r.quickRow);
    const stackRows = mobile || soundW + colGap + quickW > rowContentW;
    const sw = r.switchesWrap;

    if (stackRows) {
      soundW = layoutRow(r.soundRow, rowContentW);
      quickW = layoutRow(r.quickRow, rowContentW);
      const blockW = Math.max(soundW, quickW);
      sw.x = (panelW - blockW) / 2 + 0.5;
      sw.y = mobile ? Math.round(186 * ry) : Math.round(219 * ry);
      r.soundRow.x = 0;
      r.soundRow.y = 0;
      const soundLabel = r.soundRow.children[0];
      const soundRowH =
        soundLabel instanceof Text
          ? Math.max(22, Math.ceil(soundLabel.height))
          : Math.round(22 * ry);
      const rowStackGap = Math.round(12 * ry);
      r.quickRow.x = 0;
      r.quickRow.y = soundRowH + rowStackGap;
    } else if (tablet) {
      const blockW = soundW + colGap + quickW;
      sw.x = (panelW - blockW) / 2 + 0.5;
      sw.y = Math.round(219 * ry);
      r.soundRow.x = 0;
      r.soundRow.y = 0;
      r.quickRow.x = soundW + colGap;
      r.quickRow.y = 0;
    } else {
      const blockW = soundW + colGap + quickW;
      sw.x = (panelW - blockW) / 2 + 0.5;
      sw.y = 219;
      r.soundRow.x = 0;
      r.soundRow.y = 0;
      r.quickRow.x = soundW + colGap;
      r.quickRow.y = 0;
    }
  }

  /**
   * Portrait mobile uses a narrow/tall panel ({@link SETTINGS_PANEL_MOBILE}); landscape/desktop keeps 666×580.
   * Tab icons and typography stay the same design sizes — only the shell and positions update.
   */
  _syncSettingsModalLayout() {
    const r = this._settingsModalRefs;
    if (!r) return;

    const mobile = this._isPortraitMobileViewport();
    const tablet = !mobile && this._isTabletViewport();
    const compact = mobile || tablet;
    const compactMetrics = compact
      ? this._getSettingsPanelCompactMetrics(mobile ? 'mobile' : 'tablet')
      : null;
    const panelW = compactMetrics ? compactMetrics.panelW : SETTINGS_PANEL_DESKTOP.w;
    const panelH = compactMetrics ? compactMetrics.panelH : SETTINGS_PANEL_DESKTOP.h;
    const modalFit = compactMetrics?.modalFit ?? 1;
    const rx = compactMetrics ? panelW / compactMetrics.layoutRefW : 1;
    const ry = compactMetrics ? panelH / compactMetrics.layoutRefH : 1;
    const SCALE = r.SCALE;
    const W = 1922;
    const H = 1080;

    const upShift = (panelH * (SCALE - 1)) / 2;
    const mobileRootLower = compactMetrics?.rootOffsetY ?? 0;
    const panelRootY = mobile
      ? H - SETTINGS_PANEL_MOBILE_BOTTOM_STAGE_GAP - (panelH * SCALE) / 2
      : H / 2 - 47 - upShift + mobileRootLower;
    r.panelRoot.position.set(W / 2, panelRootY);

    r.panel.x = (-panelW * SCALE) / 2;
    r.panel.y = (-panelH * SCALE) / 2;
    r.panel.hitArea = new Rectangle(0, 0, panelW, panelH);

    r.panelBg.clear();
    r.panelBg.roundRect(0, 0, panelW, panelH, 20).fill({ width: 2, color: 0x000000, alpha: 0.8 });

    r.borderBase.clear();
    r.borderBase.roundRect(0, 0, panelW, panelH, 20).stroke({ width: 4, color: 0x171d0a, alpha: 1 });

    const edgeInset = compact ? Math.round(28 * rx) : 28;
    const edgeW = Math.max(8, panelW - edgeInset * 2);
    r.topBorder.x = edgeInset;
    r.topBorder.width = edgeW;
    r.bottomBorder.x = edgeInset;
    r.bottomBorder.y = panelH - 1;
    r.bottomBorder.width = edgeW;

    r.heading.x = panelW / 2;
    r.heading.y = compact ? Math.round(24 * ry) : 24;
    if (compact) {
      const uiTextMul = mobile ? SETTINGS_MOBILE_UI_TEXT_MUL : 1;
      r.heading.style.fontSize = Math.round((mobile ? 18 : 16) * ry * uiTextMul);
      r.heading.style.lineHeight = Math.round((mobile ? 22 : 20) * ry * uiTextMul);
      const bumpSwitchLabel = (row) => {
        const label = row?.children?.[0];
        if (label instanceof Text && mobile) {
          label.style.fontSize = Math.round(18 * ry * uiTextMul);
          label.style.lineHeight = Math.round(22 * ry * uiTextMul);
        }
      };
      bumpSwitchLabel(r.soundRow);
      bumpSwitchLabel(r.quickRow);
    } else {
      r.heading.style.fontSize = 16;
      r.heading.style.lineHeight = 20;
    }

    r.lineTop.y = compact ? Math.round(64 * ry) : 64;

    r.closeBtn.x = compact ? panelW - Math.round((mobile ? 27 : 29) * rx) : panelW - 29;
    r.closeBtn.y = compact ? Math.round((mobile ? 34 : 35) * ry) : 35;
    if (compact) {
      const closeSize = Math.max(18, Math.round((mobile ? 24 : 18) * Math.min(rx, ry)));
      r.closeBtn.width = closeSize;
      r.closeBtn.height = closeSize;
      const hs = Math.round(closeSize * 1.1);
      r.closeBtn.hitArea = new Rectangle(-hs, -hs, hs * 2, hs * 2);
    } else {
      r.closeBtn.width = 18;
      r.closeBtn.height = 18;
      r.closeBtn.hitArea = new Rectangle(-16, -16, 32, 32);
    }

    r.lineTop.width = panelW;

    const compactBottomGap = compact
      ? Math.round(SETTINGS_PANEL_MOBILE_BOTTOM_GAP * modalFit)
      : 0;
    const lineBottomY = compact ? panelH - compactBottomGap : 523;
    r.lineBottom.y = lineBottomY;
    r.lineBottom.width = panelW;

    this._layoutSettingsSwitchesWrap({ mobile, tablet, panelW, rx, ry });
    const sw = r.switchesWrap;

    const ew = r.exitLobbyWrap;
    if (ew) {
      const hasLobby = !!(this._lobbyUrl && String(this._lobbyUrl).trim());
      ew.visible = hasLobby;
      if (hasLobby) {
        const soundLabel = r.soundRow?.children?.[0];
        const quickLabel = r.quickRow?.children?.[0];
        const soundH =
          soundLabel instanceof Text ? Math.max(22, Math.ceil(soundLabel.height)) : 22;
        const quickH =
          quickLabel instanceof Text ? Math.max(22, Math.ceil(quickLabel.height)) : 22;
        const stackGap = Math.round(12 * ry);
        ew.x = panelW / 2;
        ew.y = compact
          ? sw.y + soundH + stackGap + quickH + Math.round(20 * ry)
          : 268;
      }
    }

    if (compact) {
      const tabInset = mobile ? 52 : 94;
      const leftCx = Math.round(tabInset * rx);
      const rightCx = mobile ? Math.round(308 * rx) : panelW - Math.round(tabInset * rx);
      this._settingsTabCenters = { left: leftCx, mid: panelW / 2, right: rightCx };
      r.iconRow.y = lineBottomY;
      const tabIconSize = Math.max(SETTINGS_TAB_ICON_SIZE, Math.round(30 * Math.min(rx, ry)));
      const tabIconHit = Math.round(tabIconSize * 1.3);
      fitSettingsTabIcon(r.settingsIcon, tabIconSize);
      fitSettingsTabIcon(r.autoplayIcon, tabIconSize);
      fitSettingsTabIcon(r.infoIcon, tabIconSize);
      r.settingsIcon.hitArea = new Rectangle(0, 0, tabIconHit, tabIconHit);
      r.autoplayIcon.hitArea = new Rectangle(0, 0, tabIconHit, tabIconHit);
      r.infoIcon.hitArea = new Rectangle(0, 0, tabIconHit, tabIconHit);
      const half = tabIconSize / 2;
      const tabIconY = Math.round(14 * ry);
      const centerTabIconY = (sprite) => {
        sprite.y = tabIconY + (tabIconSize - sprite.height) / 2;
      };
      r.settingsIcon.x = leftCx - half;
      centerTabIconY(r.settingsIcon);
      r.autoplayIcon.x = panelW / 2 - half;
      centerTabIconY(r.autoplayIcon);
      r.infoIcon.x = rightCx - half;
      centerTabIconY(r.infoIcon);
      r.activeLine.y = Math.round((40 + SETTINGS_ACTIVE_TAB_LINE_Y_NUDGE) * ry);
    } else {
      this._settingsTabCenters = { left: 94, mid: panelW / 2, right: panelW - 94 };
      const tc = this._settingsTabCenters;
      r.iconRow.y = 537;
      fitSettingsTabIcon(r.settingsIcon, SETTINGS_TAB_ICON_SIZE);
      fitSettingsTabIcon(r.autoplayIcon, SETTINGS_TAB_ICON_SIZE);
      fitSettingsTabIcon(r.infoIcon, SETTINGS_TAB_ICON_SIZE);
      r.settingsIcon.hitArea = new Rectangle(0, 0, SETTINGS_TAB_ICON_SIZE, SETTINGS_TAB_ICON_SIZE);
      r.autoplayIcon.hitArea = new Rectangle(0, 0, SETTINGS_TAB_ICON_SIZE, SETTINGS_TAB_ICON_SIZE);
      r.infoIcon.hitArea = new Rectangle(0, 0, SETTINGS_TAB_ICON_SIZE, SETTINGS_TAB_ICON_SIZE);
      const tabSlotY = 0;
      r.settingsIcon.x = tc.left - SETTINGS_TAB_ICON_SIZE / 2;
      r.settingsIcon.y = tabSlotY + (SETTINGS_TAB_ICON_SIZE - r.settingsIcon.height) / 2;
      r.autoplayIcon.x = tc.mid - SETTINGS_TAB_ICON_SIZE / 2;
      r.autoplayIcon.y = tabSlotY + (SETTINGS_TAB_ICON_SIZE - r.autoplayIcon.height) / 2;
      r.infoIcon.x = tc.right - SETTINGS_TAB_ICON_SIZE / 2;
      r.infoIcon.y = tabSlotY + (SETTINGS_TAB_ICON_SIZE - r.infoIcon.height) / 2;
      r.activeLine.y = 26 + SETTINGS_ACTIVE_TAB_LINE_Y_NUDGE;
    }

    const blurInsetX = compact ? SETTINGS_PANEL_MOBILE_BLUR_INSET_X : 50 * SCALE;
    let ls = this._lastScales;
    if (compact && (!ls || !ls.x || !ls.y)) {
      const { width: cw, height: ch } = this._readContainerSize();
      ls = { x: cw / W, y: ch / H };
    }
    const viewportSquashX =
      compact && ls?.x > 0 && ls?.y > 0 ? ls.y / ls.x : 1;
    const blurCoreW = panelW * SCALE - blurInsetX * 2;
    const blurW = blurCoreW * viewportSquashX;
    const blurH = panelH * SCALE;
    r.blurMask.clear();
    r.blurMask
      .roundRect(r.panelRoot.x - blurW / 2, r.panelRoot.y - blurH / 2, blurW, blurH, 20 * SCALE)
      .fill(0xffffff);

    const ap = this._autoplayModalLayoutParts;
    if (ap) {
      const rowCenterX = (rowW) => (panelW - rowW) / 2;
      const sliderX = rowCenterX(AUTOPLAY_SLIDER_ROW_W);
      ap.slider1.x = sliderX;
      ap.slider2.x = sliderX;
      ap.slider3.x = sliderX;
      ap.chipsRow.x = rowCenterX(AUTOPLAY_CHIPS_TOTAL_W) + 0.5;
      ap.playBtn.x = rowCenterX(AUTOPLAY_PLAY_BTN_W) - 0.5;

      if (mobile) {
        ap.stopHeading.anchor.set(0, 0);
        ap.stopHeading.x = sliderX;
        const balanceBetRowW =
          AUTOPLAY_BALANCE_BLOCK_W + AUTOPLAY_MOBILE_BALANCE_BET_BLOCK_GAP + AUTOPLAY_BET_BLOCK_W;
        const balanceBetRowX = rowCenterX(balanceBetRowW);
        ap.balanceBlock.x = balanceBetRowX;
        ap.betBlock.x =
          balanceBetRowX + AUTOPLAY_BALANCE_BLOCK_W + AUTOPLAY_MOBILE_BALANCE_BET_BLOCK_GAP;
        const balanceBetY = Math.round(
          (AUTOPLAY_SLIDER3_Y + AUTOPLAY_SLIDER_ROW_H + AUTOPLAY_MOBILE_BALANCE_BET_GAP) * ry,
        );
        ap.balanceBlock.y = balanceBetY;
        ap.betBlock.y = balanceBetY;
      } else {
        ap.stopHeading.anchor.set(0, 0);
        ap.stopHeading.x = (panelW - 104) / 2 - 122;
        ap.balanceBlock.x = 33;
        ap.betBlock.x = panelW - 102;
        ap.balanceBlock.y = 344;
        ap.betBlock.y = 344;
      }
    }

    const ir = this._infoSettingsLayoutRefs;
    if (ir?.mask && ir.content) {
      const VIEWPORT_TOP = compact ? Math.round(66 * ry) : 66;
      const VIEWPORT_BOTTOM = compact ? lineBottomY - 1 : 522;
      /* Content is authored at the desktop baseline width and uniformly scaled to the
         active inner width (full panel on desktop; portrait phone + tablet use a small
         side inset so the Information copy reads slightly narrower). Word-wrap widths stay
         in desktop space so line breaks never shift. */
      const baselinePanelW = SETTINGS_PANEL_DESKTOP.w;
      const BASE_PAD_X = 28;
      const infoTight = mobile || this._isTabletViewport();
      const infoSideInset = infoTight
        ? (mobile ? 0 : SETTINGS_INFO_VIEW_EXTRA_SIDE_INSET)
        : 0;
      const innerPanelW = Math.max(240, panelW - infoSideInset * 2);
      const contentScale = innerPanelW / baselinePanelW;
      ir.mask.clear();
      ir.mask
        .rect(infoSideInset, VIEWPORT_TOP, innerPanelW, VIEWPORT_BOTTOM - VIEWPORT_TOP)
        .fill(0xffffff);
      ir.view.hitArea = new Rectangle(
        infoSideInset,
        VIEWPORT_TOP,
        innerPanelW,
        VIEWPORT_BOTTOM - VIEWPORT_TOP,
      );
      ir.content.scale.set(contentScale);
      ir.content.x = infoSideInset + BASE_PAD_X * contentScale;
      this._infoContentScale = contentScale;
      this._infoViewportTop = VIEWPORT_TOP;
      this._infoViewportBottom = VIEWPORT_BOTTOM;
      this._refreshInfoScrollBounds(VIEWPORT_TOP, VIEWPORT_BOTTOM);
    }

    const tab = this._settingsModalActiveTab ?? 'settings';
    const tcLine = this._settingsTabCenters;
    if (tab === 'settings') r.activeLine.x = tcLine.left;
    else if (tab === 'autoplay') r.activeLine.x = tcLine.mid;
    else r.activeLine.x = tcLine.right;

    this._settingsLayoutMobile = mobile;
  }

  /**
   * Recompute info-tab scroll limits when the settings panel viewport height changes (mobile responsive).
   * @param {number} viewportTop
   * @param {number} viewportBottom
   */
  _refreshInfoScrollBounds(viewportTop, viewportBottom) {
    const ir = this._infoSettingsLayoutRefs;
    const totalH = this._infoScrollContentHeight;
    if (!ir?.scroll || !totalH) return;
    /* Content is scaled uniformly to fit the active panel width — scroll limits
       must follow the scaled height, otherwise the bottom of the info tab is
       unreachable on smaller panels. */
    const scale = this._infoContentScale ?? 1;
    const effectiveH = totalH * scale;
    const viewportH = viewportBottom - viewportTop;
    const naturalTop = viewportTop + 6;
    const minY = effectiveH > viewportH ? naturalTop - (effectiveH - viewportH) : naturalTop;
    const maxY = naturalTop;
    this._infoScrollClamp = { minY, maxY, naturalTop };
    ir.scroll.y = Math.max(minY, Math.min(maxY, ir.scroll.y));
  }

  async _createBetPopupPixi() {
    if (this._betPopupDimRoot || !this.world || !this.app) return;

    const W = 1922;
    const H = 1080;
    this.world.sortableChildren = true;

    const dimRoot = new Container();
    dimRoot.visible = false;
    dimRoot.zIndex = 200;
    dimRoot.eventMode = 'static';

    /* Oversized vs design rect so the dim always covers the full canvas after world scale/pivot (same idea as autoplay dim). */
    const dim = new Graphics();
    dim.rect(-8000, -8000, 16000, 16000).fill({ color: 0x000000, alpha: 0.65 });
    dim.eventMode = 'static';
    dim.cursor = 'default';
    dim.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._hideBetPopup();
    });
    dimRoot.addChild(dim);

    let bgTex = Texture.EMPTY;
    let closeTex = Texture.EMPTY;
    try {
      bgTex = await Assets.load('/assets/quick_bets_background.png');
    } catch {
      /* fallback panel size only */
    }
    let bgTexMobile = bgTex;
    let bgTexTablet = bgTex;
    try {
      bgTexMobile = await Assets.load('/assets/quick_bets_background_mobile.png');
    } catch {
      /* use default */
    }
    try {
      bgTexTablet = await Assets.load('/assets/quick_bets_background_tablet.png');
    } catch {
      /* use default */
    }
    this._betPopupBgTexDefault = bgTex;
    this._betPopupBgTexMobile = bgTexMobile;
    this._betPopupBgTexTablet = bgTexTablet;
    try {
      closeTex = await Assets.load('/assets/close_button.png');
    } catch {
      /* optional */
    }

    const panel = new Container();
    panel.eventMode = 'static';

    const panelFill = new Graphics();
    panelFill.eventMode = 'none';
    const roundMask = new Graphics();
    roundMask.eventMode = 'none';
    const panelBorder = new Graphics();
    panelBorder.eventMode = 'none';

    const bgSprite = new Sprite(bgTex);
    const tw = bgTex !== Texture.EMPTY && bgTex.width > 0 ? bgTex.width : 1000;
    const th = bgTex !== Texture.EMPTY && bgTex.height > 0 ? bgTex.height : 780;
    this._betPopupBgNatW = tw;
    this._betPopupBgNatH = th;
    /* Final width/height come from {@link #_syncBetPopupLayout}. */
    bgSprite.width = tw;
    bgSprite.height = th;
    bgSprite.eventMode = 'none';

    const title = new Text({
      text: this._tu('BET_OPTIONS'),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 25,
        fontWeight: '900',
        fill: 0xffffff,
        align: 'center',
      },
    });
    title.anchor.set(0.5, 0);
    title.eventMode = 'none';

    const closeBtn = new Sprite(closeTex);
    closeBtn.eventMode = 'static';
    closeBtn.cursor = 'pointer';
    closeBtn.anchor.set(0.5, 0.5);
    closeBtn.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      this._hideBetPopup();
    });

    const chipsArea = new Container();
    chipsArea.eventMode = 'static';

    panel.addChild(panelFill, bgSprite, roundMask, panelBorder, title, closeBtn, chipsArea);
    bgSprite.mask = roundMask;

    this._betPopupPanelFill = panelFill;
    this._betPopupRoundMask = roundMask;
    this._betPopupPanelBorder = panelBorder;

    const panelRoot = new Container();
    panelRoot.visible = false;
    panelRoot.zIndex = 201;
    panelRoot.eventMode = 'static';
    panelRoot.position.set(W / 2, H / 2);
    panelRoot.addChild(panel);

    this.world.addChild(dimRoot, panelRoot);
    this._betPopupDimRoot = dimRoot;
    this._betPopupPanelRoot = panelRoot;
    this._betPopupDim = dim;
    this._betPopupChipsArea = chipsArea;
    this._betPopupBgSprite = bgSprite;
    this._betPopupTitle = title;
    this._betPopupCloseBtn = closeBtn;
    this._betPopupPanel = panel;

    const cr = this._readContainerSize();
    const sx = cr.width / W;
    const sy = cr.height / H;
    this._applyBetPopupViewportScale(sx, sy);
    this._syncBetPopupLayout();
  }

  _refreshBetPopupChips() {
    const area = this._betPopupChipsArea;
    if (!area || !this.slotMachine || !this._qbNormTex) return;
    area.removeChildren();

    const ui = this.slotMachine.ui;
    const bets = ui._quickBets ?? [];
    const current = ui.betValue;
    const innerW = this._betPopupChipInnerW ?? 800;
    const innerH = this._betPopupChipInnerH ?? 500;
    const font = '"Montserrat", "Arial", sans-serif';

    const n = bets.length;
    if (n === 0) return;

    const mobileUi = this._isPortraitMobileViewport() || this._isTabletViewport();

    if (mobileUi) {
      const isPhone = this._isPortraitMobileViewport();
      const gw = isPhone ? MOBILE_BET_MODAL_GRID_INNER_W : TABLET_BET_MODAL_GRID_INNER_W;
      const cw0 = isPhone ? MOBILE_BET_CHIP_REF_W : TABLET_BET_CHIP_REF_W;
      const ch0 = isPhone ? MOBILE_BET_CHIP_REF_H : TABLET_BET_CHIP_REF_H;
      const g0 = isPhone ? MOBILE_BET_CHIP_GAP_REF : TABLET_BET_CHIP_GAP_REF;
      const gridInnerH = isPhone ? MOBILE_BET_MODAL_GRID_INNER_H : TABLET_BET_MODAL_GRID_INNER_H;
      const w = Math.max(1, innerW);
      const u = w / gw;
      let cw = cw0 * u;
      let ch = ch0 * u;
      let g = g0 * u;
      let cols = Math.max(1, Math.floor((w + g) / (cw + g)));
      cols = Math.min(cols, n);
      const rows = Math.ceil(n / cols);
      let needW = cols * cw + (cols - 1) * g;
      let needH = rows * ch + (rows - 1) * g;
      const innerHAvail = gridInnerH * (w / gw);
      let s = Math.min(w / Math.max(1, needW), innerHAvail / Math.max(1, needH), 1.55);
      if (!(s > 0) || !Number.isFinite(s)) s = 1;
      cw *= s;
      ch *= s;
      g *= s;
      needW = cols * cw + (cols - 1) * g;
      needH = rows * ch + (rows - 1) * g;
      const rowW = needW;

      bets.forEach((amount, i) => {
        const sel = Math.abs(amount - current) < 0.001;
        const btn = createQuickBetChipPixi({
          betValue: amount,
          selected: sel,
          textureNormal: this._qbNormTex,
          textureSelected: this._qbSelTex,
          cellW: cw,
          cellH: ch,
          bottomText: this._tu('BET'),
          currencyLabel: this._currencyLabel(),
          currencyPrecision: this._currencyPrecision,
          fontFamily: font,
        });

        const col = i % cols;
        const row = Math.floor(i / cols);
        btn.x = (innerW - rowW) / 2 + col * (cw + g);
        btn.y = row * (ch + g);

        btn.on('pointertap', (e) => {
          if (!isPrimaryPointerAction(e)) return;
          this._applyBetFromBetPopup(amount);
        });

        area.addChild(btn);
      });
      return;
    }

    /* Desktop: square chips (unchanged layout). */
    const baseSide = 130;
    const gap = 15;
    let cols = Math.max(1, Math.floor((innerW + gap) / (baseSide + gap)));
    cols = Math.min(cols, Math.max(1, n));
    const rows = Math.ceil(n / cols);
    let needW = cols * (baseSide + gap) - gap;
    let needH = rows * (baseSide + gap) - gap;
    let chipScale = Math.min(innerW / needW, innerH / needH);
    chipScale = Math.min(chipScale, 1.55);
    if (!(chipScale > 0) || !Number.isFinite(chipScale)) chipScale = 1;

    const btnW = baseSide * chipScale;
    const btnH = btnW;
    const g = gap * chipScale;
    const rowW = cols * (btnW + g) - g;

    bets.forEach((amount, i) => {
      const sel = Math.abs(amount - current) < 0.001;
      const btn = createQuickBetChipPixi({
        betValue: amount,
        selected: sel,
        textureNormal: this._qbNormTex,
        textureSelected: this._qbSelTex,
        cellW: btnW,
        cellH: btnH,
        bottomText: this._tu('BET'),
        currencyLabel: this._currencyLabel(),
        currencyPrecision: this._currencyPrecision,
        fontFamily: font,
      });

      const col = i % cols;
      const row = Math.floor(i / cols);
      btn.x = (innerW - rowW) / 2 + col * (btnW + g);
      btn.y = row * (btnH + g);

      btn.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        this._applyBetFromBetPopup(amount);
      });

      area.addChild(btn);
    });
  }

  /** Desktop only — on phone/tablet the trigger stays hidden while the modal is open. */
  _betPopupTriggerVisibleWhenModalOpen() {
    return (
      !this._isPortraitMobileViewport() &&
      !this._isTabletViewport() &&
      !this._isMobileLandscapeViewport()
    );
  }

  _showBetPopupTriggerAboveModal() {
    const btn = this._betPopupTriggerBtn;
    const world = this.world;
    const controls = this._canvasControls;
    if (!btn || !world || !controls || btn.parent !== controls) return;
    this._betPopupTriggerControlsIndex = controls.getChildIndex(btn);
    world.sortableChildren = true;
    btn.zIndex = 202;
    world.reparentChildAt(btn, world.children.length);
  }

  _restoreBetPopupTriggerToControls() {
    const btn = this._betPopupTriggerBtn;
    const world = this.world;
    const controls = this._canvasControls;
    if (!btn || !world || !controls || btn.parent !== world) return;
    const idx = Math.min(
      Math.max(0, this._betPopupTriggerControlsIndex),
      controls.children.length,
    );
    controls.reparentChildAt(btn, idx);
    btn.zIndex = 0;
  }

  _syncBetPopupTriggerForModalOpen(isOpen) {
    const btn = this._betPopupTriggerBtn;
    if (!btn) return;
    if (isOpen) {
      if (this._betPopupTriggerVisibleWhenModalOpen()) {
        this._showBetPopupTriggerAboveModal();
      } else {
        btn.visible = false;
        btn.eventMode = 'none';
        btn.cursor = 'default';
      }
      return;
    }
    this._restoreBetPopupTriggerToControls();
    btn.visible = true;
    btn.eventMode = 'static';
    btn.cursor = 'pointer';
  }

  _toggleBetPopup() {
    if (!this._betPopupDimRoot || !this._betPopupPanelRoot) return;
    if (this._betPopupDimRoot.visible) {
      this._hideBetPopup();
    } else {
      if (this._isBlockingModalOpen('bet')) return;
      this._dismissOverlappingModals('bet');
      this._syncBetPopupLayout();
      this._refreshBetPopupChips();
      this._betPopupDimRoot.visible = true;
      this._betPopupPanelRoot.visible = true;
      this._syncBetPopupTriggerForModalOpen(true);
      sound.play('uiPopupOpen');
    }
  }

  _hideBetPopup() {
    /* Only emit close sfx if it was actually open — _hideBetPopup is also
     * called preemptively on resize / orientation / settings open. */
    const wasOpen = !!(this._betPopupDimRoot && this._betPopupDimRoot.visible);
    if (this._betPopupDimRoot) this._betPopupDimRoot.visible = false;
    if (this._betPopupPanelRoot) this._betPopupPanelRoot.visible = false;
    this._syncBetPopupTriggerForModalOpen(false);
    if (wasOpen) sound.play('uiPopupClose');
  }

  _ensureAutoplayPixiDim() {
    if (this._autoplayPixiDim) return;
    const g = new Graphics();
    g.rect(-8000, -8000, 16000, 16000).fill({ color: 0x000000, alpha: 0.72 });
    g.eventMode = 'static';
    g.cursor = 'default';
    g.zIndex = 10;
    g.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._hideAutoplayPicker();
    });
    this._autoplayPixiDim = g;
  }

  _showAutoplayPicker() {
    const cluster = this._autoplayCluster;
    const world = this.world;
    const controls = this._canvasControls;
    if (!this._autoplayPickerRow || !cluster || !world || !controls || cluster.parent !== controls) return;
    if (this._isBlockingModalOpen('autoplay')) return;

    this._dismissOverlappingModals('autoplay');
    this._ensureAutoplayPixiDim();
    const dim = this._autoplayPixiDim;
    if (!dim) return;

    this._autoplayClusterControlsIndex = controls.getChildIndex(cluster);
    this._autoplayPickerOpen = true;
    this._autoplayPickerRow.visible = true;

    world.sortableChildren = true;
    if (this.slotMachine?.root) this.slotMachine.root.zIndex = 0;
    dim.zIndex = 10;
    cluster.zIndex = 20;

    world.reparentChildAt(dim, world.children.length);
    world.reparentChildAt(cluster, world.children.length);
    sound.play('uiPopupOpen');
  }

  _hideAutoplayPicker() {
    const wasOpen = this._autoplayPickerOpen;
    this._autoplayPickerOpen = false;
    if (this._autoplayPickerRow) this._autoplayPickerRow.visible = false;

    const cluster = this._autoplayCluster;
    const world = this.world;
    const controls = this._canvasControls;
    const dim = this._autoplayPixiDim;

    if (cluster && controls && cluster.parent === world) {
      const idx = Math.min(
        Math.max(0, this._autoplayClusterControlsIndex),
        controls.children.length,
      );
      controls.reparentChildAt(cluster, idx);
      cluster.zIndex = 20;
    }

    dim?.removeFromParent();
    if (wasOpen) sound.play('uiPopupClose');
  }

  _onAutoplayIconPointerUp() {
    if (this._autoplaying) {
      sound.play('uiClick');
      this.handleAutoplayToggle(false);
      this._hideAutoplayPicker();
      return;
    }
    if (this._autoplayPickerOpen) {
      this._hideAutoplayPicker();
      return;
    }
    this._showAutoplayPicker();
  }

  /**
   * @param {number | null} remainingSpins null = unlimited
   */
  _startAutoplayWithRounds(remainingSpins) {
    this._autoplayRoundsRemaining = remainingSpins;
    this._hideAutoplayPicker();
    this.handleAutoplayToggle(true);
  }

  /** Full-screen background image + bottom vignette (design space 1922×1080). */
  async _createPixiStageBackdrop() {
    if (this._backdropRoot) return;
    const W = 1922;
    const H = 1080;
    const root = new Container();
    root.eventMode = 'none';

    try { this._bgTexDesktop = await Assets.load('/assets/background.png'); } catch { this._bgTexDesktop = Texture.EMPTY; }
    try { this._bgTexMobile = await Assets.load('/assets/mobile_background.png'); } catch { this._bgTexMobile = this._bgTexDesktop; }
    try { this._bgTexTablet = await Assets.load('/assets/tablet_background.png'); } catch { this._bgTexTablet = this._bgTexDesktop; }

    const spr = new Sprite(this._pickBackdropTextureForViewport());
    spr.eventMode = 'none';
    this._backdropSprite = spr;
    this._syncStageBackdropForViewport();

    root.addChild(spr);
    this._backdropRoot = root;
  }

  /** Settings (paylines toggle) — Pixi modal on stage, above {@link this.world}. */
  async _createSettingsModalPixi() {
    if (this._settingsRoot || !this.app) return;

    const W = 1922;
    const H = 1080;
    const mobileInit = this._isPortraitMobileViewport();
    const tabletInit = !mobileInit && this._isTabletViewport();
    const compactInit = mobileInit || tabletInit;
    const compactMetricsInit = compactInit
      ? this._getSettingsPanelCompactMetrics(mobileInit ? 'mobile' : 'tablet')
      : null;
    const panelW = compactMetricsInit ? compactMetricsInit.panelW : SETTINGS_PANEL_DESKTOP.w;
    const panelH = compactMetricsInit ? compactMetricsInit.panelH : SETTINGS_PANEL_DESKTOP.h;
    /** Uniform up-scale (preserves all internal aspect ratios — no stretching of children). */
    const SCALE = SETTINGS_MODAL_PANEL_SCALE;

    const cr = this._readContainerSize();
    const sx = cr.width / W;
    const sy = cr.height / H;

    const root = new Container();
    root.visible = false;
    root.eventMode = 'static';
    root.zIndex = 999990;

    const dim = new Graphics();
    dim.rect(-8000, -8000, 16000, 16000).fill({ color: 0x000000, alpha: 0.5 });
    dim.eventMode = 'static';
    dim.cursor = 'default';
    dim.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      this._hideSettingsPanel();
    });

    const panel = new Container();
    panel.eventMode = 'static';
    panel.scale.set(SCALE);
    panel.x = (-panelW * SCALE) / 2;
    panel.y = (-panelH * SCALE) / 2;

    const panelRoot = new Container();
    panelRoot.eventMode = 'static';
    const panelHForShift = panelH;
    const upShiftInit = (panelHForShift * (SCALE - 1)) / 2;
    const mobileRootLowerInit = compactMetricsInit?.rootOffsetY ?? 0;
    const panelRootYInit = mobileInit
      ? H - SETTINGS_PANEL_MOBILE_BOTTOM_STAGE_GAP - (panelH * SCALE) / 2
      : H / 2 - 47 - upShiftInit + mobileRootLowerInit;
    panelRoot.position.set(W / 2, panelRootYInit);
    panelRoot.addChild(panel);

    const blurLayer = new Container();
    blurLayer.visible = false;
    blurLayer.eventMode = 'none';
    const blurSprite = new Sprite(Texture.EMPTY);
    blurSprite.eventMode = 'none';
    if (!this._settingsBlurFilter) {
      this._settingsBlurFilter = new BlurFilter({
        strengthX: 5,
        strengthY: 5,
      });
    }
    blurSprite.filters = [this._settingsBlurFilter];
    const blurMask = new Graphics();
    const blurInsetX = mobileInit ? SETTINGS_PANEL_MOBILE_BLUR_INSET_X : 50 * SCALE;
    /** Match {@link GameApp#_applySettingsViewportScale}: panelRoot uses sy/sx; blur is a sibling so widen mask on mobile. */
    const viewportSquashXInit =
      mobileInit && sx > 0 && sy > 0 ? sy / sx : 1;
    const blurCoreWInit = panelW * SCALE - blurInsetX * 2;
    const blurW = blurCoreWInit * viewportSquashXInit;
    const blurH = panelH * SCALE;
    blurMask.roundRect(panelRoot.x - blurW / 2, panelRoot.y - blurH / 2, blurW, blurH, 20 * SCALE).fill(0xffffff);
    blurMask.eventMode = 'none';
    blurLayer.addChild(blurSprite, blurMask);
    blurSprite.mask = blurMask;

    const panelBg = new Graphics();
    panelBg.roundRect(0, 0, panelW, panelH, 20).fill({ width: 2, color: 0x000000, alpha: 0.8 });
    panelBg.eventMode = 'none';

    const borderBase = new Graphics();
    const borderFx = new Container();
    const edgeInset = 28;
    const edgeW = panelW - edgeInset * 2;
    const gradCanvas = document.createElement('canvas');
    gradCanvas.width = 512;
    gradCanvas.height = 2;
    const gradCtx = gradCanvas.getContext('2d');
    if (gradCtx) {
      const g = gradCtx.createLinearGradient(0, 0, gradCanvas.width, 0);
      g.addColorStop(0, '#171D0A');
      g.addColorStop(0.5, '#829F38');
      g.addColorStop(1, '#171D0A');
      gradCtx.fillStyle = g;
      gradCtx.fillRect(0, 0, gradCanvas.width, gradCanvas.height);
    }
    const borderGradTex = Texture.from(gradCanvas);
    borderBase.roundRect(0, 0, panelW, panelH, 20).stroke({ width: 4, color: 0x171d0a, alpha: 1 });
    borderBase.eventMode = 'none';
    const topBorder = new Sprite(borderGradTex);
    topBorder.x = edgeInset;
    topBorder.y = -2;
    topBorder.width = edgeW;
    topBorder.height = 4;
    topBorder.eventMode = 'none';
    const bottomBorder = new Sprite(borderGradTex);
    bottomBorder.x = edgeInset;
    bottomBorder.y = panelH - 1;
    bottomBorder.width = edgeW;
    bottomBorder.height = 4;
    bottomBorder.eventMode = 'none';
    borderFx.addChild(topBorder, bottomBorder);
    borderFx.eventMode = 'none';

    const heading = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 16,
        fontWeight: '700',
        lineHeight: 20,
        fill: 0xffffff,
        align: 'center',
      },
    }), 'GAME_SETTINGS', { upper: true });
    heading.anchor.set(0.5, 0);
    heading.x = panelW / 2;
    heading.y = 24;
    heading.eventMode = 'none';
    this._settingsHeadingText = heading;

    let closeTex = Texture.EMPTY;
    try {
      closeTex = await Assets.load('/assets/settings/close.svg');
    } catch {
      /* optional fallback */
    }
    const closeBtn = new Sprite(closeTex);
    closeBtn.anchor.set(0.5, 0.5);
    closeBtn.x = panelW - 29;
    closeBtn.y = 35;
    closeBtn.width = 18;
    closeBtn.height = 18;
    closeBtn.eventMode = 'static';
    closeBtn.cursor = 'pointer';
    closeBtn.hitArea = new Rectangle(-16, -16, 32, 32);
    closeBtn.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      this._hideSettingsPanel();
    });

    let settingsFrameTex = Texture.EMPTY;
    try { settingsFrameTex = await Assets.load('/assets/settings/settings_frame.png'); } catch { /* noop */ }
    const lineTop = new Sprite(settingsFrameTex);
    lineTop.width = panelW;
    lineTop.height = 2;
    lineTop.x = 0;
    lineTop.y = 64;
    lineTop.eventMode = 'none';
    const lineBottom = new Sprite(settingsFrameTex);
    lineBottom.width = panelW;
    lineBottom.height = 2;
    lineBottom.x = 0;
    lineBottom.y = 523;
    lineBottom.eventMode = 'none';

    const makeSwitch = ({ x, y, labelKey, switchId, getValue, setValue }) => {
      const row = new Container();
      row.x = x;
      row.y = y;
      row.eventMode = 'passive';

      const text = this._bindLocalizedText(
        new Text({
          text: '',
          style: {
            fontFamily: 'Montserrat',
            fontSize: 18,
            lineHeight: 22,
            fontWeight: '600',
            fill: 0xffffff,
            align: 'left',
          },
        }),
        labelKey,
        { afterApply: () => this._relayoutSettingsSwitchesOnly() },
      );
      text.anchor.set(0, 0.5);
      text.x = 0;
      text.y = 11;
      text.eventMode = 'none';

      const sw = new Container();
      sw.x = 0;
      sw.y = 0;
      sw.eventMode = 'static';
      sw.cursor = 'pointer';
      sw.hitArea = new Rectangle(0, 0, 42, 22);

      const swBg = new Graphics();
      const knob = new Graphics();

      const redraw = () => {
        const on = !!getValue();
        swBg.clear();
        swBg.roundRect(0, 0, 42, 22, 100).fill({ color: on ? 0x191f0c : 0x1f1f1f, alpha: 1 });

        const knobGrad = new FillGradient({
          type: 'linear',
          start: { x: 0, y: 0 },
          end: { x: 1, y: 0 },
          textureSpace: 'local',
          colorStops: on
            ? [
                { offset: 0, color: '#4A5E1E' },
                { offset: 0.5, color: '#829F38' },
                { offset: 1, color: '#4A5E1E' },
              ]
            : [
                { offset: 0, color: '#2F3422' },
                { offset: 0.5, color: '#323A1D' },
                { offset: 1, color: '#2F3422' },
              ],
        });
        knob.clear();
        knob.roundRect(0, 0, 22, 22, 100).fill(knobGrad);
        knob.roundRect(0, 0, 22, 22, 100).stroke({ width: 1, color: 0x1f1f1f, alpha: 1 });
        knob.x = on ? 20 : 0;
      };
      redraw();
      sw.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        e.stopPropagation();
        setValue(!getValue());
        redraw();
      });
      sw.addChild(swBg, knob);
      row.addChild(text, sw);
      return row;
    };

    const settingsView = new Container();
    settingsView.eventMode = 'passive';
    const switchesWrap = new Container();
    switchesWrap.x = (panelW - 345) / 2 + 0.5;
    switchesWrap.y = 219;
    switchesWrap.addChild(
      makeSwitch({
        x: 0,
        y: 0,
        labelKey: 'SOUND',
        switchId: 'sound',
        getValue: () => this._settingsSoundOn,
        setValue: (next) => {
          this._settingsSoundOn = !!next;
          sound.setMuted(!this._settingsSoundOn);
          this._syncCanvasSoundIcon();
          sound.play('uiClick');
        },
      }),
      makeSwitch({
        x: 192,
        y: 0,
        labelKey: 'SETTINGS_LABEL_QUICK_SPIN',
        switchId: 'quickSpin',
        getValue: () => this._settingsQuickSpinOn,
        setValue: (next) => {
          const on = !!next;
          const wasOn = this._settingsQuickSpinOn;
          if (on && !wasOn) {
            this._betSpeedTierBeforeQuickSpin = this._betSpeedTier;
            this._betSpeedTier = 3;
            this._syncBetSpeedArrows();
          } else if (!on && wasOn) {
            const prev = this._betSpeedTierBeforeQuickSpin;
            this._betSpeedTierBeforeQuickSpin = null;
            if (prev != null) {
              this._betSpeedTier = Math.min(3, Math.max(1, prev));
            }
            this._syncBetSpeedArrows();
          }
          this._settingsQuickSpinOn = on;
          sound.play('uiClick');
        },
      }),
    );
    const exitLobbyWrap = new Container();
    exitLobbyWrap.eventMode = 'passive';
    exitLobbyWrap.visible = !!this._lobbyUrl;

    const exitTap = new Container();
    exitTap.eventMode = 'static';
    exitTap.cursor = 'pointer';
    const exitW = 280;
    const exitH = 44;
    const exitBg = new Graphics();
    exitBg.roundRect(-exitW / 2, -exitH / 2, exitW, exitH, 12).fill({ color: 0x191f0c, alpha: 1 });
    exitBg.roundRect(-exitW / 2, -exitH / 2, exitW, exitH, 12).stroke({ width: 2, color: 0x4a5e1e, alpha: 0.55 });
    const exitLabel = this._bindLocalizedText(
      new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: 15,
          fontWeight: '700',
          fill: 0xffffff,
          align: 'center',
        },
      }),
      'EXIT_TO_LOBBY',
    );
    exitLabel.anchor.set(0.5, 0.5);
    exitTap.hitArea = new Rectangle(-exitW / 2, -exitH / 2, exitW, exitH);
    exitTap.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      const url = String(this._lobbyUrl ?? '').trim();
      if (!url) return;
      sound.play('uiClick');
      window.location.assign(url);
    });
    exitTap.addChild(exitBg, exitLabel);
    exitLobbyWrap.addChild(exitTap);

    settingsView.addChild(switchesWrap, exitLobbyWrap);
    this._settingsViewContainer = settingsView;

    const autoplayView = await this._buildAutoplaySettingsView({ panelW, panelH });
    autoplayView.visible = false;
    this._autoplayViewContainer = autoplayView;

    const infoView = await this._buildInfoSettingsView({ panelW, panelH });
    infoView.visible = false;
    this._infoViewContainer = infoView;

    let settingsIconTex = Texture.EMPTY;
    let autoplayIconTex = Texture.EMPTY;
    let infoIconTex = Texture.EMPTY;
    let activeSettingsLineTex = Texture.EMPTY;
    try { settingsIconTex = await Assets.load('/assets/settings/settings.svg'); } catch { /* noop */ }
    try { autoplayIconTex = await Assets.load('/assets/settings/autoplay.svg'); } catch { /* noop */ }
    try { infoIconTex = await Assets.load('/assets/settings/info.svg'); } catch { /* noop */ }
    try { activeSettingsLineTex = await Assets.load('/assets/settings/active_settings_icon.svg'); } catch { /* noop */ }

    const iconRow = new Container();
    iconRow.y = 537;
    iconRow.eventMode = 'passive';
    const centerX = panelW / 2;
    const leftCenterX = this._settingsTabCenters.left;
    const rightCenterX = this._settingsTabCenters.right;
    const settingsIcon = new Sprite(settingsIconTex);
    fitSettingsTabIcon(settingsIcon, SETTINGS_TAB_ICON_SIZE);
    settingsIcon.x = leftCenterX - SETTINGS_TAB_ICON_SIZE / 2;
    settingsIcon.y = (SETTINGS_TAB_ICON_SIZE - settingsIcon.height) / 2;
    settingsIcon.eventMode = 'static';
    settingsIcon.cursor = 'pointer';
    settingsIcon.hitArea = new Rectangle(0, 0, SETTINGS_TAB_ICON_SIZE, SETTINGS_TAB_ICON_SIZE);
    const autoplayIcon = new Sprite(autoplayIconTex);
    fitSettingsTabIcon(autoplayIcon, SETTINGS_TAB_ICON_SIZE);
    autoplayIcon.x = centerX - SETTINGS_TAB_ICON_SIZE / 2;
    autoplayIcon.y = (SETTINGS_TAB_ICON_SIZE - autoplayIcon.height) / 2;
    autoplayIcon.alpha = 0.6;
    autoplayIcon.eventMode = 'static';
    autoplayIcon.cursor = 'pointer';
    autoplayIcon.hitArea = new Rectangle(0, 0, SETTINGS_TAB_ICON_SIZE, SETTINGS_TAB_ICON_SIZE);
    const infoIcon = new Sprite(infoIconTex);
    fitSettingsTabIcon(infoIcon, SETTINGS_TAB_ICON_SIZE);
    infoIcon.x = rightCenterX - SETTINGS_TAB_ICON_SIZE / 2;
    infoIcon.y = (SETTINGS_TAB_ICON_SIZE - infoIcon.height) / 2;
    infoIcon.alpha = 0.6;
    infoIcon.eventMode = 'static';
    infoIcon.cursor = 'pointer';
    infoIcon.hitArea = new Rectangle(0, 0, SETTINGS_TAB_ICON_SIZE, SETTINGS_TAB_ICON_SIZE);

    const activeLine = new Sprite(activeSettingsLineTex);
    activeLine.width = 44;
    activeLine.height = 2;
    activeLine.anchor.set(0.5, 0);
    activeLine.x = leftCenterX;
    activeLine.y = 26 + SETTINGS_ACTIVE_TAB_LINE_Y_NUDGE;
    activeLine.eventMode = 'none';

    const setActiveTab = (tabName) => {
      const prev = this._settingsModalActiveTab;
      if (prev !== tabName) {
        sound.play('uiClick');
      }
      this._settingsModalActiveTab = tabName;
      const tc = this._settingsTabCenters;
      if (tabName === 'settings') {
        activeLine.x = tc.left;
        settingsIcon.alpha = 1;
        autoplayIcon.alpha = 0.6;
        infoIcon.alpha = 0.6;
        if (this._settingsHeadingText) this._settingsHeadingText.text = this._tu('GAME_SETTINGS');
        settingsView.visible = true;
        autoplayView.visible = false;
        infoView.visible = false;
      } else if (tabName === 'autoplay') {
        activeLine.x = tc.mid;
        settingsIcon.alpha = 0.6;
        autoplayIcon.alpha = 1;
        infoIcon.alpha = 0.6;
        if (this._settingsHeadingText) this._settingsHeadingText.text = this._tu('AUTOPLAY_SETTINGS');
        settingsView.visible = false;
        autoplayView.visible = true;
        infoView.visible = false;
        this._refreshAutoplaySettingsView();
      } else {
        activeLine.x = tc.right;
        settingsIcon.alpha = 0.6;
        autoplayIcon.alpha = 0.6;
        infoIcon.alpha = 1;
        if (this._settingsHeadingText) this._settingsHeadingText.text = this._tu('INFO');
        settingsView.visible = false;
        autoplayView.visible = false;
        infoView.visible = true;
        this._infoScroll?.reset();
      }
    };
    setActiveTab('settings');
    settingsIcon.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      setActiveTab('settings');
    });
    autoplayIcon.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      setActiveTab('autoplay');
    });
    infoIcon.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      setActiveTab('info');
    });

    iconRow.addChild(settingsIcon, autoplayIcon, infoIcon, activeLine);
    panel.addChild(
      panelBg,
      borderBase,
      borderFx,
      heading,
      closeBtn,
      lineTop,
      settingsView,
      autoplayView,
      infoView,
      lineBottom,
      iconRow,
    );
    panel.hitArea = new Rectangle(0, 0, panelW, panelH);

    this._settingsModalRefs = {
      SCALE,
      panelRoot,
      panel,
      panelBg,
      borderBase,
      topBorder,
      bottomBorder,
      blurMask,
      heading,
      closeBtn,
      lineTop,
      lineBottom,
      switchesWrap,
      soundRow: switchesWrap.children[0],
      quickRow: switchesWrap.children[1],
      exitLobbyWrap,
      iconRow,
      settingsIcon,
      autoplayIcon,
      infoIcon,
      activeLine,
    };

    root.addChild(blurLayer, dim, panelRoot);
    this.app.stage.sortableChildren = true;
    this.app.stage.addChild(root);
    this._settingsRoot = root;
    this._settingsPanelRoot = panelRoot;
    this._settingsBlurLayer = blurLayer;
    this._settingsBlurSprite = blurSprite;

    this._applySettingsViewportScale(sx, sy);
    this._syncSettingsModalLayout();
  }

  /**
   * Build the “AUTOPLAY SETTINGS” view (preset chips, PLAY NOW, three Stop-Autoplay sliders,
   * BALANCE / BET sidebar). Returns a Container positioned absolutely within the modal panel.
   * @param {{ panelW: number, panelH: number }} args
   * @returns {Promise<Container>}
   */
  async _buildAutoplaySettingsView({ panelW }) {
    const view = new Container();
    view.eventMode = 'passive';

    let qbBarrelTex = Texture.EMPTY;
    let qbBarrelSelTex = Texture.EMPTY;
    try {
      qbBarrelTex = await Assets.load('/assets/quick_bet.svg');
    } catch {
      /* optional fallback */
    }
    try {
      qbBarrelSelTex = await Assets.load('/assets/selected_quick_bet.svg');
    } catch {
      /* optional fallback */
    }

    /** Spin presets (matches the bottom autoplay picker). null = unlimited */
    const chipSpecs = [
      { spins: 10, label: '10' },
      { spins: 20, label: '20' },
      { spins: 50, label: '50' },
      { spins: 100, label: '100' },
      { spins: null, label: '∞' },
    ];

    /* ── Bet-amount display row (sits just below the lineTop rule at y=64) ── */
    const betAmountRow = new Container();
    betAmountRow.eventMode = 'none';
    betAmountRow.y = 70;

    const betAmountLabel = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 11,
        lineHeight: 14,
        fontWeight: '600',
        fill: 0xffffff,
        align: 'right',
      },
    }), 'BET', { upper: true, suffix: ':' });
    betAmountLabel.alpha = 0.6;
    betAmountLabel.anchor.set(1, 0.5);

    const betAmountValue = new Text({
      text: this._formatCurrencyAmount(0),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 11,
        lineHeight: 14,
        fontWeight: '700',
        fill: 0xffea00,
        align: 'left',
      },
    });
    betAmountValue.anchor.set(0, 0.5);
    betAmountValue.x = 5;

    const centerBetX = panelW / 2;
    betAmountLabel.x = centerBetX;
    betAmountValue.x = centerBetX + 5;

    betAmountRow.addChild(betAmountLabel, betAmountValue);

    const chipsRow = new Container();
    chipsRow.eventMode = 'passive';
    const chipW = 57;
    const chipH = 52;
    const chipGap = 6;
    const chipsTotalW = chipSpecs.length * chipW + (chipSpecs.length - 1) * chipGap;
    chipsRow.x = (panelW - chipsTotalW) / 2 + 0.5;
    chipsRow.y = 91;

    const chipButtons = [];
    /** @type {Text[]} */
    const chipFunTexts = [];
    chipSpecs.forEach((spec, i) => {
      const chip = new Container();
      chip.x = i * (chipW + chipGap);
      chip.y = 0;
      chip.eventMode = 'static';
      chip.cursor = 'pointer';
      chip.hitArea = new Rectangle(0, 0, chipW, chipH);

      const bg = new Sprite(qbBarrelTex);
      bg.width = chipW;
      bg.height = chipH;
      if (qbBarrelTex?.source) qbBarrelTex.source.scaleMode = 'linear';
      if (qbBarrelSelTex?.source) qbBarrelSelTex.source.scaleMode = 'linear';

      const numText = new Text({
        text: spec.label,
        style: {
          fontFamily: 'Montserrat',
          fontSize: spec.label === '∞' ? 30 : 14,
          lineHeight: 17,
          fontWeight: '700',
          fill: 0xffffff,
          align: 'center',
        },
      });
      numText.anchor.set(0.5, 0.5);
      numText.x = chipW / 2;
      numText.y = chipH * 0.36;
      numText.eventMode = 'none';

      const spinsText = this._bindLocalizedText(new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: 8,
          lineHeight: 10,
          fontWeight: '600',
          fill: 0xffea00,
          align: 'center',
        },
      }), 'SPINS', { upper: true });
      spinsText.anchor.set(0.5, 0.5);
      spinsText.x = chipW / 2;
      spinsText.y = chipH * 0.62;
      spinsText.eventMode = 'none';

      const funText = new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: 8,
          lineHeight: 10,
          fontWeight: '700',
          fill: 0xffffff,
          align: 'center',
        },
      });
      funText.anchor.set(0.5, 0.5);
      funText.x = chipW / 2;
      funText.y = chipH * 0.88;
      funText.eventMode = 'none';
      chipFunTexts.push(funText);

      chip.addChild(bg, numText, spinsText, funText);
      chip._spec = spec;
      chip._bg = bg;
      chip.on('pointertap', (e) => {
        if (!isPrimaryPointerAction(e)) return;
        e.stopPropagation();
        this._autoplayModalSpinsSelected = spec.spins;
        chipButtons.forEach((c) => {
          c._bg.texture = c._spec.spins === spec.spins ? qbBarrelSelTex : qbBarrelTex;
        });
      });

      chipsRow.addChild(chip);
      chipButtons.push(chip);
    });
    this._autoplayChipButtons = chipButtons;

    const refreshChips = (bet) => {
      const b = Number(bet) || 0;
      betAmountValue.text = this._formatCurrencyAmount(b);
      chipSpecs.forEach((spec, i) => {
        const t = chipFunTexts[i];
        if (!t) return;
        if (spec.spins == null) {
          t.text = '';
        } else {
          const total = b * spec.spins;
          t.text = this._formatCurrencyAmount(total);
        }
      });
      chipButtons.forEach((c) => {
        c._bg.texture = c._spec.spins === this._autoplayModalSpinsSelected ? qbBarrelSelTex : qbBarrelTex;
      });
    };
    this._autoplayChipsRefresh = refreshChips;

    /* PLAY NOW button (88×24, bg #779133, radius 6, white text). */
    const playBtn = new Container();
    playBtn.x = (panelW - 88) / 2 - 0.5;
    playBtn.y = 161;
    playBtn.eventMode = 'static';
    playBtn.cursor = 'pointer';
    playBtn.hitArea = new Rectangle(0, 0, 88, 24);
    const playBg = new Graphics();
    playBg.roundRect(0, 0, 88, 24, 6).fill({ color: 0x779133, alpha: 1 });
    playBg.eventMode = 'none';
    const playLabel = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 10,
        lineHeight: 12,
        fontWeight: '700',
        fill: 0xffffff,
        align: 'center',
      },
    }), 'START', { upper: true });
    playLabel.anchor.set(0.5, 0.5);
    playLabel.x = 44;
    playLabel.y = 12;
    playLabel.eventMode = 'none';
    playBtn.addChild(playBg, playLabel);
    playBtn.on('pointertap', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      this._startAutoplayFromModal();
    });

    /* "Stop Autoplay" sub-heading. */
    const stopHeading = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 14,
        lineHeight: 17,
        fontWeight: '700',
        fill: 0xffffff,
        align: 'left',
      },
    }), 'INFO_CTRL_STOP_AUTO_NAME');
    stopHeading.anchor.set(0, 0);
    stopHeading.x = (panelW - AUTOPLAY_SLIDER_ROW_W) / 2;
    stopHeading.y = 220;
    stopHeading.eventMode = 'none';

    const sliderRefreshers = [];
    const slider1 = this._buildAutoplaySlider({
      label: 'IF_BALANCE_DECREASES_BY',
      getMax: () => {
        const bet = Number(this.slotMachine?.ui?.betValue) || 0;
        return Math.max(0, bet * 100);
      },
      getValue: () => this._autoplayStopOnBalanceDecreaseBy,
      setValue: (v) => {
        this._autoplayStopOnBalanceDecreaseBy = v;
      },
    });
    slider1.container.x = 159;
    slider1.container.y = 252;
    sliderRefreshers.push(slider1.refresh);

    const slider2 = this._buildAutoplaySlider({
      label: 'IF_SINGLE_WIN_EXCEEDS',
      getMax: () => {
        const bet = Number(this.slotMachine?.ui?.betValue) || 0;
        return Math.max(0, bet * 100);
      },
      getValue: () => this._autoplayStopOnSingleWinExceeds,
      setValue: (v) => {
        this._autoplayStopOnSingleWinExceeds = v;
      },
    });
    slider2.container.x = 159;
    slider2.container.y = 335;
    sliderRefreshers.push(slider2.refresh);

    const slider3 = this._buildAutoplaySlider({
      label: 'IF_BALANCE_INCREASES_BY',
      getMax: () => {
        const bet = Number(this.slotMachine?.ui?.betValue) || 0;
        return Math.max(0, bet * 200);
      },
      getValue: () => this._autoplayStopOnBalanceIncreaseBy,
      setValue: (v) => {
        this._autoplayStopOnBalanceIncreaseBy = v;
      },
    });
    slider3.container.x = 159;
    slider3.container.y = 418;
    sliderRefreshers.push(slider3.refresh);
    this._autoplaySliderRefreshers = sliderRefreshers;

    /* BALANCE / BET sidebar (under the “Stop Autoplay” heading on each side). */
    const balanceBlock = new Container();
    balanceBlock.x = 33;
    balanceBlock.y = 344;
    balanceBlock.eventMode = 'none';
    const balanceLabel = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 10,
        lineHeight: 12,
        fontWeight: '700',
        fill: 0xffffff,
        align: 'center',
      },
    }), 'BALANCE', { upper: true, suffix: ':' });
    balanceLabel.alpha = 0.6;
    balanceLabel.anchor.set(0.5, 0);
    balanceLabel.x = 41;
    balanceLabel.y = 0;
    const balanceValue = new Text({
      text: this._formatCurrencyAmount(0),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 10,
        lineHeight: 12,
        fontWeight: '700',
        fill: 0xffffff,
        align: 'center',
      },
    });
    balanceValue.anchor.set(0.5, 0);
    balanceValue.x = 41;
    balanceValue.y = 12;
    balanceBlock.addChild(balanceLabel, balanceValue);

    const betBlock = new Container();
    betBlock.x = 564;
    betBlock.y = 344;
    betBlock.eventMode = 'none';
    const betLabel = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 10,
        lineHeight: 12,
        fontWeight: '700',
        fill: 0xffffff,
        align: 'center',
      },
    }), 'BET', { upper: true, suffix: ':' });
    betLabel.alpha = 0.6;
    betLabel.anchor.set(0.5, 0);
    betLabel.x = 24;
    betLabel.y = 0;
    const betValue = new Text({
      text: this._formatCurrencyAmount(0),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 10,
        lineHeight: 12,
        fontWeight: '700',
        fill: 0xffffff,
        align: 'center',
      },
    });
    betValue.anchor.set(0.5, 0);
    betValue.x = 24;
    betValue.y = 12;
    betBlock.addChild(betLabel, betValue);

    this._autoplaySidebarRefresh = (balance, bet) => {
      balanceValue.text = this._formatCurrencyAmount(balance);
      betValue.text = this._formatCurrencyAmount(bet);
    };

    view.addChild(
      betAmountRow,
      chipsRow,
      playBtn,
      stopHeading,
      slider1.container,
      slider2.container,
      slider3.container,
      balanceBlock,
      betBlock,
    );
    this._autoplayModalLayoutParts = {
      betAmountRow,
      chipsRow,
      playBtn,
      stopHeading,
      slider1: slider1.container,
      slider2: slider2.container,
      slider3: slider3.container,
      balanceBlock,
      betBlock,
    };
    return view;
  }

  /**
   * Build a single horizontal slider row matching the Frame 263/264/265 design.
   * Returns the container and a refresh function used by `_refreshAutoplaySettingsView`.
   * @param {{
   *   label: string,
   *   getMax: () => number,
   *   getValue: () => number,
   *   setValue: (v: number) => void,
   * }} args
   */
  _buildAutoplaySlider({ label, getMax, getValue, setValue }) {
    const rowW = 348;
    const rowH = 63;
    const trackY = 25 + 6;
    const trackH = 4;
    const knobR = 8;

    const row = new Container();
    row.eventMode = 'passive';
    row.hitArea = new Rectangle(0, 0, rowW, rowH);

    const labelText = this._bindLocalizedText(new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 12,
        lineHeight: 15,
        fontWeight: '500',
        fill: 0xffffff,
        align: 'left',
      },
    }), label);
    labelText.anchor.set(0, 0);
    labelText.x = 0;
    labelText.y = 0;
    labelText.eventMode = 'none';

    const valueText = new Text({
      text: this._formatCurrencyAmount(0),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 12,
        lineHeight: 15,
        fontWeight: '500',
        fill: 0xffffff,
        align: 'right',
      },
    });
    valueText.anchor.set(1, 0);
    valueText.x = rowW;
    valueText.y = 0;
    valueText.eventMode = 'none';

    const minText = new Text({
      text: this._formatCurrencyAmount(0),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 12,
        lineHeight: 15,
        fontWeight: '500',
        fill: 0xffffff,
        align: 'left',
      },
    });
    minText.alpha = 0.5;
    minText.anchor.set(0, 0);
    minText.x = 0;
    minText.y = 48;
    minText.eventMode = 'none';

    const maxText = new Text({
      text: this._formatCurrencyAmount(0),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 12,
        lineHeight: 15,
        fontWeight: '500',
        fill: 0xffffff,
        align: 'right',
      },
    });
    maxText.alpha = 0.5;
    maxText.anchor.set(1, 0);
    maxText.x = rowW;
    maxText.y = 48;
    maxText.eventMode = 'none';

    const trackBg = new Graphics();
    trackBg.roundRect(0, trackY, rowW, trackH, trackH / 2).fill({
      color: 0xffffff,
      alpha: 0.21,
    });
    trackBg.eventMode = 'none';

    const trackFill = new Graphics();
    trackFill.eventMode = 'none';

    const knob = new Graphics();
    knob.eventMode = 'static';
    knob.cursor = 'pointer';
    knob.hitArea = new Rectangle(-knobR - 4, -knobR - 4, (knobR + 4) * 2, (knobR + 4) * 2);

    const drawKnob = () => {
      const knobGrad = new FillGradient({
        type: 'radial',
        center: { x: 0.5, y: 0.5 },
        innerRadius: 0,
        outerCenter: { x: 0.5, y: 0.5 },
        outerRadius: 0.5,
        textureSpace: 'local',
        colorStops: [
          { offset: 0, color: 0x779133 },
          { offset: 1, color: 0x232b0f },
        ],
      });
      knob.clear();
      knob.circle(0, 0, knobR).fill(knobGrad);
      knob.circle(0, 0, knobR).stroke({
        width: 1,
        color: 0xffffff,
        alpha: 0.6,
      });
    };
    drawKnob();
    knob.y = trackY + trackH / 2;

    /** Re-render fill width / knob position based on current value. */
    const redraw = () => {
      const max = Math.max(0, getMax());
      const value = Math.max(0, Math.min(max, getValue()));
      const t = max > 0 ? value / max : 0;
      const fillW = Math.max(0, t * rowW);
      trackFill.clear();
      trackFill.roundRect(0, trackY, fillW, trackH, trackH / 2).fill({
        color: 0x779133,
        alpha: 1,
      });
      knob.x = t * rowW;
      valueText.text = this._formatCurrencyAmount(value);
      minText.text = this._formatCurrencyAmount(0);
      maxText.text = this._formatCurrencyAmount(max);
    };

    let dragging = false;
    let activePointerId = null;

    const setValueFromLocalX = (localX) => {
      const max = Math.max(0, getMax());
      const t = Math.max(0, Math.min(1, localX / rowW));
      const next = max > 0 ? t * max : 0;
      setValue(next);
      redraw();
    };

    knob.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      dragging = true;
      activePointerId = e.pointerId ?? null;
    });
    /* Click on the track jumps the knob to that position. */
    row.eventMode = 'static';
    row.cursor = 'pointer';
    row.on('pointerdown', (e) => {
      if (!isPrimaryPointerAction(e)) return;
      const local = row.toLocal(e.global);
      if (local.y < trackY - 12 || local.y > trackY + trackH + 12) return;
      e.stopPropagation();
      dragging = true;
      activePointerId = e.pointerId ?? null;
      setValueFromLocalX(local.x);
    });
    row.on('globalpointermove', (e) => {
      if (!dragging) return;
      if (activePointerId != null && e.pointerId != null && e.pointerId !== activePointerId) {
        return;
      }
      const local = row.toLocal(e.global);
      setValueFromLocalX(local.x);
    });
    const endDrag = (e) => {
      if (!dragging) return;
      if (activePointerId != null && e.pointerId != null && e.pointerId !== activePointerId) {
        return;
      }
      dragging = false;
      activePointerId = null;
    };
    row.on('pointerup', endDrag);
    row.on('pointerupoutside', endDrag);
    knob.on('pointerup', endDrag);
    knob.on('pointerupoutside', endDrag);

    row.addChild(labelText, valueText, trackBg, trackFill, knob, minText, maxText);
    redraw();
    return { container: row, refresh: redraw };
  }

  /** Refresh chip FUN values, sidebar BALANCE/BET, and slider min/max/value displays. */
  _refreshAutoplaySettingsView() {
    const bet = Number(this.slotMachine?.ui?.betValue) || 0;
    const balance = Number(this.slotMachine?.ui?.balanceValue) || 0;
    if (this._autoplayChipsRefresh) this._autoplayChipsRefresh(bet);
    if (this._autoplaySidebarRefresh) this._autoplaySidebarRefresh(balance, bet);
    /* Reclamp current threshold values in case max changed (e.g. balance/bet updated). */
    if (Array.isArray(this._autoplaySliderRefreshers)) {
      this._autoplaySliderRefreshers.forEach((fn) => fn?.());
    }
  }

  /**
   * Build the “INFORMATION” view shown in the settings modal.
   * Content area is masked between the modal’s top/bottom rules and is wheel/drag scrollable
   * so the heading and the bottom tab row stay fixed.
   * Jackpot, Bonus Spin Mode and Gamble sections are intentionally omitted.
   * @param {{ panelW: number, panelH: number }} args
   * @returns {Promise<Container>}
   */
  async _buildInfoSettingsView({ panelW }) {
    this._infoPayoutAmountTexts = [];
    this._infoRuleLimitLineRefreshers = [];
    this._infoRuleMaxWinLines = [];
    const view = new Container();
    view.eventMode = 'static';

    /* Visible bounds inside the panel — strictly between lineTop (y:64) and lineBottom (y:523). */
    const VIEWPORT_TOP = 66;
    const VIEWPORT_BOTTOM = 522;
    const VIEWPORT_H = VIEWPORT_BOTTOM - VIEWPORT_TOP;
    /* Layout is built at the desktop baseline; {@link GameApp#_syncSettingsModalLayout}
       scales the content uniformly so it looks identical on every device. */
    const BASE_PANEL_W = SETTINGS_PANEL_DESKTOP.w;
    const PAD_X = 28;
    const INNER_W = BASE_PANEL_W - PAD_X * 2;
    const infoMobile = this._isPortraitMobileViewport();
    const INFO_TEXT_MUL = infoMobile ? SETTINGS_INFO_MOBILE_TEXT_MUL : 1;
    const INFO_ICON_MUL = infoMobile ? SETTINGS_INFO_MOBILE_ICON_MUL : 1;
    const scaledText = (v) => Math.max(1, Math.round(v * INFO_TEXT_MUL));
    const scaledIcon = (v) => Math.max(1, Math.round(v * INFO_ICON_MUL));

    /* Hit area covers viewport so wheel + drag events have a target even over empty space. */
    view.hitArea = new Rectangle(0, VIEWPORT_TOP, panelW, VIEWPORT_H);

    /* Mask is a sibling of the scroll container (Pixi v8 requires a display-tree node). */
    const mask = new Graphics();
    mask.rect(0, VIEWPORT_TOP, panelW, VIEWPORT_H).fill(0xffffff);
    mask.eventMode = 'none';
    view.addChild(mask);

    const scroll = new Container();
    scroll.eventMode = 'passive';
    scroll.mask = mask;
    view.addChild(scroll);

    const content = new Container();
    content.x = PAD_X;
    content.y = 0;
    scroll.addChild(content);

    /* Reusable dummy icon texture; we’ll swap real assets later. */
    let dummyIconTex = Texture.EMPTY;
    try { dummyIconTex = await Assets.load('/assets/settings/info.svg'); } catch { /* noop */ }
    let scatterIconTex = dummyIconTex;
    try { scatterIconTex = await Assets.load('/assets/settings/active_settings_icon.svg'); } catch { /* noop */ }

    /* Symbol artwork shown across WILD/SCATTER/paytable sections. */
    const infoAssetPaths = {
      wild: '/assets/info/cheast.png',
      coin: '/assets/info/coin.png',
      star: '/assets/info/star.png',
      seven: '/assets/info/seven.png',
      watermelon: '/assets/info/watermelon.png',
      grape: '/assets/info/grape.png',
      bell: '/assets/info/bell.png',
      plumeOrange: '/assets/info/plume_orange.png',
      lemonCherry: '/assets/info/lemon_cherry.png',
      paylines: '/assets/info/paylines.png',
    };
    const infoTex = {};
    await Promise.all(
      Object.entries(infoAssetPaths).map(async ([k, p]) => {
        try { infoTex[k] = await Assets.load(p); } catch { infoTex[k] = null; }
      }),
    );

    /** Section heading helper (translation key). */
    const makeHeadingFromKey = (key) =>
      this._bindLocalizedText(
        new Text({
          text: '',
          style: {
            fontFamily: 'Montserrat',
            fontSize: scaledText(16),
            fontWeight: '700',
            letterSpacing: 1,
            fill: INFO_TAB_TEXT_YELLOW,
            align: 'center',
          },
        }),
        key,
      );

    /** Body paragraph helper (translation key). */
    const makeBodyFromKey = (key, w = INNER_W, opts = {}) =>
      this._bindLocalizedText(
        new Text({
          text: '',
          style: {
            fontFamily: 'Montserrat',
            fontSize: scaledText(12),
            lineHeight: scaledText(17),
            fontWeight: '400',
            fill: 0xd9d9d9,
            align: opts.align || 'left',
            wordWrap: true,
            wordWrapWidth: w,
          },
        }),
        key,
        { replace: opts.replace },
      );

    /** Small caption helper. */
    const makeCaption = (txt, color = 0x9aa37a) =>
      new Text({
        text: txt,
        style: {
          fontFamily: 'Montserrat',
          fontSize: scaledText(11),
          fontWeight: '600',
          fill: color,
          align: 'center',
        },
      });

    /* Section divider sprite (re-uses the gold/green frame line used by the modal). */
    let frameLineTex = Texture.EMPTY;
    try { frameLineTex = await Assets.load('/assets/settings/settings_frame.png'); } catch { /* noop */ }
    const makeDivider = (y) => {
      const d = new Sprite(frameLineTex);
      d.width = INNER_W;
      d.height = 2;
      d.x = 0;
      d.y = y;
      d.eventMode = 'none';
      return d;
    };

    /** Dummy icon badge used as a placeholder when an info asset fails to load. */
    const makeIconBadge = (label, w, h, accent = 0xe7c463) => {
      const c = new Container();
      const bg = new Graphics();
      bg.roundRect(0, 0, w, h, 14)
        .fill({ color: 0x14140a, alpha: 1 })
        .stroke({ width: 2, color: accent, alpha: 0.9 });
      bg.eventMode = 'none';
      const t = new Text({
        text: label,
        style: {
          fontFamily: 'Montserrat',
          fontSize: Math.max(11, Math.round(Math.min(w, h) * 0.18)),
          fontWeight: '800',
          fill: accent,
          letterSpacing: 1,
          align: 'center',
        },
      });
      t.anchor.set(0.5, 0.5);
      t.x = w / 2;
      t.y = h / 2;
      c.addChild(bg, t);
      return c;
    };

    /**
     * Render a real symbol image scaled to fit a (w × h) box while preserving aspect ratio.
     * Falls back to the dummy badge if the asset failed to load.
     * @param {keyof typeof infoAssetPaths} texKey
     * @param {number} w
     * @param {number} h
     * @param {{ fallbackLabel?: string, fallbackAccent?: number }} [opts]
     */
    const makeSymbolSprite = (texKey, w, h, opts = {}) => {
      const tex = infoTex[texKey];
      if (tex && tex.width > 0 && tex.height > 0) {
        const c = new Container();
        const s = new Sprite(tex);
        const k = Math.min(w / tex.width, h / tex.height);
        s.width = tex.width * k;
        s.height = tex.height * k;
        s.x = (w - s.width) / 2;
        s.y = (h - s.height) / 2;
        s.eventMode = 'none';
        c.addChild(s);
        return c;
      }
      return makeIconBadge(opts.fallbackLabel ?? String(texKey).toUpperCase(), w, h, opts.fallbackAccent);
    };

    const INFO_PAYOUT_AMOUNT_FILL = 0xffffff;
    /** Payout row: multiplier (x5/x4/…) in #FFEA00 + white amount for current bet. */
    const makePayoutLine = ({ count, odd }) => {
      const baseStyle = {
        fontFamily: 'Montserrat',
        fontSize: scaledText(12),
        fontWeight: '700',
        letterSpacing: 0.4,
      };
      const row = new Container();
      const multText = new Text({
        text: `x${count} - `,
        style: { ...baseStyle, fill: INFO_TAB_TEXT_YELLOW },
      });
      const amountText = new Text({
        text: this._formatCurrencyAmount(0),
        style: { ...baseStyle, fill: INFO_PAYOUT_AMOUNT_FILL },
      });
      amountText.x = multText.width;
      row.addChild(multText, amountText);
      this._infoPayoutAmountTexts.push({
        multText,
        amountText,
        count: Number(count),
        odd: Number(odd),
      });
      return row;
    };

    /** RULES limit line — same yellow label + white amount styling as payout rows. */
    const makeRuleLimitLine = (key, replaceFn) => {
      const baseStyle = {
        fontFamily: 'Montserrat',
        fontSize: scaledText(12),
        fontWeight: '700',
        letterSpacing: 0.4,
      };
      const row = new Container();
      const labelText = new Text({ text: '', style: { ...baseStyle, fill: INFO_TAB_TEXT_YELLOW } });
      const valueText = new Text({ text: '', style: { ...baseStyle, fill: INFO_PAYOUT_AMOUNT_FILL } });
      row.addChild(labelText, valueText);

      const applyLine = () => {
        const rep = typeof replaceFn === 'function' ? replaceFn() : replaceFn;
        const full = this._t(key, rep);
        const colonIdx = full.indexOf(':');
        if (colonIdx >= 0) {
          labelText.text = `${full.slice(0, colonIdx + 1)} `;
          valueText.text = full.slice(colonIdx + 1).trim();
        } else {
          labelText.text = full;
          valueText.text = '';
        }
        valueText.x = labelText.width;
      };

      applyLine();
      this._infoRuleLimitLineRefreshers.push(applyLine);
      return row;
    };

    let cursorY = 4;

    /* ── Game logo ───────────────────────────────────────────────── */
    let logoTex = Texture.EMPTY;
    try { logoTex = await Assets.load('/assets/Logo0.png'); } catch { /* noop */ }
    const logoTargetH = scaledIcon(96);
    const logoSprite = new Sprite(logoTex);
    if (logoTex && logoTex.width > 0 && logoTex.height > 0) {
      const aspect = logoTex.width / logoTex.height;
      logoSprite.height = logoTargetH;
      logoSprite.width = logoTargetH * aspect;
    } else {
      logoSprite.width = 160;
      logoSprite.height = logoTargetH;
    }
    logoSprite.x = (INNER_W - logoSprite.width) / 2;
    logoSprite.y = cursorY;
    logoSprite.eventMode = 'none';
    content.addChild(logoSprite);
    cursorY += logoTargetH + 6;

    /* ── "Introduction" sub-heading ─────────────────────────────── */
    const introHeading = this._bindLocalizedText(
      new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: scaledText(14),
          fontWeight: '700',
          fill: 0xffffff,
          align: 'center',
        },
      }),
      'INFO_INTRO_TITLE',
    );
    introHeading.anchor.set(0.5, 0);
    introHeading.x = INNER_W / 2;
    introHeading.y = cursorY;
    content.addChild(introHeading);
    cursorY += scaledText(22);

    /* ── Intro paragraph ─────────────────────────────────────────── */
    const introBodyWrapW = infoMobile ? INNER_W : Math.min(INNER_W, 410);
    const introBody = makeBodyFromKey('INFO_INTRO_BODY', introBodyWrapW, { align: 'center' });
    introBody.anchor.set(0.5, 0);
    introBody.x = INNER_W / 2;
    introBody.y = cursorY;
    content.addChild(introBody);
    cursorY += introBody.height + scaledText(14);

    content.addChild(makeDivider(cursorY));
    cursorY += scaledText(18);

    /* ── WILD section ────────────────────────────────────────────── */
    const wildLeftW = INNER_W * 0.46;
    const wildIconW = scaledIcon(130);
    const wildIconH = scaledIcon(100);
    const wildSectionH = wildIconH + scaledText(36);
    const wildSection = new Container();
    wildSection.y = cursorY;

    const wildHeading = this._bindLocalizedText(
      new Text({
        text: '',
        style: {
          fontFamily: 'Montserrat',
          fontSize: scaledText(18),
          fontWeight: '800',
          fill: INFO_TAB_TEXT_YELLOW,
          letterSpacing: 1.5,
        },
      }),
      'INFO_WILD_HEADING',
    );
    wildHeading.anchor.set(0.5, 0);
    wildHeading.x = wildLeftW / 2;
    wildHeading.y = 0;
    wildSection.addChild(wildHeading);

    const wildBadge = makeSymbolSprite('wild', wildIconW, wildIconH, {
      fallbackLabel: 'WILD',
      fallbackAccent: 0xe7c463,
    });
    wildBadge.x = (wildLeftW - wildIconW) / 2;
    wildBadge.y = scaledText(30);
    wildSection.addChild(wildBadge);

    /* Right-side description; built as multiple Text + Sprite parts so the inline coin/scatter icons sit on the same baseline as the words. */
    const wildTextStyle = {
      fontFamily: 'Montserrat',
      fontSize: scaledText(13),
      lineHeight: scaledText(20),
      fontWeight: '400',
      fill: 0xffffff,
    };
    const wildRightX = wildLeftW + (infoMobile ? 4 : scaledText(14));
    const wildRightW = INNER_W - wildRightX - 4;
    const wildText = new Container();
    wildText.x = wildRightX;
    const wildLine1 = this._bindLocalizedText(new Text({ text: '', style: wildTextStyle }), 'INFO_WILD_LINE1');
    const wildLine2 = this._bindLocalizedText(new Text({ text: '', style: wildTextStyle }), 'INFO_WILD_LINE2');
    const wildLine3 = this._bindLocalizedText(new Text({ text: '', style: wildTextStyle }), 'INFO_WILD_LINE3');
    const wildLine4 = this._bindLocalizedText(new Text({ text: '', style: wildTextStyle }), 'INFO_WILD_LINE4');
    wildLine1.y = 0;
    const wildLineStep = scaledText(20);
    wildLine2.y = wildLineStep;
    wildLine3.y = wildLineStep * 2;
    wildLine4.y = wildLineStep * 3;
    /* Inline coin/star icons sit on the same baseline as the words they replace. */
    const wildCoinIcon = new Sprite(infoTex.coin ?? dummyIconTex);
    const wildCoinBoxW = scaledIcon(20);
    const wildCoinBoxH = scaledIcon(20);
    if (infoTex.coin && infoTex.coin.width > 0) {
      const kc = Math.min(wildCoinBoxW / infoTex.coin.width, wildCoinBoxH / infoTex.coin.height);
      wildCoinIcon.width = infoTex.coin.width * kc;
      wildCoinIcon.height = infoTex.coin.height * kc;
    } else {
      wildCoinIcon.width = scaledIcon(18);
      wildCoinIcon.height = scaledIcon(18);
      wildCoinIcon.tint = 0xf4c850;
    }
    wildCoinIcon.x = wildLine3.width;
    wildCoinIcon.y = wildLine3.y + scaledText(1);
    wildCoinIcon.eventMode = 'none';
    const wildScatterIcon = new Sprite(infoTex.star ?? scatterIconTex);
    const wildStarBoxW = scaledIcon(22);
    const wildStarBoxH = scaledIcon(22);
    if (infoTex.star && infoTex.star.width > 0) {
      const ks = Math.min(wildStarBoxW / infoTex.star.width, wildStarBoxH / infoTex.star.height);
      wildScatterIcon.width = infoTex.star.width * ks;
      wildScatterIcon.height = infoTex.star.height * ks;
    } else {
      wildScatterIcon.width = scaledIcon(22);
      wildScatterIcon.height = scaledIcon(18);
      wildScatterIcon.tint = 0xffd166;
    }
    wildScatterIcon.x = wildLine4.width;
    wildScatterIcon.y = wildLine4.y + scaledText(1);
    wildScatterIcon.eventMode = 'none';
    wildText.addChild(wildLine1, wildLine2, wildLine3, wildCoinIcon, wildLine4, wildScatterIcon);
    /* Vertically centre the description block against the icon area. */
    const wildBlockH = scaledText(80);
    wildText.y = (wildSectionH - wildBlockH) / 2 + scaledText(2);
    /* If the right column is narrower than expected, tighten the text wrapping by truncating lines. */
    if (wildRightW < 200) {
      wildLine1.style = { ...wildTextStyle, wordWrap: true, wordWrapWidth: wildRightW };
      wildLine2.style = { ...wildTextStyle, wordWrap: true, wordWrapWidth: wildRightW };
      wildLine3.style = { ...wildTextStyle, wordWrap: true, wordWrapWidth: wildRightW };
    }
    wildSection.addChild(wildText);
    this._infoWildRelayout = () => {
      wildCoinIcon.x = wildLine3.width;
      wildScatterIcon.x = wildLine4.width;
    };

    content.addChild(wildSection);
    cursorY += wildSectionH + scaledText(18);

    /* ── Two SCATTER columns ─────────────────────────────────────── */
    const scatterColW = INNER_W / 2;
    const scatterIconW = scaledIcon(120);
    const scatterIconH = scaledIcon(110);
    const scatterRow = new Container();
    scatterRow.y = cursorY;

    const buildScatterCol = ({ x, iconTexKey, fallbackLabel, fallbackAccent, footerLines }) => {
      const col = new Container();
      col.x = x;
      const head = this._bindLocalizedText(
        new Text({
          text: '',
          style: {
            fontFamily: 'Montserrat',
            fontSize: scaledText(16),
            fontWeight: '800',
            fill: INFO_TAB_TEXT_YELLOW,
            letterSpacing: 1.5,
          },
        }),
        'INFO_SCATTER_HEADING',
      );
      head.anchor.set(0.5, 0);
      head.x = scatterColW / 2;
      head.y = 0;
      col.addChild(head);

      const badge = makeSymbolSprite(iconTexKey, scatterIconW, scatterIconH, {
        fallbackLabel,
        fallbackAccent,
      });
      badge.x = (scatterColW - scatterIconW) / 2;
      badge.y = scaledText(26);
      col.addChild(badge);

      let lineY = scaledText(26) + scatterIconH + scaledText(12);
      footerLines.forEach((ln) => {
        const style = {
          fontFamily: 'Montserrat',
          fontSize: scaledText(12),
          fontWeight: ln.muted ? '500' : '700',
          fill: ln.color,
          align: 'center',
          wordWrap: true,
          wordWrapWidth: scatterColW - 16,
          lineHeight: scaledText(17),
        };
        let t;
        if (ln.count != null && ln.odd != null) {
          t = makePayoutLine({ count: ln.count, odd: ln.odd });
          t.pivot.set(t.width / 2, 0);
        } else {
          t = this._bindLocalizedText(new Text({ text: '', style }), ln.key);
          t.anchor.set(0.5, 0);
        }
        t.x = scatterColW / 2;
        t.y = lineY;
        col.addChild(t);
        lineY += t.height + scaledText(2);
      });
      col.__bottomY = lineY;
      return col;
    };

    const coinPayRows = this._paytableCountOdds(this._paytableEntry(10));
    const starPayRows = this._paytableCountOdds(this._paytableEntry(11));
    const scLeft = buildScatterCol({
      x: 0,
      iconTexKey: 'coin',
      fallbackLabel: 'COIN',
      fallbackAccent: 0xf4c850,
      footerLines: coinPayRows.map((r) => ({ count: r.count, odd: r.odd })),
    });
    const scRight = buildScatterCol({
      x: scatterColW,
      iconTexKey: 'star',
      fallbackLabel: 'STAR',
      fallbackAccent: 0xffd166,
      footerLines: [
        { key: 'INFO_SCATTER_STAR_INTRO', color: 0xd9d9d9, muted: true },
        ...starPayRows.map((r) => ({ count: r.count, odd: r.odd })),
      ],
    });
    scatterRow.addChild(scLeft, scRight);
    content.addChild(scatterRow);
    const scatterRowH = Math.max(scLeft.__bottomY ?? 0, scRight.__bottomY ?? 0);
    cursorY += scatterRowH + scaledText(18);

    content.addChild(makeDivider(cursorY));
    cursorY += scaledText(18);

    /* ── Symbol paytable (3 columns × N rows) ────────────────────── */
    /* Paired entries (PLUM+ORANGE, LEMON+CHERRY) ship as one image showing both symbols.
     * Odds come from StartGameAction paytable; amounts scale with the current bet. */
    const lineRowsFor = (symbolId) =>
      this._paytableCountOdds(this._paytableEntry(symbolId)).map((r) => ({
        count: r.count,
        odd: r.odd,
      }));
    const paytable = [
      {
        texKey: 'seven',
        fallbackLabel: '7',
        fallbackAccent: 0xffd447,
        lines: lineRowsFor(1),
      },
      {
        texKey: 'watermelon',
        fallbackLabel: 'WMELON',
        fallbackAccent: 0x4dd17a,
        lines: lineRowsFor(3),
      },
      {
        texKey: 'grape',
        fallbackLabel: 'GRAPE',
        fallbackAccent: 0x8e6bff,
        lines: lineRowsFor(2),
      },
      {
        texKey: 'bell',
        fallbackLabel: 'BELL',
        fallbackAccent: 0xffc83d,
        lines: lineRowsFor(4),
      },
      {
        texKey: 'plumeOrange',
        pair: true,
        fallbackLabel: 'PLUM+ORANGE',
        fallbackAccent: 0xffa64d,
        lines: lineRowsFor(6),
      },
      {
        texKey: 'lemonCherry',
        pair: true,
        fallbackLabel: 'LEMON+CHERRY',
        fallbackAccent: 0xff5b6e,
        lines: lineRowsFor(5),
      },
    ].filter((s) => s.lines.length > 0);
    const ptCols = 3;
    const ptColW = INNER_W / ptCols;
    const ptIconW = scaledIcon(80);
    const ptIconH = scaledIcon(80);
    /* Paired-symbol images are wider; cap by the column width so they never overflow. */
    const ptPairIconW = Math.min(ptColW - scaledText(8), scaledIcon(116));
    const ptRowMaxLines = Math.max(1, ...paytable.map((s) => s.lines.length));
    const ptRowH = ptIconH + scaledText(12) + ptRowMaxLines * scaledText(17) + scaledText(10);

    paytable.forEach((sym, i) => {
      const r = Math.floor(i / ptCols);
      const cIdx = i % ptCols;
      const cell = new Container();
      cell.x = cIdx * ptColW;
      cell.y = cursorY + r * ptRowH;

      const boxW = sym.pair ? ptPairIconW : ptIconW;
      const boxH = ptIconH;
      const iconsLeftX = (ptColW - boxW) / 2;
      const sprite = makeSymbolSprite(sym.texKey, boxW, boxH, {
        fallbackLabel: sym.fallbackLabel,
        fallbackAccent: sym.fallbackAccent,
      });
      sprite.x = iconsLeftX;
      sprite.y = 0;
      cell.addChild(sprite);

      const textStartX = iconsLeftX + scaledText(4);
      sym.lines.forEach((ln, li) => {
        const row = makePayoutLine({ count: ln.count, odd: ln.odd });
        row.x = textStartX;
        row.y = ptIconH + scaledText(8) + li * scaledText(17);
        cell.addChild(row);
      });
      content.addChild(cell);
    });
    cursorY += Math.ceil(Math.max(paytable.length, 1) / ptCols) * ptRowH + scaledText(8);

    this._refreshInfoPaytableAmounts();

    cursorY += scaledText(8);

    /* ── PAYLINES AND RULES ──────────────────────────────────────── */
    const paylinesHeading = makeHeadingFromKey('INFO_HEADING_PAYLINES_AND_RULES');
    paylinesHeading.anchor.set(0.5, 0);
    paylinesHeading.x = INNER_W / 2;
    paylinesHeading.y = cursorY;
    content.addChild(paylinesHeading);
    cursorY += scaledText(30);

    /* Payline diagram: single static image (replaces procedurally drawn grids). */
    const paylinesTex = infoTex.paylines;
    let paylinesImgH = 0;
    if (paylinesTex && paylinesTex.width > 0 && paylinesTex.height > 0) {
      const paylinesSprite = new Sprite(paylinesTex);
      const paylinesTargetW = INNER_W * (infoMobile ? 0.84 : 0.78);
      paylinesSprite.width = paylinesTargetW;
      paylinesSprite.height = (paylinesTargetW / paylinesTex.width) * paylinesTex.height;
      paylinesSprite.x = (INNER_W - paylinesTargetW) / 2;
      paylinesSprite.y = cursorY;
      paylinesSprite.eventMode = 'none';
      content.addChild(paylinesSprite);
      paylinesImgH = paylinesSprite.height;
    }
    cursorY += paylinesImgH + scaledText(18);

    const rulesPara1 = makeBodyFromKey('INFO_RULES_PARA1', INNER_W);
    rulesPara1.x = 0;
    rulesPara1.y = cursorY;
    content.addChild(rulesPara1);
    cursorY += rulesPara1.height + scaledText(12);

    const rulesPara2 = makeBodyFromKey('INFO_RULES_PARA2', INNER_W);
    rulesPara2.x = 0;
    rulesPara2.y = cursorY;
    content.addChild(rulesPara2);
    cursorY += rulesPara2.height + scaledText(12);

    const rulesPara3 = makeBodyFromKey('INFO_RULES_PARA3', INNER_W);
    rulesPara3.x = 0;
    rulesPara3.y = cursorY;
    content.addChild(rulesPara3);
    cursorY += rulesPara3.height + scaledText(18);

    content.addChild(makeDivider(cursorY));
    cursorY += scaledText(18);

    /* ── HOW TO BET ──────────────────────────────────────────────── */
    const howToBetHeading = makeHeadingFromKey('INFO_HEADING_HOW_TO_BET');
    howToBetHeading.anchor.set(0.5, 0);
    howToBetHeading.x = INNER_W / 2;
    howToBetHeading.y = cursorY;
    content.addChild(howToBetHeading);
    cursorY += scaledText(26);

    const bullet1 = makeBodyFromKey('INFO_HOW_TO_BET_BULLET1', INNER_W);
    bullet1.x = 0;
    bullet1.y = cursorY;
    content.addChild(bullet1);
    cursorY += bullet1.height + scaledText(10);

    const bullet2 = makeBodyFromKey('INFO_HOW_TO_BET_BULLET2', INNER_W);
    bullet2.x = 0;
    bullet2.y = cursorY;
    content.addChild(bullet2);
    cursorY += bullet2.height + scaledText(18);

    /* ── GAME CONTROLS ───────────────────────────────────────────── */
    const gameControlsHeading = makeHeadingFromKey('INFO_HEADING_GAME_CONTROLS');
    gameControlsHeading.anchor.set(0.5, 0);
    gameControlsHeading.x = INNER_W / 2;
    gameControlsHeading.y = cursorY;
    content.addChild(gameControlsHeading);
    cursorY += scaledText(26);

    const fieldsIntro = makeBodyFromKey('INFO_FIELDS_INTRO', INNER_W);
    fieldsIntro.x = 0;
    fieldsIntro.y = cursorY;
    content.addChild(fieldsIntro);
    cursorY += fieldsIntro.height + scaledText(10);

    const fieldBalance = makeBodyFromKey('INFO_FIELD_BALANCE', INNER_W);
    fieldBalance.x = 0;
    fieldBalance.y = cursorY;
    content.addChild(fieldBalance);
    cursorY += fieldBalance.height + scaledText(10);

    const fieldWin = makeBodyFromKey('INFO_FIELD_WIN', INNER_W);
    fieldWin.x = 0;
    fieldWin.y = cursorY;
    content.addChild(fieldWin);
    cursorY += fieldWin.height + scaledText(10);

    const fieldWinLine = makeBodyFromKey('INFO_FIELD_WINLINE', INNER_W);
    fieldWinLine.x = 0;
    fieldWinLine.y = cursorY;
    content.addChild(fieldWinLine);
    cursorY += fieldWinLine.height + scaledText(14);

    const buttonsIntro = makeBodyFromKey('INFO_BUTTONS_INTRO', INNER_W);
    buttonsIntro.x = 0;
    buttonsIntro.y = cursorY;
    content.addChild(buttonsIntro);
    cursorY += buttonsIntro.height + scaledText(14);

    /* ── Buttons table (BUTTON | NAME | DESCRIPTION) ─────────────── */
    /* Preload every real button asset used by this table; loaded in parallel. */
    const buttonAssetPaths = {
      bet: '/assets/bet.png',
      stop: '/assets/stop.svg',
      start: '/assets/start.png',
      collectWin: '/assets/collect_win.svg',
      gamble: '/assets/gamble.svg',
      betOptions: '/assets/bet.png',
      quickBetNormal: '/assets/quick_bet.svg',
      quickBetSelected: '/assets/selected_quick_bet.svg',
      autoplay: '/assets/autoplay.png',
      autoplaySettings: '/assets/settings/autoplay.svg',
      settings: '/assets/settings/settings.svg',
      sound: '/assets/sounds_on.svg',
      fullscreen: '/assets/full_screen.svg',
      menu: '/assets/menu.svg',
      close: '/assets/close.svg',
      home: '/assets/home.svg',
      betSpeed: '/assets/bet_speed.png',
      betSpeedActiveArrow: '/assets/bet_speed_active_arrow.png',
      betSpeedInactiveArrow: '/assets/bet_speed_inactive_arrow.png',
    };
    const buttonTex = {};
    await Promise.all(
      Object.entries(buttonAssetPaths).map(async ([k, p]) => {
        try { buttonTex[k] = await Assets.load(p); } catch { buttonTex[k] = dummyIconTex; }
      }),
    );

    /** Build a sprite icon at a target box size, preserving aspect ratio. */
    const makeIconSprite = (texKey, boxW, boxH) => {
      const normTex = buttonTex.quickBetNormal ?? dummyIconTex;
      const selTex = buttonTex.quickBetSelected ?? normTex;
      const sampleBet =
        Number(this.slotMachine?.ui?.betValue) ||
        Number(this.slotMachine?.ui?._quickBets?.[0]) ||
        gameConfig.minBet ||
        1;
      const makeQuickBetChipIcon = ({ bottomText, selected }) => {
        const chip = createQuickBetChipPixi({
          betValue: sampleBet,
          selected,
          textureNormal: normTex,
          textureSelected: selTex,
          cellW: boxW,
          cellH: boxH,
          bottomText,
          currencyLabel: this._currencyLabel(),
          currencyPrecision: this._currencyPrecision,
          fontFamily: '"Montserrat", "Arial", sans-serif',
        });
        chip.eventMode = 'none';
        chip.__width = boxW;
        chip.__height = boxH;
        return chip;
      };
      if (texKey === 'quickBetBet' && normTex?.width > 0) {
        return makeQuickBetChipIcon({
          bottomText: this._tu('BET'),
          selected: false,
        });
      }
      if (texKey === 'quickBetStopAll' && normTex?.width > 0) {
        return makeQuickBetChipIcon({
          bottomText: this._tu('STOP_ALL'),
          selected: false,
        });
      }

      const bgTex = buttonTex.betSpeed ?? dummyIconTex;
      const activeArrowTex = buttonTex.betSpeedActiveArrow;
      const inactiveArrowTex = buttonTex.betSpeedInactiveArrow;
      if (
        texKey === 'betSpeed'
        && bgTex?.width > 0
        && activeArrowTex?.width > 0
        && inactiveArrowTex?.width > 0
      ) {
        const wrap = new Container();
        wrap.eventMode = 'none';

        const bg = new Sprite(bgTex);
        const bgScale = Math.min(boxW / bgTex.width, boxH / bgTex.height);
        bg.width = bgTex.width * bgScale;
        bg.height = bgTex.height * bgScale;
        bg.x = (boxW - bg.width) / 2;
        bg.y = (boxH - bg.height) / 2;
        wrap.addChild(bg);

        const arrowW = Math.max(4, bg.width * (8 / 82));
        const gapBetweenArrows = Math.max(2, bg.width * (4 / 82));
        const totalArrowRowW = 3 * arrowW + 2 * gapBetweenArrows;
        const arrowRow = new Container();
        arrowRow.x = boxW / 2;
        arrowRow.y = bg.y + bg.height * 0.48;
        for (let i = 0; i < 3; i++) {
          const arrow = new Sprite(activeArrowTex);
          arrow.anchor.set(0.5);
          arrow.width = arrowW;
          arrow.height = (activeArrowTex.height / activeArrowTex.width) * arrowW;
          arrow.x = -totalArrowRowW / 2 + arrowW / 2 + i * (arrowW + gapBetweenArrows);
          arrowRow.addChild(arrow);
        }
        wrap.addChild(arrowRow);

        wrap.__width = boxW;
        wrap.__height = boxH;
        return wrap;
      }

      const tex = buttonTex[texKey] ?? dummyIconTex;
      const s = new Sprite(tex);
      if (tex && tex.width > 0 && tex.height > 0) {
        const k = Math.min(boxW / tex.width, boxH / tex.height);
        s.width = tex.width * k;
        s.height = tex.height * k;
      } else {
        s.width = boxW;
        s.height = boxH;
      }
      s.eventMode = 'none';
      s.__width = s.width;
      s.__height = s.height;
      return s;
    };

    /* Slightly narrower than full content width on phone; centred in the scroll column. */
    const TABLE_W = infoMobile ? Math.round(INNER_W * 0.93) : INNER_W;
    const tableX = (INNER_W - TABLE_W) / 2;

    /* Column widths: Figma proportions (81 / 81 / 408 of 568), then nudge NAME from DESC if the name column is too narrow to wrap labels. */
    let colBtn = Math.round((81 / 568) * TABLE_W);
    let colName = Math.round((81 / 568) * TABLE_W);
    let colDesc = TABLE_W - colBtn - colName;
    const nameMin = Math.min(56, Math.max(28, Math.floor(TABLE_W * 0.16)));
    if (colName < nameMin && colDesc > nameMin + Math.floor(TABLE_W * 0.32)) {
      const delta = nameMin - colName;
      colName = nameMin;
      colDesc -= delta;
    }
    const tableCols = { btn: colBtn, name: colName, desc: colDesc };
    const colXBtn = 0;
    const colXName = tableCols.btn;
    const colXDesc = tableCols.btn + tableCols.name;

    const tableHeaderH = scaledText(infoMobile ? 32 : 36);
    const tableBorderColor = 0xffffff;
    const tableBorderAlpha = 1;
    const tableRadius = 10;

    /* Build the table inside its own container so we can clip and outline it cleanly. */
    const tableRoot = new Container();
    tableRoot.x = tableX;
    tableRoot.y = cursorY;
    tableRoot.eventMode = 'passive';
    const tableContent = new Container();
    tableContent.eventMode = 'passive';
    tableRoot.addChild(tableContent);
    content.addChild(tableRoot);

    let tableY = 0;

    /** Header row. */
    const headerEl = new Container();
    headerEl.y = tableY;
    const headerBg = new Graphics();
    headerBg.rect(0, 0, TABLE_W, tableHeaderH).fill({ color: 0x000000, alpha: 0 });
    headerBg.eventMode = 'none';
    headerEl.addChild(headerBg);
    const headerStyle = {
      fontFamily: 'Montserrat',
      fontSize: scaledText(infoMobile ? 10 : 12),
      fontWeight: '700',
      fill: 0xffffff,
      letterSpacing: 0.4,
    };
    const hdrBtn = this._bindLocalizedText(new Text({ text: '', style: headerStyle }), 'INFO_TABLE_HDR_BUTTON');
    hdrBtn.anchor.set(0.5, 0.5);
    hdrBtn.x = colXBtn + tableCols.btn / 2;
    hdrBtn.y = tableHeaderH / 2;
    const hdrName = this._bindLocalizedText(new Text({ text: '', style: headerStyle }), 'INFO_TABLE_HDR_NAME');
    hdrName.anchor.set(0.5, 0.5);
    hdrName.x = colXName + tableCols.name / 2;
    hdrName.y = tableHeaderH / 2;
    const hdrDesc = this._bindLocalizedText(new Text({ text: '', style: headerStyle }), 'INFO_TABLE_HDR_DESC');
    hdrDesc.anchor.set(0.5, 0.5);
    hdrDesc.x = colXDesc + tableCols.desc / 2;
    hdrDesc.y = tableHeaderH / 2;
    headerEl.addChild(hdrBtn, hdrName, hdrDesc);
    tableContent.addChild(headerEl);
    tableY += tableHeaderH;

    /** Data rows.
     * `icons` is a list of texture keys; multiple icons stack vertically inside the BUTTON cell.
     * `iconBox` is the target box for each icon ([w, h]).
     * `minH` mirrors the row heights from the Figma spec (the row will grow if text needs more).
     */
    const buttonRowsBase = [
      { icons: ['quickBetBet'], iconBox: [56, 53], nameKey: 'INFO_CTRL_BET_NAME', bodyKey: 'INFO_CTRL_BET_BODY', minH: 66 },
      { icons: ['quickBetStopAll'], iconBox: [56, 53], nameKey: 'INFO_CTRL_STOP_ALL_NAME', bodyKey: 'INFO_CTRL_STOP_ALL_BODY', minH: 86 },
      { icons: ['quickBetStopAll', 'collectWin'], iconBox: [56, 53], nameKey: 'INFO_CTRL_COLLECT_NAME', bodyKey: 'INFO_CTRL_COLLECT_BODY', minH: 173 },
      { icons: ['start'], iconBox: [56, 56], nameKey: 'INFO_CTRL_START_NAME', bodyKey: 'INFO_CTRL_START_BODY', minH: 86 },
      { icons: ['collectWin'], iconBox: [56, 56], nameKey: 'INFO_CTRL_COLLECT_WIN_NAME', bodyKey: 'INFO_CTRL_COLLECT_WIN_BODY', minH: 86 },
      { icons: ['gamble'], iconBox: [56, 56], nameKey: 'INFO_CTRL_GAMBLE_NAME', bodyKey: 'INFO_CTRL_GAMBLE_BODY', minH: 86 },
      { icons: ['betOptions'], iconBox: [54, 54], nameKey: 'INFO_CTRL_BET_OPTIONS_NAME', bodyKey: 'INFO_CTRL_BET_OPTIONS_BODY', minH: 86 },
      { icons: ['autoplaySettings'], iconBox: [54, 54], nameKey: 'INFO_CTRL_AUTOPLAY_SETTINGS_NAME', bodyKey: 'INFO_CTRL_AUTOPLAY_SETTINGS_BODY', minH: 86 },
      { icons: ['settings'], iconBox: [54, 54], nameKey: 'INFO_CTRL_GAME_SETTINGS_NAME', bodyKey: 'INFO_CTRL_GAME_SETTINGS_BODY', minH: 86, iconAlpha: 0.6 },
      { icons: ['bet'], iconBox: [56, 50], nameKey: 'INFO_CTRL_BET_VALUE_NAME', bodyKey: 'INFO_CTRL_BET_VALUE_BODY', minH: 86 },
      { icons: ['autoplay'], iconBox: [54, 54], nameKey: 'INFO_CTRL_AUTOPLAY_MODE_NAME', bodyKey: 'INFO_CTRL_AUTOPLAY_MODE_BODY', minH: 86 },
      { icons: ['stop'], iconBox: [54, 54], nameKey: 'INFO_CTRL_STOP_AUTO_NAME', bodyKey: 'INFO_CTRL_STOP_AUTO_BODY', minH: 86 },
      { icons: ['betSpeed'], iconBox: [56, 24], nameKey: 'INFO_CTRL_FAST_PLAY_NAME', bodyKey: 'INFO_CTRL_FAST_PLAY_BODY', minH: 76 },
      { icons: ['sound'], iconBox: [34, 34], nameKey: 'INFO_CTRL_SOUND_CONTROL_NAME', bodyKey: 'INFO_CTRL_SOUND_CONTROL_BODY', minH: 76 },
      { icons: ['fullscreen'], iconBox: [34, 34], nameKey: 'INFO_CTRL_SCREEN_SIZE_NAME', bodyKey: 'INFO_CTRL_SCREEN_SIZE_BODY', minH: 76 },
      { icons: ['menu'], iconBox: [34, 34], nameKey: 'INFO_CTRL_MENU_NAME', bodyKey: 'INFO_CTRL_MENU_BODY', minH: 76 },
      { icons: ['close'], iconBox: [34, 34], nameKey: 'INFO_CTRL_CLOSE_NAME', bodyKey: 'INFO_CTRL_CLOSE_BODY', minH: 76 },
      { icons: ['home'], iconBox: [34, 34], nameKey: 'INFO_CTRL_EXIT_NAME', bodyKey: 'INFO_CTRL_EXIT_BODY', minH: 125 },
    ];
    const buttonRows = buttonRowsBase.map((row) => ({
      ...row,
      iconBox: [scaledIcon(row.iconBox[0]), scaledIcon(row.iconBox[1])],
      minH: scaledText(row.minH ?? 70),
    }));

    /* Row content insets — must never exceed the column width or wordWrapWidth becomes larger than the cell and text draws outside the grid. */
    const ROW_PAD_X = scaledText(10);
    const ROW_PAD_Y = scaledText(10);
    const hPadForCol = (colW) =>
      Math.min(ROW_PAD_X, Math.max(2, Math.floor(colW * 0.12)));
    const padDesc = hPadForCol(tableCols.desc);
    const padName = hPadForCol(tableCols.name);
    const descInterior = Math.max(1, tableCols.desc - padDesc * 2);
    const nameInterior = Math.max(1, tableCols.name - padName * 2);
    /** Shrink NAME labels when the column is tight; mobile uses a smaller base so copy fits the column. */
    const nameFontRef = infoMobile ? 8 : 10;
    const nameBaseFontSize = nameInterior < 40
      ? Math.max(7, Math.floor((nameInterior / 40) * nameFontRef))
      : nameFontRef;
    const nameFontSize = scaledText(nameBaseFontSize);
    const nameLineHeight = Math.round(nameFontSize * 1.3);
    /** Per-row final height; used to draw the internal grid lines below. */
    const rowHeights = [];
    /** @type {Array<{ rowEl: import('pixi.js').Container, rowBg: import('pixi.js').Graphics, nameText: import('pixi.js').Text, desc: import('pixi.js').Text, iconSprites: import('pixi.js').Sprite[], minH?: number, nameKey: string, bodyKey: string }>} */
    const layoutRows = [];
    const ICON_STACK_GAP = scaledText(8);

    buttonRows.forEach((row) => {
      const nameText = new Text({
        text: this._t(row.nameKey),
        style: {
          fontFamily: 'Montserrat',
          fontSize: nameFontSize,
          lineHeight: nameLineHeight,
          fontWeight: '500',
          fill: 0xffffff,
          align: 'center',
          wordWrap: true,
          wordWrapWidth: nameInterior,
        },
      });

      const desc = new Text({
        text: this._t(row.bodyKey),
        style: {
          fontFamily: 'Montserrat',
          fontSize: scaledText(10),
          lineHeight: scaledText(12),
          fontWeight: '500',
          fill: 0xffffff,
          align: 'left',
          wordWrap: true,
          wordWrapWidth: descInterior,
        },
      });

      const iconSprites = row.icons.map((k) => makeIconSprite(k, row.iconBox[0], row.iconBox[1]));
      if (typeof row.iconAlpha === 'number') {
        iconSprites.forEach((s) => { s.alpha = row.iconAlpha; });
      }
      const iconStackH = iconSprites.reduce(
        (s, sp, idx) => s + (sp.__height ?? 0) + (idx > 0 ? ICON_STACK_GAP : 0),
        0,
      );

      /* Row must fit wrapped NAME and DESCRIPTION (minH is only a design minimum). */
      const textBlockH = Math.max(
        nameText.height + ROW_PAD_Y * 2,
        desc.height + ROW_PAD_Y * 2,
      );
      const cellH = Math.max(row.minH ?? scaledText(70), textBlockH, iconStackH + scaledText(20));
      rowHeights.push(cellH);

      const rowEl = new Container();
      rowEl.y = tableY;
      rowEl.eventMode = 'passive';

      /* No fill — keep the background transparent so the panel blur shows through. */
      const rowBg = new Graphics();
      rowBg.rect(0, 0, TABLE_W, cellH).fill({ color: 0x000000, alpha: 0 });
      rowBg.eventMode = 'none';
      rowEl.addChild(rowBg);

      /* Stack icons vertically and centre the stack inside the BUTTON cell. */
      let stackY = (cellH - iconStackH) / 2;
      iconSprites.forEach((sp, idx) => {
        sp.x = colXBtn + (tableCols.btn - (sp.__width ?? 0)) / 2;
        sp.y = stackY;
        rowEl.addChild(sp);
        stackY += (sp.__height ?? 0) + (idx < iconSprites.length - 1 ? ICON_STACK_GAP : 0);
      });

      /* Name column — wrapped; row height already includes full bounds. */
      nameText.anchor.set(0.5, 0.5);
      nameText.x = colXName + tableCols.name / 2;
      nameText.y = cellH / 2;
      rowEl.addChild(nameText);

      /* Description column — wrapped to new lines within the column width (wordWrapWidth === interior width). */
      desc.anchor.set(0, 0);
      desc.x = colXDesc + padDesc;
      desc.y = Math.max(0, (cellH - desc.height) / 2);
      rowEl.addChild(desc);

      tableContent.addChild(rowEl);
      tableY += cellH;

      layoutRows.push({
        rowEl,
        rowBg,
        nameText,
        desc,
        iconSprites,
        minH: row.minH,
        nameKey: row.nameKey,
        bodyKey: row.bodyKey,
      });
    });

    /* Total table height; used for outer border + internal divider lines. */
    const tableTotalH = tableY;

    /* Internal grid: vertical column separators and horizontal row separators.
       Drawn after the rows so they sit on top. */
    const tableGrid = new Graphics();
    /* Vertical column separators (full table height). */
    tableGrid.moveTo(colXName, 0).lineTo(colXName, tableTotalH);
    tableGrid.moveTo(colXDesc, 0).lineTo(colXDesc, tableTotalH);
    /* Horizontal separators: header bottom + each row bottom except the last. */
    let yWalker = tableHeaderH;
    tableGrid.moveTo(0, yWalker).lineTo(TABLE_W, yWalker);
    rowHeights.forEach((h, idx) => {
      yWalker += h;
      if (idx < rowHeights.length - 1) {
        tableGrid.moveTo(0, yWalker).lineTo(TABLE_W, yWalker);
      }
    });
    tableGrid.stroke({ width: 1, color: tableBorderColor, alpha: tableBorderAlpha });
    tableGrid.eventMode = 'none';
    tableContent.addChild(tableGrid);

    /* Outer rounded border. */
    const outerBorder = new Graphics();
    outerBorder.roundRect(0.5, 0.5, TABLE_W - 1, tableTotalH - 1, tableRadius)
      .stroke({ width: 1, color: tableBorderColor, alpha: tableBorderAlpha });
    outerBorder.eventMode = 'none';
    tableContent.addChild(outerBorder);

    const relayoutInfoButtonTable = () => {
      let ty = tableHeaderH;
      const newRowHeights = [];
      layoutRows.forEach((r) => {
        r.nameText.text = this._t(r.nameKey);
        r.desc.text = this._t(r.bodyKey);
        const stackH = r.iconSprites.reduce(
          (s, sp, idx) => s + (sp.__height ?? 0) + (idx > 0 ? ICON_STACK_GAP : 0),
          0,
        );
        const textBlockH = Math.max(
          r.nameText.height + ROW_PAD_Y * 2,
          r.desc.height + ROW_PAD_Y * 2,
        );
        const cellH = Math.max(r.minH ?? scaledText(70), textBlockH, stackH + scaledText(20));
        newRowHeights.push(cellH);
        r.rowEl.y = ty;
        r.rowBg.clear().rect(0, 0, TABLE_W, cellH).fill({ color: 0x000000, alpha: 0 });
        let stackY = (cellH - stackH) / 2;
        r.iconSprites.forEach((sp, idx) => {
          sp.x = colXBtn + (tableCols.btn - (sp.__width ?? 0)) / 2;
          sp.y = stackY;
          stackY += (sp.__height ?? 0) + (idx < r.iconSprites.length - 1 ? ICON_STACK_GAP : 0);
        });
        r.nameText.y = cellH / 2;
        r.desc.y = Math.max(0, (cellH - r.desc.height) / 2);
        ty += cellH;
      });
      const newTotal = ty;
      const delta = newTotal - this._infoLastTableTotalH;
      this._infoLastTableTotalH = newTotal;
      if (delta !== 0 && this._infoAfterTableChildren?.length) {
        this._infoAfterTableChildren.forEach((ch) => { ch.y += delta; });
        this._infoScrollContentHeight += delta;
        this._refreshInfoScrollBounds(this._infoViewportTop ?? 66, this._infoViewportBottom ?? 522);
      }
      tableGrid.clear();
      tableGrid.moveTo(colXName, 0).lineTo(colXName, newTotal);
      tableGrid.moveTo(colXDesc, 0).lineTo(colXDesc, newTotal);
      let yWalker2 = tableHeaderH;
      tableGrid.moveTo(0, yWalker2).lineTo(TABLE_W, yWalker2);
      newRowHeights.forEach((h, idx) => {
        yWalker2 += h;
        if (idx < newRowHeights.length - 1) {
          tableGrid.moveTo(0, yWalker2).lineTo(TABLE_W, yWalker2);
        }
      });
      tableGrid.stroke({ width: 1, color: tableBorderColor, alpha: tableBorderAlpha });
      outerBorder.clear();
      outerBorder.roundRect(0.5, 0.5, TABLE_W - 1, newTotal - 1, tableRadius)
        .stroke({ width: 1, color: tableBorderColor, alpha: tableBorderAlpha });
    };

    this._infoLastTableTotalH = tableTotalH;
    this._infoButtonTableRelayout = relayoutInfoButtonTable;

    cursorY += tableTotalH + 16;

    /* ── Closing paragraphs after the buttons table ──────────────── */
    const tableFooter1 = makeBodyFromKey('INFO_TABLE_FOOTER_CLOCK', INNER_W);
    tableFooter1.x = 0;
    tableFooter1.y = cursorY;
    content.addChild(tableFooter1);
    cursorY += tableFooter1.height + 12;

    const tableFooter2 = makeBodyFromKey('INFO_TABLE_FOOTER_SPACE_KEY', INNER_W);
    tableFooter2.x = 0;
    tableFooter2.y = cursorY;
    content.addChild(tableFooter2);
    cursorY += tableFooter2.height + 18;

    content.addChild(makeDivider(cursorY));
    cursorY += 18;

    /* ── RULES ───────────────────────────────────────────────────── */
    const rulesHeading = makeHeadingFromKey('INFO_HEADING_RULES');
    rulesHeading.anchor.set(0.5, 0);
    rulesHeading.x = INNER_W / 2;
    rulesHeading.y = cursorY;
    content.addChild(rulesHeading);
    cursorY += scaledText(26);

    const ruleBulletSpecs = [
      { key: 'INFO_RULE_BULLET_1' },
      { key: 'INFO_RULE_BULLET_2' },
      { key: 'INFO_RULE_BULLET_3' },
    ];
    ruleBulletSpecs.forEach((spec) => {
      const t = makeBodyFromKey(spec.key, INNER_W, { replace: spec.replace });
      t.x = 0;
      t.y = cursorY;
      content.addChild(t);
      cursorY += t.height + scaledText(10);
    });

    const minMaxLine = makeRuleLimitLine('INFO_RULE_BULLET_MINMAX', () => ({
      min: this._formatCurrencyAmount(gameConfig.minBet),
      max: this._formatCurrencyAmount(gameConfig.maxBet),
    }));
    minMaxLine.x = 0;
    minMaxLine.y = cursorY;
    content.addChild(minMaxLine);
    cursorY += scaledText(17) + scaledText(10);

    const maxWinLine = makeRuleLimitLine('INFO_RULE_BULLET_MAXWIN', () => {
      const maxOdd = Number(gameConfig.maxOdd);
      return {
        maxWin: this._formatCurrencyAmount(
          Number.isFinite(maxOdd) && maxOdd > 0 ? gameConfig.maxBet * maxOdd : 0,
        ),
      };
    });
    maxWinLine.x = 0;
    maxWinLine.y = cursorY;
    const maxOddAtBuild = Number(gameConfig.maxOdd);
    maxWinLine.visible = Number.isFinite(maxOddAtBuild) && maxOddAtBuild > 0;
    this._infoRuleMaxWinLines.push(maxWinLine);
    content.addChild(maxWinLine);
    cursorY += scaledText(17) + scaledText(10);
    cursorY += 8;

    /* ── GAME INTERRUPTIONS ──────────────────────────────────────── */
    const interruptionsHeading = makeHeadingFromKey('INFO_HEADING_GAME_INTERRUPTIONS');
    interruptionsHeading.anchor.set(0.5, 0);
    interruptionsHeading.x = INNER_W / 2;
    interruptionsHeading.y = cursorY;
    content.addChild(interruptionsHeading);
    cursorY += 22;

    const fullRecoveryHeading = makeHeadingFromKey('INFO_HEADING_FULL_GAME_RECOVERY');
    fullRecoveryHeading.anchor.set(0.5, 0);
    fullRecoveryHeading.x = INNER_W / 2;
    fullRecoveryHeading.y = cursorY;
    content.addChild(fullRecoveryHeading);
    cursorY += 24;

    const fullRecoveryBody = makeBodyFromKey('INFO_FULL_RECOVERY_BODY', INNER_W);
    fullRecoveryBody.x = 0;
    fullRecoveryBody.y = cursorY;
    content.addChild(fullRecoveryBody);
    cursorY += fullRecoveryBody.height + 18;

    const cancellationHeading = makeHeadingFromKey('INFO_HEADING_CANCELLATION');
    cancellationHeading.anchor.set(0.5, 0);
    cancellationHeading.x = INNER_W / 2;
    cancellationHeading.y = cursorY;
    content.addChild(cancellationHeading);
    cursorY += 24;

    const cancellationBody = makeBodyFromKey('INFO_CANCELLATION_BODY', INNER_W);
    cancellationBody.x = 0;
    cancellationBody.y = cursorY;
    content.addChild(cancellationBody);
    cursorY += cancellationBody.height + 22;

    /* ── RESPONSIBLE GAMING ──────────────────────────────────────── */
    const responsibleHeading = makeHeadingFromKey('INFO_HEADING_RESPONSIBLE_GAMING');
    responsibleHeading.anchor.set(0.5, 0);
    responsibleHeading.x = INNER_W / 2;
    responsibleHeading.y = cursorY;
    content.addChild(responsibleHeading);
    cursorY += 22;

    const autoplayUseHeading = makeHeadingFromKey('INFO_HEADING_HOW_TO_USE_AUTOPLAY');
    autoplayUseHeading.anchor.set(0.5, 0);
    autoplayUseHeading.x = INNER_W / 2;
    autoplayUseHeading.y = cursorY;
    content.addChild(autoplayUseHeading);
    cursorY += 24;

    const autoplayUseBody = makeBodyFromKey('INFO_AUTOPLAY_USE_BODY', INNER_W);
    autoplayUseBody.x = 0;
    autoplayUseBody.y = cursorY;
    content.addChild(autoplayUseBody);
    cursorY += autoplayUseBody.height + 24;

    /* ── RETURN TO PLAYER ────────────────────────────────────────── */
    const rtpHeading = makeHeadingFromKey('INFO_HEADING_RETURN_TO_PLAYER');
    rtpHeading.anchor.set(0.5, 0);
    rtpHeading.x = INNER_W / 2;
    rtpHeading.y = cursorY;
    content.addChild(rtpHeading);
    cursorY += 26;

    const rtpBody = makeBodyFromKey('INFO_RTP_BODY', INNER_W);
    rtpBody.x = 0;
    rtpBody.y = cursorY;
    content.addChild(rtpBody);
    cursorY += rtpBody.height + 24;

    /* ── Wire scrolling state ────────────────────────────────────── */
    const tableIdx = content.getChildIndex(tableRoot);
    this._infoAfterTableChildren =
      tableIdx >= 0 ? content.children.slice(tableIdx + 1).filter((ch) => ch != null) : [];

    const totalH = cursorY;
    this._infoScrollContentHeight = totalH;
    this._refreshInfoScrollBounds(VIEWPORT_TOP, VIEWPORT_BOTTOM);

    const setScrollY = (next) => {
      const c = this._infoScrollClamp;
      if (!c) return;
      scroll.y = Math.max(c.minY, Math.min(c.maxY, next));
    };

    this._infoScroll = {
      reset: () => {
        const c = this._infoScrollClamp;
        if (c) scroll.y = c.naturalTop;
      },
      scrollBy: (dy) => {
        const c = this._infoScrollClamp;
        if (!c) return;
        scroll.y = Math.max(c.minY, Math.min(c.maxY, scroll.y - dy));
      },
    };

    /* Wheel scroll over the info viewport (Pixi v8 federated wheel events). */
    view.on('wheel', (e) => {
      if (!view.visible) return;
      const dy = typeof e.deltaY === 'number' ? e.deltaY : 0;
      if (dy === 0) return;
      setScrollY(scroll.y - dy);
      if (typeof e.preventDefault === 'function') e.preventDefault();
    });

    /* Pointer drag scroll for touch devices. */
    let dragging = false;
    let lastY = 0;
    let pointerId = null;
    view.on('pointerdown', (e) => {
      if (!view.visible) return;
      dragging = true;
      lastY = e.global?.y ?? 0;
      pointerId = e.pointerId;
    });
    const endDrag = (e) => {
      if (pointerId != null && e?.pointerId !== pointerId) return;
      dragging = false;
      pointerId = null;
    };
    view.on('pointermove', (e) => {
      if (!dragging) return;
      if (pointerId != null && e.pointerId !== pointerId) return;
      const ny = e.global?.y ?? 0;
      const dy = ny - lastY;
      lastY = ny;
      setScrollY(scroll.y + dy);
    });
    view.on('pointerup', endDrag);
    view.on('pointerupoutside', endDrag);
    view.on('pointercancel', endDrag);

    this._infoSettingsLayoutRefs = {
      mask,
      scroll,
      content,
      view,
    };

    return view;
  }

  /**
   * Only one blocking modal at a time (settings / bet / autoplay / connection).
   * @param {'settings'|'bet'|'autoplay'|'connection'|null} [keep] null = close all
   */
  _dismissOverlappingModals(keep = null) {
    if (keep !== 'settings') this._hideSettingsPanel();
    if (keep !== 'bet') this._hideBetPopup();
    if (keep !== 'autoplay') this._hideAutoplayPicker();
    if (keep !== 'connection') this._hideConnectionLostModal();
  }

  /** Connection / duplicate-session shell is active (flags or already painted). */
  _isConnectionModalBlocking() {
    return !!(
      this._connectionLostRoot?.visible ||
      this._connectionLostUIOpen ||
      this._sessionLoadFailed ||
      this._duplicateSessionEnded
    );
  }

  /** @param {'settings'|'bet'|'autoplay'|'connection'|null} [except] */
  _isBlockingModalOpen(except = null) {
    if (except !== 'connection' && this._isConnectionModalBlocking()) return true;
    if (except !== 'settings' && this._settingsRoot?.visible) return true;
    if (except !== 'bet' && this._betPopupDimRoot?.visible) return true;
    if (except !== 'autoplay' && this._autoplayPickerOpen) return true;
    return false;
  }

  /** PLAY NOW handler for the autoplay-settings modal. */
  _startAutoplayFromModal() {
    const spins = this._autoplayModalSpinsSelected;
    this._hideSettingsPanel();
    this._startAutoplayWithRounds(spins ?? null);
  }

  _shouldShowQuickBets() {
    if (this._sessionLoadFailed || this._connectionLostUIOpen || this._duplicateSessionEnded) {
      return false;
    }
    if (!this._gameDataReady) return false;
    const bets = this.slotMachine?.ui?._quickBets ?? [];
    return bets.length > 0;
  }

  /** Session-ended always wins over connection-lost (same modal shell). */
  _isDuplicateSessionModalActive() {
    return (
      this._duplicateSessionEnded ||
      this._forceDisconnectReceived ||
      this._connModalMode === 'duplicate'
    );
  }

  _shouldShowConnectionLostModal() {
    if (this._initializing || this._loadingRoot) return false;
    if (this._isDuplicateSessionModalActive()) {
      return this._connectionLostUIOpen;
    }
    return this._sessionLoadFailed || this._connectionLostUIOpen;
  }

  /** Force duplicate copy/layout; never downgrade to connection-lost. */
  _prioritizeDuplicateSessionModal() {
    if (!this._isDuplicateSessionModalActive()) return false;
    this._connModalMode = 'duplicate';
    this._connectionLostUIOpen = true;
    this._hadConnectionLoss = false;
    this._reconnectAfterTabVisible = false;
    this._stopConnectionAutoRetry();
    this._clearConnectionLostGraceTimer();
    this._clearDisconnectResolveTimer();
    this._setConnectionModalReconnecting(false);
    this._refreshConnectionLostModalCopy();
    if (this._connLostPanel) this._layoutConnectionLostModal();
    return true;
  }

  _clearConnectionLostGraceTimer() {
    if (this._connLostGraceTimer != null) {
      clearTimeout(this._connLostGraceTimer);
      this._connLostGraceTimer = null;
    }
  }

  _clearDisconnectResolveTimer() {
    if (this._disconnectResolveTimer != null) {
      clearTimeout(this._disconnectResolveTimer);
      this._disconnectResolveTimer = null;
    }
  }

  _scheduleDisconnectResolution(delayMs = FORCE_DISCONNECT_WAIT_MS) {
    if (!this._connectionMonitoringEnabled || this._isDuplicateSessionModalActive()) return;
    if (this._isPostBootHubGraceActive()) return;
    this._clearDisconnectResolveTimer();
    if (typeof window === 'undefined') return;
    const ms = Number.isFinite(delayMs) ? delayMs : FORCE_DISCONNECT_WAIT_MS;
    this._disconnectResolveTimer = window.setTimeout(() => {
      this._disconnectResolveTimer = null;
      void this._resolvePostHubDisconnect();
    }, ms);
  }

  _isOnline() {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  }

  _isPostBootHubGraceActive() {
    return Date.now() < this._hubIgnoreUntilTs;
  }

  _armPostBootHubGrace() {
    this._hubIgnoreUntilTs = Date.now() + POST_BOOT_HUB_GRACE_MS;
  }

  _attachHubConnectionHandlers() {
    this.backend.setConnectionHandlers({
      onDisconnected: () => this._handleHubDisconnected(),
      onReconnecting: () => this._handleHubReconnecting(),
      onConnected: () => this._handleHubReconnected(),
      onForceDisconnect: () => this._handleForceDisconnect(),
    });
  }

  _unwrapInitialPayload(state) {
    let payload = state?.arguments?.[0] ?? state;
    if (payload?.result != null && typeof payload.result === 'object') {
      payload = payload.result;
    } else if (payload?.Result != null && typeof payload.Result === 'object') {
      payload = payload.Result;
    }
    return payload;
  }

  _stopConnectionAutoRetry() {
    if (this._connectionAutoRetryTimer != null) {
      clearInterval(this._connectionAutoRetryTimer);
      this._connectionAutoRetryTimer = null;
    }
    this._setConnectionModalReconnecting(false);
  }

  _setConnectionModalReconnecting(active) {
    if (this._isDuplicateSessionModalActive()) {
      this._connectionModalReconnecting = false;
      return;
    }
    this._connectionModalReconnecting = !!active;
  }

  _startConnectionAutoRetry() {
    if (this._connectionAutoRetryTimer != null) return;
    if (this._isDuplicateSessionModalActive()) return;
    const tick = async () => {
      if (!this._connectionLostUIOpen || this._duplicateSessionEnded) {
        this._stopConnectionAutoRetry();
        return;
      }
      await this._runConnectionCheck({ allowShowModal: true });
    };
    void tick();
    this._connectionAutoRetryTimer = window.setInterval(() => {
      void tick();
    }, CONNECTION_AUTO_RETRY_MS);
  }

  /** Tab return / background: reconnect without opening the connection-lost modal. */
  async _attemptSilentReconnect({ restartHubFirst = false, pingOnly = false } = {}) {
    return this._runConnectionCheck({ restartHubFirst, pingOnly, allowShowModal: false });
  }

  /** Debounced hub ping (InitialState). Modal only opens if the ping fails. */
  _readBalanceFromRecord(record) {
    if (record == null) return null;
    const fieldNames = [
      'balance',
      'Balance',
      'currentBalance',
      'CurrentBalance',
      'walletBalance',
      'WalletBalance',
      'playerBalance',
      'PlayerBalance',
      'totalBalance',
      'TotalBalance',
    ];
    /** @type {unknown[]} */
    const queue = [record];
    const seen = new Set();
    while (queue.length) {
      const obj = queue.shift();
      if (!obj || typeof obj !== 'object' || seen.has(obj)) continue;
      seen.add(obj);
      for (const name of fieldNames) {
        if (!(name in obj)) continue;
        const n = Number(obj[name]);
        if (Number.isFinite(n)) return n;
      }
      for (const key of ['result', 'Result', 'model', 'Model', 'data', 'Data', 'state', 'State']) {
        const child = obj[key];
        if (child && typeof child === 'object') queue.push(child);
      }
      const args = obj.arguments;
      if (Array.isArray(args) && args[0] && typeof args[0] === 'object') {
        queue.push(args[0]);
      }
    }
    return null;
  }

  _readSessionBalance(initial) {
    const payload = this._unwrapInitialPayload(initial) ?? initial;
    return this._readBalanceFromRecord(payload) ?? this._readBalanceFromRecord(initial);
  }

  _readCurrencyCodeFromRecord(record) {
    if (record == null) return null;
    const fieldNames = [
      'currencyCode',
      'CurrencyCode',
      'Currency',
      'currency',
    ];
    /** @type {unknown[]} */
    const queue = [record];
    const seen = new Set();
    while (queue.length) {
      const obj = queue.shift();
      if (!obj || typeof obj !== 'object' || seen.has(obj)) continue;
      seen.add(obj);
      for (const name of fieldNames) {
        if (!(name in obj)) continue;
        const raw = obj[name];
        if (raw == null) continue;
        const code = String(raw).trim().toUpperCase();
        if (code) return code;
      }
      for (const key of ['result', 'Result', 'model', 'Model', 'data', 'Data', 'state', 'State']) {
        const child = obj[key];
        if (child && typeof child === 'object') queue.push(child);
      }
      const args = obj.arguments;
      if (Array.isArray(args) && args[0] && typeof args[0] === 'object') {
        queue.push(args[0]);
      }
    }
    return null;
  }

  _readSessionCurrencyCode(initial) {
    const payload = this._unwrapInitialPayload(initial) ?? initial;
    return (
      this._readCurrencyCodeFromRecord(payload) ?? this._readCurrencyCodeFromRecord(initial)
    );
  }

  _roundStatusStorageKey() {
    const session = this._sessionParams ?? this._resolveSessionAuth?.() ?? {};
    const partnerId = String(session.partnerId ?? '');
    const gameName = String(session.gameName ?? '');
    if (!partnerId || !gameName) return null;
    return roundStatusStorageKey(partnerId, gameName);
  }

  /** @param {import('./utils/gameRoundStatusStore.js').RoundStatusSnapshot} status */
  _persistRoundStatus(status) {
    this._roundStatusLive = { ...status, savedAt: Date.now() };
    const key = this._roundStatusStorageKey();
    if (key) saveRoundStatus(key, status);
    saveRoundStatus(ROUND_STATUS_FALLBACK_KEY, status);
  }

  /** @returns {import('./utils/gameRoundStatusStore.js').RoundStatusSnapshot | null} */
  _loadRoundStatus() {
    const key = this._roundStatusStorageKey();
    const keyed = key ? loadRoundStatus(key) : null;
    if (keyed) return keyed;
    return loadRoundStatus(ROUND_STATUS_FALLBACK_KEY);
  }

  _clearRoundStatus() {
    this._roundStatusLive = null;
    const key = this._roundStatusStorageKey();
    if (key) clearRoundStatus(key);
    clearRoundStatus(ROUND_STATUS_FALLBACK_KEY);
  }

  _installRoundStatusPageHideFlush() {
    if (this._roundStatusPageHideFlush || typeof window === 'undefined') return;
    this._roundStatusPageHideFlush = () => {
      if (!this._roundStatusLive || !this._isPendingRoundPhase(this._roundStatusLive.phase)) return;
      this._persistRoundStatus(this._roundStatusLive);
    };
    window.addEventListener('pagehide', this._roundStatusPageHideFlush);
  }

  /** @param {string | undefined} phase */
  _isPendingRoundPhase(phase) {
    return phase === 'celebrating' || phase === 'collect_hold' || phase === 'rollup_collect';
  }

  /**
   * Server-reported uncollected win only — not the “last win” HUD stat.
   * @param {Record<string, unknown>} initial
   * @returns {number}
   */
  _readExplicitPendingWinFromInitial(initial) {
    const direct = Number(
      initial?.pendingWin ??
        initial?.PendingWin ??
        initial?.uncollectedWin ??
        initial?.UncollectedWin ??
        0,
    );
    if (direct > 0) return direct;

    const roundState = String(initial?.roundState ?? initial?.RoundState ?? '').toLowerCase();
    const needsCollect =
      initial?.needsCollect === true ||
      initial?.NeedsCollect === true ||
      initial?.awaitingCollect === true ||
      initial?.AwaitingCollect === true ||
      roundState === 'collect' ||
      roundState === 'pendingcollect' ||
      roundState === 'pending';

    if (needsCollect) {
      const w = Number(
        initial?.winAmount ??
          initial?.WinAmount ??
          initial?.roundWin ??
          initial?.RoundWin ??
          0,
      );
      if (w > 0) return w;
    }
    return 0;
  }

  /**
   * @param {Record<string, unknown>} initial
   * @returns {number | null}
   */
  _readRoundBetFromInitial(initial) {
    const candidates = [
      initial?.betAmount,
      initial?.BetAmount,
      initial?.lastBetAmount,
      initial?.LastBetAmount,
      initial?.roundBetAmount,
      initial?.RoundBetAmount,
      initial?.stake,
      initial?.Stake,
    ];
    for (const raw of candidates) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }

  /**
   * Merge server InitialState with client session snapshot for interrupted rounds.
   * @param {Record<string, unknown>} initial
   * @returns {{ pendingWin: number, betAmount: number | null, snapshot: import('./utils/gameRoundStatusStore.js').RoundStatusSnapshot | null, source: string } | null}
   */
  _resolveInterruptedRound(initial) {
    const snapshot = this._loadRoundStatus();
    const serverPending = this._readExplicitPendingWinFromInitial(initial);

    if (
      snapshot &&
      this._isPendingRoundPhase(snapshot.phase) &&
      Number(snapshot.pendingWin) > 0
    ) {
      const snapWin = Number(snapshot.pendingWin);
      const serverMatches =
        serverPending > 0 && Math.abs(serverPending - snapWin) < 0.001;
      return {
        pendingWin: serverMatches ? serverPending : snapWin,
        betAmount:
          Number(snapshot.betAmount) > 0
            ? Number(snapshot.betAmount)
            : this._readRoundBetFromInitial(initial),
        snapshot,
        source: serverMatches ? 'server+client' : 'client',
      };
    }

    if (serverPending > 0) {
      return {
        pendingWin: serverPending,
        betAmount:
          (snapshot?.betAmount > 0 ? Number(snapshot.betAmount) : null) ??
          this._readRoundBetFromInitial(initial),
        snapshot,
        source: 'server',
      };
    }

    return null;
  }

  /**
   * Play deferred big-win overlay after bootstrap loading screen is gone.
   * @param {{ replaySounds?: boolean }} [opts]
   */
  _scheduleDeferredRoundResumeVisuals({ replaySounds = false } = {}) {
    const run = () => this._applyDeferredRoundResumeVisuals({ replaySounds });
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
      run();
    }
  }

  /**
   * Play deferred big-win overlay after bootstrap loading screen is gone.
   * @param {{ replaySounds?: boolean }} [opts]
   */
  _applyDeferredRoundResumeVisuals({ replaySounds = false } = {}) {
    const d = this._deferredRoundResumeVisuals;
    if (!d || !this.slotMachine) return;
    this._deferredRoundResumeVisuals = null;

    const { winMult, pendingWin, celebrationMode, showCelebration, statusBase, elapsedSec } = d;
    if (!showCelebration) return;

    const onBuildupComplete = () => {
      this._clearRoundStatus();
      if (this._spinButtonSprite) {
        this._setSpinButtonTexture(this._autoplaying ? this._spinStopTex : this._spinStartTex);
      }
      this.slotMachine?.ui?.setSpinEnabled(this._shouldShowQuickBets());
      this._setBetPromptVisible(true);
      this._refreshQuickBets();
    };

    if (celebrationMode === 'static') {
      this.slotMachine.showWinCelebrationStaticHold(winMult, pendingWin);
      onBuildupComplete();
      return;
    }

    this._persistRoundStatus({
      ...statusBase,
      phase: 'celebrating',
      celebrationStartedAt: Date.now(),
    });
    this.slotMachine.playWinCelebrationContinue(
      winMult,
      pendingWin,
      elapsedSec ?? 0,
      onBuildupComplete,
    );
  }

  /**
   * Restore an unfinished round from InitialState (reload, reconnect, or tab return).
   * @param {Record<string, unknown>} initial
   * @param {number} pendingWin
   * @param {unknown} wins
   * @param {{ betAmount?: number | null, snapshot?: import('./utils/gameRoundStatusStore.js').RoundStatusSnapshot | null }} [opts]
   */
  _resumePendingRoundFromServer(initial, pendingWin, wins, { betAmount: betOverride = null, snapshot = null } = {}) {
    if (!this.slotMachine || !(pendingWin > 0)) return;
    if (this._spinCycleActive || this._isWinCollectDismissActive()) return;
    if (this.slotMachine.reelContainer?._winLineCycleActive) return;

    this._applySessionMatrixToReels(initial, { force: true });

    const ui = this.slotMachine.ui;
    const snap = snapshot ?? this._loadRoundStatus();
    const betAmount =
      (betOverride != null && betOverride > 0 ? betOverride : null) ??
      (snap?.betAmount > 0 ? Number(snap.betAmount) : null) ??
      this._readRoundBetFromInitial(initial) ??
      ui.betValue;
    if (Number.isFinite(betAmount) && betAmount > 0) {
      ui.setBet(betAmount);
      this._updateCanvasBetPrompt(betAmount);
    }

    let winMult = betAmount > 0 ? pendingWin / betAmount : 0;
    if (snap && Number(snap.winMult) > 0) {
      winMult = Number(snap.winMult);
    }
    let showCelebration =
      !!snap?.showCelebration ||
      (betAmount > 0 && winMult >= winCelebrationMinMultiple());

    const grid = this._getDisplayGridFromInitial(initial);
    const hits = Array.isArray(wins) ? this._mapBackendWinsToHits(wins, grid) : [];

    ui.setWin(pendingWin, hits);
    this._updateCanvasWin(pendingWin);
    if (hits.length) {
      const hitsSorted = [...hits].sort(
        (a, b) =>
          (a.paylineIndex ?? Number.MAX_SAFE_INTEGER) - (b.paylineIndex ?? Number.MAX_SAFE_INTEGER),
      );
      this.slotMachine.beginPostSpinWin(hitsSorted, pendingWin);
    }

    const winMatches =
      !!snap && Math.abs(Number(snap.pendingWin) - pendingWin) < 0.001;

    /** @type {'continue' | 'static' | 'none'} */
    let celebrationMode = 'none';
    if (showCelebration) {
      if (winMatches && snap?.phase === 'collect_hold') {
        celebrationMode = 'static';
      } else {
        celebrationMode = 'continue';
      }
    }

    const statusBase = {
      phase: /** @type {const} */ (
        showCelebration && celebrationMode === 'static' ? 'collect_hold' : 'rollup_collect'
      ),
      pendingWin,
      betAmount,
      winMult,
      showCelebration,
    };

    let elapsedSec = 0;
    if (celebrationMode === 'continue' && snap?.celebrationStartedAt) {
      elapsedSec = Math.max(0, (Date.now() - Number(snap.celebrationStartedAt)) / 1000);
    }

    if (showCelebration && celebrationMode === 'continue') {
      const seqSec = getCelebrationSequenceDurationSec(this.spineAssets, winMult, false);
      this._animateLastWinRollup(pendingWin, betAmount, { fixedDurationSec: seqSec });
    } else if (showCelebration && celebrationMode === 'static') {
      this._animateLastWinRollup(pendingWin, betAmount, { immediate: true });
    } else {
      this._animateLastWinRollup(pendingWin, betAmount, { immediate: true });
      this._clearRoundStatus();
    }

    if (showCelebration) {
      this._resumeRoundSkipCollect = true;
      if (this._spinButtonSprite) {
        this._setSpinButtonTexture(this._autoplaying ? this._spinStopTex : this._spinStartTex);
      }
      ui.setSpinEnabled(this._shouldShowQuickBets());
      this._deferredRoundResumeVisuals = {
        winMult,
        pendingWin,
        celebrationMode,
        showCelebration,
        statusBase,
        elapsedSec,
      };
      if (!this._loadingRoot) {
        this._scheduleDeferredRoundResumeVisuals({ replaySounds: true });
      }
    }

    this._refreshQuickBets();
  }

  _scheduleConnectionCheck(delayMs = CONNECTION_LOST_GRACE_MS) {
    if (!this._connectionMonitoringEnabled) return;
    if (this._isDuplicateSessionModalActive()) return;
    if (this._isPostBootHubGraceActive()) return;
    this._clearConnectionLostGraceTimer();
    if (typeof window === 'undefined') return;
    const ms = Number.isFinite(delayMs) ? delayMs : CONNECTION_LOST_GRACE_MS;
    this._connLostGraceTimer = window.setTimeout(() => {
      this._connLostGraceTimer = null;
      void this._runConnectionCheck();
    }, ms);
  }

  _refreshConnectionLostModalCopy() {
    const duplicate = this._isDuplicateSessionModalActive();
    const titleKey = duplicate ? 'DUPLICATE_SESSION_TITLE' : 'CONNECTION_LOST_TITLE';
    const bodyKey = duplicate ? 'DUPLICATE_SESSION_BODY' : 'CONNECTION_LOST_BODY';
    if (this._connLostTitleText) this._connLostTitleText.text = this._tu(titleKey);
    if (this._connLostBodyText) this._connLostBodyText.text = this._t(bodyKey);
  }

  _installHubConnectionMonitor() {
    if (this._hubMonitorDispose || typeof window === 'undefined') return;
    this._hubMonitorDispose = installHubConnectionMonitor({
      onOffline: () => this._onBrowserOffline(),
      onOnline: () => this._onBrowserOnline(),
      onForeground: (detail) => this._onAppForeground(detail),
      onBackground: () => this._onAppBackground(),
    });
  }

  _onBrowserOffline() {
    if (!this._connectionMonitoringEnabled) return;
    if (this._duplicateSessionEnded || this._sessionLoadFailed || !this._gameDataReady) return;
    this._hadConnectionLoss = true;
    this._suspendPlayForConnectivity();
    if (typeof document !== 'undefined' && document.hidden) {
      void this._runConnectionCheck({ allowShowModal: true });
      return;
    }
    this._scheduleConnectionCheck(CONNECTION_OFFLINE_GRACE_MS);
  }

  _onBrowserOnline() {
    if (!this._connectionMonitoringEnabled) return;
    if (this._isPostBootHubGraceActive()) return;
    if (this._duplicateSessionEnded || !this._gameDataReady) return;
    if (!this._hadConnectionLoss && !this._connectionLostUIOpen && this.backend.isConnected()) {
      return;
    }
    void this._runConnectionCheck();
  }

  _onAppBackground() {
    if (!this._connectionMonitoringEnabled) return;
    if (this._duplicateSessionEnded || this._sessionLoadFailed || !this._gameDataReady) return;
    if (this._isDuplicateSessionModalActive()) return;
    this._clearConnectionLostGraceTimer();
    if (
      this._connectionLostUIOpen ||
      this._reconnectAfterTabVisible ||
      this._hadConnectionLoss ||
      !this.backend.isConnected()
    ) {
      void this._runConnectionCheck({ allowShowModal: true });
    }
  }

  _onAppForeground({ hiddenMs = 0 } = {}) {
    if (!this._connectionMonitoringEnabled) return;
    if (this._isPostBootHubGraceActive()) return;
    if (this._duplicateSessionEnded || this._sessionLoadFailed || !this._gameDataReady) return;
    if (this._isDuplicateSessionModalActive()) return;
    this._lastForegroundHiddenMs = hiddenMs;
    this._clearDisconnectResolveTimer();
    this._clearConnectionLostGraceTimer();
    this._reconnectAfterTabVisible = false;
    const restartHub = hiddenMs >= IOS_BACKGROUND_RESTART_MS;
    if (this._connectionLostUIOpen) {
      void this._showConnectionLostModal();
      void this._runConnectionCheck({
        restartHubFirst: restartHub,
        allowShowModal: true,
        foregroundReturn: true,
      });
      return;
    }
    void this._runConnectionCheck({
      restartHubFirst: restartHub,
      allowShowModal: true,
      foregroundReturn: true,
    });
  }

  _shouldKeepSpinDisabled() {
    return (
      this._duplicateSessionEnded ||
      this._sessionLoadFailed ||
      this._connectionLostUIOpen ||
      !this._gameDataReady ||
      this._sessionSyncInFlight
    );
  }

  _suspendPlayForConnectivity() {
    this._hubConnected = false;
    this.slotMachine?.ui?.setSpinEnabled(false);
    if (this._autoplaying) this.handleAutoplayToggle(false);
    if (this._isConnectionModalBlocking()) {
      this._dismissOverlappingModals('connection');
      return;
    }
    if (this._hadConnectionLoss || this._connLostGraceTimer != null) {
      this._dismissOverlappingModals(null);
    }
  }

  async _fetchSessionPayload({ retryHub = true } = {}) {
    const session = this._sessionParams ?? this._resolveSessionAuth();
    const state = await this.backend.pingSession({
      isDemo: this._isDemo,
      gameName: session.gameName,
      partnerId: session.partnerId,
    });
    const initial = this._unwrapInitialPayload(state) ?? state;
    if (this._readSessionBalance(initial) == null) {
      if (retryHub && this.backend.isConnected()) {
        await this.backend.restartConnection();
        return this._fetchSessionPayload({ retryHub: false });
      }
      throw new Error('Session ping missing balance');
    }
    return initial;
  }

  _isDuplicateSessionError(err) {
    const parts = [
      err?.message,
      err?.error,
      err?.code,
      err?.reason,
      typeof err === 'string' ? err : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return (
      parts.includes('duplicate') ||
      parts.includes('force disconnect') ||
      parts.includes('forcedisconnect') ||
      parts.includes('another session') ||
      parts.includes('another tab') ||
      parts.includes('other window') ||
      parts.includes('session ended') ||
      parts.includes('session replaced') ||
      parts.includes('session already') ||
      parts.includes('logged in elsewhere') ||
      parts.includes('multi session') ||
      parts.includes('multisession')
    );
  }

  _isLatestSessionInstance() {
    return isLatestSessionInstance(this._sessionInstanceId);
  }

  _showDuplicateSessionEndedModal() {
    if (this._initializing) return;
    if (this._isLatestSessionInstance()) return;
    this._reconnectAfterTabVisible = false;
    this._clearConnectionLostGraceTimer();
    this._clearDisconnectResolveTimer();
    this._stopConnectionAutoRetry();
    void this.backend.ceaseSession();
    this._forceDisconnectReceived = true;
    this._duplicateSessionEnded = true;
    this._connModalMode = 'duplicate';
    this._hubConnected = false;
    this._connectionLostUIOpen = true;
    this._prioritizeDuplicateSessionModal();
    this._dismissOverlappingModals('connection');
    this._syncConnectionLostModal();
    this._refreshQuickBets();
    this.slotMachine?.ui?.setSpinEnabled(false);
    if (this._autoplaying) this.handleAutoplayToggle(false);
  }

  _showConnectionLostAfterRequestFailure() {
    if (this._isDuplicateSessionModalActive()) {
      this._prioritizeDuplicateSessionModal();
      this._syncConnectionLostModal();
      return;
    }
    const alreadyOpen =
      this._connectionLostUIOpen &&
      this._connModalMode === 'lost' &&
      !this._isDuplicateSessionModalActive();
    this._dismissOverlappingModals('connection');
    this._connModalMode = 'lost';
    this._connectionLostUIOpen = true;
    this._hubConnected = false;
    this._hadConnectionLoss = true;
    this._refreshConnectionLostModalCopy();
    this._syncConnectionLostModal();
    this._refreshQuickBets();
    this.slotMachine?.ui?.setSpinEnabled(false);
    if (!alreadyOpen) this._startConnectionAutoRetry();
  }

  _clearConnectionLostAfterRequestSuccess(wasDisconnected) {
    if (this._isDuplicateSessionModalActive()) return;
    this._hubConnected = true;
    this._lastForegroundHiddenMs = 0;
    this._reconnectAfterTabVisible = false;
    this._stopConnectionAutoRetry();
    this._clearConnectionLostGraceTimer();
    this._connectionLostUIOpen = false;
    this._hideConnectionLostModal();
    this._refreshQuickBets();
    if (!this._spinCycleActive && !this._winCollectPending) {
      this.slotMachine?.ui?.setSpinEnabled(true);
    }
    if (wasDisconnected) this._showConnectionRestoredToast();
    this._hadConnectionLoss = false;
  }

  /**
   * @param {{ restartHubFirst?: boolean, pingOnly?: boolean, allowShowModal?: boolean, bypassGridPreserve?: boolean, foregroundReturn?: boolean }} [opts]
   * @returns {Promise<boolean>}
   */
  async _runConnectionCheck(opts = {}) {
    if (
      !this._connectionMonitoringEnabled ||
      this._isDuplicateSessionModalActive() ||
      this._sessionLoadFailed ||
      !this._gameDataReady
    ) {
      return false;
    }
    if (this._sessionSyncPromise) {
      return this._sessionSyncPromise;
    }
    const run = this._runConnectionCheckImpl(opts);
    this._sessionSyncPromise = run;
    try {
      return await run;
    } finally {
      if (this._sessionSyncPromise === run) {
        this._sessionSyncPromise = null;
      }
    }
  }

  /**
   * @param {{ restartHubFirst?: boolean, pingOnly?: boolean, allowShowModal?: boolean, bypassGridPreserve?: boolean, foregroundReturn?: boolean }} [opts]
   * @returns {Promise<boolean>}
   */
  async _runConnectionCheckImpl({
    restartHubFirst = false,
    pingOnly = false,
    allowShowModal = true,
    bypassGridPreserve = false,
    foregroundReturn = false,
  } = {}) {
    if (this._shouldPreserveVisibleGrid() && !bypassGridPreserve && !foregroundReturn) {
      this._scheduleConnectionCheck(CONNECTION_LOST_GRACE_MS);
      return false;
    }

    this._sessionSyncInFlight = true;
    const wasDisconnected = this._connectionLostUIOpen || this._hadConnectionLoss;
    if (allowShowModal) this._setConnectionModalReconnecting(true);
    try {
      if (!this._isOnline()) throw new Error('Browser offline');
      if (this.backend.isReconnecting()) {
        if (allowShowModal) {
          this._scheduleConnectionCheck(foregroundReturn ? 250 : 1000);
        }
        return false;
      }
      if (pingOnly) {
        if (!this.backend.isConnected()) return false;
      } else if (restartHubFirst && this.backend.isConnected()) {
        await this.backend.restartConnection();
      } else if (!this.backend.isConnected()) {
        await this.backend.connect(this._accessToken);
      }
      if (this._isDuplicateSessionModalActive()) return false;
      const initial = await this._fetchSessionPayload();
      if (this._isDuplicateSessionModalActive()) return false;
      this._applyServerSessionState(initial, { resumeInterruptedRound: true });
      this._clearConnectionLostAfterRequestSuccess(wasDisconnected);
      return true;
    } catch (err) {
      console.warn('Session ping failed', err);
      if (this._isDuplicateSessionModalActive()) return false;
      if (this._isDuplicateSessionError(err)) {
        this._showDuplicateSessionEndedModal();
        return false;
      }
      if (!allowShowModal || pingOnly) return false;
      try {
        if (restartHubFirst) {
          await this.backend.restartConnection();
        } else if (!this.backend.isConnected()) {
          await this.backend.connect(this._accessToken);
        }
        if (this._isDuplicateSessionModalActive()) return false;
        const initial = await this._fetchSessionPayload();
        if (this._isDuplicateSessionModalActive()) return false;
        this._applyServerSessionState(initial, { resumeInterruptedRound: true });
        this._clearConnectionLostAfterRequestSuccess(wasDisconnected);
        return true;
      } catch (err2) {
        console.warn('Session ping retry failed', err2);
        if (this._isDuplicateSessionModalActive()) return false;
        if (this._isDuplicateSessionError(err) || this._isDuplicateSessionError(err2)) {
          this._showDuplicateSessionEndedModal();
          return false;
        }
        if (!this._isDuplicateSessionModalActive()) {
          this._showConnectionLostAfterRequestFailure();
        }
        return false;
      }
    } finally {
      if (allowShowModal && !this._isDuplicateSessionModalActive()) {
        this._setConnectionModalReconnecting(false);
      }
      this._sessionSyncInFlight = false;
    }
  }

  async _resolvePostHubDisconnect() {
    if (
      !this._connectionMonitoringEnabled ||
      this._isDuplicateSessionModalActive() ||
      this._isPostBootHubGraceActive()
    ) {
      return;
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      this._reconnectAfterTabVisible = true;
      void this._runConnectionCheck({ allowShowModal: true });
      return;
    }
    if (this.backend.isConnected()) {
      await this._attemptSilentReconnect({ pingOnly: true });
      return;
    }
    if (this._reconnectAfterTabVisible) {
      await this._attemptSilentReconnect();
      return;
    }
    await this._runConnectionCheck({ allowShowModal: true });
  }

  _showConnectionRestoredToast() {
    if (typeof document === 'undefined') return;
    this._hideConnectionRestoredToast();
    const host = this._containerEl ?? document.body;
    const el = document.createElement('div');
    el.setAttribute('role', 'status');
    el.style.cssText = [
      'position:fixed',
      'left:50%',
      'top:max(12px, env(safe-area-inset-top))',
      'transform:translateX(-50%)',
      'z-index:999997',
      'box-sizing:border-box',
      'padding:12px 20px',
      'border-radius:12px',
      'background:rgba(23,29,10,0.96)',
      'border:1px solid rgba(130,159,56,0.65)',
      'color:#e8f0d4',
      'font-family:Montserrat,system-ui,sans-serif',
      'font-size:14px',
      'font-weight:600',
      'line-height:1.35',
      'text-align:center',
      'max-width:min(92vw, 420px)',
      'pointer-events:none',
    ].join(';');
    el.textContent = this._t('CONNECTION_RESTORED');
    host.appendChild(el);
    this._connRestoredToastEl = el;
    this._connRestoredToastTimer = window.setTimeout(() => {
      this._hideConnectionRestoredToast();
    }, CONNECTION_RESTORED_TOAST_MS);
  }

  _hideConnectionRestoredToast() {
    if (this._connRestoredToastTimer != null) {
      clearTimeout(this._connRestoredToastTimer);
      this._connRestoredToastTimer = null;
    }
    if (this._connRestoredToastEl) {
      this._connRestoredToastEl.remove();
      this._connRestoredToastEl = null;
    }
  }

  _handleForceDisconnect() {
    if (this._sessionLoadFailed || this._isDuplicateSessionModalActive()) return;
    this._showDuplicateSessionEndedModal();
  }

  /**
   * Apply InitialState payload (bootstrap + reconnect recovery).
   * @param {Record<string, unknown>} initial
   * @param {{ resumeInterruptedRound?: boolean }} [opts]
   */
  _applyServerSessionState(initial, { resumeInterruptedRound = false } = {}) {
    if (!this.slotMachine || !initial) return;
    const ui = this.slotMachine.ui;

    const quickBets = initial?.quickBets ?? initial?.QuickBets ?? initial?.betOptions ?? initial?.BetOptions;
    if (Array.isArray(quickBets) && quickBets.length) {
      gameConfig.minBet = Math.min(...quickBets);
      gameConfig.maxBet = Math.max(...quickBets);
      ui.setQuickBets([...quickBets]);
    }

    const maxOdd = Number(initial?.maxOdd ?? initial?.MaxOdd);
    if (Number.isFinite(maxOdd) && maxOdd > 0) {
      gameConfig.maxOdd = maxOdd;
    }

    const paytable = decodePaytable(initial?.paytable ?? initial?.Paytable);
    if (paytable.length) {
      this._serverPaytable = paytable;
      this._refreshInfoPaytableAmounts();
    }

    const serverCurrencyCode = this._readSessionCurrencyCode(initial);
    const launchCurrencyCode = this._isDemo ? null : this._sessionParams?.currencyCode;
    const initialCurrencyCode =
      serverCurrencyCode ?? launchCurrencyCode ?? (this._isDemo ? 'COIN' : this._currencyCode);
    const initialCurrencySymbol =
      initial.currencySymbol ?? initial.CurrencySymbol ?? undefined;
    this._setCurrency({
      currencyCode: initialCurrencyCode,
      currencyPrecision:
        initial.precision ??
        initial.Precision ??
        initial.currencyPrecision ??
        initial.CurrencyPrecision ??
        this._currencyPrecision,
      currencySymbol: initialCurrencySymbol,
    });

    const balance = this._readSessionBalance(initial);
    if (balance != null) {
      ui.setBalance(balance);
      this._updateCanvasBalance(balance);
    }

    const defaultBet = Number(
      initial?.defaultBetAmount ?? initial?.DefaultBetAmount ?? initial?.defaultBet ?? initial?.DefaultBet,
    );
    const wins = initial?.wins ?? initial?.Wins ?? initial?.additional?.wins ?? initial?.Additional?.Wins;
    const interrupted = resumeInterruptedRound ? this._resolveInterruptedRound(initial) : null;

    if (!interrupted && Number.isFinite(defaultBet)) {
      let betAmount = defaultBet;
      betAmount = Math.min(Math.max(betAmount, gameConfig.minBet), gameConfig.maxBet);
      ui.setBet(betAmount);
      this._updateCanvasBetPrompt(betAmount);
    }

    this._applySessionMatrixToReels(initial);

    if (interrupted) {
      this._resumePendingRoundFromServer(initial, interrupted.pendingWin, wins, {
        betAmount: interrupted.betAmount,
        snapshot: interrupted.snapshot,
      });
    } else if (!this._winCollectPending) {
      ui.setWin(0);
      this._updateCanvasWin(0);
    }
  }

  /** True while reels / collect / celebration / payline cycling should keep the on-screen grid. */
  _shouldPreserveVisibleGrid() {
    const rc = this.slotMachine?.reelContainer;
    return !!(
      this._spinCycleActive ||
      this._spinning ||
      this._reelsSpinning ||
      this._isWinCollectDismissActive() ||
      rc?._winLineCycleActive
    );
  }

  /**
   * Server matrix plus expanding-wild overlay (matches post-spin display grid).
   * @param {Record<string, unknown>} initial
   * @returns {number[][] | null}
   */
  _getDisplayGridFromInitial(initial) {
    const matrix = initial?.matrix ?? initial?.Matrix ?? initial?.initialBoard ?? initial?.InitialBoard;
    if (!Array.isArray(matrix)) return null;
    const baseGrid = this._normalizeMatrix(matrix);
    if (!baseGrid) return null;
    const expandingWild =
      initial?.additional?.expandingWild ??
      initial?.Additional?.ExpandingWild ??
      initial?.expandingWild ??
      initial?.ExpandingWild;
    return this._applyExpandingWild(baseGrid, expandingWild);
  }

  /**
   * @param {Record<string, unknown>} initial
   * @param {{ force?: boolean }} [opts] When true, always repaint (interrupted-round resume).
   */
  _applySessionMatrixToReels(initial, { force = false } = {}) {
    if (!this.slotMachine) return;
    if (!force && this._shouldPreserveVisibleGrid()) return;
    // After bootstrap, keep what the player sees — server ping matrix is not the last spin grid.
    if (!force && this._gameDataReady) return;
    // Never repaint reel cards while win-line animations are playing — this is the only safe
    // guard that covers the tab-switch foreground reconnect path even when force=true.
    if (this.slotMachine.reelContainer?._winLineCycleActive) return;
    if (this._shouldPreserveVisibleGrid()) return;
    const displayGrid = this._getDisplayGridFromInitial(initial);
    if (displayGrid) this.slotMachine.reelContainer.setGridInstant(displayGrid);
  }

  _isHubSessionPlayable() {
    return this._gameDataReady && !this._connectionLostUIOpen && !this._duplicateSessionEnded;
  }

  /** Verify hub + InitialState before accepting a bet (iOS resume / stale socket). */
  async _ensureSessionReadyForBet() {
    const staleAfterBackground = this._lastForegroundHiddenMs >= IOS_BACKGROUND_RESTART_MS;
    const needsRefresh =
      staleAfterBackground ||
      this._hadConnectionLoss ||
      this._reconnectAfterTabVisible ||
      !this.backend.isConnected() ||
      this.backend.isReconnecting() ||
      !!this._sessionSyncPromise;
    if (!needsRefresh) return true;
    return this._runConnectionCheck({
      restartHubFirst: staleAfterBackground,
      allowShowModal: true,
    });
  }

  _notifyBetBlocked() {
    if (this._duplicateSessionEnded || this._connectionLostUIOpen) return;
    sound.play('uiError');
    if (!this._sessionSyncInFlight && !this._sessionSyncPromise) {
      void this._runConnectionCheck({ allowShowModal: true });
    }
  }

  async _loadConnectionLostIconTexture() {
    if (this._connLostIconTex) return this._connLostIconTex;
    const paths = ['/assets/connection_lost.svg', 'assets/connection_lost.svg'];
    for (const p of paths) {
      try {
        const tex = await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(Texture.from(img));
          img.onerror = () => resolve(null);
          img.src = assetUrl(p);
        });
        if (tex && tex !== Texture.EMPTY) {
          this._connLostIconTex = tex;
          return tex;
        }
      } catch (_) {
        /* try next path */
      }
    }
    this._connLostIconTex = Texture.EMPTY;
    return this._connLostIconTex;
  }

  /** Green gradient strip used on settings / game modals (171D0A → 829F38 → 171D0A). */
  _getPanelBorderGradientTexture() {
    if (this._panelBorderGradTex) return this._panelBorderGradTex;
    const gradCanvas = document.createElement('canvas');
    gradCanvas.width = 512;
    gradCanvas.height = 2;
    const gradCtx = gradCanvas.getContext('2d');
    if (gradCtx) {
      const g = gradCtx.createLinearGradient(0, 0, gradCanvas.width, 0);
      g.addColorStop(0, '#171D0A');
      g.addColorStop(0.5, '#829F38');
      g.addColorStop(1, '#171D0A');
      gradCtx.fillStyle = g;
      gradCtx.fillRect(0, 0, gradCanvas.width, gradCanvas.height);
    }
    this._panelBorderGradTex = Texture.from(gradCanvas);
    return this._panelBorderGradTex;
  }

  /**
   * Settings-style panel fill + dark stroke + top/bottom accent borders.
   * @param {number} panelW
   * @param {number} panelH
   * @param {number} [edgeInset]
   */
  _paintConnLostPanelChrome(panelW, panelH, edgeInset = 28) {
    const edgeW = panelW - edgeInset * 2;
    const bg = this._connLostPanelBg;
    const borderBase = this._connLostBorderBase;
    const topBorder = this._connLostTopBorder;
    const bottomBorder = this._connLostBottomBorder;
    if (!bg || !borderBase || !topBorder || !bottomBorder) return;
    bg.clear();
    bg.roundRect(0, 0, panelW, panelH, 20).fill({ width: 2, color: 0x000000, alpha: 0.8 });
    borderBase.clear();
    borderBase.roundRect(0, 0, panelW, panelH, 20).stroke({ width: 4, color: 0x171d0a, alpha: 1 });
    topBorder.x = edgeInset;
    topBorder.y = -2;
    topBorder.width = edgeW;
    topBorder.height = 4;
    bottomBorder.x = edgeInset;
    bottomBorder.y = panelH - 1;
    bottomBorder.width = edgeW;
    bottomBorder.height = 4;
  }

  _createConnectionLostIconWrap(iconTex) {
    const badgeR = 30;
    const wrap = new Container();
    const badge = new Graphics();
    badge.circle(0, 0, badgeR).fill({ color: 0x191f0c, alpha: 1 });
    badge.circle(0, 0, badgeR).stroke({ width: 2, color: 0x4a5e1e, alpha: 0.55 });
    wrap.addChild(badge);
    if (iconTex && iconTex !== Texture.EMPTY) {
      const icon = new Sprite(iconTex);
      icon.anchor.set(0.5, 0.5);
      icon.width = 30;
      icon.height = 30;
      wrap.addChild(icon);
    }
    return wrap;
  }

  /**
   * @param {number} scaleX
   * @param {number} scaleY
   */
  _applyConnectionLostViewportScale(scaleX, scaleY) {
    const shell = this._connectionLostPanelRoot;
    if (!shell) return;
    if (scaleX > 0 && scaleY > 0 && Number.isFinite(scaleX) && Number.isFinite(scaleY)) {
      shell.scale.set(scaleY / scaleX, 1);
    } else {
      shell.scale.set(1, 1);
    }
  }

  async _ensureConnectionLostModal() {
    if (this._connectionLostRoot || !this.app?.stage) return;
    if (!this._connectionLostEnsurePromise) {
      this._connectionLostEnsurePromise = this._buildConnectionLostModal().finally(() => {
        this._connectionLostEnsurePromise = null;
      });
    }
    await this._connectionLostEnsurePromise;
  }

  async _buildConnectionLostModal() {
    if (this._connectionLostRoot || !this.app?.stage) return;

    const W = 1922;
    const H = 1080;
    const SCALE = CONNECTION_LOST_MODAL_SCALE_DESKTOP;
    const panelW = CONNECTION_LOST_PANEL_DESKTOP_W;
    const panelPadX = 40;
    const panelPadTop = 36;
    const panelPadBottom = 36;
    const iconTex = await this._loadConnectionLostIconTexture();
    const borderGradTex = this._getPanelBorderGradientTexture();

    const root = new Container();
    root.visible = false;
    root.eventMode = 'static';
    root.zIndex = 1000000;
    root._isConnectionLostShell = true;

    const dim = new Graphics();
    dim.rect(-8000, -8000, 16000, 16000).fill({ color: 0x000000, alpha: 0.5 });
    dim.eventMode = 'static';
    root.addChild(dim);

    const panelRoot = new Container();
    panelRoot.eventMode = 'static';
    panelRoot.position.set(W / 2, H / 2 - 47);

    const panel = new Container();
    panel.eventMode = 'static';
    panel.scale.set(SCALE);

    const panelBg = new Graphics();
    panelBg.eventMode = 'none';
    const borderBase = new Graphics();
    borderBase.eventMode = 'none';
    const borderFx = new Container();
    borderFx.eventMode = 'none';
    const topBorder = new Sprite(borderGradTex);
    topBorder.eventMode = 'none';
    const bottomBorder = new Sprite(borderGradTex);
    bottomBorder.eventMode = 'none';
    borderFx.addChild(topBorder, bottomBorder);
    panel.addChild(panelBg, borderBase, borderFx);
    this._connLostPanelBg = panelBg;
    this._connLostBorderBase = borderBase;
    this._connLostTopBorder = topBorder;
    this._connLostBottomBorder = bottomBorder;

    const iconWrap = this._createConnectionLostIconWrap(iconTex);
    panel.addChild(iconWrap);
    this._connLostIconWrap = iconWrap;

    const title = new Text({
      text: this._tu('CONNECTION_LOST_TITLE'),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 16,
        fontWeight: '700',
        lineHeight: 20,
        fill: 0xffffff,
        align: 'center',
      },
    });
    title.anchor.set(0.5, 0);
    this._connLostTitleText = title;
    panel.addChild(title);

    const body = new Text({
      text: this._t('CONNECTION_LOST_BODY'),
      style: {
        fontFamily: 'Montserrat',
        fontSize: 14,
        fontWeight: '500',
        lineHeight: 20,
        fill: 0xffffff,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: panelW - panelPadX * 2,
      },
    });
    body.anchor.set(0.5, 0);
    this._connLostBodyText = body;
    panel.addChild(body);

    const status = new Text({
      text: '',
      style: {
        fontFamily: 'Montserrat',
        fontSize: 13,
        fontWeight: '600',
        lineHeight: 18,
        fill: 0xc8d4a8,
        align: 'center',
      },
    });
    status.anchor.set(0.5, 0);
    status.visible = false;
    this._connLostStatusText = status;
    panel.addChild(status);

    const btnW = 280;
    const btnH = 44;
    const makeBtn = (labelKey, fillColor, onTap) => {
      const tap = new Container();
      tap.eventMode = 'static';
      tap.cursor = 'pointer';
      const bg = new Graphics();
      bg.roundRect(-btnW / 2, -btnH / 2, btnW, btnH, 12).fill({ color: fillColor, alpha: 1 });
      const label = new Text({
        text: this._tu(labelKey),
        style: {
          fontFamily: 'Montserrat',
          fontSize: 15,
          fontWeight: '700',
          fill: 0xffffff,
          align: 'center',
        },
      });
      label.anchor.set(0.5, 0.5);
      tap.hitArea = new Rectangle(-btnW / 2, -btnH / 2, btnW, btnH);
      tap.on('pointertap', onTap);
      tap.addChild(bg, label);
      return { tap, bg, label };
    };

    const reload = makeBtn('RELOAD_PAGE', 0x5a6e28, (e) => {
      if (!isPrimaryPointerAction(e)) return;
      e.stopPropagation();
      sound.play('uiClick');
      if (typeof window !== 'undefined') window.location.reload();
    });
    this._connLostReloadBtn = reload.tap;
    this._connLostReloadBtnBg = reload.bg;
    this._connLostReloadLabel = reload.label;
    panel.addChild(reload.tap);

    this._connLostPanel = panel;
    this._connLostPanelW = panelW;
    this._connLostPanelPadX = panelPadX;
    this._connLostPanelPadTop = panelPadTop;
    this._connLostPanelPadBottom = panelPadBottom;
    this._connLostModalScale = SCALE;
    panelRoot.addChild(panel);
    root.addChild(panelRoot);

    const stage = this.app.stage;
    stage.sortableChildren = true;
    for (const child of [...stage.children]) {
      if (child !== root && child._isConnectionLostShell) {
        child.destroy({ children: true });
      }
    }
    if (this._connectionLostRoot && this._connectionLostRoot !== root) {
      this._connectionLostRoot.destroy({ children: true });
    }
    stage.addChild(root);
    this._connectionLostRoot = root;
    this._connectionLostPanelRoot = panelRoot;
    this._layoutConnectionLostModal();
    const s = this._lastScales ?? { x: 1, y: 1 };
    this._applyConnectionLostViewportScale(s.x, s.y);
  }

  /**
   * Viewport-aware panel width, scale, and typography for the connection-lost modal.
   * @returns {{
   *   panelW: number,
   *   modalScale: number,
   *   panelPadX: number,
   *   panelPadTop: number,
   *   panelPadBottom: number,
   *   titleFs: number,
   *   bodyFs: number,
   *   btnW: number,
   *   btnH: number,
   *   btnFs: number,
   *   edgeInset: number,
   *   mobileRootLower: number,
   *   iconBadgeR: number,
   *   iconSize: number,
   *   gapIconTitle: number,
   *   gapTitleBody: number,
   *   gapBodyBtn: number,
   * }}
   */
  _computeConnectionLostLayoutMetrics() {
    const designH = 1080;
    const { width: cw, height: ch } = this._readContainerSize();
    const scaleY =
      this._lastScales?.y > 0 && Number.isFinite(this._lastScales.y)
        ? this._lastScales.y
        : ch / designH;

    const mobile = this._isPortraitMobileViewport();
    const tablet = !mobile && this._isTabletViewport();
    const compact = mobile || tablet;
    const marginCss = compact
      ? CONNECTION_LOST_MARGIN_CSS_COMPACT
      : CONNECTION_LOST_MARGIN_CSS_DESKTOP;
    const maxScreenW = Math.max(CONNECTION_LOST_PANEL_MIN_W, cw - 2 * marginCss);
    const maxScreenH = Math.max(200, ch - 2 * marginCss);

    let modalScale = CONNECTION_LOST_MODAL_SCALE_DESKTOP;
    let panelW = CONNECTION_LOST_PANEL_DESKTOP_W;
    let panelPadX = 40;
    let panelPadTop = 36;
    let panelPadBottom = 36;
    let titleFs = 16;
    let bodyFs = 14;
    let btnW = 280;
    let btnH = 44;
    let btnFs = 15;
    let edgeInset = 28;
    let mobileRootLower = 0;
    let iconBadgeR = 30;
    let iconSize = 30;
    let gapIconTitle = 14;
    let gapTitleBody = 12;
    let gapBodyBtn = 16;
    let gapBodyStatus = 8;

    if (compact) {
      modalScale = 1;
      const mobileFit = mobile ? this._getSettingsPanelMobileFit() : 1;
      const refW = mobile
        ? Math.round(SETTINGS_PANEL_MOBILE.w * mobileFit)
        : SETTINGS_PANEL_DESKTOP.w - 2 * SETTINGS_INFO_VIEW_EXTRA_SIDE_INSET;
      panelW = refW;
      const maxDesignW = maxScreenW / Math.max(1e-6, modalScale * scaleY);
      panelW = Math.min(panelW, Math.floor(maxDesignW));
      panelW = Math.max(CONNECTION_LOST_PANEL_MIN_W, panelW);

      const rw = panelW / Math.max(1, refW);
      panelPadX = Math.round(24 * rw);
      panelPadTop = mobile ? Math.round(28 * mobileFit) : 32;
      panelPadBottom = mobile ? Math.round(28 * mobileFit) : 32;
      titleFs = mobile ? Math.round(15 * mobileFit) : 16;
      bodyFs = mobile ? Math.round(13 * mobileFit) : 14;
      btnW = Math.min(280, Math.max(200, panelW - panelPadX * 2 - 8));
      btnH = mobile ? Math.round(42 * mobileFit) : 44;
      btnFs = mobile ? Math.round(14 * mobileFit) : 15;
      edgeInset = Math.round(20 * rw);
      gapIconTitle = Math.round(12 * rw);
      gapTitleBody = Math.round(10 * rw);
      gapBodyBtn = Math.round(14 * rw);
      gapBodyStatus = Math.round(6 * rw);
      iconBadgeR = Math.round(26 * rw);
      iconSize = Math.round(28 * rw);
      if (mobile) {
        mobileRootLower = Math.round(SETTINGS_PANEL_MOBILE_ROOT_OFFSET_Y * mobileFit * 0.35);
      }
    } else {
      const maxDesignW = maxScreenW / Math.max(1e-6, modalScale * scaleY);
      panelW = Math.min(CONNECTION_LOST_PANEL_DESKTOP_W, Math.floor(maxDesignW));
      panelW = Math.max(360, panelW);
      btnW = Math.min(280, Math.max(220, panelW - panelPadX * 2 - 8));
    }

    return {
      panelW,
      modalScale,
      panelPadX,
      panelPadTop,
      panelPadBottom,
      titleFs,
      bodyFs,
      btnW,
      btnH,
      btnFs,
      edgeInset,
      mobileRootLower,
      iconBadgeR,
      iconSize,
      gapIconTitle,
      gapTitleBody,
      gapBodyBtn,
      gapBodyStatus,
      maxScreenH,
      scaleY,
    };
  }

  _layoutConnectionLostModal() {
    const panel = this._connLostPanel;
    const iconWrap = this._connLostIconWrap;
    const title = this._connLostTitleText;
    const body = this._connLostBodyText;
    const status = this._connLostStatusText;
    const reloadBtn = this._connLostReloadBtn;
    const reloadBg = this._connLostReloadBtnBg;
    const reloadLabel = this._connLostReloadLabel;
    if (!panel || !iconWrap || !title || !body || !reloadBtn) return;

    const m = this._computeConnectionLostLayoutMetrics();
    let {
      panelW,
      modalScale,
      panelPadX,
      panelPadTop,
      panelPadBottom,
      titleFs,
      bodyFs,
      btnW,
      btnH,
      btnFs,
      edgeInset,
      mobileRootLower,
      iconBadgeR,
      iconSize,
      gapIconTitle,
      gapTitleBody,
      gapBodyBtn,
      gapBodyStatus,
      maxScreenH,
      scaleY,
    } = m;

    this._connLostPanelW = panelW;
    this._connLostPanelPadX = panelPadX;
    this._connLostPanelPadTop = panelPadTop;
    this._connLostPanelPadBottom = panelPadBottom;

    title.style.fontSize = titleFs;
    body.style.fontSize = bodyFs;
    body.style.wordWrapWidth = Math.max(120, panelW - panelPadX * 2);
    if (reloadLabel) reloadLabel.style.fontSize = btnFs;
    if (status) {
      status.style.fontSize = Math.max(11, bodyFs - 1);
      status.style.wordWrapWidth = Math.max(120, panelW - panelPadX * 2);
    }

    const iconBadge = iconWrap.children[0];
    if (iconBadge instanceof Graphics) {
      iconBadge.clear();
      iconBadge.circle(0, 0, iconBadgeR).fill({ color: 0x191f0c, alpha: 1 });
      iconBadge
        .circle(0, 0, iconBadgeR)
        .stroke({ width: 2, color: 0x4a5e1e, alpha: 0.55 });
    }
    const iconSpr = iconWrap.children.find((c) => c instanceof Sprite);
    if (iconSpr) {
      iconSpr.width = iconSize;
      iconSpr.height = iconSize;
    }

    const cx = panelW / 2;
    const iconTop = panelPadTop + iconBadgeR;

    iconWrap.x = cx;
    iconWrap.y = iconTop;

    title.x = cx;
    title.y = iconTop + iconBadgeR + gapIconTitle;

    body.x = cx;
    body.y = title.y + title.height + gapTitleBody;

    let cursorY = body.y + body.height + gapBodyBtn;
    if (status) {
      status.x = cx;
      status.y = cursorY;
      status.visible = false;
    }

    const paintBtn = (bg, tap, y, fillColor = 0x779133) => {
      if (!bg || !tap) return y;
      bg.clear();
      bg.roundRect(-btnW / 2, -btnH / 2, btnW, btnH, 12).fill({ color: fillColor, alpha: 1 });
      tap.hitArea = new Rectangle(-btnW / 2, -btnH / 2, btnW, btnH);
      tap.x = cx;
      tap.y = y + btnH / 2;
      return tap.y + btnH / 2;
    };

    if (reloadBtn) {
      reloadBtn.visible = true;
      cursorY = paintBtn(reloadBg, reloadBtn, cursorY, 0x5a6e28);
    }

    let panelH = cursorY + panelPadBottom;
    const maxDesignH = maxScreenH / Math.max(1e-6, modalScale * scaleY);
    if (panelH > maxDesignH) {
      const heightFit = Math.max(0.72, maxDesignH / panelH);
      modalScale *= heightFit;
    }

    this._connLostModalScale = modalScale;
    this._paintConnLostPanelChrome(panelW, panelH, edgeInset);

    panel.scale.set(modalScale);
    panel.x = (-panelW * modalScale) / 2;
    panel.y = (-panelH * modalScale) / 2;
    panel.hitArea = new Rectangle(0, 0, panelW, panelH);

    const upShift = (panelH * (modalScale - 1)) / 2;
    if (this._connectionLostPanelRoot) {
      const W = 1922;
      const H = 1080;
      this._connectionLostPanelRoot.position.set(
        W / 2,
        H / 2 - 47 - upShift + mobileRootLower,
      );
    }
  }

  _syncConnectionLostModal() {
    if (this._prioritizeDuplicateSessionModal()) {
      this._dismissOverlappingModals('connection');
      void this._showConnectionLostModal();
      return;
    }
    if (this._shouldShowConnectionLostModal()) {
      this._dismissOverlappingModals('connection');
      void this._showConnectionLostModal();
      return;
    }
    if (this._connectionLostRoot) this._connectionLostRoot.visible = false;
  }

  _showConnectionLostModal() {
    if (!this._connectionLostShowPromise) {
      this._connectionLostShowPromise = this._showConnectionLostModalImpl().finally(() => {
        this._connectionLostShowPromise = null;
      });
    }
    return this._connectionLostShowPromise;
  }

  async _showConnectionLostModalImpl() {
    if (!this._shouldShowConnectionLostModal() && !this._isDuplicateSessionModalActive()) {
      return;
    }
    this._dismissOverlappingModals('connection');
    await this._ensureConnectionLostModal();
    if (!this._shouldShowConnectionLostModal() && !this._isDuplicateSessionModalActive()) {
      return;
    }
    this._dismissOverlappingModals('connection');
    this._prioritizeDuplicateSessionModal();
    this._layoutConnectionLostModal();
    const s = this._lastScales ?? { x: 1, y: 1 };
    this._applyConnectionLostViewportScale(s.x, s.y);
    const root = this._connectionLostRoot;
    if (!root) return;
    root.visible = true;
    root.zIndex = 1000000;
    if (this.app?.stage) {
      this.app.stage.sortableChildren = true;
      this.app.stage.setChildIndex(root, this.app.stage.children.length - 1);
    }
  }

  _hideConnectionLostModal() {
    this._stopConnectionAutoRetry();
    if (this._connectionLostRoot) this._connectionLostRoot.visible = false;
  }

  _clearCanvasQuickBetStrip() {
    const group = this._canvasQbGroup;
    if (!group) return;
    group.removeChildren();
    this._canvasQbScrollWrapper = null;
    this._canvasQbBetCount = 0;
  }

  _updateQbArrowVisibility() {
    const show = this._shouldShowQuickBets();
    const arL = this._qbArrowLeft;
    const arR = this._qbArrowRight;
    if (!arL && !arR) return;
    if (!show) {
      if (arL) arL.visible = false;
      if (arR) arR.visible = false;
      return;
    }
    if (this._isPortraitStackedHud()) {
      if (arL) arL.visible = false;
      if (arR) arR.visible = false;
      return;
    }
    if (arL) arL.visible = true;
    if (arR) arR.visible = true;
  }

  _handleHubReconnecting() {
    if (!this._connectionMonitoringEnabled || this._initializing) return;
    if (this._isPostBootHubGraceActive()) return;
    if (this._sessionLoadFailed || this._isDuplicateSessionModalActive()) {
      return;
    }
    this._hadConnectionLoss = true;
    this._suspendPlayForConnectivity();
    if (this._connectionLostUIOpen) {
      this._setConnectionModalReconnecting(true);
    }
  }

  _handleHubReconnected() {
    if (!this._connectionMonitoringEnabled || this._initializing) return;
    if (this._isPostBootHubGraceActive()) return;
    if (this._sessionLoadFailed || this._isDuplicateSessionModalActive()) {
      return;
    }
    this._setConnectionModalReconnecting(false);
    void this._attemptSilentReconnect({ pingOnly: true });
  }

  _handleHubDisconnected() {
    if (!this._connectionMonitoringEnabled || this._initializing) return;
    if (this._isPostBootHubGraceActive()) return;
    if (this._sessionLoadFailed || this._isDuplicateSessionModalActive()) {
      return;
    }
    this._hadConnectionLoss = true;
    this._suspendPlayForConnectivity();
    if (typeof document !== 'undefined' && document.hidden) {
      this._reconnectAfterTabVisible = true;
      void this._runConnectionCheck({ allowShowModal: true });
      return;
    }
    this._scheduleDisconnectResolution(FORCE_DISCONNECT_WAIT_MS);
  }

  _refreshQuickBets() {
    if (!this.slotMachine) return;
    if (this._isConnectionModalBlocking()) {
      if (this._shouldShowConnectionLostModal()) {
        this._syncConnectionLostModal();
      }
      this._clearCanvasQuickBetStrip();
      this._updateQbArrowVisibility();
      return;
    }
    this._hideConnectionLostModal();
    const ui = this.slotMachine.ui;
    const bets = ui._quickBets ?? [];
    const current = ui.betValue;
    this._buildCanvasQuickBets(bets, current);
    this._updateQbArrowVisibility();
  }

  _updateCanvasBalance(balance) {
    const n = Number(balance);
    const formatted = this._formatCurrencyDigits(n);
    if (this._canvasBalanceText) {
      this._canvasBalanceText.text = formatted;
    }
    if (this._portraitBalanceValueText) {
      this._portraitBalanceValueText.text = formatted;
    }
    this._canvasBalancePanelRelayout?.();
    this._relayoutPortraitBalanceStack?.();
  }

  _updateCanvasWin(win) {
    const n = Number(win);
    const formatted = this._formatCurrencyDigits(n);
    if (this._canvasWinText) {
      this._canvasWinText.text = formatted;
    }
    if (this._portraitWinValueText) {
      this._portraitWinValueText.text = formatted;
    }
    this._canvasWinPanelRelayout?.();
    this._relayoutPortraitWinStack?.();
  }

  _stopWinRollup() {
    if (this._winRollupRaf != null) {
      cancelAnimationFrame(this._winRollupRaf);
      this._winRollupRaf = null;
    }
    this._winRollupTarget = null;
    if (this._winRollupResolve) {
      const r = this._winRollupResolve;
      this._winRollupResolve = null;
      r();
    }
  }

  /** Skip count-up; show final win on the canvas (used by collect tap). */
  _skipWinRollupToFinal() {
    if (this._winRollupRaf != null) {
      cancelAnimationFrame(this._winRollupRaf);
      this._winRollupRaf = null;
    }
    if (this._winRollupTarget != null) {
      this._updateCanvasWin(this._winRollupTarget);
      this._winRollupTarget = null;
    }
    if (this._winRollupResolve) {
      const r = this._winRollupResolve;
      this._winRollupResolve = null;
      r();
    }
  }

  /**
   * @param {number} targetWin
   * @param {number} [stakeAmount] Spin stake; when set, rollup duration follows payout multiple (win/stake).
   * @param {{ immediate?: boolean, fixedDurationSec?: number }} [opts] Celebration: `fixedDurationSec` = Spine length. Regular wins (&lt;5×): fixed `lastWinRollupDurationSec`. `immediate` = full amount at once.
   */
  _animateLastWinRollup(targetWin, stakeAmount, opts = {}) {
    this._stopWinRollup();
    const target = Math.max(0, Number(targetWin) || 0);
    this._winRollupTarget = target;

    if (opts.immediate) {
      this._updateCanvasWin(target);
      this._winRollupTarget = null;
      return Promise.resolve();
    }

    let durationSec;
    if (opts.fixedDurationSec != null && Number.isFinite(opts.fixedDurationSec)) {
      durationSec = Math.max(0.05, Number(opts.fixedDurationSec));
    } else {
      const raw = Number(gameConfig.lastWinRollupDurationSec);
      durationSec = Math.max(0.05, Number.isFinite(raw) && raw > 0 ? raw : 1);
    }
    const effectiveRate = target / durationSec;
    if (target <= 0) {
      this._updateCanvasWin(0);
      this._winRollupTarget = null;
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this._winRollupResolve = resolve;
      const startTs = performance.now();
      const tick = () => {
        const shown = Math.min(target, ((performance.now() - startTs) / 1000) * effectiveRate);
        this._updateCanvasWin(shown);
        if (shown >= target) {
          this._winRollupRaf = null;
          this._winRollupTarget = null;
          const r = this._winRollupResolve;
          this._winRollupResolve = null;
          r();
          return;
        }
        this._winRollupRaf = requestAnimationFrame(tick);
      };
      this._winRollupRaf = requestAnimationFrame(tick);
    });
  }

  _updateCanvasBetPrompt(_bet) {
    const prompt = this._tu('PLEASE_PLACE_YOUR_BETS');
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.text = prompt;
    }
    if (this._portraitTopPromptText) {
      this._portraitTopPromptText.text = this._twoLineText('PLEASE_PLACE_YOUR_BET');
    }
  }

  /** Shown under the reels while they spin (replaces "place your bets"). */
  _showGoodLuckPrompt() {
    const text = this._tu('GOOD_LUCK');
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.text = text;
    }
    if (this._portraitTopPromptText) {
      this._portraitTopPromptText.text = this._twoLineText('GOOD_LUCK');
    }
  }

  /** Single source of truth for prompt visibility across desktop and portrait-mobile top bar. */
  _setBetPromptVisible(visible) {
    this._betPromptVisible = !!visible;
    const stacked = this._isPortraitStackedHud();
    if (this._canvasBetPrompt) {
      this._canvasBetPrompt.visible = this._betPromptVisible && !stacked;
    }
    if (this._portraitTopPromptText) {
      this._portraitTopPromptText.visible = this._betPromptVisible && stacked;
    }
  }

  _toggleSettingsPanel() {
    if (!this._settingsRoot) return;
    const willOpen = !this._settingsRoot.visible;
    if (willOpen) {
      if (this._isBlockingModalOpen('settings')) return;
      this._dismissOverlappingModals('settings');
      if (this._settingsModalActiveTab === 'autoplay') {
        this._refreshAutoplaySettingsView();
      }
    }
    this._settingsRoot.visible = willOpen;
    this._setSettingsBlurEnabled(willOpen);
    sound.play(willOpen ? 'uiPopupOpen' : 'uiPopupClose');
  }

  handleAutoplayToggle(on) {
    this._autoplaying = on;
    if (on) {
      this._autoplayStartBalance = Number(this.slotMachine?.ui?.balanceValue);
      if (!Number.isFinite(this._autoplayStartBalance)) this._autoplayStartBalance = null;
      this._autoplayLastSingleWin = 0;
      this._runAutoplayLoop();
    } else {
      this._autoplayRoundsRemaining = null;
      this._autoplayStartBalance = null;
      this._autoplayLastSingleWin = 0;
      if (this._autoplayTimer != null) {
        clearTimeout(this._autoplayTimer);
        this._autoplayTimer = null;
      }
      /* handleSpin() finally still sees autoplay on and sets stop; restore start when idle. */
      if (
        this._spinButtonSprite &&
        !this._spinning &&
        !this._spinCycleActive &&
        !this._winCollectPending
      ) {
        this._setSpinButtonTexture(this._spinStartTex);
      }
    }
    this._refreshSpinAutoplayRemainLabel();
  }

  async _runAutoplayLoop() {
    while (this._autoplaying) {
      if (this._spinCycleActive || this._initializing) {
        await new Promise((r) => { this._autoplayTimer = setTimeout(r, 150); });
        continue;
      }
      /* Decrement BEFORE the spin so the spin-button counter reflects
       * "spins left after this one" — e.g. preset 10 displays 9, 8, …, 0. */
      if (
        this._autoplayRoundsRemaining != null &&
        Number.isFinite(this._autoplayRoundsRemaining)
      ) {
        this._autoplayRoundsRemaining -= 1;
      }
      if (!this._spinning && this._spinButtonSprite) {
        this._setSpinButtonTexture(this._spinStopTex);
        this._refreshSpinAutoplayRemainLabel();
      }
      await this.handleSpin();
      if (!this._autoplaying) break;
      if (this._shouldStopAutoplayForThresholds()) {
        this.handleAutoplayToggle(false);
        break;
      }
      if (
        this._autoplayRoundsRemaining != null &&
        Number.isFinite(this._autoplayRoundsRemaining)
      ) {
        this._refreshSpinAutoplayRemainLabel();
        if (this._autoplayRoundsRemaining <= 0) {
          this.handleAutoplayToggle(false);
          break;
        }
      }
      if (this._autoplaying) {
        const tier = Math.min(3, Math.max(1, this._betSpeedTier || 1));
        const delayMs = this._turboHoldActive
          ? 0
          : tier >= 3
            ? 120
            : tier >= 2
              ? 400
              : 700;
        await new Promise((r) => { this._autoplayTimer = setTimeout(r, delayMs); });
      }
    }
  }

  /**
   * Evaluate the three Stop-Autoplay thresholds set in the autoplay-settings modal.
   * A threshold of 0 is treated as disabled (matches the design’s default = 0.00 FUN).
   * Compares against the most recent spin’s win and the balance recorded when autoplay started.
   */
  _shouldStopAutoplayForThresholds() {
    const ui = this.slotMachine?.ui;
    if (!ui) return false;
    const balanceNow = Number(ui.balanceValue);
    if (!Number.isFinite(balanceNow)) return false;
    const start = Number(this._autoplayStartBalance);
    const lastWin = Number(this._autoplayLastSingleWin) || 0;

    const decBy = Number(this._autoplayStopOnBalanceDecreaseBy) || 0;
    if (decBy > 0 && Number.isFinite(start) && start - balanceNow >= decBy) return true;

    const incBy = Number(this._autoplayStopOnBalanceIncreaseBy) || 0;
    if (incBy > 0 && Number.isFinite(start) && balanceNow - start >= incBy) return true;

    const winLimit = Number(this._autoplayStopOnSingleWinExceeds) || 0;
    if (winLimit > 0 && lastWin >= winLimit) return true;

    return false;
  }

  _syncBetSpeedArrows() {
    const sprites = this._betSpeedArrowSprites;
    if (!sprites?.length) return;
    const tier = Math.min(3, Math.max(1, this._betSpeedTier || 1));
    const active = this._betSpeedActiveTex;
    const inactive = this._betSpeedInactiveTex;
    if (!active || !inactive) return;
    for (let i = 0; i < 3; i++) {
      sprites[i].texture = i < tier ? active : inactive;
    }
  }

  handleMaxBet() {
    if (!this.slotMachine) return;
    sound.play('uiClick');
    this.slotMachine.ui.setBet(gameConfig.maxBet);
    this._updateCanvasBetPrompt(gameConfig.maxBet);
    this._refreshQuickBets();
  }

  _setSpinButtonHoldProgress(progress) {
    const root = this._spinButtonRoot;
    if (!root) return;
    const t = Math.max(0, Math.min(1, progress));
    const eased = 1 - (1 - t) ** 3;
    const scale = this._spinButtonBaseScale + (this._spinHoldMaxScale - this._spinButtonBaseScale) * eased;
    root.scale.set(scale);
  }

  _resetSpinButtonHoldVisual() {
    const root = this._spinButtonRoot;
    if (!root) return;
    root.scale.set(this._spinButtonBaseScale);
  }

  _refreshSpinAutoplayRemainLabel() {
    const label = this._spinAutoplayRemainText;
    const img = this._spinButtonSprite;
    const stopTex = this._spinStopTex;
    if (!label || !img || !stopTex) return;
    const show = !!this._autoplaying && img.texture === stopTex;
    label.visible = show;
    if (!show) return;
    const r = this._autoplayRoundsRemaining;
    const mobile = this._isPortraitMobileViewport();
    const d = SPIN_AUTOPLAY_REMAIN_DESKTOP;

    if (mobile) {
      const spinW = Math.max(1, img.width);
      const k = (spinW / SPIN_BTN_REF_SIZE) * 1.14;
      label.anchor.set(0.5, 0.5);
      const nudge = SPIN_AUTOPLAY_REMAIN_MOBILE_NUDGE;
      label.x = nudge.x;
      label.y = nudge.y;
      if (r != null && Number.isFinite(r)) {
        label.text = String(r);
        label.style.fontSize = Math.round(d.fontSize * k);
      } else {
        label.text = '∞';
        label.style.fontSize = Math.round(d.fontInf * k);
      }
      return;
    }

    label.anchor.set(0.5, 0.5);
    label.x = d.x;
    label.y = d.y;
    if (r != null && Number.isFinite(r)) {
      label.text = String(r);
      label.style.fontSize = d.fontSize;
    } else {
      label.text = '∞';
      label.style.fontSize = d.fontInf;
    }
  }

  _stopSpinHoldTracking() {
    if (this._spinHoldTimer != null) {
      clearTimeout(this._spinHoldTimer);
      this._spinHoldTimer = null;
    }
    if (this._spinHoldRaf != null) {
      cancelAnimationFrame(this._spinHoldRaf);
      this._spinHoldRaf = null;
    }
    this._detachSpinHoldWindowReleaseGuard();
    const canvas = this.app?.canvas;
    const capPid = this._spinHoldPointerId;
    if (
      canvas &&
      this._spinHoldPointerCaptureActive &&
      capPid != null &&
      typeof canvas.releasePointerCapture === 'function'
    ) {
      try {
        if (typeof canvas.hasPointerCapture === 'function' && canvas.hasPointerCapture(capPid)) {
          canvas.releasePointerCapture(capPid);
        }
      } catch {
        /* ignore */
      }
    }
    this._spinHoldPointerCaptureActive = false;
    this._spinHoldInProgress = false;
    this._spinHoldStartTs = 0;
    this._spinHoldPointerId = null;
  }

  _activateTurboFromHold() {
    if (!this._spinHoldInProgress || this._spinHoldTriggeredTurbo) return;
    this._spinHoldTriggeredTurbo = true;
    this._turboHoldActive = true;
    this._resetSpinButtonHoldVisual();
    this.handleAutoplayToggle(true);
    if (!this._spinCycleActive) {
      this.handleSpin();
    }
  }

  /** Same as tapping the main stop control while reels are in motion. */
  _requestStopSpin() {
    sound.onUserGesture();
    const willTakeEffect = this._reelsSpinning && !this._stopRequested;
    this._stopRequested = true;
    this.handleAutoplayToggle(false);
    this.slotMachine?.reelContainer?.requestStop();
    if (willTakeEffect) sound.play('spinStop');
  }

  /** Same as tapping the main button when it shows collect (skip rollup + continue spin flow). */
  async _performWinCollectTap() {
    if (!this._isWinCollectDismissActive()) return;
    /* The original tap's `skipWinCelebration()` is still awaiting the tier `endXxxWin` clip;
     * `skip()` is idempotent so additional taps must NOT abort or restart it. */
    if (this._winCollectHandling) return;
    this._winCollectHandling = true;
    this._winCollectPending = false;
    try {
      sound.play('uiClick');
      this._skipWinRollupToFinal();
      await (this.slotMachine?.skipWinCelebration?.() ?? Promise.resolve());
      this._clearRoundStatus();
      if (this._resolveWinCollectUser) {
        const r = this._resolveWinCollectUser;
        this._resolveWinCollectUser = null;
        r();
      }
    } finally {
      this._winCollectHandling = false;
      if (!this._spinCycleActive && !this._shouldKeepSpinDisabled()) {
        this.slotMachine?.ui?.setSpinEnabled(true);
        this._updateCanvasBetPrompt();
        this._setBetPromptVisible(true);
      }
    }
  }

  /**
   * Canvas quick-bet chips: set bet + start spin when idle; while spinning, act as stop.
   */
  _handleCanvasQuickBet(amount) {
    if (this._isWinCollectDismissActive()) {
      void this._performWinCollectTap();
      return;
    }
    if (this._spinning) {
      this._requestStopSpin();
      return;
    }
    this.slotMachine?.ui.setBet(amount);
    this._updateCanvasBetPrompt(amount);
    if (this._autoplaying) this.handleAutoplayToggle(false);
    const now = performance.now();
    if (now < this._spinToggleLockUntilTs) {
      this._refreshQuickBets();
      return;
    }
    this._spinToggleLockUntilTs = now + this._spinToggleCooldownMs;
    void this.handleSpin();
    this._refreshQuickBets();
  }

  async handleSpin() {
    if (this._spinCycleActive || this._initializing || !this.slotMachine) return;
    if (this._connectionLostUIOpen || this._duplicateSessionEnded) return;
    sound.onUserGesture();
    const sessionOk = await this._ensureSessionReadyForBet();
    if (!sessionOk) {
      this._notifyBetBlocked();
      return;
    }
    const ui = this.slotMachine.ui;
    const amount = ui.betValue;
    const betTier = Math.min(3, Math.max(1, this._betSpeedTier || 1));
    const holdTurbo = this._turboHoldActive;
    /** Snap-to-end after min wait (tier 3 + long-press turbo). Tier 2 uses faster timeline only. */
    const useTurboSnap = holdTurbo || betTier >= 3;
    const spinStartTs = performance.now();
    const turboMinSpinMs = gameConfig.turboSnapMinSpinMs;
    const minReelSpinMs =
      Number.isFinite(gameConfig.minReelSpinAnimationMs) && gameConfig.minReelSpinAnimationMs > 0
        ? gameConfig.minReelSpinAnimationMs
        : 500;
    const tier2StaggerScale =
      Number.isFinite(gameConfig.betSpeedTier2StaggerScale) && gameConfig.betSpeedTier2StaggerScale > 0
        ? gameConfig.betSpeedTier2StaggerScale
        : 0.5;

    this._spinCycleActive = true;
    this._spinning = true;
    this._stopRequested = false;
    this._winCollectPending = false;
    this._resumeRoundSkipCollect = false;
    this._resolveWinCollectUser = null;
    this._clearRoundStatus();
    this._stopWinRollup();
    this._showGoodLuckPrompt();
    this._setBetPromptVisible(true);
    if (this._spinButtonSprite) this._setSpinButtonTexture(this._spinStopTex);
    this._refreshSpinAutoplayRemainLabel();
    this._refreshQuickBets();
    ui.setSpinEnabled(false);
    /* Close a leftover overlay only when still on screen (plays tier end* via skip). */
    if (this._isWinCelebrationVisible()) {
      await (this.slotMachine?.skipWinCelebration?.() ?? Promise.resolve());
    }
    this.slotMachine.prepareForNewRound();
    this.debug.refreshOverlay();
    /* Silence any leftover one-shots from the previous round (popup tails,
     * symbol chimes, win tier audio) before the new spin starts. Bg music
     * keeps playing — it lives on its own bus. */
    sound.stopAllSfx();
    sound.play('spinStart');
    if (typeof this.animationController?.getActiveTweenCount === 'function') {
      console.debug('[perf] activeTweens:start', this.animationController.getActiveTweenCount());
    }

    try {
      if (!this._isDemo && !this._accessToken) {
        throw new Error('Missing access token');
      }
      const rc = this.slotMachine.reelContainer;
      rc.beginPendingSpin();
      this._reelsSpinning = true;
      const spinState = await this.backend.spin(amount);
      const envelope = spinState?.arguments?.[0] ?? spinState;
      const actionResult = envelope?.result ?? envelope?.Result ?? envelope;
      const gameResult = actionResult?.result ?? actionResult?.Result ?? actionResult;
      const baseGrid = this._normalizeMatrix(
        gameResult?.matrix ?? gameResult?.Matrix ?? gameResult?.viewResult?.matrix,
      );
      const expandingWild =
        gameResult?.additional?.expandingWild ??
        gameResult?.Additional?.ExpandingWild ??
        gameResult?.viewResult?.additional?.expandingWild;
      /** Per-reel landing thud + wild/scatter/coin chimes. Natural stop (regular,
       *  tier 2, turbo snap): `reelDefaultStop` — one per reel when staggered, one
       *  total when reels land together. Hand stop: `reelStopper` (single thud).
       *
       *  When quick-stopped, symbol chimes are suppressed. Win tier audio still
       *  fires later, after the reels fully settle. */
      let reelLandSoundPlayed = false;
      const onReelLanded = (col) => {
        /* Read _stopRequested when each reel lands — not when the API returns. */
        if (this._stopRequested) {
          if (!reelLandSoundPlayed) {
            reelLandSoundPlayed = true;
            sound.play('reelStopper', { allowOverlap: true });
          }
        } else if (useTurboSnap) {
          if (!reelLandSoundPlayed) {
            reelLandSoundPlayed = true;
            sound.play('reelDefaultStop', { allowOverlap: true });
          }
        } else {
          sound.play('reelDefaultStop', { allowOverlap: true });
        }
        if (this._stopRequested) return;
        const reelSymbols = baseGrid && baseGrid[col];
        if (!reelSymbols) return;
        let hasWild = false;
        let hasScatter = false;
        let hasCoin = false;
        for (let r = 0; r < reelSymbols.length; r++) {
          const sid = reelSymbols[r];
          if (sid === 9) hasWild = true;
          else if (sid === 10) hasCoin = true;
          else if (sid === 11) hasScatter = true;
        }
        /* Per-key throttle in soundManager dedupes adjacent reels landing
         * within ~40 ms, so back-to-back wilds across reels still feel clean. */
        if (hasWild) sound.play('wild');
        if (hasScatter) sound.play('scatter');
        if (hasCoin) sound.play('coin');
      };
      if (baseGrid) {
        if (this._stopRequested) {
          /** Stop was tapped during the API wait — never snap the grid without motion (see {@link gameConfig.minReelSpinAnimationMs}). */
          rc.clearStopRequest();
          const reelAnimStart = performance.now();
          const spinPromise = rc.spinToGrid(baseGrid, { onReelLanded });
          const waitMs = Math.max(0, minReelSpinMs - (performance.now() - reelAnimStart));
          if (waitMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, waitMs));
          }
          rc.requestStop();
          await spinPromise;
        } else if (useTurboSnap) {
          const spinPromise = rc.spinToGrid(baseGrid, { onReelLanded });
          const elapsed = performance.now() - spinStartTs;
          const waitMs = Math.max(turboMinSpinMs, minReelSpinMs) - elapsed;
          if (waitMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, waitMs));
          }
          rc.requestStop();
          await spinPromise;
        } else if (betTier === 2) {
          await rc.spinToGrid(baseGrid, { reelStopStaggerScale: tier2StaggerScale, onReelLanded });
        } else {
          await rc.spinToGrid(baseGrid, { onReelLanded });
        }
      } else {
        rc.setAllVisibleSymbolsSpinning(false);
      }
      this._reelsSpinning = false;
      this._setBetPromptVisible(false);
      /** Apply expanding wild on the strips immediately so the main button can flip to SPIN/COLLECT
       *  as soon as reels lock — no post-reel tier hold or wild tween wait. */
      if (baseGrid && Array.isArray(expandingWild) && expandingWild.length) {
        for (let reel = 0; reel < Math.min(expandingWild.length, rc.reels.length); reel++) {
          const wildSymbolId = Number(expandingWild[reel]);
          if (!Number.isFinite(wildSymbolId) || wildSymbolId <= 0) continue;
          for (let row = 0; row < gameConfig.rowCount; row++) {
            rc.reels[reel].setVisibleRowSymbolId(row, wildSymbolId);
          }
        }
      }
      const grid = this._applyExpandingWild(baseGrid, expandingWild);
      if (baseGrid && Array.isArray(expandingWild) && expandingWild.length) {
        // Ensure win popups/celebrations begin only after expanding-wild visuals finish.
        await rc.animateExpandingWildColumns(expandingWild);
      }
      const newBalance =
        this._readBalanceFromRecord(actionResult) ??
        this._readBalanceFromRecord(envelope) ??
        this._readBalanceFromRecord(spinState);
      if (newBalance == null) {
        throw new Error('Spin result missing balance');
      }
      ui.setBalance(newBalance);
      this._updateCanvasBalance(newBalance);
      const totalWin = Number(actionResult?.winAmount ?? actionResult?.WinAmount) || 0;
      this._autoplayLastSingleWin = totalWin;
      const wins =
        gameResult?.additional?.wins ??
        gameResult?.Additional?.Wins ??
        gameResult?.viewResult?.additional?.wins;
      const hits = this._mapBackendWinsToHits(wins, grid);
      let rollupPromise = null;
      if (totalWin > 0) {
        ui.setWin(totalWin, hits);
        const winMult = amount > 0 ? totalWin / amount : 0;
        const showCelebration =
          amount > 0 && winMult >= winCelebrationMinMultiple();
        if (!showCelebration) {
          sound.play('win');
        }
        /** Sync bottom-bar count-up with Spine celebration length. */
        let celebrationSeqSec = 0;
        let rollupOpts = {};
        if (showCelebration && this.spineAssets) {
          const seqSec = getCelebrationSequenceDurationSec(
            this.spineAssets,
            winMult,
            false,
          );
          celebrationSeqSec = seqSec;
          rollupOpts = { fixedDurationSec: seqSec };
        }
        this._spinning = false;
        if (this._spinButtonSprite) this._setSpinButtonTexture(this._spinCollectTex ?? this._spinStartTex);
        this._refreshSpinAutoplayRemainLabel();
        const userCollectPromise = new Promise((resolve) => {
          this._resolveWinCollectUser = resolve;
        });
        this._winCollectPending = true;
        this._refreshQuickBets();
        const roundStatusBase = {
          pendingWin: totalWin,
          betAmount: amount,
          winMult,
          showCelebration,
        };
        this._persistRoundStatus({
          ...roundStatusBase,
          phase: showCelebration ? 'celebrating' : 'rollup_collect',
          celebrationStartedAt: showCelebration ? Date.now() : undefined,
        });
        rollupPromise = this._animateLastWinRollup(totalWin, amount, rollupOpts);
        if (hits.length) {
          const hitsSorted = [...hits].sort(
            (a, b) => (a.paylineIndex ?? Number.MAX_SAFE_INTEGER) - (b.paylineIndex ?? Number.MAX_SAFE_INTEGER),
          );
          this.slotMachine.beginPostSpinWin(hitsSorted, totalWin);
        }
        if (showCelebration) {
          // Temporary QA delay to verify post-wild timing before popup celebration starts.
          // await new Promise((resolve) => setTimeout(resolve, 500));
          this.slotMachine.playWinCelebration(winMult, false, totalWin, () => {
            this._persistRoundStatus({
              ...roundStatusBase,
              phase: 'collect_hold',
            });
          });
        }
        if (showCelebration && this._autoplaying) {
          const delayMs = Math.max(0, Math.round(celebrationSeqSec * 1000)) + 1000;
          void (async () => {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
            if (this._isWinCollectDismissActive()) {
              await this._performWinCollectTap();
            }
          })();
        }
        if (showCelebration) {
          /* Celebration tiers must close only through explicit collect/end animation flow.
           * Never allow timer/rollup completion to auto-advance the round. */
          await userCollectPromise;
        } else {
          await Promise.race([rollupPromise ?? Promise.resolve(), userCollectPromise]);
        }
        this._resolveWinCollectUser = null;
        /* Do NOT auto-fire skipWinCelebration here: celebration close is only via
         * explicit collect/end animation flow:
         *   • user collect tap → `_performWinCollectTap` → `skipWinCelebration`
         *   • next spin start → `handleSpin` awaits `skipWinCelebration` before `prepareForNewRound` */
        if (!showCelebration && useTurboSnap && !this._stopRequested) {
          await new Promise((resolve) => setTimeout(resolve, 900));
        } else if (!showCelebration && betTier === 2 && !this._stopRequested) {
          await new Promise((resolve) =>
            setTimeout(resolve, gameConfig.betSpeedTier2PostWinPadMs),
          );
        }
        this._refreshQuickBets();
      } else {
        this._spinning = false;
        if (this._spinButtonSprite) {
          this._setSpinButtonTexture(this._autoplaying ? this._spinStopTex : this._spinStartTex);
        }
        this._refreshSpinAutoplayRemainLabel();
        this._refreshQuickBets();
        ui.setSpinEnabled(true);
      }
    } catch (err) {
      console.error('Spin failed', err);
      this.slotMachine?.reelContainer.setAllVisibleSymbolsSpinning(false);
      if (this._isDuplicateSessionError(err)) {
        this._showDuplicateSessionEndedModal();
      } else if (
        err?.actionStatus === ActionStatus.Unauthorized ||
        err?.actionStatus === ActionStatus.GameUnavailable
      ) {
        this._showConnectionLostAfterRequestFailure();
        sound.play('uiError');
      } else if (
        err?.actionStatus === ActionStatus.Rejected ||
        err?.actionStatus === ActionStatus.InsufficientBalance ||
        err?.actionStatus === ActionStatus.ServerFailure
      ) {
        // Refusals are not connection loss — refresh wallet/session and keep playable.
        sound.play('uiError');
        await this._runConnectionCheck({
          allowShowModal: false,
          bypassGridPreserve: true,
        });
      } else {
        const sessionOk = await this._runConnectionCheck({
          allowShowModal: true,
          bypassGridPreserve: true,
        });
        if (sessionOk) sound.play('uiError');
      }
    } finally {
      this._resolveWinCollectUser = null;
      this._winCollectPending = false;
      this.debug.refreshOverlay();
      if (typeof this.animationController?.getActiveTweenCount === 'function') {
        console.debug('[perf] activeTweens:end', this.animationController.getActiveTweenCount());
      }
      this._spinCycleActive = false;
      this._spinning = false;
      this._reelsSpinning = false;
      if (this._spinButtonSprite) {
        if (this._autoplaying) {
          this._setSpinButtonTexture(this._spinStopTex);
        } else {
          this._setSpinButtonTexture(this._spinStartTex);
        }
      }
      this._refreshSpinAutoplayRemainLabel();
      this._refreshQuickBets();
      if (!this._shouldKeepSpinDisabled()) {
        ui.setSpinEnabled(true);
        this._updateCanvasBetPrompt();
        this._setBetPromptVisible(true);
      }
    }
  }

  async _loadInitialState() {
    if (!this.slotMachine) return;
    this._initializing = true;
    this._sessionLoadFailed = false;
    this._connectionLostUIOpen = false;
    this._duplicateSessionEnded = false;
    this._connectionMonitoringEnabled = false;
    this._forceDisconnectReceived = false;
    this._reconnectAfterTabVisible = false;
    this._clearDisconnectResolveTimer();
    this._hadConnectionLoss = false;
    this._connModalMode = 'lost';
    this._connGraceMs = CONNECTION_LOST_GRACE_MS;
    this._clearConnectionLostGraceTimer();
    this._hideConnectionLostModal();
    this.slotMachine.ui.setSpinEnabled(false);
    try {
      const session = this._resolveSessionAuth();
      const { isDemo, oneTimeToken } = session;
      this._isDemo = isDemo;
      this._oneTimeToken = oneTimeToken;
      this._sessionParams = session;
      if (!session.gameName || !session.partnerId) {
        throw new Error('GameName and PartnerId are required in URL params');
      }
      if (!isDemo && !oneTimeToken) {
        throw new Error('OneTimeToken is required when not in demo mode');
      }
      if (!isDemo && oneTimeToken) {
        this._accessToken = await exchangeOneTimeToken(oneTimeToken);
      }
      if (typeof document !== 'undefined' && session.culture) {
        document.documentElement.lang = session.culture.split('-')[0] || 'en';
      }
      await loadTranslations({
        accessToken: this._accessToken,
        gameName: session.gameName,
        culture: session.culture,
        translationStore: this.translation,
      });
      this._refreshLocalizedText();
      this._sessionInstanceId = claimLatestSessionInstance();
      await this.backend.connect(this._accessToken);
      this._hubConnected = true;
      const state = await this.backend.startGameAction({
        isDemo,
        gameName: session.gameName,
        partnerId: session.partnerId,
      });
      const initial = this._unwrapInitialPayload(state) ?? state;
      const ui = this.slotMachine.ui;
      if (this._readSessionBalance(initial) == null) throw new Error('StartGameAction missing balance');
      const defaultBetAmount = Number(
        initial?.defaultBetAmount ?? initial?.DefaultBetAmount ?? initial?.defaultBet ?? initial?.DefaultBet,
      );
      if (!Number.isFinite(defaultBetAmount)) {
        throw new Error('StartGameAction missing defaultBet');
      }
      const quickBets =
        initial?.quickBets ?? initial?.QuickBets ?? initial?.betOptions ?? initial?.BetOptions;
      if (!Array.isArray(quickBets) || !quickBets.length) {
        throw new Error('StartGameAction missing betOptions');
      }
      const matrix =
        initial?.matrix ?? initial?.Matrix ?? initial?.initialBoard ?? initial?.InitialBoard;
      if (!Array.isArray(matrix)) throw new Error('StartGameAction missing initialBoard');

      this._applyServerSessionState(initial, { resumeInterruptedRound: true });
      this._refreshQuickBets();

      const initialGrid = this._normalizeMatrix(matrix);
      if (!initialGrid) throw new Error('InitialState matrix shape mismatch');
      this._gameDataReady = true;
      this._hubConnected = true;
      this._clearConnectionLostGraceTimer();
      this._clearDisconnectResolveTimer();
      this._connectionLostUIOpen = false;
      this._hideConnectionLostModal();
      this._armPostBootHubGrace();
      this._attachHubConnectionHandlers();
      this._connectionMonitoringEnabled = true;
      this._installHubConnectionMonitor();
      this._startIdleReloadWatch();
    } catch (err) {
      console.error('StartGameAction failed', err);
      relinquishSessionInstance(this._sessionInstanceId);
      this._gameDataReady = false;
      this._hubConnected = false;
      this._connectionMonitoringEnabled = false;
      this._sessionLoadFailed = true;
      this._connectionLostUIOpen = true;
      this._connModalMode = 'lost';
      this._refreshConnectionLostModalCopy();
      this._clearConnectionLostGraceTimer();
    }
    this._initializing = false;
    this._refreshQuickBets();
    this._updateQbArrowVisibility();
    this.slotMachine.ui.setSpinEnabled(
      this._winCollectPending ? false : this._shouldShowQuickBets(),
    );
  }

  _resolveSessionAuth() {
    const raw = getParamsFromUrl(window.location.search);
    const mode = raw.Mode || null;
    const isDemo = mode === 'demo';
    const partnerId = raw.PartnerId?.trim() || null;
    const gameName = raw.GameName?.trim() || null;
    const culture = (raw.Culture ?? raw.culture ?? 'en').trim() || 'en';
    const currencyCode = isDemo
      ? null
      : (raw.Currency ?? raw.currency ?? raw.CurrencyCode ?? raw.currencyCode)?.trim().toUpperCase() ||
        null;
    let oneTimeToken = raw.OneTimeToken?.trim() || null;
    if (!isDemo && !oneTimeToken) {
      const entered = window.prompt('Enter OneTimeToken');
      oneTimeToken = entered?.trim() || null;
    }
    return {
      isDemo,
      oneTimeToken,
      gameName,
      partnerId,
      culture,
      currencyCode,
    };
  }

  _syncLobbyUrlFromSearch() {
    if (typeof window === 'undefined') return;
    const raw = getParamsFromUrl(window.location.search);
    this._lobbyUrl = String(raw.LobbyUrl ?? raw.lobbyUrl ?? raw.lobbyURL ?? '').trim();
  }

  _startIdleReloadWatch() {
    if (this._idleReloadDispose || typeof window === 'undefined') return;
    this._idleReloadDispose = installGameIdleReload({
      onIdle: () => {
        this._idleReloadDispose?.();
        this._idleReloadDispose = null;
        this._showIdleTimeoutErrorAndReload();
      },
    });
  }

  _showIdleTimeoutErrorAndReload() {
    if (this._idleTimeoutOverlay || typeof document === 'undefined') return;
    const host = this._containerEl ?? document.body;
    const el = document.createElement('div');
    el.setAttribute('role', 'alert');
    el.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:999999',
      'display:flex',
      'flex-direction:column',
      'align-items:center',
      'justify-content:center',
      'box-sizing:border-box',
      'padding:24px',
      'text-align:center',
      'background:rgba(9,11,16,0.94)',
      'color:#f1f5f9',
      'font-family:Montserrat,system-ui,sans-serif',
      'font-size:16px',
      'line-height:1.5',
    ].join(';');
    const p = document.createElement('p');
    p.style.margin = '0';
    p.style.maxWidth = '28rem';
    p.textContent = this._t('IDLE_TIMEOUT_RELOAD');
    el.appendChild(p);
    host.appendChild(el);
    this._idleTimeoutOverlay = el;
    window.setTimeout(() => {
      window.location.reload();
    }, 2500);
  }

  _ensureTokenForCurrentMode() {
    if (this._isDemo) return null;
    if (this._oneTimeToken) return this._oneTimeToken;
    const entered = window.prompt('Enter OneTimeToken');
    const token = entered?.trim() || null;
    if (token) {
      this._oneTimeToken = token;
      window.localStorage.setItem('shiningCrownOneTimeToken', token);
    }
    return token;
  }

  /** @returns {'mobile' | 'tablet' | 'desktop'} */
  _getLoadingViewportProfile(cw, ch) {
    const width = cw ?? this._readContainerSize().width;
    const height = ch ?? this._readContainerSize().height;
    const portrait = height > width;
    if (Math.min(width, height) <= 760) return 'mobile';
    if (portrait || this._isTabletViewport()) return 'tablet';
    return 'desktop';
  }

  _getLoadingCanvasTransform(cw, ch) {
    const width = cw ?? this._readContainerSize().width;
    const height = ch ?? this._readContainerSize().height;
    const scaleX = width / LOADING_DESIGN_W;
    const scaleY = height / LOADING_DESIGN_H;
    const u = Math.max(scaleX, scaleY);
    return {
      u,
      tx: (width - LOADING_DESIGN_W * u) / 2,
      ty: (height - LOADING_DESIGN_H * u) / 2,
    };
  }

  _loadingCssToDesignX(cssX, transform) {
    return (cssX - transform.tx) / transform.u;
  }

  _loadingCssToDesignY(cssY, transform) {
    return (cssY - transform.ty) / transform.u;
  }

  _pickLoadingBackgroundTexture(cw, ch) {
    const profile = this._getLoadingViewportProfile(cw, ch);
    if (profile === 'mobile') {
      return this._loadingBgTexMobile || Texture.EMPTY;
    }
    if (profile === 'tablet') {
      return this._loadingBgTexTablet || Texture.EMPTY;
    }
    return this._loadingBgTexDesktop || Texture.EMPTY;
  }

  _pickLoadingStudioTexture(cw, ch) {
    const profile = this._getLoadingViewportProfile(cw, ch);
    if (profile === 'mobile') {
      return this._loadingStudioTexMobile || this._loadingStudioTexDesktop || Texture.EMPTY;
    }
    return this._loadingStudioTexDesktop || Texture.EMPTY;
  }

  /** Cover-fit background to the visible viewport region (matches portrait/mobile asset aspect). */
  _layoutLoadingBackgroundCover(sprite, tex, cw, ch) {
    if (!sprite || !tex?.width) return;
    const transform = this._getLoadingCanvasTransform(cw, ch);
    const visibleW = cw / transform.u;
    const visibleH = ch / transform.u;
    const visibleAspect = visibleW / visibleH;
    const bleed = LOADING_BG_BLEED;
    const tw = tex.width;
    const th = tex.height;
    const texAspect = tw / th;
    let bgW;
    let bgH;
    if (texAspect > visibleAspect) {
      bgH = visibleH + bleed * 2;
      bgW = bgH * texAspect;
    } else {
      bgW = visibleW + bleed * 2;
      bgH = bgW / texAspect;
    }
    sprite.texture = tex;
    sprite.width = bgW;
    sprite.height = bgH;
    sprite.x = LOADING_DESIGN_W / 2 - bgW / 2;
    sprite.y = LOADING_DESIGN_H / 2 - bgH / 2;
  }

  _layoutLoadingScreen() {
    const studio = this._loadingStudioSprite;
    const gameLogo = this._loadingGameLogoSprite;
    const barTrack = this._loadingBarTrack;
    if (!studio || !gameLogo || !barTrack) return;

    const { width: cw, height: ch } = this._readContainerSize();
    const transform = this._getLoadingCanvasTransform(cw, ch);

    const tex = this._pickLoadingBackgroundTexture(cw, ch);
    if (this._loadingBgSprite && tex?.width > 0) {
      this._layoutLoadingBackgroundCover(this._loadingBgSprite, tex, cw, ch);
    }

    const studioTex = this._pickLoadingStudioTexture(cw, ch);
    if (studioTex?.width > 0 && studio.texture !== studioTex) {
      studio.texture = studioTex;
    }

    /* Unified ratio-based layout matching the HD design spec (1920x1080):
       - 9tails group: 280 x 76, top 110.12
       - Game logo:    708 x 408, top 323.94
       - Bar:          420 x 22,  top 801
       Every element is a fixed fraction of the game logo's height, so the
       stack scales uniformly across viewports and the elements never
       overlap. The CSS sizes act as absolute maximums on large screens. */
    const MAX_GAME_H = 408;
    const MAX_GAME_W = 708;
    const MAX_STUDIO_H = 76;
    const MAX_STUDIO_W = 280;
    const MAX_BAR_W = 420;
    const MAX_BAR_H = 22;

    const STUDIO_H_R = MAX_STUDIO_H / MAX_GAME_H; // 0.1863
    /** Visual scale for 9tails only; stack slot/spacing stays at HD design size. */
    const STUDIO_DISPLAY_SCALE = 1.6;
    const STUDIO_DISPLAY_SCALE_MOBILE = 2.3;
    /** Nudge 9tails down on tablet/desktop; nudge up on mobile phones. */
    const STUDIO_Y_OFFSET_R = 0.13;
    const STUDIO_Y_OFFSET_R_MOBILE = -0.08;
    /* Tighter gaps than the raw HD spec so the 9tails logo and the loader
       bar sit close to the main logo (vertically near the center). */
    const GAP1_R = 0.08; // studio bottom -> game logo top, relative to game H
    const GAP2_R = 0.04; // game logo bottom -> bar top, relative to game H
    const BAR_H_R = MAX_BAR_H / MAX_GAME_H; // 0.0539
    const BAR_W_R = MAX_BAR_W / MAX_GAME_W; // 0.5932
    const STACK_TOTAL = STUDIO_H_R + GAP1_R + 1.0 + GAP2_R + BAR_H_R;

    /* Padding around the stack (fraction of viewport) - keeps the stack
       compact and visually close to the viewport center. */
    const HORIZ_PAD = 0.86;
    const VERT_PAD = 0.78;

    /* Stack scale uses the HD design slot (280×76), not the PNG aspect ratio —
       otherwise a wider 9tails asset shrinks the entire loading screen. */
    const STUDIO_DESIGN_ASPECT = MAX_STUDIO_W / MAX_STUDIO_H;
    const studioTexAspect =
      studio.texture?.width > 0 && studio.texture?.height > 0
        ? studio.texture.width / studio.texture.height
        : STUDIO_DESIGN_ASPECT;
    const gameAspect =
      gameLogo.texture?.width > 0 && gameLogo.texture?.height > 0
        ? gameLogo.texture.width / gameLogo.texture.height
        : MAX_GAME_W / MAX_GAME_H;

    const cwAvail = cw * HORIZ_PAD;
    const chAvail = ch * VERT_PAD;

    /* Solve for a single game-logo height that satisfies every viewport
       constraint AND every CSS max-size cap. Picking the smallest value
       guarantees uniform scaling, no overlap, and that no element ever
       exceeds its HD design size on large screens. */
    const gameH_css = Math.max(
      1,
      Math.min(
        chAvail / STACK_TOTAL,
        cwAvail / gameAspect,
        cwAvail / (STUDIO_H_R * STUDIO_DESIGN_ASPECT),
        cwAvail / (BAR_W_R * gameAspect),
        MAX_GAME_H,
        MAX_GAME_W / gameAspect,
        MAX_STUDIO_H / STUDIO_H_R,
        MAX_STUDIO_W / (STUDIO_H_R * STUDIO_DESIGN_ASPECT),
        MAX_BAR_W / (BAR_W_R * gameAspect),
        MAX_BAR_H / BAR_H_R,
      ),
    );

    const studioSlotH_css = gameH_css * STUDIO_H_R;
    const studioSlotW_css = gameH_css * (MAX_STUDIO_W / MAX_GAME_H);
    const isLoadingMobile = this._getLoadingViewportProfile(cw, ch) === 'mobile';
    const studioDisplayScale = isLoadingMobile ? STUDIO_DISPLAY_SCALE_MOBILE : STUDIO_DISPLAY_SCALE;
    const studioMaxW_css = studioSlotW_css * studioDisplayScale;
    const studioMaxH_css = studioSlotH_css * studioDisplayScale;
    let studioW_css;
    let studioH_css;
    if (studioTexAspect > studioMaxW_css / studioMaxH_css) {
      studioW_css = studioMaxW_css;
      studioH_css = studioMaxW_css / studioTexAspect;
    } else {
      studioH_css = studioMaxH_css;
      studioW_css = studioMaxH_css * studioTexAspect;
    }
    const gameW_css = gameH_css * gameAspect;
    const barW_css = gameW_css * BAR_W_R;
    const barH_css = gameH_css * BAR_H_R;

    const totalStackH_css = gameH_css * STACK_TOTAL;
    const stackTop_css = (ch - totalStackH_css) / 2;
    const studioY_css = stackTop_css;
    const gameTopY_css = studioY_css + studioSlotH_css + gameH_css * GAP1_R;
    const gameCenterY_css = gameTopY_css + gameH_css / 2;
    const barY_css = gameTopY_css + gameH_css + gameH_css * GAP2_R;

    const u = transform.u || 1;
    const centerX_design = this._loadingCssToDesignX(cw / 2, transform);
    const studioYOffsetR = isLoadingMobile ? STUDIO_Y_OFFSET_R_MOBILE : STUDIO_Y_OFFSET_R;

    studio.anchor.set(0.5, 0);
    studio.height = studioH_css / u;
    studio.width = studioW_css / u;
    studio.x = centerX_design;
    studio.y = this._loadingCssToDesignY(
      studioY_css + (studioSlotH_css - studioH_css) / 2 + gameH_css * studioYOffsetR,
      transform,
    );

    gameLogo.anchor.set(0.5, 0.5);
    gameLogo.width = gameW_css / u;
    gameLogo.height = gameH_css / u;
    gameLogo.x = centerX_design;
    gameLogo.y = this._loadingCssToDesignY(gameCenterY_css, transform);

    this._loadingBarW = barW_css / u;
    this._loadingBarH = barH_css / u;
    const barRadius = this._loadingBarH / 2;

    barTrack.clear();
    barTrack
      .roundRect(0, 0, this._loadingBarW, this._loadingBarH, barRadius)
      .fill({ color: 0x0a0c10, alpha: 0.55 });
    barTrack
      .roundRect(0, 0, this._loadingBarW, this._loadingBarH, barRadius)
      .stroke({ color: 0xffffff, width: 1.5, alpha: 1 });
    barTrack.x = this._loadingCssToDesignX((cw - barW_css) / 2, transform);
    barTrack.y = this._loadingCssToDesignY(barY_css, transform);

    this._redrawLoadingBarFill();
  }

  _redrawLoadingBarFill() {
    const fill = this._loadingBarFill;
    const track = this._loadingBarTrack;
    if (!fill || !track) return;
    fill.clear();
    const barW = this._loadingBarW || LOADING_BAR_W_MAX;
    const barH = this._loadingBarH || LOADING_BAR_H_MAX;
    const inset = Math.max(1.5, barH * 0.12);
    const innerW = barW - inset * 2;
    const innerH = barH - inset * 2;
    const progressW = Math.max(0, innerW * Math.min(1, this._loadingProgress));
    if (progressW <= 0) return;
    fill
      .roundRect(track.x + inset, track.y + inset, progressW, innerH, Math.max(0, barH / 2 - inset))
      .fill({
        color: 0xffffff,
        alpha: 1,
      });
  }

  _syncLoadingProgressTick(ticker) {
    if (!this._loadingRoot) return;
    const elapsedMs = performance.now() - (this._loadingProgressStartedAt ?? performance.now());
    const t = Math.min(1, elapsedMs / LOADING_MIN_DISPLAY_MS);
    const eased = 0.96 * (1 - (1 - t) ** 1.6);
    if (Math.abs(eased - this._loadingProgress) > 0.002) {
      this._loadingProgress = eased;
      this._redrawLoadingBarFill();
    }
  }

  async _showLoading() {
    if (!this.app?.stage || this._loadingRoot) return;

    const loadTex = async (path) => {
      try {
        return await Assets.load(path);
      } catch {
        return Texture.EMPTY;
      }
    };

    if (!this._loadingBgTexDesktop) {
      this._loadingBgTexDesktop = await loadTex('/assets/loading/loading_background.png');
    }
    if (!this._loadingBgTexTablet) {
      this._loadingBgTexTablet = await loadTex('/assets/loading/loading_background_tablet.png');
    }
    if (!this._loadingBgTexMobile) {
      this._loadingBgTexMobile = await loadTex('/assets/loading/loading_background_mobile.png');
    }
    if (!this._loadingStudioTexDesktop) {
      this._loadingStudioTexDesktop = await loadTex('/assets/loading/9tails_logo.png');
    }
    if (!this._loadingStudioTexMobile) {
      this._loadingStudioTexMobile = await loadTex('/assets/loading/9tails_logo_mobile.png');
    }
    let gameLogoTex = Texture.EMPTY;
    for (const p of ['/assets/Logo0.png', 'assets/Logo0.png', '/Logo0.png']) {
      try {
        gameLogoTex = await Assets.load(p);
        if (gameLogoTex?.width > 0) break;
      } catch {
        /* try next candidate path */
      }
    }
    if (!gameLogoTex?.width) {
      gameLogoTex = await loadTex('/assets/info/logo_mini.png');
    }

    const { width: cw, height: ch } = this._readContainerSize();
    const studioTex = this._pickLoadingStudioTexture(cw, ch);
    const bgTex = this._pickLoadingBackgroundTexture(cw, ch);

    const root = new Container();
    root.eventMode = 'static';
    root.zIndex = 999999;
    root.scale.set(1, 1);
    root.x = 0;

    const bg = new Sprite(bgTex);
    this._layoutLoadingBackgroundCover(bg, bgTex, cw, ch);
    bg.filters = [
      new BlurFilter({
        strength: LOADING_BLUR_STRENGTH,
        strengthX: LOADING_BLUR_STRENGTH,
        strengthY: LOADING_BLUR_STRENGTH,
      }),
    ];
    bg.eventMode = 'none';

    const content = new Container();
    content.eventMode = 'none';

    const studio = new Sprite(studioTex);
    studio.roundPixels = true;
    studio.eventMode = 'none';

    const gameLogo = new Sprite(gameLogoTex);
    gameLogo.roundPixels = true;
    gameLogo.eventMode = 'none';

    const barTrack = new Graphics();
    barTrack.eventMode = 'none';

    const barFill = new Graphics();
    barFill.eventMode = 'none';

    content.addChild(studio, gameLogo, barTrack, barFill);
    root.addChild(bg, content);

    this.app.stage.sortableChildren = true;
    this.app.stage.addChild(root);

    this._loadingRoot = root;
    this._loadingContent = content;
    this._loadingBgSprite = bg;
    this._loadingStudioSprite = studio;
    this._loadingGameLogoSprite = gameLogo;
    this._loadingBarTrack = barTrack;
    this._loadingBarFill = barFill;
    this._loadingProgress = 0;
    this._loadingProgressStartedAt = performance.now();
    this._loadingShownAt = this._loadingProgressStartedAt;
    this._loadingProgressTick = () => this._syncLoadingProgressTick();
    this.app.ticker.add(this._loadingProgressTick);

    this._layoutLoadingScreen();
    this._fitRendererToContainer();
  }

  async _ensureLoadingMinDisplayElapsed() {
    if (this._loadingShownAt == null) return;
    const remain = LOADING_MIN_DISPLAY_MS - (performance.now() - this._loadingShownAt);
    if (remain > 0) {
      await new Promise((resolve) => setTimeout(resolve, remain));
    }
  }

  _hideLoading() {
    if (this._loadingProgressTick && this.app?.ticker) {
      this.app.ticker.remove(this._loadingProgressTick);
    }
    this._loadingProgressTick = null;
    this._loadingRoot?.destroy({ children: true });
    this._loadingRoot = null;
    this._loadingContent = null;
    this._loadingBgSprite = null;
    this._loadingStudioSprite = null;
    this._loadingGameLogoSprite = null;
    this._loadingBarTrack = null;
    this._loadingBarFill = null;
    this._loadingProgress = 0;
    this._loadingShownAt = null;
    this._fitRendererToContainer();
    if (this._isConnectionModalBlocking()) {
      this._syncConnectionLostModal();
    }
  }

  _hideSettingsPanel() {
    const root = this._settingsRoot;
    const wasOpen = !!(root && root.visible);
    if (root) root.visible = false;
    this._setSettingsBlurEnabled(false);
    if (wasOpen) sound.play('uiPopupClose');
  }

  _setSettingsBlurEnabled(enabled) {
    if (!this.app || !this.world || !this._settingsBlurLayer || !this._settingsBlurSprite) return;
    if (enabled) {
      const renderer = this.app.renderer;
      const width = Math.ceil(this.app.screen.width);
      const height = Math.ceil(this.app.screen.height);
      if (!this._settingsBlurTexture) {
        this._settingsBlurTexture = RenderTexture.create({ width, height, dynamic: true });
      } else {
        this._settingsBlurTexture.resize(width, height);
      }
      renderer.render({ container: this.world, target: this._settingsBlurTexture, clear: true });
      this._settingsBlurSprite.texture = this._settingsBlurTexture;
      this._settingsBlurLayer.visible = true;
      return;
    }
    this._settingsBlurLayer.visible = false;
  }

  _normalizeMatrix(matrix) {
    const unpacked = toMatrix(matrix);
    if (!Array.isArray(unpacked) || unpacked.length !== gameConfig.reelCount) return null;
    const normalized = unpacked.map((col) => {
      if (!Array.isArray(col) || col.length !== gameConfig.rowCount) return null;
      const values = col.map((symbol) => Number(symbol));
      if (values.some((v) => !Number.isFinite(v))) return null;
      return values;
    });
    if (normalized.some((col) => col == null)) return null;
    return normalized;
  }

  _applyExpandingWild(grid, expandingWild) {
    if (!Array.isArray(grid)) return null;
    if (!Array.isArray(expandingWild) || !expandingWild.length) return grid;
    const next = grid.map((col) => [...col]);
    for (let reel = 0; reel < Math.min(next.length, expandingWild.length); reel++) {
      const wildSymbolId = Number(expandingWild[reel]);
      if (!Number.isFinite(wildSymbolId) || wildSymbolId <= 0) continue;
      for (let row = 0; row < next[reel].length; row++) {
        next[reel][row] = wildSymbolId;
      }
    }
    return next;
  }

  _mapBackendWinsToHits(wins, grid) {
    if (!Array.isArray(wins)) return [];
    const coinSymbolId = 10;
    const scatterSymbolId = 11;
    return wins
      .map((w) => {
        // Backend-authoritative mapping only: if required fields are missing/invalid,
        // skip this win instead of synthesizing fallback values on the client.
        const lineNo = Number(w?.line ?? w?.Line);
        if (!Number.isFinite(lineNo)) return null;
        const count = Math.max(
          0,
          Math.min(gameConfig.reelCount, Number(w?.count ?? w?.Count) || 0),
        );
        const payout = Number(w?.lineWinAmount ?? w?.LineWinAmount);
        if (!Number.isFinite(payout)) return null;
        const symbolId = Number(w?.symbol ?? w?.Symbol);
        if (!Number.isFinite(symbolId)) return null;
        const isCoinHit = symbolId === coinSymbolId && count >= 3;
        const isScatterHit = symbolId === scatterSymbolId && count >= 3;
        const isSymbolOnlyHit = isCoinHit || isScatterHit;
        let paylineIndex = null;
        let cells = [];

        if (isSymbolOnlyHit) {
          // Coin/scatter wins are symbol-only: animate each matching symbol, no line path.
          cells = this._cellsForSymbolFromGrid(grid, symbolId, count);
        } else {
          paylineIndex = Math.max(0, lineNo - 1);
          const line = gameConfig.paylines[paylineIndex];
          if (!line) return null;
          cells = this._cellsFromBackendSpin({
            grid,
            count,
            lineTemplate: line,
          });
        }

        if (!cells.length) return null;
        return {
          paylineIndex,
          symbolId,
          count,
          payout,
          cells,
          displayMode: isSymbolOnlyHit ? 'symbols' : 'line',
        };
      })
      .filter(Boolean);
  }

  _cellsFromBackendSpin({ grid, count, lineTemplate }) {
    if (!Array.isArray(grid) || count <= 0) return [];
    const cells = [];
    for (let reel = 0; reel < count; reel++) {
      const col = grid[reel];
      if (!Array.isArray(col)) return [];
      const targetRow = lineTemplate[reel];
      if (!Number.isInteger(targetRow) || targetRow < 0 || targetRow >= col.length) return [];
      cells.push({ reel, row: targetRow });
    }
    return cells;
  }

  _cellsForSymbolFromGrid(grid, symbolId, count) {
    if (!Array.isArray(grid) || count <= 0) return [];
    const cells = [];
    for (let reel = 0; reel < grid.length; reel++) {
      const col = grid[reel];
      if (!Array.isArray(col)) continue;
      for (let row = 0; row < col.length; row++) {
        if (Number(col[row]) === symbolId) {
          cells.push({ reel, row });
          if (cells.length >= count) return cells;
        }
      }
    }
    return cells;
  }

  destroy() {
    if (this._resizeRaf != null) cancelAnimationFrame(this._resizeRaf);
    this._resizeRaf = null;
    if (this._orientTimer) clearTimeout(this._orientTimer);
    this._orientTimer = null;
    if (this._autoplayTimer != null) clearTimeout(this._autoplayTimer);
    this._autoplayTimer = null;
    this._idleReloadDispose?.();
    this._idleReloadDispose = null;
    this._landscapeBlocker?.dispose?.();
    this._landscapeBlocker = null;
    this._hubMonitorDispose?.();
    this._hubMonitorDispose = null;
    this._hideConnectionRestoredToast();
    this._stopConnectionAutoRetry();
    this._clearConnectionLostGraceTimer();
    this._clearDisconnectResolveTimer();
    if (this._idleTimeoutOverlay) {
      this._idleTimeoutOverlay.remove();
      this._idleTimeoutOverlay = null;
    }
    this._stopWinRollup();
    this._stopQbScrollInertia();
    this._stopSpinHoldTracking();
    this._resetSpinButtonHoldVisual();
    this._spinHoldTriggeredTurbo = false;
    this._turboHoldActive = false;
    this._autoplaying = false;
    window.removeEventListener('resize', this._onResize);
    window.visualViewport?.removeEventListener('resize', this._onResize);
    window.removeEventListener('orientationchange', this._onOrientation);
    document.removeEventListener('fullscreenchange', this._onResize);
    window.removeEventListener('keydown', this._onKey);
    this._detachWinCollectWindowPointerGuard();
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._settingsRoot?.destroy({ children: true });
    this._settingsRoot = null;
    this._settingsPanelRoot = null;
    this._settingsModalRefs = null;
    this._autoplayModalLayoutParts = null;
    this._infoSettingsLayoutRefs = null;
    this._infoScrollContentHeight = 0;
    this._infoScrollClamp = null;
    this._backdropRoot?.destroy({ children: true });
    this._backdropRoot = null;
    this._betPopupDimRoot?.destroy({ children: true });
    this._betPopupDimRoot = null;
    this._betPopupPanelRoot?.destroy({ children: true });
    this._betPopupPanelRoot = null;
    this._betPopupDim = null;
    this._betPopupChipsArea = null;
    this._betPopupBgSprite = null;
    this._betPopupBgTexDefault = null;
    this._betPopupBgTexMobile = null;
    this._betPopupBgTexTablet = null;
    this._betPopupTitle = null;
    this._betPopupCloseBtn = null;
    this._betPopupPanel = null;
    this._betPopupPanelFill = null;
    this._betPopupRoundMask = null;
    this._betPopupPanelBorder = null;
    this._betPopupTriggerBtn = null;
    this._hideAutoplayPicker();
    this._autoplayPixiDim = null;
    this._betTapZone?.remove();
    this._betTapZone = null;
    this._controlObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    this._controlObjectUrls = [];
    this._hideLoading();
    this._connectionLostEnsurePromise = null;
    this._connectionLostShowPromise = null;
    this._connectionLostRoot?.destroy({ children: true });
    this._connectionLostRoot = null;
    this._connectionLostPanelRoot = null;
    this._connLostIconTex = null;
    this._connLostPanel = null;
    this._connLostPanelBg = null;
    this._connLostBorderBase = null;
    this._connLostTopBorder = null;
    this._connLostBottomBorder = null;
    this._connLostIconWrap = null;
    this._connLostTitleText = null;
    this._connLostBodyText = null;
    this._connLostStatusText = null;
    this._connLostReloadBtn = null;
    this._connLostReloadBtnBg = null;
    this._connLostReloadLabel = null;
    this.animationController.killAll();
    this.backend.disconnect().catch(() => {});
    this.app?.destroy(true, { children: true, texture: false });
  }
}
